var __defProp = Object.defineProperty;
var __export = (target, all) => {
  for (var name in all)
    __defProp(target, name, { get: all[name], enumerable: true });
};

// src/gemini/gemini.service.ts
import { Logger } from "@boosting/core";

// src/gemini/gemini.types.ts
var DEFAULT_GEMINI_MODEL = "gemini-3-flash-preview";
var GEMINI3_MODELS = {
  FLASH: "gemini-3-flash-preview",
  PRO: "gemini-3.1-pro-preview"
};
var LEGACY_MODELS = {
  FLASH_2_5: "gemini-2.5-flash",
  PRO_2_5: "gemini-2.5-pro"
};
var GEMINI_ERROR_CODES = {
  INVALID_ARGUMENT: "invalid-argument",
  FAILED_PRECONDITION: "failed-precondition",
  INTERNAL: "internal",
  UNAVAILABLE: "unavailable",
  UNAUTHENTICATED: "unauthenticated",
  PERMISSION_DENIED: "permission-denied",
  RESOURCE_EXHAUSTED: "resource-exhausted",
  DEADLINE_EXCEEDED: "deadline-exceeded"
};

// src/gemini/gemini.service.ts
var logger = Logger.getLogger("GeminiService");
var handleGeminiError = (error, defaultMessage) => {
  logger.error("Error calling Gemini Cloud Function", error instanceof Error ? error : new Error(String(error)), {
    code: error?.code,
    message: error?.message
  });
  if (error.code === GEMINI_ERROR_CODES.FAILED_PRECONDITION) {
    return "Error de configuraci\xF3n del servidor: La clave de la API de Gemini no est\xE1 configurada.";
  }
  if (error.code === GEMINI_ERROR_CODES.INTERNAL) {
    return `Error interno del servidor. La IA puede haber fallado al procesar la solicitud. Detalles: ${error.message}`;
  }
  if (error.code === GEMINI_ERROR_CODES.UNAVAILABLE) {
    return "El servicio no est\xE1 disponible en este momento. Por favor, int\xE9ntalo m\xE1s tarde.";
  }
  if (error.code === GEMINI_ERROR_CODES.DEADLINE_EXCEEDED) {
    return "La solicitud tard\xF3 demasiado. Intenta con menos datos o m\xE1s tarde.";
  }
  return error.message || defaultMessage;
};
var GeminiService = class {
  callGemini;
  config;
  /**
   * Creates a new GeminiService instance
   * 
   * @param callGemini - The Cloud Function callable reference
   * @param config - Optional configuration
   */
  constructor(callGemini, config) {
    this.callGemini = callGemini;
    this.config = {
      projectId: config?.projectId || "",
      location: config?.location || "us-central1",
      timeout: config?.timeout || 3e5,
      // 5 minutes default
      maxRetries: config?.maxRetries || 0
    };
  }
  /**
   * Transcribes audio using Gemini
   * 
   * @param audioBase64 - Base64 encoded audio data
   * @param mimeType - MIME type of the audio
   * @returns Transcribed text or error message
   */
  async transcribeAudio(audioBase64, mimeType) {
    if (!audioBase64 || !mimeType) {
      return "No se proporcion\xF3 audio para transcribir.";
    }
    try {
      const result = await this.callGemini({
        task: "transcribeAudio",
        data: { audioBase64, mimeType }
      });
      return result.data.trim();
    } catch (error) {
      return handleGeminiError(
        error,
        "Hubo un error al transcribir el audio a trav\xE9s del servidor."
      );
    }
  }
  /**
   * Generates practices from a document (multi-page support)
   * 
   * @param documentParts - Array of image parts (base64 with mimeType)
   * @param categoryId - Optional category to focus generation
   * @returns Array of suggested practices or error string
   */
  async generatePracticesFromDocument(documentParts, categoryId) {
    try {
      const result = await this.callGemini({
        task: "generatePracticesFromDocument",
        data: { documentParts, categoryId }
      });
      return result.data;
    } catch (error) {
      return handleGeminiError(
        error,
        "Error al comunicarse con la IA a trav\xE9s del servidor."
      );
    }
  }
  /**
   * Generates a genogram structure from text description
   * 
   * @param familyDescription - Free text description of family structure
   * @param patientName - Optional patient name
   * @param existingMembers - Optional existing family members
   * @returns Generated genogram structure or error string
   */
  async generateGenogramFromText(familyDescription, patientName, existingMembers) {
    try {
      const result = await this.callGemini({
        task: "generateGenogramFromText",
        data: { familyDescription, patientName, existingMembers }
      });
      return result.data;
    } catch (error) {
      return handleGeminiError(
        error,
        "Error al generar el genograma con IA."
      );
    }
  }
  /**
   * Generic content generation
   * 
   * @param prompt - The prompt to send
   * @param options - Generation options
   * @returns Generated content or error
   */
  async generateContent(prompt, options) {
    try {
      const result = await this.callGemini({
        task: options?.task || "generateContent",
        data: {
          prompt,
          model: options?.model,
          temperature: options?.temperature,
          maxTokens: options?.maxTokens
        }
      });
      return result;
    } catch (error) {
      logger.error("Failed to generate content", error);
      throw error;
    }
  }
  /**
   * Executes a generic task with arbitrary data
   * 
   * @param task - The task name
   * @param data - The data payload
   * @returns The result data
   */
  async executeTask(task, data) {
    try {
      const result = await this.callGemini({ task, data });
      return result.data;
    } catch (error) {
      logger.error(`Failed to execute task ${task}`, error);
      throw error;
    }
  }
};
function createGeminiService(callGemini, config) {
  return new GeminiService(callGemini, config);
}

// src/ocr/ocr.service.ts
import { Logger as Logger2 } from "@boosting/core";

// src/ocr/ocr.types.ts
var DEFAULT_OCR_CONFIG = {
  region: "europe-west1",
  imageTimeout: 12e4,
  // 2 minutes
  pdfTimeout: 18e4,
  // 3 minutes
  batchSize: 3,
  maxImageSizeMB: 10
};
var OCR_ERROR_MESSAGES = {
  EMPTY_IMAGE: "La imagen est\xE1 vac\xEDa o es muy peque\xF1a. Intenta con otra imagen.",
  IMAGE_TOO_LARGE: "La imagen es demasiado grande. M\xE1ximo 10MB.",
  DEADLINE_EXCEEDED: "La solicitud tard\xF3 demasiado. Intenta con una imagen m\xE1s peque\xF1a o mejor iluminada.",
  SERVICE_UNAVAILABLE: "Servicio no disponible. Intenta de nuevo en unos segundos.",
  INTERNAL_ERROR: "Error interno. La imagen puede ser dif\xEDcil de leer.",
  PDF_TOO_LARGE: "El PDF es demasiado grande o tiene un formato no soportado.",
  PDF_ACCESS_ERROR: "No se pudo acceder al PDF. Verifica que el archivo existe.",
  RATE_LIMIT: "Se alcanz\xF3 el l\xEDmite de la API. Intenta de nuevo en unos minutos.",
  INVALID_PDF: "El PDF no es v\xE1lido o no se puede procesar.",
  NO_IMAGES: "No hay im\xE1genes para procesar.",
  GENERIC: "Error al procesar. Intenta de nuevo o usa otro archivo."
};

// src/ocr/ocr.service.ts
var logger2 = Logger2.getLogger("OCRService");
var OCRService = class {
  callGemini;
  config;
  /**
   * Creates a new OCRService instance
   * 
   * @param callGemini - Cloud Function callable for Gemini
   * @param config - Optional configuration overrides
   */
  constructor(callGemini, config) {
    this.callGemini = callGemini;
    this.config = { ...DEFAULT_OCR_CONFIG, ...config };
  }
  // ============================================
  // UTILITY METHODS
  // ============================================
  /**
   * Extracts pure base64 from a data URL
   */
  extractBase64(imageData) {
    if (imageData.startsWith("data:")) {
      const base64Part = imageData.split(",")[1];
      if (base64Part) return base64Part;
    }
    return imageData;
  }
  /**
   * Detects MIME type from URL or data
   */
  detectMimeType(imageUrl, fallback = "image/jpeg") {
    if (imageUrl.startsWith("data:")) {
      const match = imageUrl.match(/data:([^;]+);/);
      if (match) return match[1];
    }
    const urlLower = imageUrl.toLowerCase();
    if (urlLower.includes(".png")) return "image/png";
    if (urlLower.includes(".gif")) return "image/gif";
    if (urlLower.includes(".webp")) return "image/webp";
    if (urlLower.includes(".jpg") || urlLower.includes(".jpeg")) return "image/jpeg";
    return fallback;
  }
  /**
   * Validates image size from base64
   */
  validateImageSize(base64) {
    const sizeMB = base64.length * 0.75 / (1024 * 1024);
    logger2.info("Image size validation", { sizeMB: sizeMB.toFixed(2) });
    if (sizeMB > (this.config.maxImageSizeMB || 10)) {
      throw new Error(OCR_ERROR_MESSAGES.IMAGE_TOO_LARGE);
    }
  }
  // ============================================
  // PROCESSING METHODS
  // ============================================
  /**
   * Processes a single image with Gemini Vision via Cloud Function
   * Supports both data URLs (base64) and Firebase Storage URLs
   */
  async processImage(imageData, mimeType = "image/jpeg") {
    const startTime = Date.now();
    logger2.info("Processing image with OCR", { dataLength: imageData.length, mimeType });
    try {
      const actualMimeType = this.detectMimeType(imageData, mimeType);
      let imageBase64;
      let imageUrl;
      if (imageData.startsWith("http://") || imageData.startsWith("https://")) {
        logger2.info("Image is a URL, sending to Cloud Function for download");
        imageUrl = imageData;
      } else {
        imageBase64 = this.extractBase64(imageData);
        if (!imageBase64 || imageBase64.length < 100) {
          throw new Error(OCR_ERROR_MESSAGES.EMPTY_IMAGE);
        }
        this.validateImageSize(imageBase64);
      }
      const result = await this.callGemini({
        task: "processImageOCR",
        data: {
          imageBase64,
          imageUrl,
          mimeType: actualMimeType
        }
      });
      const ocrData = result.data;
      const ocrResult = {
        extractedText: ocrData.extractedText || "",
        language: ocrData.language || "es",
        confidence: ocrData.confidence || 0.8,
        summary: ocrData.summary,
        suggestedPractices: ocrData.practices || ocrData.suggestedPractices,
        suggestedAmar: ocrData.suggestedAmar
      };
      const elapsed = Date.now() - startTime;
      logger2.info("Image processed successfully", {
        textLength: ocrResult.extractedText.length,
        language: ocrResult.language,
        confidence: ocrResult.confidence,
        elapsedMs: elapsed
      });
      return ocrResult;
    } catch (error) {
      const elapsed = Date.now() - startTime;
      logger2.error("Failed to process image", error, { elapsedMs: elapsed });
      throw new Error(this.getFriendlyErrorMessage(error));
    }
  }
  /**
   * Processes a PDF using Cloud Functions
   */
  async processPDF(pdfUrl) {
    logger2.info("Processing PDF via Cloud Functions", { pdfUrl });
    try {
      const result = await this.callGemini({
        task: "processPDF",
        data: { pdfUrl }
      });
      const data = result.data;
      logger2.info("PDF processed successfully", data);
      return {
        extractedText: data.extractedText || "No se pudo extraer texto del PDF",
        language: data.language || "es",
        confidence: data.confidence || 0.7,
        summary: data.summary,
        suggestedPractices: data.keyPoints,
        suggestedAmar: void 0
      };
    } catch (error) {
      logger2.error("Failed to process PDF", error, { pdfUrl });
      throw new Error(this.getPDFErrorMessage(error));
    }
  }
  /**
   * Processes multiple images (e.g., multiple pages)
   * Processes in parallel with batching to avoid API saturation
   */
  async processMultipleImages(imageUrls) {
    logger2.info("Processing multiple images", { count: imageUrls.length });
    if (imageUrls.length === 0) {
      throw new Error(OCR_ERROR_MESSAGES.NO_IMAGES);
    }
    if (imageUrls.length === 1) {
      return this.processImage(imageUrls[0]);
    }
    try {
      const batchSize = this.config.batchSize || 3;
      const results = [];
      const errors = [];
      for (let i = 0; i < imageUrls.length; i += batchSize) {
        const batch = imageUrls.slice(i, i + batchSize);
        logger2.info(`Processing batch ${Math.floor(i / batchSize) + 1}`, {
          batchSize: batch.length,
          totalProcessed: i
        });
        const batchPromises = batch.map(async (url, idx) => {
          try {
            return await this.processImage(url);
          } catch (error) {
            logger2.error(`Failed to process image ${i + idx + 1}`, error);
            errors.push(`Imagen ${i + idx + 1}: ${error.message}`);
            return null;
          }
        });
        const batchResults = await Promise.all(batchPromises);
        results.push(...batchResults.filter((r) => r !== null));
      }
      if (results.length === 0) {
        throw new Error(`No se pudo procesar ninguna imagen. Errores: ${errors.join("; ")}`);
      }
      if (errors.length > 0) {
        logger2.warn("Some images failed to process", {
          successCount: results.length,
          errorCount: errors.length
        });
      }
      const combinedText = results.map((r, i) => `[Imagen ${i + 1}]
${r.extractedText}`).join("\n\n---\n\n");
      const avgConfidence = results.reduce((sum, r) => sum + r.confidence, 0) / results.length;
      const allPractices = results.flatMap((r) => r.suggestedPractices || []);
      const uniquePractices = [...new Set(allPractices)];
      logger2.info("Multiple images processed successfully", {
        totalImages: imageUrls.length,
        successfulImages: results.length,
        failedImages: errors.length,
        avgConfidence
      });
      return {
        extractedText: combinedText,
        language: results[0]?.language || "es",
        confidence: avgConfidence,
        summary: results.find((r) => r.summary)?.summary,
        suggestedPractices: uniquePractices.length > 0 ? uniquePractices : void 0,
        suggestedAmar: results.find((r) => r.suggestedAmar)?.suggestedAmar
      };
    } catch (error) {
      logger2.error("Failed to process multiple images", error);
      throw new Error(`Error al procesar ${imageUrls.length} im\xE1genes: ${error.message}`);
    }
  }
  /**
   * Processes an image specifically for invoice data extraction
   */
  async extractInvoiceData(imageData) {
    try {
      const result = await this.callGemini({
        task: "extractInvoiceData",
        data: {
          imageBase64: this.extractBase64(imageData),
          mimeType: this.detectMimeType(imageData)
        }
      });
      return result.data;
    } catch (error) {
      logger2.error("Failed to extract invoice data", error);
      throw new Error("Error al extraer datos de la factura");
    }
  }
  // ============================================
  // ERROR HANDLING
  // ============================================
  getFriendlyErrorMessage(error) {
    if (error.code === "functions/deadline-exceeded" || error.message?.includes("deadline")) {
      return OCR_ERROR_MESSAGES.DEADLINE_EXCEEDED;
    }
    if (error.code === "functions/unavailable") {
      return OCR_ERROR_MESSAGES.SERVICE_UNAVAILABLE;
    }
    if (error.code === "functions/internal") {
      return OCR_ERROR_MESSAGES.INTERNAL_ERROR;
    }
    if (error.message?.includes("vac\xEDa") || error.message?.includes("peque\xF1a") || error.message?.includes("grande")) {
      return error.message;
    }
    return error.message || OCR_ERROR_MESSAGES.GENERIC;
  }
  getPDFErrorMessage(error) {
    if (error.code === "functions/internal") {
      return OCR_ERROR_MESSAGES.PDF_TOO_LARGE;
    }
    if (error.code === "functions/failed-precondition") {
      return error.message || OCR_ERROR_MESSAGES.PDF_ACCESS_ERROR;
    }
    if (error.code === "functions/resource-exhausted") {
      return OCR_ERROR_MESSAGES.RATE_LIMIT;
    }
    if (error.code === "functions/invalid-argument") {
      return OCR_ERROR_MESSAGES.INVALID_PDF;
    }
    return error.message || OCR_ERROR_MESSAGES.GENERIC;
  }
};
function createOCRService(callGemini, config) {
  return new OCRService(callGemini, config);
}

// src/costs/cost-tracker.ts
import { Logger as Logger3 } from "@boosting/core";

// src/costs/cost.types.ts
var GEMINI_PRICING = {
  "gemini-3-pro-preview": {
    model: "gemini-3-pro-preview",
    input: { pricePerMillionTokens: 1.5, pricePerMillionTokensAbove200k: 3, base: 1.5, above200k: 3 },
    output: { pricePerMillionTokens: 12, pricePerMillionTokensAbove200k: 18, base: 12, above200k: 18 }
  },
  "gemini-3-flash": {
    model: "gemini-3-flash",
    input: { pricePerMillionTokens: 0.1, pricePerMillionTokensAbove200k: 0.2, base: 0.1, above200k: 0.2 },
    output: { pricePerMillionTokens: 0.4, pricePerMillionTokensAbove200k: 0.8, base: 0.4, above200k: 0.8 }
  },
  "gemini-3-flash-preview": {
    model: "gemini-3-flash-preview",
    input: { pricePerMillionTokens: 0.1, pricePerMillionTokensAbove200k: 0.2, base: 0.1, above200k: 0.2 },
    output: { pricePerMillionTokens: 0.4, pricePerMillionTokensAbove200k: 0.8, base: 0.4, above200k: 0.8 }
  },
  "gemini-2.5-pro": {
    model: "gemini-2.5-pro",
    input: { pricePerMillionTokens: 1.25, pricePerMillionTokensAbove200k: 2.5, base: 1.25, above200k: 2.5 },
    output: { pricePerMillionTokens: 10, pricePerMillionTokensAbove200k: 15, base: 10, above200k: 15 }
  },
  "gemini-2.5-flash": {
    model: "gemini-2.5-flash",
    input: { pricePerMillionTokens: 0.075, pricePerMillionTokensAbove200k: 0.15, base: 0.075, above200k: 0.15 },
    output: { pricePerMillionTokens: 0.3, pricePerMillionTokensAbove200k: 0.6, base: 0.3, above200k: 0.6 }
  }
};
var EMBEDDING_PRICING = {
  "text-embedding-004": 25e-5,
  // $0.25 per 1M tokens
  "text-embedding-005": 25e-5,
  "textembedding-gecko": 1e-4
};
var VISION_API_PRICING = {
  pricePerUnit: 15e-4,
  features: {
    "document-text-detection": { displayName: "Document Text Detection (OCR)", pricePerUnit: 15e-4 },
    "text-detection": { displayName: "Text Detection", pricePerUnit: 15e-4 },
    "label-detection": { displayName: "Label Detection", pricePerUnit: 15e-4 },
    "face-detection": { displayName: "Face Detection", pricePerUnit: 15e-4 }
  }
};
var ELEVENLABS_PRICING = {
  /** Text-to-Speech: price per 1000 characters */
  tts: {
    standard: 0.3,
    // $0.30 per 1K chars (Starter plan rate)
    premium: 0.18
    // $0.18 per 1K chars (Creator+ plan rate)
  },
  /** Dubbing: price per minute of video */
  dubbing: {
    standard: 2.5,
    // ~$2.50 per minute estimated
    premium: 1.8
    // Premium tier
  },
  /** Voice cloning (per voice created) */
  voiceCloning: {
    instant: 0,
    // Free with subscription
    professional: 50
    // $50 per professional clone
  }
};
var RASK_PRICING = {
  /** Price per minute of video */
  dubbing: {
    basic: 1,
    // Basic tier
    pro: 0.75
    // Pro tier
  }
};
var WHISPER_PRICING = {
  /** Price per minute of audio */
  transcription: 6e-3
  // $0.006 per minute
};
var KLING_PRICING = {
  /** Price per second of generated video */
  video: {
    "kling-3.0-pro": 0.15,
    // $0.15/s — best product fidelity
    "kling-2.0": 0.08
    // $0.08/s — older model
  },
  /** fal.ai gateway overhead (if used) */
  falGatewayMarkup: 0
  // Currently no additional markup
};
var RUNWAY_PRICING = {
  /** Price per second of generated video */
  video: {
    "gen4.5": 0.05,
    // $0.05/s — current best
    "gen3a_turbo": 0.025
    // $0.025/s — legacy cheaper option
  }
};
var SORA_PRICING = {
  /** Price per second of generated video */
  video: {
    "sora-2": 0.1,
    // $0.10/s — standard (720p)
    "sora-2-pro": 0.3,
    // $0.30/s — pro standard (720p)
    "sora-2-pro-hd": 0.5
    // $0.50/s — pro high-res (1080p+)
  }
};
var SEEDANCE_PRICING = {
  /** Estimated price per second of generated video */
  video: {
    "seedance-2.0": 0.12
    // $0.12/s — estimated, TBD
  }
};
var VEO_EXTERNAL_PRICING = {
  /** Price per second of generated video */
  video: {
    "veo-3.1-generate-preview": 0.3,
    "veo-3.0-generate-001": 0.25,
    "veo-3.0-fast-generate-001": 0.1,
    "veo-3-generate": 0.25,
    "veo-3-fast": 0.1
  }
};
var YOUTUBE_API_PRICING = {
  /** Quota units per operation */
  quotaUnits: {
    search: 100,
    videosList: 1,
    channelsList: 1,
    captionsList: 50,
    captionsDownload: 200
  },
  /** Daily quota limit */
  dailyQuotaLimit: 1e4
};
function calculateExternalAPICost(provider, service, units, tier = "standard") {
  switch (provider) {
    case "elevenlabs": {
      if (service === "tts") {
        const rate = ELEVENLABS_PRICING.tts[tier];
        return units / 1e3 * rate;
      }
      if (service === "dubbing") {
        const rate = ELEVENLABS_PRICING.dubbing[tier];
        return units * rate;
      }
      return 0;
    }
    case "rask": {
      if (service === "dubbing") {
        const rate = RASK_PRICING.dubbing[tier === "premium" ? "pro" : "basic"];
        return units * rate;
      }
      return 0;
    }
    case "whisper": {
      return units * WHISPER_PRICING.transcription;
    }
    case "kling": {
      if (service === "video") {
        const modelRate = KLING_PRICING.video[tier === "premium" ? "kling-3.0-pro" : "kling-3.0-pro"] || 0.15;
        return units * modelRate;
      }
      return 0;
    }
    case "fal_ai": {
      if (service === "kling_video") {
        const rate = KLING_PRICING.video["kling-3.0-pro"] + KLING_PRICING.falGatewayMarkup;
        return units * rate;
      }
      return 0;
    }
    case "runway": {
      if (service === "video") {
        const rate = RUNWAY_PRICING.video[tier === "premium" ? "gen4.5" : "gen3a_turbo"];
        return units * rate;
      }
      return 0;
    }
    case "sora": {
      if (service === "video") {
        const rate = SORA_PRICING.video[tier === "premium" ? "sora-2-pro" : "sora-2"];
        return units * rate;
      }
      return 0;
    }
    case "seedance": {
      if (service === "video") {
        return units * SEEDANCE_PRICING.video["seedance-2.0"];
      }
      return 0;
    }
    case "google_veo": {
      if (service === "video") {
        return units * (VEO_EXTERNAL_PRICING.video["veo-3-generate"] || 0.25);
      }
      return 0;
    }
    case "youtube": {
      return 0;
    }
    default:
      return 0;
  }
}
var MODEL_LABELS = {
  // Google AI
  "gemini-3-pro-preview": "\u{1F9E0} Gemini 3 Pro Preview",
  "gemini-3-flash": "\u26A1 Gemini 3 Flash",
  "gemini-3-flash-preview": "\u26A1 Gemini 3 Flash Preview",
  "gemini-2.5-pro": "\u{1F9E0} Gemini 2.5 Pro",
  "gemini-2.5-flash": "\u26A1 Gemini 2.5 Flash",
  "vision-api": "\u{1F441}\uFE0F Cloud Vision API",
  "text-embedding-004": "\u{1F9EC} Text Embedding 004",
  "text-embedding-005": "\u{1F9EC} Text Embedding 005",
  "textembedding-gecko": "\u{1F98E} Embedding Gecko",
  // Google Video (Veo)
  "veo-3-generate": "\u{1F3AC} Veo 3 (Contexto)",
  "veo-3-fast": "\u26A1 Veo 3 Fast",
  "veo-3.1-generate-preview": "\u{1F3AC} Veo 3.1 Preview (I2V)",
  // Kling (Product Video)
  "kling-3.0-pro": "\u{1F4E6} Kling 3.0 Pro (Producto)",
  "kling-2.0": "\u{1F4E6} Kling 2.0",
  // Runway (Draft Video)
  "gen4.5": "\u{1F3AC} Runway Gen-4.5",
  "gen3a_turbo": "\u{1F3C3} Runway Gen-3a Turbo (Budget)",
  // Sora (Cinematic Video)
  "sora-2-pro": "\u{1F3A5} Sora 2 Pro",
  "sora-2": "\u{1F4F9} Sora 2",
  // Seedance (Branded Video - Future)
  "seedance-2.0": "\u{1F331} Seedance 2.0 (Pendiente)",
  // External providers
  "elevenlabs-tts": "\u{1F50A} ElevenLabs TTS",
  "elevenlabs-dubbing": "\u{1F3AC} ElevenLabs Dubbing",
  "rask-dubbing": "\u{1F310} Rask Dubbing",
  "whisper-transcription": "\u{1F4DD} Whisper Transcription",
  "youtube-api": "\u{1F4FA} YouTube Data API"
};

// src/costs/cost-calculator.ts
function calculateCost(inputTokens, outputTokens, model) {
  const pricing = GEMINI_PRICING[model] || GEMINI_PRICING["gemini-3-flash"];
  const calcCost = (tokens, tier) => {
    if (tokens <= 2e5) {
      return tokens / 1e6 * tier.pricePerMillionTokens;
    }
    const baseCost = 2e5 / 1e6 * tier.pricePerMillionTokens;
    const extraCost = (tokens - 2e5) / 1e6 * (tier.pricePerMillionTokensAbove200k || tier.pricePerMillionTokens);
    return baseCost + extraCost;
  };
  const inputCost = calcCost(inputTokens, pricing.input);
  const outputCost = calcCost(outputTokens, pricing.output);
  return {
    inputCost: +inputCost.toFixed(6),
    outputCost: +outputCost.toFixed(6),
    totalCost: +(inputCost + outputCost).toFixed(6)
  };
}
function calculateEmbeddingCost(inputCharacters, model) {
  const estimatedTokens = Math.ceil(inputCharacters / 4);
  const pricing = EMBEDDING_PRICING[model] || EMBEDDING_PRICING["text-embedding-004"];
  return estimatedTokens / 1e3 * pricing;
}

// src/costs/cost-analytics.ts
var CostAnalytics = class {
  db;
  COLLECTION = "ai_usage_logs";
  constructor(db) {
    this.db = db;
  }
  /**
   * Gets usage summary for a user
   */
  async getUsageSummary(userId, period, profileId) {
    const { startDate, endDate } = this.getPeriodDates(period);
    let query = this.db.collection(this.COLLECTION).where("userId", "==", userId);
    if (period !== "total") {
      query = query.where("timestamp", ">=", startDate).where("timestamp", "<=", endDate);
    }
    const snap = await query.get();
    let logs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    if (profileId) {
      logs = logs.filter((l) => l.profileId === profileId);
    }
    return this.buildSummary(userId, period, startDate, endDate, logs, profileId);
  }
  /**
   * Gets global usage summary (all users)
   */
  async getGlobalUsageSummary(period) {
    const { startDate, endDate } = this.getPeriodDates(period);
    let query = this.db.collection(this.COLLECTION);
    if (period !== "total") {
      query = query.where("timestamp", ">=", startDate).where("timestamp", "<=", endDate);
    }
    const snap = await query.get();
    const logs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    return this.buildSummary("GLOBAL", period, startDate, endDate, logs);
  }
  /**
   * Gets usage history for a user
   */
  async getUsageHistory(userId, limit = 50, profileId) {
    const snap = await this.db.collection(this.COLLECTION).where("userId", "==", userId).orderBy("timestamp", "desc").limit(limit).get();
    let logs = snap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
      timestamp: d.data().timestamp.toDate?.() || new Date(d.data().timestamp)
    }));
    return profileId ? logs.filter((l) => l.profileId === profileId) : logs;
  }
  /**
   * Gets top users by spend
   */
  async getTopUsersBySpend(period, limit = 10) {
    const { startDate, endDate } = this.getPeriodDates(period);
    let query = this.db.collection(this.COLLECTION);
    if (period !== "total") {
      query = query.where("timestamp", ">=", startDate).where("timestamp", "<=", endDate);
    }
    const snap = await query.get();
    const logs = snap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
      timestamp: d.data().timestamp?.toDate?.() || new Date(d.data().timestamp)
    }));
    const userStats = {};
    for (const log of logs) {
      if (!userStats[log.userId]) {
        userStats[log.userId] = { userId: log.userId, totalCost: 0, requestCount: 0, tasks: {}, lastUsage: log.timestamp };
      }
      userStats[log.userId].totalCost += log.totalCost;
      userStats[log.userId].requestCount++;
      userStats[log.userId].tasks[log.task] = (userStats[log.userId].tasks[log.task] || 0) + 1;
      if (log.timestamp > userStats[log.userId].lastUsage) {
        userStats[log.userId].lastUsage = log.timestamp;
      }
    }
    return Object.values(userStats).map((u) => ({
      userId: u.userId,
      totalCost: u.totalCost,
      requestCount: u.requestCount,
      topTask: Object.entries(u.tasks).sort((a, b) => b[1] - a[1])[0]?.[0] || "N/A",
      lastUsage: u.lastUsage
    })).sort((a, b) => b.totalCost - a.totalCost).slice(0, limit);
  }
  // ============================================
  // HELPERS
  // ============================================
  buildSummary(userId, period, startDate, endDate, logs, profileId) {
    const summary = {
      userId,
      profileId,
      period,
      startDate,
      endDate,
      totalInputTokens: 0,
      totalOutputTokens: 0,
      totalCost: 0,
      requestCount: logs.length,
      taskBreakdown: {}
    };
    logs.forEach((log) => {
      summary.totalInputTokens += log.inputTokens;
      summary.totalOutputTokens += log.outputTokens;
      summary.totalCost += log.totalCost;
      if (!summary.taskBreakdown[log.task]) {
        summary.taskBreakdown[log.task] = { count: 0, inputTokens: 0, outputTokens: 0, cost: 0 };
      }
      summary.taskBreakdown[log.task].count++;
      summary.taskBreakdown[log.task].inputTokens += log.inputTokens;
      summary.taskBreakdown[log.task].outputTokens += log.outputTokens;
      summary.taskBreakdown[log.task].cost += log.totalCost;
    });
    return summary;
  }
  getPeriodDates(period) {
    const now = /* @__PURE__ */ new Date();
    const endDate = now;
    let startDate = new Date(now);
    if (period === "daily") {
      startDate.setHours(0, 0, 0, 0);
    } else if (period === "weekly") {
      startDate.setDate(now.getDate() - 7);
      startDate.setHours(0, 0, 0, 0);
    } else if (period === "monthly") {
      startDate.setDate(1);
      startDate.setHours(0, 0, 0, 0);
    } else {
      startDate = /* @__PURE__ */ new Date(0);
    }
    return { startDate, endDate };
  }
};
function createCostAnalytics(db) {
  return new CostAnalytics(db);
}

// src/costs/cost-tracker.ts
var logger3 = Logger3.getLogger("CostTracker");
var CostTracker = class {
  db;
  analytics;
  COLLECTION = "ai_usage_logs";
  LIMITS_COLLECTION = "ai_usage_limits";
  constructor(db) {
    this.db = db;
    this.analytics = createCostAnalytics(db);
  }
  // ============================================
  // LOGGING METHODS
  // ============================================
  /**
   * Logs AI usage to Firestore
   */
  async logUsage(log) {
    try {
      const { inputCost, outputCost, totalCost } = calculateCost(
        log.inputTokens,
        log.outputTokens,
        log.model
      );
      await this.db.collection(this.COLLECTION).add({
        ...log,
        inputCost,
        outputCost,
        totalCost,
        timestamp: /* @__PURE__ */ new Date()
      });
      logger3.debug(`[AI Cost] ${log.task}: $${totalCost.toFixed(6)}`);
      await this.checkLimits(log.userId, log.profileId);
    } catch (error) {
      logger3.error("[AI Cost] Failed to log usage", error);
    }
  }
  /**
   * Tracks AI usage from Gemini result with usageMetadata
   */
  async trackAIUsage(params) {
    try {
      const usageMetadata = params.result?.response?.usageMetadata;
      if (usageMetadata) {
        await this.logUsage({
          userId: params.userId,
          task: params.task,
          model: params.model,
          inputTokens: usageMetadata.promptTokenCount || 0,
          outputTokens: usageMetadata.candidatesTokenCount || 0,
          inputCost: 0,
          outputCost: 0,
          totalCost: 0,
          metadata: params.metadata
        });
      }
    } catch (error) {
      logger3.error("[AI Cost] Failed to track usage", error);
    }
  }
  /**
   * Logs embedding usage
   */
  async logEmbeddingUsage(params) {
    try {
      const estimatedTokens = Math.ceil(params.inputCharacters / 4);
      const totalCost = calculateEmbeddingCost(params.inputCharacters, params.model);
      await this.db.collection(this.COLLECTION).add({
        userId: params.userId,
        timestamp: /* @__PURE__ */ new Date(),
        task: params.task,
        model: params.model,
        inputTokens: estimatedTokens,
        outputTokens: 0,
        inputCost: +totalCost.toFixed(8),
        outputCost: 0,
        totalCost: +totalCost.toFixed(8),
        metadata: {
          ...params.metadata,
          inputCharacters: params.inputCharacters,
          embeddingDimensions: params.embeddingDimensions,
          type: "embedding"
        }
      });
      logger3.debug(`[Embedding Cost] ${params.task}: $${totalCost.toFixed(8)}`);
    } catch (error) {
      logger3.error("[Embedding Cost] Failed to log usage", error);
    }
  }
  // ============================================
  // LIMITS
  // ============================================
  async setUsageLimit(limit) {
    await this.db.collection(this.LIMITS_COLLECTION).doc(limit.userId).set(limit);
  }
  async getUsageLimit(userId) {
    const doc = await this.db.collection(this.LIMITS_COLLECTION).doc(userId).get();
    return doc.exists ? doc.data() : null;
  }
  async checkLimits(userId, profileId) {
    try {
      const limit = await this.getUsageLimit(userId);
      if (!limit?.enabled) return;
      const periods = [
        { period: "daily", limit: limit.dailyLimit },
        { period: "weekly", limit: limit.weeklyLimit },
        { period: "monthly", limit: limit.monthlyLimit }
      ];
      for (const { period, limit: limitVal } of periods) {
        if (!limitVal) continue;
        const summary = await this.analytics.getUsageSummary(userId, period, profileId);
        if (summary.totalCost >= limitVal) {
          logger3.warn(`${period} limit reached`, { userId, cost: summary.totalCost, limit: limitVal });
        }
      }
    } catch {
    }
  }
  async getUsageStatus(userId, period, profileId) {
    const limit = await this.getUsageLimit(userId);
    if (!limit?.enabled) return null;
    const limitVal = period === "daily" ? limit.dailyLimit : period === "weekly" ? limit.weeklyLimit : limit.monthlyLimit;
    if (!limitVal) return null;
    const summary = await this.analytics.getUsageSummary(userId, period, profileId);
    const pct = summary.totalCost / limitVal * 100;
    return {
      period,
      currentUsage: summary.totalCost,
      limit: limitVal,
      percentage: +pct.toFixed(2),
      alertTriggered: pct >= limit.alertThreshold,
      limitReached: pct >= 100
    };
  }
  // ============================================
  // ANALYTICS PROXY
  // ============================================
  async getDashboardStats(period) {
    const [summary, topUsers] = await Promise.all([
      this.analytics.getGlobalUsageSummary(period),
      this.analytics.getTopUsersBySpend(period, 10)
    ]);
    return { summary, topUsers, costTrend: [], modelBreakdown: {} };
  }
  // ============================================
  // EXTERNAL API TRACKING
  // ============================================
  /**
   * Logs external API usage (ElevenLabs, Rask, Whisper, etc.)
   */
  async logExternalAPIUsage(params) {
    try {
      const totalCost = params.costOverride ?? calculateExternalAPICost(
        params.provider,
        params.service,
        params.units
      );
      await this.db.collection(EXTERNAL_API_COLLECTION).add({
        userId: params.userId,
        tenantId: params.tenantId,
        timestamp: /* @__PURE__ */ new Date(),
        provider: params.provider,
        service: params.service,
        task: params.task,
        units: params.units,
        unitType: params.unitType,
        totalCost: +totalCost.toFixed(6),
        metadata: params.metadata
      });
      logger3.debug(`[External API Cost] ${params.provider}/${params.service}: $${totalCost.toFixed(4)} (${params.units} ${params.unitType})`);
    } catch (error) {
      logger3.error("[External API Cost] Failed to log usage", error);
    }
  }
};
var EXTERNAL_API_COLLECTION = "external_api_usage_logs";
function createCostTracker(db) {
  return new CostTracker(db);
}
async function logAIUsage(db, params) {
  const tracker = new CostTracker(db);
  await tracker.logUsage({ ...params, inputCost: 0, outputCost: 0, totalCost: 0 });
}
async function trackAIUsage(db, params) {
  const tracker = new CostTracker(db);
  await tracker.trackAIUsage(params);
}
async function logEmbeddingUsage(db, params) {
  const tracker = new CostTracker(db);
  await tracker.logEmbeddingUsage(params);
}
async function trackExternalAPIUsage(db, params) {
  const tracker = new CostTracker(db);
  await tracker.logExternalAPIUsage(params);
}

// src/agents/research-agent.ts
import { Logger as Logger4 } from "@boosting/core";
var logger4 = Logger4.getLogger("ResearchAgent");
var ResearchAgent = class {
  gemini;
  intelligence;
  constructor(config) {
    this.gemini = config.gemini;
    this.intelligence = config.intelligence;
  }
  /**
   * AUTONOMOUS RESEARCH - Main entry point
   * 
   * Discovers everything needed without user input
   */
  async autonomousResearch(seed) {
    logger4.info("Starting autonomous research", { domain: seed.domain, niche: seed.niche });
    try {
      const [currentPosition, competitors] = await Promise.all([
        this.assessCurrentPosition(seed.domain),
        this.discoverCompetitors(seed)
      ]);
      logger4.info("Discovered competitors", { count: competitors.length });
      const competitorInsights = await this.analyzeAllCompetitors(competitors);
      const keywords = await this.discoverKeywords(seed, competitorInsights);
      logger4.info("Discovered keywords", { count: keywords.length });
      const serpInsights = await this.analyzeSERPs(keywords.slice(0, 20));
      const opportunities = await this.identifyOpportunities(
        seed.domain,
        keywords,
        competitorInsights
      );
      logger4.info("Research complete", {
        keywords: keywords.length,
        opportunities: opportunities.total
      });
      return {
        domain: seed.domain,
        currentPosition,
        competitors,
        competitorInsights,
        keywords,
        serpInsights,
        opportunities,
        timestamp: /* @__PURE__ */ new Date()
      };
    } catch (error) {
      logger4.error("Research failed", error);
      throw error;
    }
  }
  /**
   * Assess current domain position
   */
  async assessCurrentPosition(domain) {
    try {
      const backlinks = await this.intelligence.analyzeBacklinkProfile?.(domain);
      return {
        domain,
        authority: backlinks?.metrics?.domainRank || 0,
        avgPosition: 0,
        // Would need rank tracking
        traffic: 0,
        // Would need analytics integration
        indexedPages: 0,
        // Would need GSC integration
        backlinks: backlinks?.metrics?.totalBacklinks || 0
      };
    } catch {
      return {
        domain,
        authority: 0,
        avgPosition: 0,
        traffic: 0,
        indexedPages: 0,
        backlinks: 0
      };
    }
  }
  /**
   * Discover competitors automatically
   */
  async discoverCompetitors(seed) {
    logger4.info("Discovering competitors automatically");
    try {
      const direct = await this.intelligence.findCompetitors?.(seed.domain, { limit: 5 });
      if (direct && direct.length > 0) {
        logger4.info("Found direct competitors", { count: direct.length });
        return direct.map((c) => ({
          domain: c.domain,
          authority: 0,
          relevance: c.relevance || 0,
          discovered: "automatic"
        }));
      }
    } catch (error) {
      logger4.warn("Direct competitor discovery failed, using fallback", error);
    }
    const nicheKeywords = await this.generateNicheKeywords(seed.niche);
    logger4.info("Generated niche keywords", { keywords: nicheKeywords });
    const domainCounts = /* @__PURE__ */ new Map();
    for (const keyword of nicheKeywords.slice(0, 5)) {
      try {
        const relatedKeywords = await this.intelligence.analyzeCompetitor?.(
          seed.domain,
          { limit: 10 }
        );
        if (relatedKeywords) {
          relatedKeywords.forEach((kw) => {
            const domain = this.extractDomain(kw.url || "");
            if (domain && domain !== seed.domain) {
              domainCounts.set(domain, (domainCounts.get(domain) || 0) + 1);
            }
          });
        }
      } catch (error) {
        logger4.warn(`Failed to analyze keyword: ${keyword}`, error);
      }
    }
    const competitors = Array.from(domainCounts.entries()).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([domain, count]) => ({
      domain,
      authority: 0,
      relevance: count,
      discovered: "automatic"
    }));
    logger4.info("Discovered competitors through niche analysis", { count: competitors.length });
    return competitors.length > 0 ? competitors : this.getFallbackCompetitors(seed.niche);
  }
  /**
   * Generate initial keywords from niche description using AI
   */
  async generateNicheKeywords(niche) {
    const prompt = `From this business description: "${niche}"

Extract the 15 most important keywords that people would search on Google.

Examples:
- Niche: "connect patients with online psychologists"
  Keywords: ["online therapy", "online psychologist", "teletherapy", "mental health counseling", ...]

Respond with ONLY a JSON array of strings. No explanations.`;
    try {
      const result = await this.gemini.executeTask("generateContent", {
        prompt,
        temperature: 0.3
      });
      const cleaned = result.text.replace(/```json\n?/g, "").replace(/```\n?/g, "").trim();
      return JSON.parse(cleaned);
    } catch (error) {
      logger4.error("Failed to generate niche keywords", error);
      return [niche];
    }
  }
  /**
   * Analyze all competitors in parallel
   */
  async analyzeAllCompetitors(competitors) {
    logger4.info("Analyzing competitors", { count: competitors.length });
    const insights = await Promise.all(
      competitors.map(async (competitor) => {
        try {
          const keywords = await this.intelligence.analyzeCompetitor?.(
            competitor.domain,
            { limit: 50 }
          );
          return {
            domain: competitor.domain,
            topKeywords: keywords || [],
            contentStrategy: {
              avgWordCount: 1500,
              // Placeholder - would need scraping
              updateFrequency: "weekly",
              contentTypes: ["blog", "landing"]
            },
            backlinkProfile: {
              total: 0,
              referringDomains: 0,
              quality: "medium"
            }
          };
        } catch (error) {
          logger4.warn(`Failed to analyze competitor: ${competitor.domain}`, error);
          return {
            domain: competitor.domain,
            topKeywords: [],
            contentStrategy: {
              avgWordCount: 0,
              updateFrequency: "unknown",
              contentTypes: []
            },
            backlinkProfile: {
              total: 0,
              referringDomains: 0,
              quality: "low"
            }
          };
        }
      })
    );
    return insights;
  }
  /**
   * Discover keywords from multiple sources
   */
  async discoverKeywords(seed, competitorInsights) {
    logger4.info("Discovering keywords from all sources");
    const allKeywords = [];
    competitorInsights.forEach((insight) => {
      allKeywords.push(...insight.topKeywords);
    });
    logger4.info(`Collected ${allKeywords.length} keywords from competitors`);
    const nicheKeywords = await this.generateNicheKeywords(seed.niche);
    for (const kw of nicheKeywords.slice(0, 5)) {
      try {
        const opportunities = await this.intelligence.findOpportunities?.({
          seedKeywords: [kw],
          minVolume: 100,
          maxDifficulty: "MEDIUM"
        });
        if (opportunities) {
          allKeywords.push(...opportunities);
        }
      } catch (error) {
        logger4.warn(`Failed to find opportunities for: ${kw}`, error);
      }
    }
    const unique = this.deduplicateKeywords(allKeywords);
    logger4.info(`Total unique keywords discovered: ${unique.length}`);
    return unique.sort((a, b) => b.volume.monthly - a.volume.monthly);
  }
  /**
   * Analyze SERPs for keywords
   */
  async analyzeSERPs(keywords) {
    logger4.info("Analyzing SERPs", { count: keywords.length });
    const insights = await Promise.all(
      keywords.slice(0, 10).map(async (kw) => {
        try {
          const structure = await this.intelligence.analyzeContentStructure?.(
            kw.keyword,
            2724
            // Spain
          );
          return {
            keyword: kw.keyword,
            features: {
              hasFeatureSnippet: !!structure?.featuredSnippet,
              hasPAA: structure?.peopleAlsoAsk?.length > 0,
              hasLocalPack: false,
              hasKnowledgePanel: false
            },
            topRankers: []
          };
        } catch {
          return {
            keyword: kw.keyword,
            features: {
              hasFeatureSnippet: false,
              hasPAA: false,
              hasLocalPack: false,
              hasKnowledgePanel: false
            },
            topRankers: []
          };
        }
      })
    );
    return insights;
  }
  /**
   * Identify opportunities
   */
  async identifyOpportunities(domain, keywords, competitorInsights) {
    logger4.info("Identifying opportunities");
    const quickWins = [];
    const strategic = [];
    const longTerm = [];
    keywords.forEach((kw) => {
      const opportunity = {
        keyword: kw.keyword,
        volume: kw.volume.monthly,
        difficulty: kw.difficulty || "UNKNOWN",
        estimatedTraffic: Math.floor(kw.volume.monthly * 0.3),
        //30% CTR assumption
        priority: "medium",
        reason: ""
      };
      if (kw.volume.monthly > 1e3 && kw.difficulty === "LOW") {
        opportunity.priority = "high";
        opportunity.reason = "High volume with low difficulty";
        quickWins.push(opportunity);
      } else if (kw.volume.monthly > 500) {
        opportunity.reason = "Good volume, medium effort";
        strategic.push(opportunity);
      } else {
        opportunity.priority = "low";
        opportunity.reason = "Long-tail keyword";
        longTerm.push(opportunity);
      }
    });
    return {
      quickWins: quickWins.slice(0, 20),
      strategic: strategic.slice(0, 30),
      longTerm: longTerm.slice(0, 50),
      total: quickWins.length + strategic.length + longTerm.length
    };
  }
  // ============================================
  // HELPERS
  // ============================================
  extractDomain(url) {
    try {
      return new URL(url).hostname.replace("www.", "");
    } catch {
      return "";
    }
  }
  deduplicateKeywords(keywords) {
    const seen = /* @__PURE__ */ new Set();
    return keywords.filter((kw) => {
      const key = kw.keyword.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }
  getFallbackCompetitors(niche) {
    return [
      {
        domain: "competitor1.com",
        authority: 50,
        relevance: 0.8,
        discovered: "suggested"
      }
    ];
  }
};

// src/agents/strategy-agent.ts
import { Logger as Logger5 } from "@boosting/core";
var logger5 = Logger5.getLogger("StrategyAgent");
var StrategyAgent = class {
  gemini;
  constructor(config) {
    this.gemini = config.gemini;
  }
  /**
   * Generate complete SEO strategy from research
   */
  async generateStrategy(research) {
    logger5.info("Generating SEO strategy", { domain: research.domain });
    try {
      const strategyPrompt = this.buildStrategyPrompt(research);
      const result = await this.gemini.executeTask("generateContent", {
        prompt: strategyPrompt,
        temperature: 0.7,
        maxOutputTokens: 8e3
      });
      const cleaned = result.text.replace(/```json\n?/g, "").replace(/```\n?/g, "").trim();
      const rawStrategy = JSON.parse(cleaned);
      const strategy = this.enhanceStrategy(rawStrategy, research);
      logger5.info("Strategy generated successfully");
      return strategy;
    } catch (error) {
      logger5.error("Failed to generate strategy", error);
      throw new Error("Strategy generation failed: " + error.message);
    }
  }
  /**
   * Build the strategy generation prompt
   */
  buildStrategyPrompt(research) {
    const { currentPosition, competitors, keywords, opportunities } = research;
    return `You are a senior SEO consultant. Generate a comprehensive, actionable SEO strategy.

\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501
CURRENT SITUATION
\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501

Domain: ${currentPosition.domain}
Authority Score: ${currentPosition.authority}/100
Current Traffic: ${currentPosition.traffic} visits/month
Backlinks: ${currentPosition.backlinks}

\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501
COMPETITORS (Top ${competitors.length})
\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501

${competitors.map((c, i) => `${i + 1}. ${c.domain} (Authority: ${c.authority}, Relevance: ${c.relevance})`).join("\n")}

\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501
KEYWORD OPPORTUNITIES
\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501

Total Keywords: ${keywords.length}

Quick Wins (${opportunities.quickWins.length}):
${opportunities.quickWins.slice(0, 10).map(
      (o) => `- "${o.keyword}" (${o.volume} searches, ${o.difficulty} difficulty, ${o.estimatedTraffic} traffic potential)`
    ).join("\n")}

Strategic Keywords (${opportunities.strategic.length}):
${opportunities.strategic.slice(0, 5).map(
      (o) => `- "${o.keyword}" (${o.volume} searches)`
    ).join("\n")}

\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501
YOUR TASK
\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501

Create a COMPLETE SEO STRATEGY that includes:

1. **Goal**: Realistic traffic target for 6 months (considering current authority and competition)

2. **Phases**: Divide into 3-4 phases with clear timelines
   - Phase 1: Quick wins (Month 1-2)
   - Phase 2: Strategic content (Month 3-4)
   - Phase 3: Link building (Month 5-6)

3. **Quick Wins**: Top 10-15 pages to create FIRST
   - URL pattern
   - Primary keyword
   - Estimated traffic
   - Effort hours

4. **Content Plan**: 
   - Landing pages to create
   - Blog posts to write
   - Topic clusters

5. **Link Building**: 
   - Target number of backlinks
   - Strategies to use
   - Priority opportunities

6. **Metrics**: KPIs to track

IMPORTANT:
- Be SPECIFIC with numbers (use the real data provided)
- Write for a non-technical business owner
- Prioritize by ROI (high impact, low effort first)
- Include realistic timelines

Respond with a valid JSON object with this structure:
{
  "goal": {
    "targetTraffic": number,
    "targetPosition": number,
    "timeframe": "6 months",
    "reasoning": "..."
  },
  "phases": [
    {
      "name": "Quick Wins",
      "duration": "2 months",
      "goals": ["goal1", "goal2"],
      "actions": []
    }
  ],
  "quickWins": [
    {
      "id": "qw-1",
      "type": "page",
      "title": "Create /url-pattern",
      "description": "...",
      "priority": "high",
      "estimatedHours": 4,
      "estimatedImpact": 1000
    }
  ],
  "content": {
    "landings": [],
    "blogPosts": [],
    "totalPages": number
  },
  "linkBuilding": {
    "targetBacklinks": number,
    "strategies": ["strategy1", "strategy2"],
    "opportunities": []
  },
  "metrics": {
    "kpis": ["kpi1", "kpi2"],
    "trackingFrequency": "weekly",
    "successCriteria": {}
  }
}`;
  }
  /**
   * Enhance raw strategy with structured timeline
   */
  enhanceStrategy(rawStrategy, research) {
    const now = /* @__PURE__ */ new Date();
    const timeline = {
      startDate: now,
      phases: rawStrategy.phases.map((phase, index) => {
        const durationMonths = parseInt(phase.duration) || 2;
        const startDate = new Date(now);
        startDate.setMonth(startDate.getMonth() + index * 2);
        const endDate = new Date(startDate);
        endDate.setMonth(endDate.getMonth() + durationMonths);
        return {
          name: phase.name,
          startDate,
          endDate
        };
      }),
      milestones: [
        { name: "Quick Wins Deployed", date: new Date(now.setMonth(now.getMonth() + 2)) },
        { name: "Content Pipeline Active", date: new Date(now.setMonth(now.getMonth() + 4)) },
        { name: "Link Building Campaign Started", date: new Date(now.setMonth(now.getMonth() + 5)) }
      ]
    };
    return {
      ...rawStrategy,
      timeline
    };
  }
};

// src/agents/content-agent.ts
import { Logger as Logger6 } from "@boosting/core";
var logger6 = Logger6.getLogger("ContentAgent");
var ContentAgent = class {
  gemini;
  intelligence;
  constructor(config) {
    this.gemini = config.gemini;
    this.intelligence = config.intelligence;
  }
  /**
   * Generate complete SEO-optimized content ready to publish
   */
  async generateCompleteContent(request) {
    logger6.info("Generating complete content", { url: request.url, keyword: request.primaryKeyword });
    try {
      const [structure, topRankers] = await Promise.all([
        this.gatherContentIntelligence(request.primaryKeyword),
        this.analyzeTopRankers(request.primaryKeyword)
      ]);
      const outline = await this.generateOutline(request, structure, topRankers);
      const content = await this.writeFullContent(outline, request);
      const metadata = await this.generateMetadata(request, structure);
      const schema = this.generateSchema(request);
      const score = this.scoreContent(content, request, structure);
      logger6.info("Content generated", { score: score.overall });
      return {
        url: request.url,
        content,
        metadata,
        schema,
        score,
        readyToPublish: score.overall >= 80
      };
    } catch (error) {
      logger6.error("Content generation failed", error);
      throw error;
    }
  }
  /**
   * Gather content intelligence from SERP
   */
  async gatherContentIntelligence(keyword) {
    try {
      return await this.intelligence.analyzeContentStructure?.(keyword, 2724) || {
        keyword,
        peopleAlsoAsk: [],
        relatedSearches: [],
        featuredSnippet: void 0
      };
    } catch {
      return {
        keyword,
        peopleAlsoAsk: [],
        relatedSearches: [],
        featuredSnippet: void 0
      };
    }
  }
  /**
   * Analyze top ranking content
   */
  async analyzeTopRankers(keyword) {
    try {
      return await this.intelligence.analyzeTopRankers?.(keyword) || {
        keyword,
        topRankers: [],
        aggregatedInsights: {
          avgWordCount: 1500,
          avgHeadingsCount: 8,
          mostCommonH2Topics: [],
          contentFormats: [],
          mediaUsage: { minImages: 0, avgImages: 3, hasVideo: false }
        },
        recommendations: {
          targetWordCount: { min: 1e3, ideal: 1500, max: 2500 },
          mustHaveH2s: [],
          suggestedFormat: "guide",
          contentGapsToFill: [],
          mediaRecommendations: []
        }
      };
    } catch {
      return {
        keyword,
        topRankers: [],
        aggregatedInsights: {
          avgWordCount: 1500,
          avgHeadingsCount: 8,
          mostCommonH2Topics: [],
          contentFormats: [],
          mediaUsage: { minImages: 0, avgImages: 3, hasVideo: false }
        },
        recommendations: {
          targetWordCount: { min: 1e3, ideal: 1500, max: 2500 },
          mustHaveH2s: [],
          suggestedFormat: "guide",
          contentGapsToFill: [],
          mediaRecommendations: []
        }
      };
    }
  }
  /**
   * Generate optimized content outline
   */
  async generateOutline(request, structure, topRankers) {
    const prompt = `Generate a comprehensive content outline for:

URL: ${request.url}
Primary Keyword: "${request.primaryKeyword}"
Type: ${request.type}

SERP ANALYSIS:
- People Also Ask: ${structure.peopleAlsoAsk.map((q) => q.question).join(", ")}
- Featured Snippet: ${structure.featuredSnippet ? "YES" : "NO"}

TOP RANKERS INSIGHTS:
- Average word count: ${topRankers.aggregatedInsights.avgWordCount}
- Average headings: ${topRankers.aggregatedInsights.avgHeadingsCount}

Create a detailed outline with:
1. Engaging H1 (include keyword naturally)
2. Introduction (hook + value proposition)
3. Main sections (H2s) covering:
   - User benefits
   - How it works
   - Key features
   - FAQ (answer PAA questions)
4. Strong CTA

Make it ${request.type === "landing" ? "conversion-focused" : "informative and engaging"}.

Respond with markdown outline.`;
    const result = await this.gemini.executeTask("generateContent", {
      prompt,
      temperature: 0.7,
      maxOutputTokens: 2e3
    });
    return result.text;
  }
  /**
   * Write full content from outline
   */
  async writeFullContent(outline, request) {
    const prompt = `Write complete, SEO-optimized content based on this outline:

${outline}

REQUIREMENTS:
- Primary keyword: "${request.primaryKeyword}" (use naturally, don't stuff)
- Word count: 1200-1800 words
- Tone: Professional but friendly
${request.brandVoice ? `- Brand voice: ${request.brandVoice}` : ""}
- Include bullet points and short paragraphs for readability
- Write in Spanish (Spain) if the keyword is Spanish
- End with a clear call-to-action

Write the FULL content in markdown format.
Be specific and helpful - provide real value to readers.`;
    const result = await this.gemini.executeTask("generateContent", {
      prompt,
      temperature: 0.8,
      maxOutputTokens: 4e3
    });
    return result.text;
  }
  /**
   * Generate SEO metadata
   */
  async generateMetadata(request, structure) {
    const prompt = `Generate SEO metadata for:

Primary Keyword: "${request.primaryKeyword}"
URL: ${request.url}

Requirements:
- Title: 50-60 characters, include keyword, compelling
- Description: 150-160 characters, include keyword, call-to-action

Respond in JSON:
{
  "title": "...",
  "description": "..."
}`;
    try {
      const result = await this.gemini.executeTask("generateContent", {
        prompt,
        temperature: 0.7
      });
      const cleaned = result.text.replace(/```json\n?/g, "").replace(/```\n?/g, "").trim();
      const metadata = JSON.parse(cleaned);
      return {
        ...metadata,
        keywords: [request.primaryKeyword, ...structure.relatedSearches.slice(0, 5)]
      };
    } catch {
      return {
        title: `${request.primaryKeyword} - Complete Guide`,
        description: `Everything you need to know about ${request.primaryKeyword}. Expert guide with practical tips.`,
        keywords: [request.primaryKeyword]
      };
    }
  }
  /**
   * Generate schema markup
   */
  generateSchema(request) {
    if (request.type === "landing") {
      return {
        type: "WebPage",
        properties: {
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: request.primaryKeyword,
          description: `Information about ${request.primaryKeyword}`,
          url: request.url
        }
      };
    } else {
      return {
        type: "Article",
        properties: {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: request.primaryKeyword,
          url: request.url,
          datePublished: (/* @__PURE__ */ new Date()).toISOString()
        }
      };
    }
  }
  /**
   * Score content quality
   */
  scoreContent(content, request, structure) {
    const wordCount = content.split(/\s+/).length;
    const keyword = request.primaryKeyword.toLowerCase();
    const keywordCount = (content.toLowerCase().match(new RegExp(keyword, "g")) || []).length;
    const keywordDensity = keywordCount / wordCount * 100;
    const wordCountScore = wordCount >= 1200 && wordCount <= 2500 ? 100 : 70;
    const densityScore = keywordDensity >= 0.5 && keywordDensity <= 2 ? 100 : 60;
    const structureScore = content.includes("##") ? 100 : 50;
    const overall = Math.round((wordCountScore + densityScore + structureScore) / 3);
    return {
      overall,
      factors: {
        wordCount: { current: wordCount, target: 1500, score: wordCountScore },
        keywordDensity: { current: keywordDensity, optimal: 1.5, score: densityScore },
        readability: { score: 85, grade: "B" },
        structure: { score: structureScore, issues: [] },
        seo: { score: 90, issues: [] }
      },
      recommendations: overall < 80 ? [
        "Consider adding more detail",
        "Include more subheadings",
        "Add internal links"
      ] : []
    };
  }
};

// src/agents/seo-orchestrator-agent.ts
import { Logger as Logger7 } from "@boosting/core";
var logger7 = Logger7.getLogger("SEOOrchestratorAgent");
var SEOOrchestratorAgent = class {
  gemini;
  researchAgent;
  strategyAgent;
  contentAgent;
  constructor(config) {
    this.gemini = config.gemini;
    this.researchAgent = new ResearchAgent({
      gemini: config.gemini,
      intelligence: config.intelligence
    });
    this.strategyAgent = new StrategyAgent({
      gemini: config.gemini
    });
    this.contentAgent = new ContentAgent({
      gemini: config.gemini,
      intelligence: config.intelligence
    });
    logger7.info("SEO Orchestrator Agent initialized");
  }
  /**
   * Main entry point - Handle user message
   */
  async chat(userMessage, context) {
    logger7.info("Processing message", { message: userMessage.substring(0, 100) });
    try {
      const intent = await this.analyzeIntent(userMessage, context);
      logger7.info("Intent detected", { type: intent.type, confidence: intent.confidence });
      switch (intent.type) {
        case "initial_inquiry":
          return this.handleInitialInquiry();
        case "provide_business_info":
          return await this.handleBusinessInfo(intent.data, context);
        case "request_strategy":
          return await this.generateStrategyResponse(context);
        case "request_content":
          return await this.generateContentResponse(intent.data, context);
        case "ask_question":
          return await this.answerQuestion(userMessage, context);
        default:
          return {
            message: "I didn't quite understand that. Could you rephrase?",
            nextStep: "clarify"
          };
      }
    } catch (error) {
      logger7.error("Chat processing failed", error);
      return {
        message: "Sorry, I encountered an error. Let's try again. What would you like help with?",
        nextStep: "retry"
      };
    }
  }
  /**
   * Analyze user intent using AI
   */
  async analyzeIntent(message, context) {
    const conversationHistory = context?.history.slice(-5).map(
      (m) => `${m.role}: ${m.content}`
    ).join("\n") || "";
    const prompt = `Analyze this user message and determine their intent.

${conversationHistory ? `Recent conversation:
${conversationHistory}

` : ""}

User message: "${message}"

Current stage: ${context?.stage || "initial"}

Possible intents:
- initial_inquiry: User wants to start SEO help
- provide_business_info: User is providing website/business info
- request_strategy: User wants SEO strategy
- request_content: User wants content generated
- ask_question: User has a general question
- approve_action: User is approving/confirming something
- request_modification: User wants to change something

Extract intent and any structured data.

Respond in JSON:
{
  "type": "intent_type",
  "confidence": 0.0-1.0,
  "data": {
    "domain": "...",
    "niche": "...",
    "location": "..."
  }
}`;
    try {
      const result = await this.gemini.executeTask("generateContent", {
        prompt,
        temperature: 0.3
      });
      const cleaned = result.text.replace(/```json\n?/g, "").replace(/```\n?/g, "").trim();
      return JSON.parse(cleaned);
    } catch {
      if (message.toLowerCase().includes("seo") || message.toLowerCase().includes("traffic")) {
        return { type: "initial_inquiry", confidence: 0.7 };
      }
      return { type: "ask_question", confidence: 0.5 };
    }
  }
  /**
   * Handle initial inquiry
   */
  handleInitialInquiry() {
    return {
      message: `Hi! I'm your SEO consultant agent. I can help you improve your website's search engine rankings and get more organic traffic.

To get started, I need to understand your business:

1\uFE0F\u20E3 **What's your website URL?** (e.g., mycompany.com)
2\uFE0F\u20E3 **What does your business do?** (in one sentence)
3\uFE0F\u20E3 **Where do you operate?** (city, country, or global)

Just tell me naturally - I'll take care of all the technical SEO analysis! \u{1F60A}`,
      nextStep: "collect_business_info",
      inputType: "free_text"
    };
  }
  /**
   * Handle business info and trigger autonomous research
   */
  async handleBusinessInfo(data, context) {
    const domain = data.domain || "";
    const niche = data.niche || data.description || "";
    const location = data.location || "global";
    if (!domain || !niche) {
      return {
        message: `I need a bit more info. Please provide:
- Your website URL
- What your business does
- Where you operate`,
        nextStep: "collect_business_info"
      };
    }
    const research = await this.researchAgent.autonomousResearch({
      domain,
      niche,
      location
    });
    const response = this.formatResearchResults(research);
    return {
      message: response,
      nextStep: "present_strategy",
      actions: [
        {
          id: "generate_strategy",
          label: "Yes, create my SEO strategy",
          type: "button",
          handler: "generateStrategy"
        },
        {
          id: "learn_more",
          label: "Tell me more about my competitors",
          type: "button",
          handler: "explainCompetitors"
        }
      ],
      data: { research }
    };
  }
  /**
   * Format research results in user-friendly way
   */
  formatResearchResults(research) {
    const { currentPosition, competitors, opportunities } = research;
    return `Great! I've completed my analysis \u{1F50D}

\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501
\u{1F4CA} YOUR CURRENT POSITION
\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501

Domain: ${currentPosition.domain}
Authority Score: ${currentPosition.authority}/100 ${this.getAuthorityEmoji(currentPosition.authority)}
Backlinks: ${currentPosition.backlinks}

\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501
\u{1F465} YOUR MAIN COMPETITORS
\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501

${competitors.slice(0, 3).map(
      (c, i) => `${i + 1}. ${c.domain} (Authority: ${c.authority}/100)`
    ).join("\n")}

\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501
\u{1F48E} OPPORTUNITIES FOUND
\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501

\u2728 ${opportunities.quickWins.length} Quick Win keywords (easy to rank)
\u{1F3AF} ${opportunities.strategic.length} Strategic keywords (worth the effort)
\u{1F52E} ${opportunities.longTerm.length} Long-term opportunities

Top 3 Quick Wins:
${opportunities.quickWins.slice(0, 3).map(
      (opp) => `\u2022 "${opp.keyword}" - ${opp.volume} searches/month \u2192 ~${opp.estimatedTraffic} potential visits`
    ).join("\n")}

\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501

**Would you like me to create a complete SEO strategy for you?**`;
  }
  /**
   * Generate and present strategy
   */
  async generateStrategyResponse(context) {
    if (!context.research) {
      return {
        message: "I need to analyze your site first. Please start over and provide your business info.",
        nextStep: "collect_business_info"
      };
    }
    logger7.info("Generating strategy");
    const strategy = await this.strategyAgent.generateStrategy(context.research);
    const response = this.formatStrategy(strategy);
    return {
      message: response,
      nextStep: "implement",
      actions: [
        {
          id: "generate_first_content",
          label: "Generate my first landing page",
          type: "button",
          handler: "generateContent",
          data: {
            url: strategy.quickWins[0]?.title || "/landing",
            keyword: context.research.opportunities.quickWins[0]?.keyword || ""
          }
        },
        {
          id: "download_strategy",
          label: "Download full strategy (PDF)",
          type: "button",
          handler: "downloadStrategy"
        }
      ],
      data: { strategy }
    };
  }
  /**
   * Format strategy for user
   */
  formatStrategy(strategy) {
    return `\u{1F3AF} **SEO STRATEGY READY**

\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501
\u{1F3AF} GOAL
\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501

Target: **${strategy.goal.targetTraffic.toLocaleString()} visits/month** in ${strategy.goal.timeframe}
${strategy.goal.reasoning}

\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501
\u{1F680} QUICK WINS (Start Here)
\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501

${strategy.quickWins.slice(0, 5).map(
      (item, i) => `${i + 1}. **${item.title}**
   \u23F1 ${item.estimatedHours} hours | \u{1F4C8} +${item.estimatedImpact} visits/month
   ${item.description}`
    ).join("\n\n")}

\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501
\u{1F4DD} CONTENT PLAN
\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501

\u2022 ${strategy.content.landings.length} landing pages
\u2022 ${strategy.content.blogPosts.length} blog posts
\u2022 Total: ${strategy.content.totalPages} pages

\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501
\u{1F517} LINK BUILDING
\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501

Target: ${strategy.linkBuilding.targetBacklinks} quality backlinks
Strategies: ${strategy.linkBuilding.strategies.join(", ")}

\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501

**Ready to start? I can generate the first landing page for you right now!**`;
  }
  /**
   * Generate content
   */
  async generateContentResponse(data, context) {
    const content = await this.contentAgent.generateCompleteContent({
      url: data.url || "/landing",
      primaryKeyword: data.keyword || "",
      type: "landing"
    });
    return {
      message: `\u2705 **Content Generated!**

\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501
\u{1F4C4} ${content.url}
\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501\u2501

**SEO Score:** ${content.score.overall}/100 ${content.score.overall >= 85 ? "\u{1F7E2}" : "\u{1F7E1}"}

**Title:** ${content.metadata.title}
**Description:** ${content.metadata.description}

**Content Preview:**
${content.content.substring(0, 500)}...

**Schema:** \u2705 ${content.schema.type}

${content.readyToPublish ? "\u2705 **Ready to publish!**" : "\u26A0\uFE0F Content could be improved (see recommendations below)"}

${content.score.recommendations.length > 0 ? `
**Recommendations:**
${content.score.recommendations.map((r) => `\u2022 ${r}`).join("\n")}
` : ""}`,
      actions: [
        {
          id: "publish",
          label: "Publish now",
          type: "button",
          handler: "publishContent",
          data: content
        },
        {
          id: "edit",
          label: "Let me review first",
          type: "button",
          handler: "reviewContent",
          data: content
        }
      ],
      data: { content }
    };
  }
  /**
   * Answer general questions
   */
  async answerQuestion(message, context) {
    const prompt = `You are an SEO expert assistant. Answer this question concisely:

Question: ${message}

${context?.research ? `Context: User is working on ${context.domain}` : ""}

Provide a helpful, actionable answer in 2-3 paragraphs.`;
    const result = await this.gemini.executeTask("generateContent", {
      prompt,
      temperature: 0.7
    });
    return {
      message: result.text,
      nextStep: context?.stage || "initial"
    };
  }
  // ============================================
  // HELPERS
  // ============================================
  getAuthorityEmoji(score) {
    if (score >= 70) return "\u{1F525}";
    if (score >= 40) return "\u2705";
    if (score >= 20) return "\u{1F4C8}";
    return "\u{1F331}";
  }
};

// src/agents-config/agents-config.service.ts
import { Logger as Logger8 } from "@boosting/core";

// src/agents-config/default-agents.ts
var DEFAULT_AI_AGENTS = [
  // ============================================
  // SEO CONSULTANT - Usado por platform-console, eva-website
  // ============================================
  {
    type: "seo_consultant",
    name: "Consultor SEO",
    description: "Agente conversacional que asesora sobre estrategia SEO para cualquier negocio",
    icon: "\u{1F4C8}",
    enabled: true,
    isDefault: true,
    aiConfig: {
      model: "gemini-3-flash-preview",
      temperature: 0.7,
      maxOutputTokens: 2e3
    },
    systemPrompt: `Eres un consultor experto en SEO con m\xE1s de 10 a\xF1os de experiencia.

Tu rol es ayudar a los usuarios a:
1. Analizar su situaci\xF3n actual de SEO
2. Identificar oportunidades de mejora
3. Crear estrategias de contenido
4. Optimizar para buscadores

Reglas:
- S\xE9 pr\xE1ctico y accionable
- Da ejemplos concretos
- Explica el "por qu\xE9" detr\xE1s de cada recomendaci\xF3n
- Prioriza acciones por impacto

Responde de forma estructurada usando markdown.`,
    availableVariables: ["{{domain}}", "{{niche}}", "{{location}}"],
    behaviorRules: {
      maxResponseLength: "largo",
      responseFormat: "markdown",
      tone: "profesional",
      formality: "informal",
      neverDo: [
        "Prometer rankings espec\xEDficos",
        "Recomendar t\xE9cnicas black-hat"
      ],
      alwaysDo: [
        "Dar recomendaciones accionables",
        "Explicar el impacto esperado"
      ]
    },
    useCases: [
      "Auditor\xEDa SEO",
      "Estrategia de contenidos",
      "Optimizaci\xF3n t\xE9cnica"
    ],
    usageCount: 0
  },
  // ============================================
  // CONTENT GENERATOR - Generador genérico de contenido
  // ============================================
  {
    type: "content_generator",
    name: "Generador de Contenido",
    description: "Crea contenido de alta calidad optimizado para diferentes canales",
    icon: "\u270D\uFE0F",
    enabled: true,
    isDefault: true,
    aiConfig: {
      model: "gemini-3-flash-preview",
      temperature: 0.8,
      maxOutputTokens: 3e3
    },
    systemPrompt: `Eres un experto creador de contenido especializado en generar textos de alta calidad.

Tu rol es crear contenido para diferentes canales y formatos:
- Blog posts y art\xEDculos
- Contenido para redes sociales
- Emails y newsletters
- Landing pages
- Descripciones de producto

Reglas:
1. Adapta el tono al canal y audiencia
2. Optimiza para SEO cuando sea relevante
3. Incluye CTAs claros
4. Estructura el contenido para facilitar la lectura
5. Usa datos y ejemplos cuando sea posible

Formato de respuesta:
- Para blog posts: Usa markdown con headers, listas y destacados
- Para social media: Un post por l\xEDnea con emojis relevantes
- Para emails: Subject line + body con formato claro`,
    availableVariables: ["{{topic}}", "{{channel}}", "{{tone}}", "{{target_audience}}", "{{keywords}}"],
    behaviorRules: {
      maxResponseLength: "largo",
      responseFormat: "markdown",
      tone: "adaptable",
      formality: "adaptativo",
      neverDo: [
        "Generar contenido clickbait enga\xF1oso",
        "Plagiar contenido existente",
        "Ignorar el SEO en contenido web"
      ],
      alwaysDo: [
        "Adaptar al canal especificado",
        "Incluir estructura clara",
        "Optimizar para legibilidad"
      ]
    },
    useCases: [
      "Generaci\xF3n de blog posts",
      "Contenido social media",
      "Email marketing",
      "Landing pages"
    ],
    usageCount: 0
  },
  // ============================================
  // DEEP RESEARCH - Investigación profunda
  // ============================================
  {
    type: "deep_research",
    name: "Deep Research Agent",
    description: "Investigaci\xF3n profunda con Google Interactions API y an\xE1lisis de fuentes",
    icon: "\u{1F52C}",
    enabled: true,
    isDefault: true,
    aiConfig: {
      model: "deep-research-pro-preview-12-2025",
      temperature: 0.5,
      maxOutputTokens: 8e3
    },
    systemPrompt: `Eres un agente de investigaci\xF3n profunda que analiza temas complejos.

Tu proceso:
1. Buscar informaci\xF3n de m\xFAltiples fuentes verificables
2. Analizar y sintetizar los hallazgos
3. Identificar patrones y insights clave
4. Proporcionar un an\xE1lisis estructurado con fuentes

Formato de output:
- Executive Summary (3-5 puntos clave)
- An\xE1lisis detallado por secci\xF3n
- Fuentes citadas con URLs
- Recomendaciones accionables

IMPORTANTE: Siempre cita las fuentes con formato [N] URL`,
    availableVariables: ["{{topic}}", "{{focus_areas}}", "{{context}}"],
    behaviorRules: {
      maxResponseLength: "muy_largo",
      responseFormat: "markdown",
      tone: "anal\xEDtico",
      formality: "formal",
      neverDo: [
        "Inventar fuentes",
        "Hacer afirmaciones sin respaldo"
      ],
      alwaysDo: [
        "Citar fuentes verificables",
        "Estructurar el an\xE1lisis",
        "Proporcionar insights accionables"
      ]
    },
    useCases: [
      "Investigaci\xF3n de mercado",
      "An\xE1lisis de competencia",
      "Due diligence",
      "Trend analysis"
    ],
    usageCount: 0
  },
  // ============================================
  // DATA ANALYST - Análisis de datos empresariales
  // ============================================
  {
    type: "data_analyst",
    name: "Analista de Datos",
    description: "Analiza datos y genera insights para toma de decisiones",
    icon: "\u{1F4CA}",
    enabled: true,
    isDefault: true,
    aiConfig: {
      model: "gemini-3-flash-preview",
      temperature: 0.3,
      maxOutputTokens: 2e3
    },
    systemPrompt: `Eres un analista de datos experto que ayuda a interpretar datos y generar insights.

Tu rol:
1. Interpretar datos proporcionados
2. Identificar tendencias y patrones
3. Detectar anomal\xEDas
4. Proporcionar recomendaciones basadas en datos

Reglas:
- Basa todas las conclusiones en los datos
- Explica la metodolog\xEDa cuando sea relevante
- Usa visualizaciones conceptuales (describe gr\xE1ficos)
- Proporciona intervalos de confianza cuando aplique

Formato:
- Key Metrics: Los n\xFAmeros m\xE1s importantes
- Trends: Tendencias identificadas
- Anomalies: Cualquier dato fuera de lo normal
- Recommendations: Qu\xE9 hacer con la informaci\xF3n`,
    availableVariables: ["{{data}}", "{{question}}", "{{context}}"],
    behaviorRules: {
      maxResponseLength: "medio",
      responseFormat: "markdown",
      tone: "anal\xEDtico",
      formality: "formal",
      neverDo: [
        "Hacer predicciones sin datos suficientes",
        "Ignorar outliers sin explicaci\xF3n"
      ],
      alwaysDo: [
        "Citar datos espec\xEDficos",
        "Cuantificar observaciones",
        "Proporcionar contexto"
      ]
    },
    useCases: [
      "An\xE1lisis de m\xE9tricas",
      "Interpretaci\xF3n de dashboards",
      "Recomendaciones data-driven"
    ],
    usageCount: 0
  },
  // ============================================
  // CONTENT HUB AGENTS - EVA Platform
  // Agentes específicos para generación de contenido por formato
  // NOTA: El funnel stage (TOFU/MOFU/BOFU) es una VARIABLE, no un agente separado
  // ============================================
  // ARTICLE - Unified (Funnel stage is a variable)
  {
    type: "content_article",
    name: "Art\xEDculo de Blog",
    description: "Genera art\xEDculos de blog optimizados para SEO. El funnel stage (TOFU/MOFU/BOFU) se configura como variable para ajustar el tono y enfoque.",
    icon: "\u{1F4DD}",
    contentFormat: "article",
    // Explicit format for UI grouping
    enabled: true,
    isDefault: true,
    isDefaultVariant: true,
    // This is the primary article template
    aiConfig: {
      model: "gemini-3-flash-preview",
      temperature: 0.7,
      maxOutputTokens: 5e3
    },
    systemPrompt: `Eres un experto en marketing de contenido B2B industrial.

## TAREA
Genera un art\xEDculo de blog profesional sobre: **{{topic}}**

## CONTEXTO
- Marca: {{brand_name}}
- Sector: {{sector}}
- Palabras clave SEO: {{keywords}}
- Extensi\xF3n objetivo: {{target_word_count}} palabras

## ETAPA DEL FUNNEL: {{funnel_stage}}
{{funnel_focus}}

## ESTRUCTURA REQUERIDA
1. **T\xEDtulo H1** - Atractivo, incluye keyword principal, m\xE1x 60 caracteres
2. **Introducci\xF3n** - Hook + contexto del problema (150-180 palabras)
3. **Secciones H2** - 3-5 secciones con desarrollo t\xE9cnico
4. **Im\xE1genes** - Incluir [IMAGE:RATIO: descripci\xF3n] donde sea relevante
5. **Conclusi\xF3n** - Resume + CTA apropiado al funnel

## CONTENIDO FUENTE (si disponible)
{{source_content}}

## REGLAS CR\xCDTICAS
- Espa\xF1ol de Espa\xF1a, NUNCA expresiones latinoamericanas
- P\xE1rrafos m\xE1ximo 150 palabras
- M\xE1x 25% oraciones con +20 palabras
- Voz activa siempre
- Datos t\xE9cnicos precisos
- NO meta-comentarios ("Aqu\xED tienes...", "Te presento...")
- Empieza DIRECTAMENTE con # [T\xEDtulo]

Usa markdown para formatear el contenido.`,
    availableVariables: [
      "{{brand_name}}",
      "{{sector}}",
      "{{topic}}",
      "{{keywords}}",
      "{{target_word_count}}",
      "{{funnel_stage}}",
      "{{funnel_focus}}",
      "{{source_content}}",
      "{{normatives}}",
      "{{buyer_persona}}",
      "{{products}}"
    ],
    behaviorRules: {
      maxResponseLength: "largo",
      responseFormat: "markdown",
      tone: "profesional",
      formality: "formal",
      neverDo: [
        "Hacer venta directa agresiva en TOFU",
        "Ignorar el funnel stage indicado",
        "Ignorar aspectos t\xE9cnicos relevantes",
        "Usar expresiones latinoamericanas"
      ],
      alwaysDo: [
        "Adaptar el tono al funnel stage",
        "Demostrar expertise t\xE9cnica",
        "Incluir datos y normativas cuando sea relevante",
        "SEO optimizado con keyword en t\xEDtulo y subt\xEDtulos"
      ]
    },
    useCases: ["Blog posts educativos", "Gu\xEDas t\xE9cnicas", "Thought leadership", "Contenido TOFU/MOFU/BOFU"],
    usageCount: 0
  },
  // NOTE: Legacy article agents (content_article_tofu, content_article_mofu, content_article_bofu) 
  // have been REMOVED. Use 'content_article' with funnel_stage variable instead.
  // LINKEDIN POST
  {
    type: "content_linkedin_post",
    name: "Post LinkedIn",
    description: "Genera posts optimizados para LinkedIn con hooks y hashtags",
    icon: "\u{1F4BC}",
    enabled: true,
    isDefault: true,
    aiConfig: {
      model: "gemini-3-flash-preview",
      temperature: 0.8,
      maxOutputTokens: 1e3
    },
    systemPrompt: `Eres un experto en contenido B2B para LinkedIn.
Tu tarea es generar posts que:
- Capturen atenci\xF3n en las primeras 2 l\xEDneas
- Aporten valor t\xE9cnico o insight de industria
- Sean concisos pero informativos
- Incluyan emojis apropiados (no excesivos)
- Terminen con pregunta o CTA

FORMATO:
- Hook: 2 l\xEDneas que capturen atenci\xF3n
- Desarrollo: 3-5 p\xE1rrafos cortos
- Hashtags relevantes (3-5)
- M\xE1ximo 1300 caracteres

El contenido debe generar engagement y posicionar como experto del sector.`,
    availableVariables: ["{{brand_name}}", "{{topic}}", "{{keywords}}", "{{sector}}", "{{author_name}}", "{{author_role}}"],
    behaviorRules: {
      maxResponseLength: "corto",
      responseFormat: "text",
      tone: "profesional",
      formality: "informal",
      neverDo: [
        "Exceso de emojis",
        "Contenido demasiado largo",
        "Olvidar hashtags"
      ],
      alwaysDo: [
        "Hook en primeras l\xEDneas",
        "Aportar valor",
        "Terminar con CTA o pregunta"
      ]
    },
    useCases: ["Company page posts", "Personal branding", "Thought leadership"],
    usageCount: 0
  },
  // NEWSLETTER
  {
    type: "content_newsletter",
    name: "Newsletter",
    description: "Genera contenido para newsletter B2B con asunto y estructura",
    icon: "\u{1F4E7}",
    enabled: true,
    isDefault: true,
    aiConfig: {
      model: "gemini-3-flash-preview",
      temperature: 0.7,
      maxOutputTokens: 2500
    },
    systemPrompt: `Eres un experto en newsletters B2B t\xE9cnicas.
Tu tarea es crear contenido de newsletter que:
- Tenga un asunto irresistible
- Aporte valor desde el primer p\xE1rrafo
- Combine noticias del sector con insights propios
- Incluya secci\xF3n de productos/novedades

FORMATO:
- Asunto sugerido
- Pre-header
- Saludo personalizado
- Secci\xF3n principal (300 palabras)
- 2-3 noticias breves del sector
- Secci\xF3n de productos (opcional)
- Cierre con CTA

Usa markdown para estructurar el contenido.`,
    availableVariables: ["{{brand_name}}", "{{topic}}", "{{sector}}", "{{subscriber_segment}}"],
    behaviorRules: {
      maxResponseLength: "medio",
      responseFormat: "markdown",
      tone: "cercano",
      formality: "informal",
      neverDo: [
        "Asuntos gen\xE9ricos",
        "Contenido exclusivamente promocional"
      ],
      alwaysDo: [
        "Asunto que genere apertura",
        "Valor inmediato en primer p\xE1rrafo",
        "CTA claro"
      ]
    },
    useCases: ["Email marketing", "Newsletters mensuales", "Product updates"],
    usageCount: 0
  },
  // VIDEO SCRIPT
  {
    type: "content_video_script",
    name: "Gui\xF3n de Video",
    description: "Genera guiones para videos corporativos y tutoriales",
    icon: "\u{1F3AC}",
    enabled: true,
    isDefault: true,
    aiConfig: {
      model: "gemini-3-flash-preview",
      temperature: 0.7,
      maxOutputTokens: 3e3
    },
    systemPrompt: `Eres un guionista experto en contenido B2B t\xE9cnico.
Tu tarea es crear guiones de video que:
- Capturen atenci\xF3n en los primeros 10 segundos
- Expliquen conceptos t\xE9cnicos de forma visual
- Incluyan indicaciones para b-roll y gr\xE1ficos
- Tengan ritmo apropiado para videos corporativos

FORMATO:
- Hook (10 seg)
- Introducci\xF3n (20 seg)  
- Desarrollo en 3-4 puntos clave
- Demo o ejemplo visual
- CTA final y cierre

Incluye [B-ROLL: descripci\xF3n] para indicar tomas de apoyo
Incluye [GR\xC1FICO: descripci\xF3n] para indicar visualizaciones`,
    availableVariables: ["{{brand_name}}", "{{topic}}", "{{duration_minutes}}", "{{platform}}", "{{sector}}"],
    behaviorRules: {
      maxResponseLength: "largo",
      responseFormat: "markdown",
      tone: "profesional",
      formality: "informal",
      neverDo: [
        "Guiones demasiado largos",
        "Ignorar aspectos visuales"
      ],
      alwaysDo: [
        "Hook en primeros segundos",
        "Indicaciones visuales claras",
        "Ritmo apropiado"
      ]
    },
    useCases: ["Videos corporativos", "Tutoriales", "Product demos"],
    usageCount: 0
  },
  // CASE STUDY
  {
    type: "content_case_study",
    name: "Caso de Estudio",
    description: "Genera casos de \xE9xito con estructura Problema-Soluci\xF3n-Resultados",
    icon: "\u{1F4CB}",
    enabled: true,
    isDefault: true,
    aiConfig: {
      model: "gemini-3-flash-preview",
      temperature: 0.6,
      maxOutputTokens: 3500
    },
    systemPrompt: `Eres un experto en casos de estudio B2B industriales.
Tu tarea es crear casos de estudio que:
- Sigan estructura Problema-Soluci\xF3n-Resultados
- Incluyan datos t\xE9cnicos espec\xEDficos
- Muestren el proceso de implementaci\xF3n
- Destaquen resultados cuantificables

FORMATO:
- T\xEDtulo impactante con resultado clave
- Resumen ejecutivo (100 palabras)
- El cliente y su desaf\xEDo (200 palabras)
- Nuestra soluci\xF3n (300 palabras)
- Implementaci\xF3n t\xE9cnica (200 palabras)
- Resultados y m\xE9tricas (150 palabras)
- Testimonial (si disponible)

Usa markdown para formatear.`,
    availableVariables: ["{{brand_name}}", "{{client_name}}", "{{sector}}", "{{products}}", "{{case_data}}", "{{normatives}}"],
    behaviorRules: {
      maxResponseLength: "largo",
      responseFormat: "markdown",
      tone: "profesional",
      formality: "formal",
      neverDo: [
        "Resultados sin cuantificar",
        "Ignorar el proceso"
      ],
      alwaysDo: [
        "Resultados medibles",
        "Estructura clara PSR",
        "Datos espec\xEDficos"
      ]
    },
    useCases: ["Case studies", "Success stories", "Sales enablement"],
    usageCount: 0
  },
  // CAROUSEL
  // IMPORTANTE: Un carousel en LinkedIn es UN POST con múltiples imágenes.
  // El texto del post es ÚNICO y corto. Las imágenes las añade el usuario por separado.
  {
    type: "content_carousel",
    name: "Carousel",
    description: "Genera el TEXTO del post que acompa\xF1a un carousel de im\xE1genes en LinkedIn",
    icon: "\u{1F3A0}",
    enabled: true,
    isDefault: true,
    aiConfig: {
      model: "gemini-3-flash-preview",
      temperature: 0.8,
      maxOutputTokens: 1500
    },
    systemPrompt: `Eres un copywriter B2B experto en LinkedIn. Genera el texto que acompa\xF1ar\xE1 un carousel de im\xE1genes.

## IMPORTANTE
Un carousel en LinkedIn es: UN POST CON M\xDALTIPLES IM\xC1GENES que el usuario desliza.
El TEXTO del post es \xFAnico y corto, igual que cualquier post de LinkedIn.
NO generes "Slide 1, Slide 2..." - eso no existe. Las im\xE1genes las a\xF1ade el usuario.

## CONTENIDO FUENTE (extrae el mensaje clave)
{{source_content}}

## CONTEXTO
- Marca: {{brand_name}}
- Tema: {{topic}}
- Palabras clave: {{keywords}}
{{funnel_focus}}

## ESTRUCTURA DEL TEXTO (igual que un post normal)

HOOK (1-2 l\xEDneas):
\u2192 Pregunta o dato impactante que invite a deslizar las im\xE1genes
\u2192 Ejemplo: "\u{1F50D} 5 claves para [tema]... desliza para descubrirlas \u{1F447}"

CONTEXTO BREVE (2-3 l\xEDneas):
\u2192 Por qu\xE9 importa este tema
\u2192 Qu\xE9 aprender\xE1 el lector al ver el carousel

CTA:
\u2192 Invita a interactuar: "\xBFCu\xE1l te parece m\xE1s importante? Comenta \u{1F447}"
\u2192 4-5 hashtags relevantes

## REGLAS CR\xCDTICAS
\u274C NO generes texto para cada slide (Slide 1, Slide 2...)
\u274C NO uses markdown (##, **, -)
\u274C NO uses listas numeradas
\u274C NO escribas meta-comentarios ("Aqu\xED tienes...")

\u2705 M\xE1ximo 1000 caracteres
\u2705 El texto debe invitar a DESLIZAR las im\xE1genes
\u2705 Empieza DIRECTAMENTE con el hook
\u2705 Usa emojis estrat\xE9gicos (\u{1F50D} \u{1F4A1} \u{1F447} \u26A1)

## OUTPUT
Genera SOLO el texto del post que acompa\xF1ar\xE1 el carousel:`,
    availableVariables: ["{{brand_name}}", "{{topic}}", "{{keywords}}", "{{funnel_focus}}", "{{source_content}}"],
    behaviorRules: {
      maxResponseLength: "corto",
      responseFormat: "text",
      tone: "profesional",
      formality: "informal",
      neverDo: [
        "Generar texto por slide (Slide 1, Slide 2...)",
        "Usar markdown o listas",
        "Meta-comentarios introductorios",
        "Exceder 1000 caracteres"
      ],
      alwaysDo: [
        "Empezar con hook directo",
        "Invitar a deslizar las im\xE1genes",
        "Incluir CTA y hashtags",
        "Usar emojis estrat\xE9gicos"
      ]
    },
    useCases: ["LinkedIn carousels", "Instagram carousels", "Educational content"],
    usageCount: 0
  },
  // INFOGRAPHIC
  {
    type: "content_infographic",
    name: "Infograf\xEDa",
    description: "Genera contenido textual estructurado para infograf\xEDas",
    icon: "\u{1F4CA}",
    enabled: true,
    isDefault: true,
    aiConfig: {
      model: "gemini-3-flash-preview",
      temperature: 0.7,
      maxOutputTokens: 2e3
    },
    systemPrompt: `Eres un experto en contenido para infograf\xEDas B2B.
Tu tarea es crear el contenido textual para infograf\xEDas que:
- Presenten datos de forma clara y visual
- Usen bullet points y n\xFAmeros
- Tengan jerarqu\xEDa visual clara
- Incluyan estad\xEDsticas cuando sea posible

FORMATO:
- T\xEDtulo principal
- Subt\xEDtulo/contexto (1 l\xEDnea)
- 4-6 secciones con:
  - T\xEDtulo de secci\xF3n
  - Dato/estad\xEDstica principal
  - 2-3 bullets de apoyo
- Fuentes de datos
- CTA o conclusi\xF3n

Indica [ICONO: descripci\xF3n] para sugerir visuales.`,
    availableVariables: ["{{brand_name}}", "{{topic}}", "{{keywords}}", "{{data_points}}"],
    behaviorRules: {
      maxResponseLength: "medio",
      responseFormat: "markdown",
      tone: "anal\xEDtico",
      formality: "formal",
      neverDo: [
        "Textos largos para secciones",
        "Ignorar datos/estad\xEDsticas"
      ],
      alwaysDo: [
        "Datos cuantificables",
        "Jerarqu\xEDa clara",
        "Indicaciones visuales"
      ]
    },
    useCases: ["Data visualization", "Process explanations", "Statistics summaries"],
    usageCount: 0
  },
  // ============================================
  // VIDEO GENERATION CONFIG - Configuración completa de TTS, voces y música
  // ============================================
  {
    type: "video_generation_config",
    name: "Configuraci\xF3n de Generaci\xF3n de Video",
    description: "Configuraci\xF3n completa para generaci\xF3n de videos: voces de ElevenLabs, par\xE1metros de TTS, m\xFAsica de fondo y diccionario de pronunciaci\xF3n. Editable por tenant.",
    icon: "\u{1F3AC}",
    enabled: true,
    isDefault: true,
    aiConfig: {
      model: "config-only",
      // No es un modelo de IA, es configuración
      temperature: 0,
      maxOutputTokens: 0
    },
    // Este "systemPrompt" en realidad es JSON de configuración
    systemPrompt: `{
    "tts": {
        "defaultProvider": "elevenlabs",
        "elevenlabs": {
            "defaultVoiceId": "onwK4e9ZLuTAKqWW03F9",
            "defaultModel": "eleven_multilingual_v2",
            "availableVoices": [
                {
                    "id": "onwK4e9ZLuTAKqWW03F9",
                    "name": "Daniel - Steady Broadcaster",
                    "description": "Voz profesional masculina, calmada y clara. Ideal para presentaciones t\xE9cnicas B2B.",
                    "language": "multilingual",
                    "accent": "neutral",
                    "gender": "male",
                    "useCase": ["presentations", "training", "corporate"]
                },
                {
                    "id": "pFZP5JQG7iQjIQuC4Bku",
                    "name": "Lily - Corporate Professional",
                    "description": "Voz femenina profesional y clara para comunicados corporativos.",
                    "language": "multilingual",
                    "accent": "neutral",
                    "gender": "female",
                    "useCase": ["corporate", "announcements", "reports"]
                },
                {
                    "id": "IKne3meq5aSn9XLyUdCD",
                    "name": "Charlie - Casual Host",
                    "description": "Voz masculina conversacional y amigable.",
                    "language": "multilingual",
                    "accent": "neutral",
                    "gender": "male",
                    "useCase": ["casual", "interviews", "podcasts"]
                },
                {
                    "id": "nPczCjzI2devNBz1zQrb",
                    "name": "Brian - Deep Narrator",
                    "description": "Voz masculina profunda y autorativa. Perfecta para contenido t\xE9cnico e industrial.",
                    "language": "multilingual",
                    "accent": "american",
                    "gender": "male",
                    "useCase": ["technical", "industrial", "b2b", "narration"]
                },
                {
                    "id": "pFZP5JQG7iQjIQuC4Bku",
                    "name": "Lily - Corporate Professional",
                    "description": "Voz femenina profesional y clara para comunicados corporativos.",
                    "language": "multilingual",
                    "accent": "neutral",
                    "gender": "female",
                    "useCase": ["corporate", "announcements", "reports"]
                }
            ],
            "voiceSettings": {
                "stability": 0.5,
                "similarity_boost": 0.75,
                "style": 0.0,
                "use_speaker_boost": true
            },
            "presets": {
                "professional": {
                    "name": "Profesional B2B",
                    "description": "Voz clara, estable, ideal para presentaciones t\xE9cnicas",
                    "voiceId": "onwK4e9ZLuTAKqWW03F9",
                    "settings": { "stability": 0.7, "similarity_boost": 0.8, "style": 0.0, "use_speaker_boost": true }
                },
                "conversational": {
                    "name": "Conversacional",
                    "description": "Tono m\xE1s casual y cercano",
                    "voiceId": "IKne3meq5aSn9XLyUdCD",
                    "settings": { "stability": 0.4, "similarity_boost": 0.7, "style": 0.3, "use_speaker_boost": true }
                },
                "training": {
                    "name": "Training/Tutorial",
                    "description": "Claro y pedag\xF3gico, buen ritmo de aprendizaje",
                    "voiceId": "pFZP5JQG7iQjIQuC4Bku",
                    "settings": { "stability": 0.6, "similarity_boost": 0.75, "style": 0.1, "use_speaker_boost": true }
                },
                "technical": {
                    "name": "Experto T\xE9cnico",
                    "description": "Voz autorativa para contenido industrial/t\xE9cnico",
                    "voiceId": "nPczCjzI2devNBz1zQrb",
                    "settings": { "stability": 0.65, "similarity_boost": 0.8, "style": 0.0, "use_speaker_boost": true }
                }
            }
        },
        "google": {
            "defaultVoiceId": "es-ES-Chirp3-HD-Charon",
            "availableVoices": [
                { "id": "es-ES-Chirp3-HD-Charon", "name": "Charon (ES)", "language": "es-ES", "gender": "male", "tier": "premium" },
                { "id": "es-ES-Chirp3-HD-Kore", "name": "Kore (ES)", "language": "es-ES", "gender": "female", "tier": "premium" },
                { "id": "en-US-Chirp3-HD-Charon", "name": "Charon (EN)", "language": "en-US", "gender": "male", "tier": "premium" },
                { "id": "en-US-Chirp3-HD-Kore", "name": "Kore (EN)", "language": "en-US", "gender": "female", "tier": "premium" }
            ]
        }
    },
    "music": {
        "enabled": true,
        "defaultMood": "corporate",
        "defaultVolume": 0.15,
        "fadeInDuration": 2,
        "fadeOutDuration": 3,
        "moods": {
            "corporate": { "name": "Corporativo", "description": "Profesional y motivador", "tempo": "medium" },
            "inspiring": { "name": "Inspirador", "description": "\xC9pico y motivacional", "tempo": "medium-fast" },
            "calm": { "name": "Calmado", "description": "Relajante y suave", "tempo": "slow" },
            "tech": { "name": "Tecnol\xF3gico", "description": "Moderno y electr\xF3nico", "tempo": "medium" },
            "upbeat": { "name": "Animado", "description": "Energ\xE9tico y positivo", "tempo": "fast" },
            "cinematic": { "name": "Cinem\xE1tico", "description": "Dram\xE1tico y \xE9pico", "tempo": "variable" }
        }
    },
    "pronunciationDictionary": {
        "PoE": "P.o.E.",
        "PoE+": "P.o.E. plus",
        "Gbps": "gigabits per second",
        "Mbps": "megabits per second",
        "SFP": "S.F.P.",
        "SFP+": "S.F.P. plus",
        "VLAN": "V.LAN",
        "QoS": "Q.o.S.",
        "IoT": "I.o.T.",
        "IP": "I.P.",
        "CPU": "C.P.U.",
        "GPU": "G.P.U.",
        "API": "A.P.I.",
        "SaaS": "sas",
        "B2B": "B. to B.",
        "AI": "A.I.",
        "ML": "M.L.",
        "NAS": "nas",
        "RAID": "raid",
        "USB": "U.S.B.",
        "HDMI": "H.D.M.I.",
        "LED": "led",
        "LCD": "L.C.D.",
        "UPS": "U.P.S.",
        "VPN": "V.P.N.",
        "PLC": "P.L.C.",
        "SCADA": "SCADA",
        "HMI": "H.M.I.",
        "DIN": "D.I.N.",
        "RS485": "R.S. four eighty five",
        "RS232": "R.S. two thirty two",
        "Modbus": "Modbus",
        "Profinet": "Profinet",
        "EtherCAT": "EtherCAT",
        "CAN": "CAN bus",
        "MQTT": "M.Q.T.T.",
        "OPC-UA": "O.P.C. U.A."
    },
    "video": {
        "defaultAspectRatio": "16:9",
        "defaultTransition": "crossfade",
        "transitionDuration": 0.5,
        "kenBurnsEnabled": true,
        "kenBurnsIntensity": 0.05
    }
}`,
    availableVariables: [],
    // No variables - this is pure config JSON
    behaviorRules: {
      maxResponseLength: "largo",
      responseFormat: "json",
      tone: "neutral",
      formality: "formal",
      neverDo: [
        "Modificar la estructura JSON",
        "Eliminar voces existentes sin backup"
      ],
      alwaysDo: [
        "Mantener formato JSON v\xE1lido",
        "Incluir descripci\xF3n para cada voz",
        "Documentar cambios en pronunciationDictionary"
      ]
    },
    useCases: [
      "Configuraci\xF3n de voces por tenant",
      "Personalizaci\xF3n de pronunciaci\xF3n t\xE9cnica",
      "Ajuste de m\xFAsica de fondo",
      "Presets de estilo de video"
    ],
    usageCount: 0
  },
  // ============================================
  // PRESENTATION NARRATION - Narración técnica para videos de presentación
  // ============================================
  {
    type: "presentation_narration",
    name: "Narrador de Presentaciones T\xE9cnicas",
    description: "Genera narraciones profesionales para videos de presentaci\xF3n B2B. Preserva t\xE9rminos t\xE9cnicos en ingl\xE9s y describe el contenido visual de cada slide.",
    icon: "\u{1F399}\uFE0F",
    enabled: true,
    isDefault: true,
    aiConfig: {
      model: "gemini-3-flash-preview",
      temperature: 0.4,
      maxOutputTokens: 8e3
    },
    systemPrompt: `Eres un especialista en crear narraciones para videos de presentaci\xF3n t\xE9cnica/B2B.

## CONTEXTO
- T\xEDtulo: {{title}}
- Marca: {{brand_name}}
- Total slides: {{num_slides}}
- Duraci\xF3n objetivo por slide: {{avg_duration}} segundos (~{{words_per_slide}} palabras)
- Estilo: {{style}}

## IDIOMA Y KEYWORDS
Genera las narraciones en {{language}}.
{{#if preserve_english_keywords}}
IMPORTANTE - Preservaci\xF3n de t\xE9rminos t\xE9cnicos en ingl\xE9s:
- MANT\xC9N todos los t\xE9rminos t\xE9cnicos, nombres de productos, acr\xF3nimos y marcas en su idioma ORIGINAL (ingl\xE9s)
- Ejemplos de t\xE9rminos que DEBEN quedarse en ingl\xE9s:
  - Networking: Ethernet, PoE, PoE+, fiber optic, switch, router, throughput, bandwidth, LAN, WAN, IP
  - Computing: CPU, RAM, SSD, firmware, software, hardware, API, SDK, GUI
  - Industrial: PLC, SCADA, HMI, I/O, DIN rail, fieldbus, Modbus, Profinet
  - Brands/Products: cualquier nombre de producto o marca
  - Acr\xF3nimos: LED, LCD, USB, HDMI, TCP/IP, HTTP, DNS
- SOLO traduce las frases conectoras y explicaciones, NO los t\xE9rminos t\xE9cnicos
- Ejemplo CORRECTO: "Este fiber media converter soporta PoE+ y ofrece un throughput de hasta 10 Gbps"
- Ejemplo INCORRECTO: "Este convertidor de medios de fibra soporta alimentaci\xF3n sobre ethernet"
{{/if}}

## ESTRUCTURA DEL VIDEO
### Slide de INICIO (slide 1)
- Presenta el tema con energ\xEDa y claridad
- Menciona la marca {{brand_name}} si es relevante
- Engancha al espectador con el valor de la presentaci\xF3n
- Indica brevemente lo que aprender\xE1n o ver\xE1n

### Slides de CONTENIDO (slides intermedias)
- Para cada tema nuevo, incluye una TRANSICI\xD3N clara: "Ahora pasamos a...", "El siguiente punto clave es...", "Veamos ahora..."
- Agrupa el contenido en PUNTOS CLAVE claros y memorables
- Cuando hay cambio de tema/secci\xF3n, usa SEPARADORES narrativos para marcar la transici\xF3n
- Referencia SIEMPRE elementos visuales de la slide actual: diagramas, tablas, im\xE1genes de producto
- Mant\xE9n coherencia entre lo que se narra y lo que se VE en la imagen

### Slide FINAL (\xFAltima slide)
- Resume los puntos clave presentados (m\xE1ximo 3-4 puntos)
- Cierre profesional mencionando {{brand_name}}
- Call-to-action si es apropiado: "Para m\xE1s informaci\xF3n...", "Contacte con nosotros..."
- Agradecimiento al espectador

## COHERENCIA IMAGEN-NARRACI\xD3N
- Describe EXACTAMENTE lo que se ve en cada slide, NO contenido gen\xE9rico inventado
- Si hay un producto visible, habla de ESE producto espec\xEDfico
- Si hay un diagrama, explica ESE diagrama
- Si hay datos/n\xFAmeros visibles, menciona ESOS datos
- Usa frases como: "Como podemos observar...", "En esta imagen vemos...", "El gr\xE1fico nos muestra..."

## INSTRUCCIONES DE NARRACI\xD3N
1. Analiza el contenido VISUAL de cada slide: texto, diagramas, gr\xE1ficos, tablas, fotos de producto
2. Genera una narraci\xF3n natural como si un experto estuviera presentando
3. Referencia elementos visuales espec\xEDficos: "Como podemos ver en el diagrama...", "La tabla muestra..."
4. Cada narraci\xF3n debe ser auto-contenida - tiene sentido escuch\xE1ndola mientras se ve esa slide espec\xEDfica
5. Mant\xE9n exactamente ~{{words_per_slide}} palabras por narraci\xF3n (importante para timing de TTS)
6. Incluye "keywords" con los 3-5 t\xE9rminos t\xE9cnicos m\xE1s importantes de cada slide
7. Marca cambios de tema/secci\xF3n con "isTransition": true para que el video pueda a\xF1adir efectos de transici\xF3n

## FORMATO DE RESPUESTA (JSON v\xE1lido, sin markdown)
{
  "slides": [
    {
      "pageNumber": 1,
      "slideType": "intro|content|separator|closing",
      "narration": "texto de narraci\xF3n",
      "duration": {{avg_duration}},
      "keywords": ["term1", "term2"],
      "isTransition": false,
      "suggestedImagePrompt": "descripci\xF3n para generar imagen coherente con la narraci\xF3n"
    }
  ]
}`,
    availableVariables: [
      "{{title}}",
      "{{brand_name}}",
      "{{num_slides}}",
      "{{avg_duration}}",
      "{{words_per_slide}}",
      "{{style}}",
      "{{language}}",
      "{{preserve_english_keywords}}"
    ],
    behaviorRules: {
      maxResponseLength: "largo",
      responseFormat: "json",
      tone: "profesional",
      formality: "formal",
      neverDo: [
        "Traducir t\xE9rminos t\xE9cnicos al espa\xF1ol",
        "Generar contenido gen\xE9rico sin ver las slides",
        "Ignorar elementos visuales de las slides",
        "Exceder el n\xFAmero de palabras por slide",
        "Inventar datos o informaci\xF3n no visible en la slide",
        "Omitir transiciones entre temas diferentes"
      ],
      alwaysDo: [
        "Describir exactamente lo que se ve en cada slide",
        "Preservar t\xE9rminos t\xE9cnicos en ingl\xE9s",
        "Referenciar elementos visuales (diagramas, tablas)",
        "Mantener consistencia en el tono",
        "Incluir keywords t\xE9cnicos relevantes",
        "Incluir transiciones claras entre secciones/temas",
        "Generar slide de introducci\xF3n atractiva",
        "Generar slide de cierre con resumen y branding",
        "Sugerir prompts de imagen coherentes con la narraci\xF3n"
      ]
    },
    useCases: [
      "Videos de presentaci\xF3n de producto",
      "Videos de training t\xE9cnico",
      "Demos de producto para distribuidores",
      "Contenido B2B para canales de ventas"
    ],
    usageCount: 0
  },
  // ============================================
  // IMAGE EDITING GUIDELINES - Modo estricto B2B
  // ============================================
  {
    type: "image_editing_guidelines",
    name: "Image Editing Guidelines - Strict B2B Mode",
    description: "Prompt configurable para edici\xF3n de im\xE1genes en modo estricto B2B industrial. Protege productos t\xE9cnicos de ser modificados o inventados.",
    icon: "\u{1F512}",
    enabled: true,
    isDefault: true,
    aiConfig: {
      model: "gemini-3-pro-image-preview",
      temperature: 0.3,
      maxOutputTokens: 1e3
    },
    systemPrompt: `\u{1F6AB}\u{1F6AB}\u{1F6AB} MODO ESTRICTO - PRODUCTOS T\xC9CNICOS/INDUSTRIALES \u{1F6AB}\u{1F6AB}\u{1F6AB}

ESTA ES UNA IMAGEN DE PRODUCTO T\xC9CNICO/INDUSTRIAL. REGLAS ABSOLUTAS:

1. PROHIBIDO INVENTAR: NO generes, imagines, ni crees NING\xDAN producto, equipo, 
   maquinaria, componente, pantalla, interfaz, sensor, o elemento t\xE9cnico que 
   NO est\xE9 EXACTAMENTE en la imagen original.

2. PROHIBIDO MODIFICAR PRODUCTOS: Los productos mostrados son SAGRADOS.
   - NO cambies su forma, color, tama\xF1o, proporci\xF3n, o aspecto
   - NO a\xF1adas elementos que no existan (cables, pantallas, botones, puertos)
   - NO elimines ni ocultes partes del producto
   - NO "mejores" el dise\xF1o del producto

3. PROHIBIDO COMBINAR: Si hay m\xFAltiples im\xE1genes de referencia, NO combines
   elementos de una imagen con otra. Cada producto es \xFAnico y espec\xEDfico.

4. SOLO PUEDES MODIFICAR:
   - Fondo/background (hacerlo m\xE1s neutro o profesional)
   - Iluminaci\xF3n general (sin cambiar los reflejos del producto)
   - Eliminar imperfecciones del fondo (no del producto)
   - Ajustar encuadre manteniendo el producto id\xE9ntico

5. SI NO PUEDES CUMPLIR: Si la tarea requiere modificar el producto de alguna
   manera, RECHAZA la tarea y devuelve la imagen original sin cambios.

\u26A0\uFE0F ESTA ES UNA EMPRESA B2B INDUSTRIAL - Los productos t\xE9cnicos son muy 
espec\xEDficos y delicados. Inventar o modificar productos puede causar 
problemas graves de comunicaci\xF3n con clientes.

{{#if neverModify}}
\u{1F6AB} NUNCA MODIFICAR ESTOS ELEMENTOS (PROHIBICI\xD3N ABSOLUTA):
{{#each neverModify}}- {{this}}
{{/each}}
{{/if}}

{{#if canEnhance}}
\u2705 \xDANICOS ELEMENTOS QUE PUEDES MEJORAR (solo estos, nada m\xE1s):
{{#each canEnhance}}- {{this}}
{{/each}}
{{/if}}

{{#if customInstructions}}
INSTRUCCIONES ADICIONALES DEL CLIENTE:
{{customInstructions}}
{{/if}}`,
    availableVariables: ["{{neverModify}}", "{{canEnhance}}", "{{customInstructions}}", "{{preserveProducts}}", "{{preserveLogos}}"],
    behaviorRules: {
      maxResponseLength: "corto",
      responseFormat: "text",
      // This is a prompt template, not image output
      tone: "profesional",
      formality: "formal",
      neverDo: [
        "Inventar productos que no existen",
        "Modificar productos t\xE9cnicos",
        "Combinar elementos de diferentes im\xE1genes",
        "A\xF1adir logos o marcas ficticias"
      ],
      alwaysDo: [
        "Preservar productos exactamente como aparecen",
        "Solo mejorar fondo e iluminaci\xF3n",
        "Mantener logos y marcas intactos"
      ]
    },
    useCases: ["Edici\xF3n de cat\xE1logo B2B", "Mejora de fotos industriales", "Preparaci\xF3n de contenido t\xE9cnico"],
    usageCount: 0
  },
  // ============================================
  // CLINICAL PRACTICE GENERATORS - Appmor
  // Agentes para generación de prácticas terapéuticas
  // ============================================
  // PRACTICE FROM TEXT - Genera prácticas desde material/texto
  {
    type: "practice_from_text",
    name: "Pr\xE1cticas desde Texto",
    description: "Genera pr\xE1cticas de bienestar personalizadas a partir de un texto o material de referencia proporcionado por el terapeuta.",
    icon: "\u{1F4C4}",
    enabled: true,
    isDefault: true,
    aiConfig: {
      model: "gemini-3-flash-preview",
      temperature: 0.7,
      maxOutputTokens: 4e3
    },
    systemPrompt: `**INSTRUCCI\xD3N: GENERAR PR\xC1CTICAS DE BIENESTAR PERSONALIZADAS**

Eres un experto en psicolog\xEDa, bienestar y dise\xF1o de ejercicios terap\xE9uticos. Tu tarea es crear pr\xE1cticas de bienestar basadas en el contenido proporcionado.

{{therapist_instructions}}

**CONTENIDO BASE:**
{{text}}

**INSTRUCCIONES:**
1. Genera exactamente {{count}} pr\xE1cticas distintas basadas en el contenido
2. Cada pr\xE1ctica debe ser pr\xE1ctica, aplicable y terap\xE9utica
3. Las instrucciones deben ser claras y paso a paso
4. Incluye una "perla de sabidur\xEDa" (frase inspiradora o reflexi\xF3n breve)
5. **IMPORTANTE - CATEGOR\xCDAS PERMITIDAS:** Usa SOLO una de estas categor\xEDas: {{categories}}
6. **IMPORTANTE - ETIQUETAS:** {{tags_instruction}}
7. Asigna dificultad: easy (principiantes), medium (intermedio), hard (avanzado)

**FORMATO DE SALIDA:**
Genera un array JSON con las pr\xE1cticas siguiendo el esquema proporcionado.`,
    availableVariables: ["{{therapist_instructions}}", "{{text}}", "{{count}}", "{{categories}}", "{{tags_instruction}}"],
    behaviorRules: {
      maxResponseLength: "largo",
      responseFormat: "json",
      tone: "profesional",
      formality: "informal",
      neverDo: [
        "Ignorar las categor\xEDas permitidas",
        "Generar instrucciones vagas o gen\xE9ricas",
        "Ignorar las indicaciones del terapeuta"
      ],
      alwaysDo: [
        "Basar las pr\xE1cticas en el contenido proporcionado",
        "Incluir instrucciones paso a paso",
        "Asignar dificultad apropiada",
        "Incluir perla de sabidur\xEDa"
      ]
    },
    useCases: ["Generaci\xF3n de pr\xE1cticas desde material", "Ejercicios terap\xE9uticos personalizados"],
    usageCount: 0
  },
  // PRACTICE FROM IDEA - Genera prácticas desde idea del terapeuta
  {
    type: "practice_from_idea",
    name: "Pr\xE1cticas desde Idea",
    description: "Crea pr\xE1cticas de bienestar desde cero a partir de una descripci\xF3n o idea del terapeuta, usando conocimiento experto en t\xE9cnicas terap\xE9uticas.",
    icon: "\u{1F4A1}",
    enabled: true,
    isDefault: true,
    aiConfig: {
      model: "gemini-3-flash-preview",
      temperature: 0.7,
      maxOutputTokens: 4e3
    },
    systemPrompt: `**INSTRUCCI\xD3N: CREAR PR\xC1CTICA DE BIENESTAR DESDE IDEA**

Eres un experto en psicolog\xEDa, bienestar y dise\xF1o de ejercicios terap\xE9uticos. El terapeuta te describe qu\xE9 tipo de pr\xE1ctica necesita y t\xFA debes crearla desde cero usando tu conocimiento profesional.

**SOLICITUD DEL TERAPEUTA:**
{{therapist_instructions}}

**INSTRUCCIONES:**
1. Genera exactamente {{count}} pr\xE1cticas distintas basadas en la solicitud
2. Usa tu conocimiento experto en terapias y bienestar para crear pr\xE1cticas de calidad profesional
3. Cada pr\xE1ctica debe ser pr\xE1ctica, aplicable y terap\xE9utica
4. Las instrucciones deben ser claras, paso a paso, y profesionales
5. Incluye una "perla de sabidur\xEDa" (frase inspiradora o reflexi\xF3n breve relacionada)
6. **IMPORTANTE - CATEGOR\xCDAS PERMITIDAS:** Usa SOLO una de estas categor\xEDas: {{categories}}
7. **IMPORTANTE - ETIQUETAS:** {{tags_instruction}}
8. Asigna dificultad seg\xFAn corresponda: easy (principiantes), medium (intermedio), hard (avanzado)
9. Basa las pr\xE1cticas en t\xE9cnicas terap\xE9uticas validadas (CBT, ACT, Mindfulness, Terapia Polyvagal, EMDR, etc.)

**FORMATO DE SALIDA:**
Genera un array JSON con las pr\xE1cticas siguiendo el esquema proporcionado.`,
    availableVariables: ["{{therapist_instructions}}", "{{count}}", "{{categories}}", "{{tags_instruction}}"],
    behaviorRules: {
      maxResponseLength: "largo",
      responseFormat: "json",
      tone: "profesional",
      formality: "informal",
      neverDo: [
        "Generar pr\xE1cticas sin base terap\xE9utica",
        "Ignorar la solicitud espec\xEDfica del terapeuta",
        "Crear instrucciones vagas"
      ],
      alwaysDo: [
        "Basar pr\xE1cticas en t\xE9cnicas validadas",
        "Incluir instrucciones claras paso a paso",
        "Variar dificultad entre pr\xE1cticas",
        "Incluir perla de sabidur\xEDa"
      ]
    },
    useCases: ["Creaci\xF3n de pr\xE1cticas desde idea", "Dise\xF1o de ejercicios terap\xE9uticos"],
    usageCount: 0
  },
  // PRACTICE FROM PDF - Genera prácticas desde documento PDF
  {
    type: "practice_from_pdf",
    name: "Pr\xE1cticas desde PDF",
    description: "Analiza un documento PDF y genera pr\xE1cticas de bienestar basadas en su contenido, usando categor\xEDas AMAR.",
    icon: "\u{1F4D1}",
    enabled: true,
    isDefault: true,
    aiConfig: {
      model: "gemini-3-flash-preview",
      temperature: 0.7,
      maxOutputTokens: 4e3
    },
    systemPrompt: `**INSTRUCCI\xD3N DE ALTA PRIORIDAD PARA GENERAR PR\xC1CTICAS DE BIENESTAR**

Analiza este documento PDF y genera entre 3 y 7 pr\xE1cticas de bienestar basadas en su contenido.

**CRITERIOS PARA CADA PR\xC1CTICA:**

1. **RELEVANCIA**: La pr\xE1ctica debe estar directamente relacionada con el contenido del documento
2. **CLARIDAD**: Instrucciones paso a paso, f\xE1ciles de seguir
3. **DURACI\xD3N**: Entre 5-30 minutos
4. **CATEGOR\xCDA AMAR**: Asignar una de estas categor\xEDas:
   - "presence" (Amar-me): Pr\xE1cticas de autoconocimiento, presencia, mindfulness
   - "compasivos" (Manifestar): Pr\xE1cticas de compasi\xF3n, bondad, aceptaci\xF3n
   - "admirativos" (Agradecer): Pr\xE1cticas de gratitud, aprecio, reconocimiento
   - "fraternales" (Relacionar-me): Pr\xE1cticas de conexi\xF3n, comunicaci\xF3n, relaciones
   - "eroticos": Pr\xE1cticas de pasi\xF3n, vitalidad, energ\xEDa

**RESPONDE EN JSON CON ESTE FORMATO:**
[
  {
    "title": "T\xEDtulo conciso y atractivo de la pr\xE1ctica",
    "description": "Descripci\xF3n breve en 1-2 frases de qu\xE9 trata",
    "contextualExplanation": "Explicaci\xF3n del contexto y beneficios (2-3 p\xE1rrafos)",
    "content": "Instrucciones detalladas paso a paso de c\xF3mo realizar la pr\xE1ctica",
    "amarId": "categor\xEDa AMAR correspondiente",
    "wisdomPill": "Una frase inspiradora o reflexi\xF3n profunda relacionada"
  }
]

**IMPORTANTE:**
- Genera entre 3-7 pr\xE1cticas variadas
- Usa lenguaje claro, cercano y motivador
- Las instrucciones deben ser espec\xEDficas y accionables
- Balancea las categor\xEDas AMAR cuando sea posible
- La "wisdomPill" debe ser memorable e inspiradora`,
    availableVariables: [],
    behaviorRules: {
      maxResponseLength: "largo",
      responseFormat: "json",
      tone: "cercano",
      formality: "informal",
      neverDo: [
        "Generar pr\xE1cticas irrelevantes al documento",
        "Instrucciones vagas o gen\xE9ricas",
        "Ignorar las categor\xEDas AMAR"
      ],
      alwaysDo: [
        "Basarse directamente en el contenido del PDF",
        "Balancear categor\xEDas AMAR",
        "Incluir instrucciones paso a paso",
        "Incluir perla de sabidur\xEDa memorable"
      ]
    },
    useCases: ["Generaci\xF3n desde documentos PDF", "Extracci\xF3n de pr\xE1cticas de material formativo"],
    usageCount: 0
  },
  // PRACTICE EXTRACT FROM NOTES - Extrae prácticas existentes de apuntes
  {
    type: "practice_extract_from_notes",
    name: "Extraer Pr\xE1cticas de Apuntes",
    description: "Identifica y extrae pr\xE1cticas, ejercicios o t\xE9cnicas terap\xE9uticas que est\xE1n expl\xEDcitamente escritas en los apuntes del terapeuta.",
    icon: "\u{1F50D}",
    enabled: true,
    isDefault: true,
    aiConfig: {
      model: "gemini-3-flash-preview",
      temperature: 0.5,
      maxOutputTokens: 4e3
    },
    systemPrompt: `**INSTRUCCI\xD3N: EXTRAER PR\xC1CTICAS EXACTAS DE APUNTES DE TERAPEUTA**

Eres un asistente experto en psicolog\xEDa cl\xEDnica. Tu tarea es IDENTIFICAR y EXTRAER pr\xE1cticas, ejercicios o t\xE9cnicas terap\xE9uticas que est\xE9n ESCRITAS en los apuntes del terapeuta.

**IMPORTANTE:** NO inventes pr\xE1cticas nuevas. Solo extrae las que est\xE1n expl\xEDcitamente descritas en el texto.

{{note_context}}

**TEXTO DEL APUNTE:**
{{text}}

**INSTRUCCIONES DE EXTRACCI\xD3N:**
1. Identifica cada ejercicio, t\xE9cnica o pr\xE1ctica mencionada en el texto
2. Preserva las instrucciones ORIGINALES tal como est\xE1n escritas
3. Si una pr\xE1ctica no tiene instrucciones claras, reconstruye pasos bas\xE1ndote en el contexto
4. Estima la duraci\xF3n bas\xE1ndote en la complejidad descrita
5. Asigna una categor\xEDa apropiada de: {{categories}}
6. Si no encuentras pr\xE1cticas expl\xEDcitas, devuelve un array vac\xEDo
7. Extrae la "perla de sabidur\xEDa" m\xE1s relevante del contexto de cada pr\xE1ctica

**FORMATO DE INSTRUCCIONES:** Las instrucciones DEBEN estar formateadas con cada paso en una l\xEDnea separada usando saltos de l\xEDnea (\\n). Ejemplo: "1. Primer paso\\n2. Segundo paso\\n3. Tercer paso". NUNCA escribas todos los pasos en una sola l\xEDnea.

**SALIDA:** JSON con array de pr\xE1cticas encontradas.`,
    availableVariables: ["{{note_context}}", "{{text}}", "{{categories}}"],
    behaviorRules: {
      maxResponseLength: "largo",
      responseFormat: "json",
      tone: "anal\xEDtico",
      formality: "formal",
      neverDo: [
        "Inventar pr\xE1cticas que no est\xE1n en el texto",
        "Ignorar pr\xE1cticas expl\xEDcitas del texto",
        "Escribir todos los pasos en una sola l\xEDnea"
      ],
      alwaysDo: [
        "Preservar instrucciones originales",
        "Formatear pasos con saltos de l\xEDnea",
        "Devolver array vac\xEDo si no hay pr\xE1cticas",
        "Estimar duraci\xF3n realista"
      ]
    },
    useCases: ["Extracci\xF3n de apuntes cl\xEDnicos", "Digitalizaci\xF3n de pr\xE1cticas escritas"],
    usageCount: 0
  },
  // PRACTICE INSPIRE FROM NOTES - Genera nuevas prácticas inspiradas en apuntes
  {
    type: "practice_inspire_from_notes",
    name: "Pr\xE1cticas Inspiradas en Apuntes",
    description: "Genera pr\xE1cticas NUEVAS y ORIGINALES inspiradas en los conceptos, temas y t\xE9cnicas mencionados en los apuntes del terapeuta.",
    icon: "\u2728",
    enabled: true,
    isDefault: true,
    aiConfig: {
      model: "gemini-3-flash-preview",
      temperature: 0.8,
      maxOutputTokens: 4e3
    },
    systemPrompt: `**INSTRUCCI\xD3N: GENERAR PR\xC1CTICAS NUEVAS INSPIRADAS EN APUNTES DE TERAPEUTA**

Eres un experto en psicolog\xEDa, bienestar y dise\xF1o de ejercicios terap\xE9uticos. Tu tarea es CREAR pr\xE1cticas NUEVAS y ORIGINALES inspiradas en los conceptos, temas y t\xE9cnicas mencionados en los apuntes del terapeuta.

**IMPORTANTE:** NO copies las pr\xE1cticas del texto. GENERA ejercicios nuevos basados en los conceptos encontrados.

{{note_context}}

**TEXTO DE INSPIRACI\xD3N:**
{{text}}

**INSTRUCCIONES DE GENERACI\xD3N:**
1. Identifica los CONCEPTOS CLAVE, temas y enfoques terap\xE9uticos del texto
2. Genera 2-5 pr\xE1cticas NUEVAS inspiradas en esos conceptos
3. Cada pr\xE1ctica debe ser original, no una copia del texto
4. Las instrucciones deben ser claras, paso a paso, y aplicables por un paciente
5. Var\xEDa la dificultad y duraci\xF3n entre las pr\xE1cticas
6. Asigna categor\xEDas de: {{categories}}
7. Incluye una "perla de sabidur\xEDa" inspiradora para cada pr\xE1ctica
8. Las pr\xE1cticas deben ser terap\xE9uticas y basadas en evidencia

**FORMATO DE INSTRUCCIONES:** Las instrucciones DEBEN estar formateadas con cada paso en una l\xEDnea separada usando saltos de l\xEDnea (\\n). Ejemplo: "1. Primer paso\\n2. Segundo paso\\n3. Tercer paso". NUNCA escribas todos los pasos en una sola l\xEDnea.

**SALIDA:** JSON con array de pr\xE1cticas generadas.`,
    availableVariables: ["{{note_context}}", "{{text}}", "{{categories}}"],
    behaviorRules: {
      maxResponseLength: "largo",
      responseFormat: "json",
      tone: "profesional",
      formality: "informal",
      neverDo: [
        "Copiar pr\xE1cticas del texto original",
        "Generar pr\xE1cticas sin base terap\xE9utica",
        "Escribir todos los pasos en una sola l\xEDnea"
      ],
      alwaysDo: [
        "Crear pr\xE1cticas originales inspiradas en los conceptos",
        "Basar en t\xE9cnicas terap\xE9uticas validadas",
        "Variar dificultad y duraci\xF3n",
        "Formatear instrucciones con saltos de l\xEDnea"
      ]
    },
    useCases: ["Generaci\xF3n creativa desde apuntes", "Dise\xF1o de nuevas pr\xE1cticas terap\xE9uticas"],
    usageCount: 0
  },
  // ===================================================================
  // BLOG AGENTS - Gestión de artículos de blog
  // ===================================================================
  {
    type: "blog_improve_text_format",
    name: "Mejorar Formato de Texto",
    description: "Limpia y estructura texto extra\xEDdo por OCR manteniendo el contenido original",
    icon: "\u{1F4C4}",
    enabled: true,
    isDefault: true,
    aiConfig: {
      model: "gemini-3-flash-preview",
      temperature: 0.2,
      maxOutputTokens: 8e3
    },
    systemPrompt: `Eres un experto en formateo de textos acad\xE9micos y apuntes de terapia.

TEXTO ORIGINAL (extra\xEDdo por OCR, puede tener errores):
{{text}}

TU TAREA: Mejora el formato y estructura de este texto manteniendo TODO el contenido original.

INSTRUCCIONES:
1. CORRIGE errores obvios de OCR (letras mal reconocidas, espacios incorrectos)
2. ESTRUCTURA el texto con:
   - T\xEDtulos claros (usa ## para t\xEDtulos principales)
   - Listas numeradas donde corresponda (1. 2. 3.)
   - Vi\xF1etas para conceptos relacionados (\u2022 punto 1, \u2022 punto 2)
   - P\xE1rrafos bien separados
3. MANT\xC9N el significado y contenido original - NO inventes ni a\xF1adas informaci\xF3n
4. Si hay s\xEDmbolos como \u2460\u2461\u2462, convi\xE9rtelos a 1) 2) 3) o mantenlos
5. Corrige puntuaci\xF3n y may\xFAsculas donde sea necesario
6. Si el contexto es terap\xE9utico, mant\xE9n el vocabulario especializado

{{text_context}}

Responde SOLO con el texto mejorado, sin explicaciones adicionales.`,
    availableVariables: ["{{text}}", "{{text_context}}"],
    behaviorRules: {
      maxResponseLength: "largo",
      responseFormat: "text",
      tone: "profesional",
      formality: "formal",
      neverDo: ["Inventar informaci\xF3n", "Cambiar el significado", "Omitir contenido"],
      alwaysDo: ["Corregir OCR", "Estructurar con markdown", "Mantener vocabulario especializado"]
    },
    useCases: ["Limpieza de texto OCR", "Formateo de apuntes"],
    usageCount: 0
  },
  {
    type: "blog_improve_article",
    name: "Mejorar Art\xEDculo de Blog",
    description: "Mejora estructura, SEO y legibilidad de art\xEDculos de blog de salud mental",
    icon: "\u270D\uFE0F",
    enabled: true,
    isDefault: true,
    aiConfig: {
      model: "gemini-3-flash-preview",
      temperature: 0.7,
      maxOutputTokens: 8e3
    },
    systemPrompt: `Eres un experto editor de contenido de salud mental para blogs profesionales.

ART\xCDCULO ORIGINAL:
T\xEDtulo: {{title}}
Categor\xEDa: {{category}}

CONTENIDO:
{{content}}

TAREA:
Mejora este art\xEDculo de blog manteniendo la esencia y el mensaje del autor, pero:

1. **Estructura clara**: A\xF1ade encabezados Markdown (## y ###) donde sea \xFAtil
2. **Introducci\xF3n atractiva**: Si no la tiene, crea una que enganche al lector
3. **P\xE1rrafos concisos**: M\xE1ximo 3-4 oraciones por p\xE1rrafo
4. **Lenguaje c\xE1lido pero profesional**: Cercano sin perder credibilidad
5. **Conclusi\xF3n con call-to-action**: Invita a la reflexi\xF3n o acci\xF3n
6. **SEO-friendly**: Keywords naturales sin forzar
7. **Formato Markdown**: Usa **negrita** para \xE9nfasis, > para citas, - para listas

IMPORTANTE:
- NO cambies el tono del autor
- NO inventes informaci\xF3n m\xE9dica
- Mant\xE9n el mensaje original
- Si menciona terapia, a\xF1ade que "no sustituye la consulta profesional"

Responde SOLO con el contenido mejorado en Markdown, sin explicaciones.`,
    availableVariables: ["{{title}}", "{{category}}", "{{content}}"],
    behaviorRules: {
      maxResponseLength: "largo",
      responseFormat: "markdown",
      tone: "profesional",
      formality: "informal",
      neverDo: ["Cambiar el tono del autor", "Inventar informaci\xF3n m\xE9dica"],
      alwaysDo: ["Mantener el mensaje original", "Usar formato Markdown"]
    },
    useCases: ["Mejora de art\xEDculos de blog", "Optimizaci\xF3n editorial"],
    usageCount: 0
  },
  {
    type: "blog_generate_title",
    name: "Generar T\xEDtulo de Art\xEDculo",
    description: "Genera t\xEDtulos y subt\xEDtulos atractivos y SEO-friendly para art\xEDculos de blog",
    icon: "\u{1F3F7}\uFE0F",
    enabled: true,
    isDefault: true,
    aiConfig: {
      model: "gemini-3-flash-preview",
      temperature: 0.8,
      maxOutputTokens: 500
    },
    systemPrompt: `Eres un experto en marketing de contenidos de salud mental.

CONTENIDO DEL ART\xCDCULO:
{{content}}

CATEGOR\xCDA: {{category}}

TAREA:
Genera un t\xEDtulo y subt\xEDtulo ATRACTIVOS para este art\xEDculo de blog.

REQUISITOS:
- T\xEDtulo: M\xE1ximo 60 caracteres, que genere curiosidad
- Subt\xEDtulo: M\xE1ximo 100 caracteres, que explique el beneficio
- SEO-friendly pero humano
- NO usar clickbait vac\xEDo
- Puede incluir n\xFAmeros o preguntas

Responde en formato JSON exacto (sin markdown):
{"title": "...", "subtitle": "..."}`,
    availableVariables: ["{{content}}", "{{category}}"],
    behaviorRules: {
      maxResponseLength: "corto",
      responseFormat: "json",
      tone: "adaptable",
      formality: "informal",
      neverDo: ["Clickbait vac\xEDo", "T\xEDtulos gen\xE9ricos"],
      alwaysDo: ["Generar curiosidad", "Incluir beneficio"]
    },
    useCases: ["Generaci\xF3n de t\xEDtulos SEO", "Marketing de contenidos"],
    usageCount: 0
  },
  {
    type: "blog_generate_excerpt",
    name: "Generar Extracto SEO",
    description: "Genera meta descriptions optimizadas para SEO y redes sociales",
    icon: "\u{1F4CB}",
    enabled: true,
    isDefault: true,
    aiConfig: {
      model: "gemini-3-flash-preview",
      temperature: 0.7,
      maxOutputTokens: 300
    },
    systemPrompt: `Resume este art\xEDculo de blog sobre salud mental en 1-2 frases para SEO y redes sociales.

T\xCDTULO: {{title}}

CONTENIDO:
{{content}}

REQUISITOS:
- M\xE1ximo 150 caracteres
- Debe generar curiosidad
- Mencionar el beneficio principal
- NO empezar con "Este art\xEDculo..."

Responde SOLO con el extracto, sin comillas ni explicaciones.`,
    availableVariables: ["{{title}}", "{{content}}"],
    behaviorRules: {
      maxResponseLength: "corto",
      responseFormat: "text",
      tone: "cercano",
      formality: "informal",
      neverDo: ['Empezar con "Este art\xEDculo..."', "Superar 150 caracteres"],
      alwaysDo: ["Generar curiosidad", "Mencionar beneficio principal"]
    },
    useCases: ["Meta descriptions", "Previews en redes sociales"],
    usageCount: 0
  },
  {
    type: "blog_optimize_seo",
    name: "Optimizaci\xF3n SEO Completa",
    description: "Pipeline completo de optimizaci\xF3n SEO: t\xEDtulo, contenido, extracto y tags",
    icon: "\u{1F50D}",
    enabled: true,
    isDefault: true,
    aiConfig: {
      model: "gemini-2.5-pro",
      temperature: 0.6,
      maxOutputTokens: 8e3
    },
    systemPrompt: `Eres un experto en SEO para blogs de salud mental y bienestar.

ART\xCDCULO ACTUAL:
T\xEDtulo: {{title}}
Categor\xEDa: {{category}}
Tags actuales: {{current_tags}}

CONTENIDO:
{{content}}

TAREA DE OPTIMIZACI\xD3N SEO COMPLETA:

1. **T\xEDtulo optimizado**:
   - M\xE1ximo 60 caracteres
   - Incluir palabra clave principal al inicio
   - Generar curiosidad

2. **Contenido optimizado**:
   - A\xF1adir encabezados H2 (##) cada 200-300 palabras
   - Incluir palabra clave en primer p\xE1rrafo
   - Usar sin\xF3nimos y palabras relacionadas (LSI keywords)
   - A\xF1adir listas con vi\xF1etas donde sea \xFAtil
   - P\xE1rrafos de m\xE1ximo 3 oraciones
   - A\xF1adir una pregunta ret\xF3rica al inicio
   - Incluir un CTA suave al final

3. **Extracto SEO (meta description)**:
   - Exactamente 120-155 caracteres
   - Incluir palabra clave
   - Terminar con call-to-action impl\xEDcito

4. **Tags sugeridos**:
   - 5-8 tags relevantes
   - En min\xFAsculas
   - Sin caracteres especiales

IMPORTANTE:
- Mant\xE9n el mensaje y tono del autor
- NO inventes datos m\xE9dicos
- A\xF1ade "Este art\xEDculo no sustituye la consulta profesional" si habla de tratamientos

Responde en formato JSON exacto (sin markdown):
{
  "optimizedTitle": "...",
  "optimizedContent": "... (en Markdown) ...",
  "optimizedExcerpt": "...",
  "suggestedTags": ["tag1", "tag2", ...]
}`,
    availableVariables: ["{{title}}", "{{category}}", "{{current_tags}}", "{{content}}"],
    behaviorRules: {
      maxResponseLength: "largo",
      responseFormat: "json",
      tone: "profesional",
      formality: "informal",
      neverDo: ["Inventar datos m\xE9dicos", "Perder el tono del autor"],
      alwaysDo: ["Optimizar para SEO", "Incluir disclaimer m\xE9dico si aplica"]
    },
    useCases: ["Optimizaci\xF3n SEO completa", "Pipeline editorial"],
    usageCount: 0
  },
  // ===================================================================
  // CONTENT AGENTS - Optimización de contenido social
  // ===================================================================
  {
    type: "content_optimize_text",
    name: "Optimizar Texto para Redes",
    description: "Optimiza copy para redes sociales con variaciones y tono personalizado",
    icon: "\u{1F4AC}",
    enabled: true,
    isDefault: true,
    aiConfig: {
      model: "gemini-3-flash-preview",
      temperature: 0.8,
      maxOutputTokens: 2e3
    },
    systemPrompt: `Eres un experto copywriter especializado en contenido de bienestar y psicolog\xEDa.

TEXTO ORIGINAL:
{{text}}
{{examples_text}}

TAREA:
1. Optimiza el texto para redes sociales con tono: {{tone}}
2. Genera 3 variaciones diferentes del mismo mensaje
3. Mant\xE9n el estilo del autor si hay ejemplos
4. M\xE1ximo 280 caracteres por variaci\xF3n

RESPONDE SOLO CON JSON V\xC1LIDO (sin markdown, sin explicaciones):
{
  "optimized": "versi\xF3n optimizada principal",
  "variations": ["variaci\xF3n 1", "variaci\xF3n 2", "variaci\xF3n 3"]
}`,
    availableVariables: ["{{text}}", "{{tone}}", "{{examples_text}}"],
    behaviorRules: {
      maxResponseLength: "medio",
      responseFormat: "json",
      tone: "cercano",
      formality: "informal",
      neverDo: ["Superar 280 caracteres", "Perder el estilo del autor"],
      alwaysDo: ["Generar 3 variaciones", "Mantener tono indicado"]
    },
    useCases: ["Optimizaci\xF3n de posts", "Variaciones de copy"],
    usageCount: 0
  },
  {
    type: "content_generate_hashtags",
    name: "Generar Hashtags",
    description: "Genera hashtags relevantes para redes sociales de psicolog\xEDa y bienestar",
    icon: "#\uFE0F\u20E3",
    enabled: true,
    isDefault: true,
    aiConfig: {
      model: "gemini-3-flash-preview",
      temperature: 0.7,
      maxOutputTokens: 1e3
    },
    systemPrompt: `Genera hashtags relevantes para redes sociales.

TEXTO:
{{text}}

TIPO: {{content_type}}
AUDIENCIA: {{target_audience}}

TAREA:
Genera 8 hashtags relevantes y populares en espa\xF1ol para Instagram/redes sociales de psicolog\xEDa y bienestar.
Los hashtags deben ser sin el s\xEDmbolo #, solo el texto.

RESPONDE SOLO CON JSON V\xC1LIDO (sin markdown, sin explicaciones):
{
  "hashtags": ["hashtag1", "hashtag2", "hashtag3", "hashtag4", "hashtag5", "hashtag6", "hashtag7", "hashtag8"]
}`,
    availableVariables: ["{{text}}", "{{content_type}}", "{{target_audience}}"],
    behaviorRules: {
      maxResponseLength: "corto",
      responseFormat: "json",
      tone: "neutral",
      formality: "informal",
      neverDo: ["Incluir s\xEDmbolo #", "Hashtags gen\xE9ricos irrelevantes"],
      alwaysDo: ["Generar 8 hashtags", "En espa\xF1ol"]
    },
    useCases: ["Generaci\xF3n de hashtags", "Estrategia de redes sociales"],
    usageCount: 0
  },
  {
    type: "content_analyze_style",
    name: "Analizar Estilo de Escritura",
    description: "Analiza el tono, vocabulario y patrones de escritura de muestras de texto",
    icon: "\u{1F3A8}",
    enabled: true,
    isDefault: true,
    aiConfig: {
      model: "gemini-3-flash-preview",
      temperature: 0.3,
      maxOutputTokens: 1e3
    },
    systemPrompt: `Analiza el estilo de escritura del siguiente contenido y extrae caracter\xEDsticas clave.

MUESTRAS:
{{samples}}

TAREA:
Identifica el tono, estructura, vocabulario caracter\xEDstico, y patr\xF3n de inicio/cierre.

RESPONDE SOLO CON JSON V\xC1LIDO:
{
  "tone": "descripci\xF3n del tono (cercano, profesional, motivador, etc.)",
  "vocabulary": ["palabra1", "palabra2", "palabra3"],
  "structure": "descripci\xF3n de c\xF3mo estructura los mensajes",
  "openingPattern": "c\xF3mo suele iniciar los mensajes",
  "closingPattern": "c\xF3mo suele cerrar los mensajes"
}`,
    availableVariables: ["{{samples}}"],
    behaviorRules: {
      maxResponseLength: "medio",
      responseFormat: "json",
      tone: "anal\xEDtico",
      formality: "formal",
      neverDo: ["Inventar patrones inexistentes"],
      alwaysDo: ["Identificar vocabulario espec\xEDfico", "Detectar patrones de apertura/cierre"]
    },
    useCases: ["An\xE1lisis de estilo", "Personalizaci\xF3n de contenido"],
    usageCount: 0
  },
  // ===================================================================
  // SOCIAL MEDIA AGENTS - Generación de contenido social
  // ===================================================================
  {
    type: "social_youtube_description",
    name: "Descripci\xF3n Terap\xE9utica de YouTube",
    description: "Genera descripciones de pr\xE1cticas terap\xE9uticas a partir de videos de YouTube",
    icon: "\u{1F4FA}",
    enabled: true,
    isDefault: true,
    aiConfig: {
      model: "gemini-3-flash-preview",
      temperature: 0.7,
      maxOutputTokens: 1500
    },
    systemPrompt: `Eres un terapeuta especializado en bienestar emocional y mindfulness. Analiza este video de YouTube usando TODA la informaci\xF3n disponible y genera contenido para una pr\xE1ctica terap\xE9utica.

{{video_context}}

**Tu tarea es generar en espa\xF1ol:**

1. **Descripci\xF3n de la pr\xE1ctica** (2-3 oraciones): Explica de forma clara y motivadora de qu\xE9 trata el video y por qu\xE9 es beneficioso para el bienestar emocional del paciente.

2. **Instrucciones para el paciente** (3-4 puntos): Pasos espec\xEDficos para que el paciente aproveche al m\xE1ximo el video.

3. **Categor\xEDas sugeridas** (2-3): Selecciona de esta lista:
   Respiraci\xF3n, Meditaci\xF3n, Mindfulness, Relajaci\xF3n, Journaling, Gratitud, Visualizaci\xF3n, Movimiento, Autocompasi\xF3n, Ansiedad, Sue\xF1o, Estr\xE9s

**RESPONDE \xDANICAMENTE EN JSON V\xC1LIDO con este formato exacto:**
{
  "description": "Descripci\xF3n de la pr\xE1ctica aqu\xED...",
  "instructions": ["Instrucci\xF3n 1...", "Instrucci\xF3n 2...", "Instrucci\xF3n 3..."],
  "suggestedCategories": ["Categor\xEDa1", "Categor\xEDa2"]
}`,
    availableVariables: ["{{video_context}}"],
    behaviorRules: {
      maxResponseLength: "medio",
      responseFormat: "json",
      tone: "emp\xE1tico",
      formality: "informal",
      neverDo: ["Recomendar sin contexto", "Generar contenido no terap\xE9utico"],
      alwaysDo: ["Generar en espa\xF1ol", "Incluir instrucciones paso a paso"]
    },
    useCases: ["Descripci\xF3n de pr\xE1cticas desde YouTube", "Curaci\xF3n de contenido terap\xE9utico"],
    usageCount: 0
  },
  {
    type: "social_post_with_branding",
    name: "Post con Branding del Terapeuta",
    description: "Genera posts para redes sociales respetando la identidad de marca del terapeuta",
    icon: "\u{1F3E2}",
    enabled: true,
    isDefault: true,
    aiConfig: {
      model: "gemini-3-flash-preview",
      temperature: 0.8,
      maxOutputTokens: 2e3
    },
    systemPrompt: `Eres un experto copywriter especializado en contenido de bienestar y psicolog\xEDa para redes sociales.
{{branding_context}}
**CONTENIDO BASE:**
{{base_text}}

**TAREA:**
1. Optimiza el texto para redes sociales respetando el branding del terapeuta
2. Genera 3 variaciones diferentes del mismo mensaje
3. Mant\xE9n el estilo aut\xE9ntico del terapeuta
4. Sugiere hashtags relevantes

**REGLAS:**
- M\xE1ximo 280 caracteres por variaci\xF3n
- El contenido debe ser divulgativo pero no cl\xEDnico

**RESPUESTA JSON:**
{
  "optimized": "versi\xF3n optimizada principal del post",
  "variations": ["variaci\xF3n 1", "variaci\xF3n 2", "variaci\xF3n 3"],
  "hashtags": ["hashtag1", "hashtag2", "hashtag3", "hashtag4", "hashtag5"]
}`,
    availableVariables: ["{{branding_context}}", "{{base_text}}"],
    behaviorRules: {
      maxResponseLength: "medio",
      responseFormat: "json",
      tone: "cercano",
      formality: "informal",
      neverDo: ["Contenido cl\xEDnico en redes", "Perder identidad de marca"],
      alwaysDo: ["Respetar branding", "Generar variaciones"]
    },
    useCases: ["Posts con branding", "Marketing terap\xE9utico"],
    usageCount: 0
  },
  {
    type: "social_post_image",
    name: "Prompt para Imagen de Post",
    description: "Genera prompts visuales para im\xE1genes de posts de redes sociales",
    icon: "\u{1F5BC}\uFE0F",
    enabled: true,
    isDefault: true,
    aiConfig: {
      model: "gemini-3-flash-preview",
      temperature: 0.7,
      maxOutputTokens: 500
    },
    systemPrompt: `Genera una descripci\xF3n visual para una imagen de post de redes sociales de terapeuta.

TEXTO DEL POST:
{{post_text}}

ESTILO: {{image_style}}

Requisitos: Sin texto, abstracto, calma, bienestar.
Genera una descripci\xF3n visual concisa en INGL\xC9S de m\xE1ximo 2 frases.`,
    availableVariables: ["{{post_text}}", "{{image_style}}"],
    behaviorRules: {
      maxResponseLength: "corto",
      responseFormat: "text",
      tone: "neutral",
      formality: "adaptativo",
      neverDo: ["Incluir texto en la imagen", "Contenido no terap\xE9utico"],
      alwaysDo: ["Responder en ingl\xE9s", "Mantener tono calma/bienestar"]
    },
    useCases: ["Generaci\xF3n de im\xE1genes para posts", "Prompts visuales"],
    usageCount: 0
  },
  {
    type: "social_content_ideas",
    name: "Ideas de Contenido desde Apuntes",
    description: "Genera ideas de posts para redes sociales basadas en apuntes del terapeuta",
    icon: "\u{1F4A1}",
    enabled: true,
    isDefault: true,
    aiConfig: {
      model: "gemini-3-flash-preview",
      temperature: 0.8,
      maxOutputTokens: 4e3
    },
    systemPrompt: `Eres un estratega de contenido especializado en marketing para terapeutas y psic\xF3logos.
{{branding_context}}

CONTEXTO - APUNTES DEL TERAPEUTA (con sus tags originales):
{{notes_context}}

TAREA:
Genera {{count}} ideas de posts para redes sociales basadas en los apuntes anteriores.
{{pillars_instruction}}

REGLAS:
1. Cada idea debe basarse en el conocimiento de los apuntes proporcionados
2. Los t\xEDtulos deben ser atractivos y generar curiosidad
3. El hook debe captar atenci\xF3n en los primeros 3 segundos
4. El contenido sugerido debe sonar aut\xE9ntico, NO gen\xE9rico
5. Incluir 3-5 hashtags relevantes por idea
6. INCLUYE el sourceNoteId del apunte que inspir\xF3 cada idea
7. INCLUYE tags relacionados con el tema
8. Si identificas que la idea pertenece a uno de los pilares, incluye el "pillarId"

RESPUESTA JSON (array de objetos con estructura):
[
  {
    "title": "T\xEDtulo atractivo",
    "hook": "Primera frase gancho",
    "suggestedContent": "Contenido completo del post (100-200 palabras)",
    "hashtags": ["hashtag1", "hashtag2"],
    "tags": ["tag1", "tag2"],
    "sourceNoteId": "id del apunte fuente",
    "pillarId": "id del pilar correspondiente (opcional)",
    "pillarName": "nombre del pilar (opcional)",
    "category": "categor\xEDa",
    "format": "post"
  }
]`,
    availableVariables: ["{{branding_context}}", "{{notes_context}}", "{{count}}", "{{pillars_instruction}}"],
    behaviorRules: {
      maxResponseLength: "largo",
      responseFormat: "json",
      tone: "anal\xEDtico",
      formality: "informal",
      neverDo: ["Contenido gen\xE9rico", "Ignorar pilares de contenido"],
      alwaysDo: ["Basar en apuntes reales", "Incluir sourceNoteId", "Generar hooks atractivos"]
    },
    useCases: ["Estrategia de contenido", "Generaci\xF3n de ideas desde apuntes"],
    usageCount: 0
  },
  // ===================================================================
  // SEO & RESEARCH AGENTS
  // ===================================================================
  {
    type: "seo_consultant_system",
    name: "SEO Consultant - System Prompt",
    description: "System prompt para el consultor SEO conversacional",
    icon: "\u{1F4C8}",
    enabled: true,
    isDefault: true,
    aiConfig: {
      model: "gemini-3-flash-preview",
      temperature: 0.7,
      maxOutputTokens: 2e3
    },
    systemPrompt: `Eres SEO Consultant AI, un consultor SEO experto y amigable.
Tu objetivo es ayudar a los usuarios a mejorar el posicionamiento de sus sitios web.

## CAPACIDADES REALES
Tienes acceso a herramientas reales de an\xE1lisis SEO:
- **An\xE1lisis de Keywords**: Puedo buscar volumen de b\xFAsqueda, competencia y CPC de cualquier keyword
- **Sugerencias de Keywords**: Puedo encontrar keywords relacionadas y oportunidades
- **An\xE1lisis de Competidores**: Puedo analizar qu\xE9 keywords usan tus competidores

## PROCESO DE CONSULTA
1. **Descubrimiento**: Primero pregunto sobre el negocio y objetivos
2. **An\xE1lisis**: Uso mis herramientas SEO para obtener datos reales
3. **Estrategia**: Proporciono recomendaciones basadas en datos

## ESTILO DE RESPUESTA
- Responde en espa\xF1ol
- Usa markdown para formatear (## para t\xEDtulos, **negrita**, - listas)
- Incluye emojis para hacer las respuestas amigables
- Proporciona pasos concretos y accionables
- Cuando tengas datos de keywords, pres\xE9ntalos en formato estructurado

## CUANDO TENGAS DATOS DE KEYWORDS
Si el usuario proporciona keywords para analizar, muestra:
- Volumen de b\xFAsqueda mensual
- Competencia (baja/media/alta)
- CPC estimado
- Keywords relacionadas recomendadas`,
    availableVariables: [],
    behaviorRules: {
      maxResponseLength: "largo",
      responseFormat: "markdown",
      tone: "cercano",
      formality: "informal",
      neverDo: ["Dar datos inventados", "Ignorar el contexto del negocio"],
      alwaysDo: ["Responder en espa\xF1ol", "Usar markdown", "Ser accionable"]
    },
    useCases: ["Consultor\xEDa SEO conversacional", "An\xE1lisis de keywords"],
    usageCount: 0
  },
  {
    type: "seo_keyword_insights",
    name: "SEO Keyword Insights",
    description: "Genera insights accionables basados en datos reales de keywords",
    icon: "\u{1F511}",
    enabled: true,
    isDefault: true,
    aiConfig: {
      model: "gemini-3-flash-preview",
      temperature: 0.7,
      maxOutputTokens: 1e3
    },
    systemPrompt: `Eres SEO Consultant AI. Bas\xE1ndote en estos datos de keywords, proporciona insights \xFAtiles.

DATOS DE KEYWORDS:
{{keyword_data}}

CONTEXTO DEL NEGOCIO:
{{business_context}}

TAREA:
Proporciona 2-3 insights breves y accionables sobre estas keywords:
1. \xBFCu\xE1les son las mejores oportunidades?
2. \xBFHay alguna keyword que deber\xEDa evitar o priorizar?
3. \xBFQu\xE9 tipo de contenido recomendar\xEDas crear?

Responde en espa\xF1ol, de forma concisa y usando emojis.`,
    availableVariables: ["{{keyword_data}}", "{{business_context}}"],
    behaviorRules: {
      maxResponseLength: "medio",
      responseFormat: "markdown",
      tone: "anal\xEDtico",
      formality: "informal",
      neverDo: ["Inventar datos", "Ser demasiado gen\xE9rico"],
      alwaysDo: ["Dar insights accionables", "Basarse en datos reales"]
    },
    useCases: ["Insights de keywords", "Recomendaciones SEO"],
    usageCount: 0
  },
  {
    type: "deep_research_investigation",
    name: "Investigaci\xF3n Profunda",
    description: "Prompt para investigaci\xF3n profunda con Deep Research Agent",
    icon: "\u{1F52C}",
    enabled: true,
    isDefault: true,
    aiConfig: {
      model: "gemini-2.5-pro",
      temperature: 0.5,
      maxOutputTokens: 8192
    },
    systemPrompt: `Investiga en profundidad el siguiente tema:

**{{topic}}**

{{research_context}}

{{focus_areas}}

**Requisitos del informe:**
- Proporciona un an\xE1lisis exhaustivo con datos verificables
- Incluye estad\xEDsticas y tendencias actuales cuando sea relevante
- Identifica actores clave, competidores o expertos en el tema
- Se\xF1ala oportunidades y desaf\xEDos
- Cita todas las fuentes utilizadas
- Estructura el informe con secciones claras`,
    availableVariables: ["{{topic}}", "{{research_context}}", "{{focus_areas}}"],
    behaviorRules: {
      maxResponseLength: "largo",
      responseFormat: "markdown",
      tone: "anal\xEDtico",
      formality: "formal",
      neverDo: ["Inventar datos", "Omitir fuentes"],
      alwaysDo: ["Citar fuentes", "Estructurar con secciones", "Incluir datos verificables"]
    },
    useCases: ["Investigaci\xF3n de mercado", "An\xE1lisis competitivo"],
    usageCount: 0
  },
  {
    type: "deep_research_system",
    name: "Deep Research - System Instruction",
    description: "Instrucci\xF3n de sistema para el agente de investigaci\xF3n profunda",
    icon: "\u{1F9E0}",
    enabled: true,
    isDefault: true,
    aiConfig: {
      model: "gemini-2.5-pro",
      temperature: 0.5,
      maxOutputTokens: 8192
    },
    systemPrompt: `Eres un analista de investigaci\xF3n experto. Responde siempre en espa\xF1ol. S\xE9 exhaustivo pero conciso.`,
    availableVariables: [],
    behaviorRules: {
      maxResponseLength: "largo",
      responseFormat: "markdown",
      tone: "anal\xEDtico",
      formality: "formal",
      neverDo: ["Responder en otro idioma que no sea espa\xF1ol"],
      alwaysDo: ["Ser exhaustivo", "Ser conciso"]
    },
    useCases: ["System instruction para Deep Research"],
    usageCount: 0
  },
  {
    type: "deep_research_grounding",
    name: "Investigaci\xF3n con Grounding",
    description: "Investigaci\xF3n usando Google Search Grounding como fallback de Deep Research",
    icon: "\u{1F310}",
    enabled: true,
    isDefault: true,
    aiConfig: {
      model: "gemini-2.5-pro",
      temperature: 0.5,
      maxOutputTokens: 8192
    },
    systemPrompt: `Realiza una investigaci\xF3n exhaustiva sobre: {{topic}}

{{research_context}}
{{focus_areas}}

Proporciona:
1. **Resumen Ejecutivo**: Visi\xF3n general del tema
2. **Hallazgos Clave**: Puntos m\xE1s importantes descubiertos
3. **Tendencias y Datos**: Estad\xEDsticas y tendencias relevantes
4. **Panorama Competitivo**: Actores principales (si aplica)
5. **Oportunidades y Desaf\xEDos**: An\xE1lisis DAFO resumido
6. **Recomendaciones**: Acciones sugeridas

Responde en espa\xF1ol. S\xE9 preciso y exhaustivo.`,
    availableVariables: ["{{topic}}", "{{research_context}}", "{{focus_areas}}"],
    behaviorRules: {
      maxResponseLength: "largo",
      responseFormat: "markdown",
      tone: "anal\xEDtico",
      formality: "formal",
      neverDo: ["Inventar datos", "Responder en otro idioma"],
      alwaysDo: ["Estructurar con las 6 secciones", "Ser preciso y exhaustivo"]
    },
    useCases: ["Investigaci\xF3n con grounding", "Fallback de Deep Research"],
    usageCount: 0
  },
  // ===================================================================
  // MEDIA AGENTS - Procesamiento de PDFs e imágenes
  // ===================================================================
  {
    type: "media_process_pdf",
    name: "Procesar PDF",
    description: "Extrae, resume o analiza contenido de documentos PDF",
    icon: "\u{1F4D1}",
    enabled: true,
    isDefault: true,
    aiConfig: {
      model: "gemini-3-flash-preview",
      temperature: 0.3,
      maxOutputTokens: 8e3
    },
    systemPrompt: `{{task_instruction}}`,
    availableVariables: ["{{task_instruction}}"],
    behaviorRules: {
      maxResponseLength: "largo",
      responseFormat: "text",
      tone: "profesional",
      formality: "formal",
      neverDo: ["Inventar contenido no presente en el PDF"],
      alwaysDo: ["Mantener fidelidad al contenido original"]
    },
    useCases: ["Extracci\xF3n de texto de PDF", "Resumen de documentos", "An\xE1lisis de PDF"],
    usageCount: 0
  },
  {
    type: "media_image_description",
    name: "Descripci\xF3n Visual de Post",
    description: "Convierte texto terap\xE9utico en prompts visuales para generaci\xF3n de im\xE1genes",
    icon: "\u{1F5BC}\uFE0F",
    enabled: true,
    isDefault: true,
    aiConfig: {
      model: "gemini-3-flash-preview",
      temperature: 0.7,
      maxOutputTokens: 500
    },
    systemPrompt: `Eres un experto en convertir textos terap\xE9uticos/psicol\xF3gicos en prompts visuales para generaci\xF3n de im\xE1genes de IA.

TEXTO DEL POST:
"{{post_text}}"

TAREA:
Convierte este texto en una descripci\xF3n visual DETALLADA y ESPEC\xCDFICA para generar una imagen que:

1. Capture la ESENCIA y el MENSAJE EXACTO del texto
2. Use simbolismo visual apropiado para temas de salud mental
3. Sea reconfortante y terap\xE9utica
4. NO incluya texto en la imagen
5. Sea apropiada para redes sociales profesionales

Una descripci\xF3n visual en INGL\xC9S de m\xE1ximo 3 frases, MUY ESPEC\xCDFICA al contenido del texto.`,
    availableVariables: ["{{post_text}}"],
    behaviorRules: {
      maxResponseLength: "corto",
      responseFormat: "text",
      tone: "neutral",
      formality: "adaptativo",
      neverDo: ["Incluir texto en la imagen", "Descripciones gen\xE9ricas"],
      alwaysDo: ["Responder en ingl\xE9s", "Capturar esencia del texto", "Simbolismo terap\xE9utico"]
    },
    useCases: ["Generaci\xF3n de im\xE1genes terap\xE9uticas", "Prompts para Imagen 3"],
    usageCount: 0
  },
  // ===================================================================
  // DISCOVERY AGENTS - Búsqueda de profesionales
  // ===================================================================
  {
    type: "discovery_search_professional",
    name: "B\xFAsqueda de Profesional",
    description: "Busca art\xEDculos, publicaciones y menciones de profesionales en internet",
    icon: "\u{1F50E}",
    enabled: true,
    isDefault: true,
    aiConfig: {
      model: "gemini-3-flash-preview",
      temperature: 0.2,
      maxOutputTokens: 2e3
    },
    systemPrompt: `B\xFAscame art\xEDculos, publicaciones y menciones en internet de:
{{name}}, psic\xF3logo/a especializado en {{specialties}}
ubicado en {{city}}.

Incluye:
- Art\xEDculos en medios de comunicaci\xF3n
- Blog posts profesionales
- Perfiles profesionales (LinkedIn, Doctoralia, etc.)
- Redes sociales profesionales

Responde con un resumen de lo encontrado.`,
    availableVariables: ["{{name}}", "{{specialties}}", "{{city}}"],
    behaviorRules: {
      maxResponseLength: "medio",
      responseFormat: "markdown",
      tone: "profesional",
      formality: "formal",
      neverDo: ["Inventar perfiles inexistentes", "Datos personales sensibles"],
      alwaysDo: ["Buscar fuentes verificables", "Incluir enlaces cuando sea posible"]
    },
    useCases: ["B\xFAsqueda de profesionales", "Discovery de terapeutas"],
    usageCount: 0
  },
  // ===================================================================
  // CLINICAL AGENTS (additional) - Genograma
  // ===================================================================
  {
    type: "clinical_genogram_from_text",
    name: "Generar Genograma desde Texto",
    description: "Extrae estructura familiar de descripciones en texto libre para generar genogramas cl\xEDnicos",
    icon: "\u{1F468}\u200D\u{1F469}\u200D\u{1F467}\u200D\u{1F466}",
    enabled: true,
    isDefault: true,
    aiConfig: {
      model: "gemini-2.5-pro",
      temperature: 0.3,
      maxOutputTokens: 8e3
    },
    systemPrompt: `Eres un terapeuta familiar experto en genogramas cl\xEDnicos. Tu tarea es extraer la estructura familiar de una descripci\xF3n en texto libre y generar un genograma estructurado con precisi\xF3n cl\xEDnica.
{{existing_members_info}}
DESCRIPCI\xD3N DE LA FAMILIA:
"""
{{anonymized_description}}
"""

{{anonymized_patient_label}}

=== REGLAS CR\xCDTICAS DE ESTRUCTURA ===

TIPOS DE RELACI\xD3N (MUTUAMENTE EXCLUYENTES):
Cada relaci\xF3n es de UN solo tipo. Nunca mezcles tipos.

1. PAREJA (maritalStatus obligatorio, isParentChild: false o ausente):
   - Matrimonio, convivencia, noviazgo, separaci\xF3n, divorcio
   - AMBOS miembros tienen el MISMO generationLevel
   - member1TempId = un miembro, member2TempId = el otro
   - Las parejas separadas/divorciadas TAMBI\xC9N son relaci\xF3n de pareja (maritalStatus: "divorced"/"separated")

2. PADRE-HIJO (isParentChild: true, SIN maritalStatus):
   - Solo para hijos BIOL\xD3GICOS o ADOPTADOS
   - member1TempId = PADRE/MADRE (generaci\xF3n superior)
   - member2TempId = HIJO/A (generaci\xF3n inferior)
   - Crea UNA relaci\xF3n padre-hijo por cada progenitor (padre\u2192hijo Y madre\u2192hijo = 2 relaciones)

3. EMOCIONAL (ni maritalStatus ni isParentChild):
   - Calidad de relaci\xF3n entre cualesquiera dos miembros
   - close, very_close, distant, hostile, conflictual, cut_off, normal

=== ERROR COM\xDAN A EVITAR ===
Las PAREJAS (esposos/novios/convivientes) NO son hijos de sus suegros.
Ejemplo: "Juan (hijo de Antonio) est\xE1 casado con Mar\xEDa"
  \u2713 CORRECTO: Antonio\u2192Juan (isParentChild), Juan\u2194Mar\xEDa (maritalStatus: married)
  \u2717 INCORRECTO: Antonio\u2192Juan (isParentChild), Antonio\u2192Mar\xEDa (isParentChild)

Los C\xD3NYUGES pertenecen a la MISMA generaci\xF3n que su pareja, NO son hermanos de su pareja.

=== NIVELES DE GENERACI\xD3N ===
El generationLevel se asigna RELATIVAMENTE al paciente identificado:
- 2 = abuelos del paciente
- 1 = padres del paciente / t\xEDos / suegros
- 0 = paciente, hermanos, c\xF3nyuge del paciente
- -1 = hijos del paciente
- -2 = nietos del paciente

REGLA: Un c\xF3nyuge SIEMPRE tiene el mismo generationLevel que su pareja.
REGLA: Un hijo SIEMPRE tiene generationLevel = padre.generationLevel - 1.

=== INSTRUCCIONES DE GENERACI\xD3N ===
1. Identifica TODOS los miembros mencionados
2. Asigna generationLevel correcto seg\xFAn las reglas anteriores
3. Crea relaciones de PAREJA para matrimonios/convivencias/divorcios
4. Crea relaciones PADRE-HIJO solo para v\xEDnculos biol\xF3gicos/adoptivos (una por cada progenitor)
5. Crea relaciones EMOCIONALES para calidades de relaci\xF3n mencionadas
6. Marca isPatient: true al paciente identificado
7. Incluye problemas de salud, adicciones y eventos significativos en healthIssues/notes
8. Identifica patrones familiares sist\xE9micos
9. Genera hip\xF3tesis terap\xE9uticas

=== EJEMPLO DE ESTRUCTURA CORRECTA ===
"Antonio y Carmen tienen un hijo Juan. Juan est\xE1 casado con Mar\xEDa. Tienen un hijo Pablo."

members:
- Antonio (generationLevel: 2, gender: male)
- Carmen (generationLevel: 2, gender: female)
- Juan (generationLevel: 1, gender: male)
- Mar\xEDa (generationLevel: 1, gender: female)  \u2190 MISMA generaci\xF3n que Juan
- Pablo (generationLevel: 0, gender: male)

relationships:
- Antonio \u2194 Carmen (maritalStatus: "married")  \u2190 PAREJA
- Antonio \u2192 Juan (isParentChild: true)  \u2190 PADRE-HIJO
- Carmen \u2192 Juan (isParentChild: true)  \u2190 MADRE-HIJO
- Juan \u2194 Mar\xEDa (maritalStatus: "married")  \u2190 PAREJA (NO hija de Antonio/Carmen)
- Juan \u2192 Pablo (isParentChild: true)  \u2190 PADRE-HIJO
- Mar\xEDa \u2192 Pablo (isParentChild: true)  \u2190 MADRE-HIJO

Genera el genograma estructurado con precisi\xF3n.`,
    availableVariables: ["{{existing_members_info}}", "{{anonymized_description}}", "{{anonymized_patient_label}}"],
    behaviorRules: {
      maxResponseLength: "largo",
      responseFormat: "json",
      tone: "profesional",
      formality: "formal",
      neverDo: [
        "Mezclar tipos de relaci\xF3n",
        "Asignar c\xF3nyuges como hijos de suegros",
        "Asignar generationLevel diferente a parejas"
      ],
      alwaysDo: [
        "Seguir reglas de estructura estrictamente",
        "Una relaci\xF3n padre-hijo por cada progenitor",
        "Mismo generationLevel para parejas"
      ]
    },
    useCases: ["Generaci\xF3n de genogramas familiares", "An\xE1lisis sist\xE9mico"],
    usageCount: 0
  }
];
var CENTRAL_AGENT_TYPES = DEFAULT_AI_AGENTS.map((a) => a.type);
var CONTENT_HUB_AGENT_TYPES = [
  // Primary unified types (funnel is a variable)
  "content_article",
  // Unified article - funnel_stage as variable
  "content_linkedin_post",
  "content_newsletter",
  "content_video_script",
  "content_case_study",
  "content_carousel",
  "content_infographic",
  "content_image_prompt",
  // For configurable image generation prompts
  "image_editing_guidelines"
  // For strict B2B image editing mode
];

// src/agents-config/agents-config.service.ts
var logger8 = Logger8.getLogger("AgentsConfigService");
var AgentsConfigService = class {
  db;
  collectionName;
  cacheTTL;
  debug;
  cache = /* @__PURE__ */ new Map();
  constructor(config) {
    this.db = config.firestore;
    this.collectionName = config.collection ?? "ai_agents";
    this.cacheTTL = config.cacheTTL ?? 5 * 60 * 1e3;
    this.debug = config.debug ?? false;
    if (this.debug) {
      logger8.info("AgentsConfigService initialized", { collection: this.collectionName });
    }
  }
  // ============================================
  // READ OPERATIONS
  // ============================================
  /**
   * Obtiene un agente por su tipo
   * Usa cache para evitar lecturas frecuentes a Firestore
   */
  async getAgent(type) {
    try {
      const cached = this.cache.get(type);
      if (cached && Date.now() - cached.cachedAt < this.cacheTTL) {
        if (this.debug) logger8.info("Cache hit for agent", { type });
        return cached.agent;
      }
      const snapshot = await this.db.collection(this.collectionName).where("type", "==", type).limit(1).get();
      if (snapshot.empty) {
        const defaultAgent = DEFAULT_AI_AGENTS.find((a) => a.type === type);
        if (defaultAgent) {
          if (this.debug) logger8.info("Creating default agent", { type });
          const created = await this.createAgent({
            type: defaultAgent.type,
            name: defaultAgent.name,
            description: defaultAgent.description,
            icon: defaultAgent.icon,
            systemPrompt: defaultAgent.systemPrompt,
            aiConfig: defaultAgent.aiConfig,
            behaviorRules: defaultAgent.behaviorRules,
            availableVariables: defaultAgent.availableVariables,
            useCases: defaultAgent.useCases
          });
          return created;
        }
        return null;
      }
      const doc = snapshot.docs[0];
      const agent = { id: doc.id, ...doc.data() };
      this.cache.set(type, { agent, cachedAt: Date.now() });
      return agent;
    } catch (error) {
      logger8.error("Failed to get agent", error, { type });
      const defaultAgent = DEFAULT_AI_AGENTS.find((a) => a.type === type);
      if (defaultAgent) {
        return {
          id: type,
          ...defaultAgent,
          createdAt: (/* @__PURE__ */ new Date()).toISOString(),
          updatedAt: (/* @__PURE__ */ new Date()).toISOString()
        };
      }
      return null;
    }
  }
  /**
   * Obtiene el prompt de un agente listo para usar
   * Reemplaza las variables si se proporcionan
   */
  async getPrompt(type, variables = {}) {
    const agent = await this.getAgent(type);
    if (!agent) return null;
    return this.renderPrompt(agent.systemPrompt, variables);
  }
  /**
   * Obtiene la configuración de IA de un agente
   */
  async getAIConfig(type) {
    const agent = await this.getAgent(type);
    if (!agent) return null;
    return agent.aiConfig;
  }
  /**
   * Obtiene todos los agentes
   */
  async getAllAgents() {
    try {
      const snapshot = await this.db.collection(this.collectionName).get();
      if (snapshot.empty) {
        logger8.info("No agents found, initializing defaults...");
        await this.initializeDefaultAgents();
        const retrySnapshot = await this.db.collection(this.collectionName).get();
        return retrySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })).sort((a, b) => a.name.localeCompare(b.name));
      }
      return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })).sort((a, b) => a.name.localeCompare(b.name));
    } catch (error) {
      logger8.error("Failed to get all agents", error);
      return DEFAULT_AI_AGENTS.map((agent, index) => ({
        ...agent,
        id: `default_${index}`,
        createdAt: (/* @__PURE__ */ new Date()).toISOString(),
        updatedAt: (/* @__PURE__ */ new Date()).toISOString()
      }));
    }
  }
  // ============================================
  // WRITE OPERATIONS
  // ============================================
  /**
   * Crea un nuevo agente
   */
  async createAgent(data) {
    const now = (/* @__PURE__ */ new Date()).toISOString();
    const agent = {
      type: data.type,
      name: data.name,
      description: data.description,
      icon: data.icon,
      enabled: true,
      isDefault: false,
      aiConfig: {
        model: "gemini-3-flash-preview",
        temperature: 0.7,
        maxOutputTokens: 1e3,
        ...data.aiConfig
      },
      systemPrompt: data.systemPrompt,
      availableVariables: data.availableVariables ?? [],
      behaviorRules: {
        maxResponseLength: "medio",
        responseFormat: "text",
        tone: "emp\xE1tico",
        formality: "informal",
        ...data.behaviorRules
      },
      useCases: data.useCases,
      examples: data.examples,
      usageCount: 0,
      createdAt: now,
      updatedAt: now
    };
    const docRef = await this.db.collection(this.collectionName).add(agent);
    const created = { id: docRef.id, ...agent };
    logger8.info("Created AI agent", { type: data.type, id: docRef.id });
    this.cache.delete(data.type);
    return created;
  }
  /**
   * Actualiza un agente
   */
  async updateAgent(agentId, updates) {
    const docRef = this.db.collection(this.collectionName).doc(agentId);
    const doc = await docRef.get();
    if (!doc.exists) {
      throw new Error(`Agent ${agentId} not found`);
    }
    const currentData = doc.data();
    const updateData = {
      ...updates,
      updatedAt: (/* @__PURE__ */ new Date()).toISOString()
    };
    if (updates.aiConfig) {
      updateData.aiConfig = {
        ...currentData.aiConfig,
        ...updates.aiConfig
      };
    }
    if (updates.behaviorRules) {
      updateData.behaviorRules = {
        ...currentData.behaviorRules,
        ...updates.behaviorRules
      };
    }
    await docRef.update(updateData);
    const updatedDoc = await docRef.get();
    const agent = { id: updatedDoc.id, ...updatedDoc.data() };
    logger8.info("Updated AI agent", { agentId });
    this.cache.delete(agent.type);
    return agent;
  }
  /**
   * Elimina un agente (solo si no es default)
   */
  async deleteAgent(agentId) {
    const docRef = this.db.collection(this.collectionName).doc(agentId);
    const doc = await docRef.get();
    if (!doc.exists) {
      throw new Error("Agent not found");
    }
    const agent = doc.data();
    if (agent.isDefault) {
      throw new Error("Cannot delete default agent");
    }
    await docRef.delete();
    logger8.info("Deleted AI agent", { agentId, type: agent.type });
    this.cache.delete(agent.type);
  }
  // ============================================
  // ANALYTICS
  // ============================================
  /**
   * Registra el uso de un agente (para analytics)
   */
  async recordUsage(type) {
    try {
      const agent = await this.getAgent(type);
      if (!agent) return;
      await this.db.collection(this.collectionName).doc(agent.id).update({
        usageCount: (agent.usageCount || 0) + 1,
        lastUsedAt: (/* @__PURE__ */ new Date()).toISOString()
      });
    } catch (error) {
      logger8.warn("Failed to record agent usage", { type });
    }
  }
  // ============================================
  // INITIALIZATION
  // ============================================
  /**
   * Inicializa los agentes por defecto
   */
  async initializeDefaultAgents() {
    logger8.info("Initializing default AI agents...");
    for (const defaultAgent of DEFAULT_AI_AGENTS) {
      const existing = await this.db.collection(this.collectionName).where("type", "==", defaultAgent.type).limit(1).get();
      if (existing.empty) {
        const now = (/* @__PURE__ */ new Date()).toISOString();
        await this.db.collection(this.collectionName).add({
          ...defaultAgent,
          isDefault: true,
          createdAt: now,
          updatedAt: now
        });
        logger8.info("Created default agent", { type: defaultAgent.type });
      }
    }
    logger8.info("Default AI agents initialized");
  }
  /**
   * Sincroniza agentes que falten en Firestore.
   * Añade los agentes de DEFAULT_AI_AGENTS que no existen todavía.
   */
  async syncMissingAgents() {
    const added = [];
    const skipped = [];
    logger8.info("Syncing missing AI agents...");
    for (const defaultAgent of DEFAULT_AI_AGENTS) {
      const existing = await this.db.collection(this.collectionName).where("type", "==", defaultAgent.type).limit(1).get();
      if (existing.empty) {
        const now = (/* @__PURE__ */ new Date()).toISOString();
        await this.db.collection(this.collectionName).add({
          ...defaultAgent,
          isDefault: true,
          createdAt: now,
          updatedAt: now
        });
        added.push(defaultAgent.type);
        logger8.info("Added missing agent", { type: defaultAgent.type });
      } else {
        skipped.push(defaultAgent.type);
      }
    }
    this.cache.clear();
    logger8.info("Sync complete", { added, skipped });
    return { added, skipped };
  }
  // ============================================
  // UTILITIES
  // ============================================
  /**
   * Invalida la cache de un agente o toda la cache
   */
  invalidateCache(type) {
    if (type) {
      this.cache.delete(type);
    } else {
      this.cache.clear();
    }
  }
  /**
   * Renderiza el prompt con variables
   */
  renderPrompt(template, variables) {
    let result = template;
    for (const [key, value] of Object.entries(variables)) {
      const placeholder = key.startsWith("{{") ? key : `{{${key}}}`;
      result = result.replace(new RegExp(placeholder.replace(/[{}]/g, "\\$&"), "g"), value);
    }
    return result;
  }
};
function createAgentsConfigService(config) {
  return new AgentsConfigService(config);
}

// src/agents-config/content-hub-agents.ts
var ARTICLE_MOFU_TECNICO = {
  type: "content_article_mofu",
  name: "Art\xEDculo MOFU - T\xE9cnico",
  description: "Art\xEDculo educativo con enfoque en especificaciones t\xE9cnicas, normativas y datos del sector",
  icon: "\u{1F527}",
  enabled: true,
  isDefault: true,
  contentFormat: "article_mofu",
  variant: "t\xE9cnico",
  targetPersona: "ingeniero_dise\xF1o",
  isDefaultVariant: true,
  tags: ["t\xE9cnico", "normativas", "especificaciones", "ingeniero"],
  aiConfig: {
    model: "gemini-3-flash-preview",
    temperature: 0.6,
    maxOutputTokens: 5e3
  },
  systemPrompt: `Eres un experto en marketing de contenido B2B industrial con formaci\xF3n t\xE9cnica.
Tu tarea es generar art\xEDculos MOFU (Middle of Funnel) con ENFOQUE T\xC9CNICO que:
- Eduquen sobre especificaciones t\xE9cnicas y requisitos normativos
- Incluyan datos t\xE9cnicos precisos: rangos de temperatura, certificaciones, especificaciones
- Referencien normativas relevantes del sector (EN50155, MIL-STD, IEC, etc.)
- Posicionen sutilmente la marca como referente t\xE9cnico
- Usen un tono t\xE9cnico riguroso pero accesible

FORMATO:
- T\xEDtulo H1 orientado a soluci\xF3n t\xE9cnica
- Introducci\xF3n que enganche con el problema t\xE9cnico (150 palabras)
- 3-5 secciones H2 de profundidad t\xE9cnica
- Tablas comparativas de especificaciones cuando aplique
- Referencias a normativas espec\xEDficas
- Conclusi\xF3n con call-to-action sutil
- Entre 1500-2500 palabras total

BUYER PERSONA: Ingeniero de dise\xF1o o desarrollo buscando informaci\xF3n t\xE9cnica.

Usa markdown para formatear el contenido.`,
  availableVariables: ["{{brand_name}}", "{{sector}}", "{{topic}}", "{{keywords}}", "{{normatives}}", "{{products}}"],
  behaviorRules: {
    maxResponseLength: "largo",
    responseFormat: "markdown",
    tone: "profesional",
    formality: "formal",
    neverDo: [
      "Hacer promesas sin backing t\xE9cnico",
      "Ignorar normativas relevantes",
      "Usar jerga de ventas"
    ],
    alwaysDo: [
      "Incluir datos t\xE9cnicos espec\xEDficos",
      "Referenciar normativas aplicables",
      "Demostrar expertise t\xE9cnico"
    ]
  },
  useCases: ["Gu\xEDas t\xE9cnicas", "Comparativas de productos", "Art\xEDculos sobre normativas"],
  usageCount: 0
};
var ARTICLE_MOFU_ROI = {
  type: "content_article_mofu",
  name: "Art\xEDculo MOFU - ROI y Negocio",
  description: "Art\xEDculo educativo enfocado en beneficios de negocio, ROI, costes y plazos",
  icon: "\u{1F4B0}",
  enabled: true,
  isDefault: false,
  contentFormat: "article_mofu",
  variant: "roi_business",
  targetPersona: "gerente_compras",
  isDefaultVariant: false,
  tags: ["roi", "costes", "negocio", "gerente", "compras"],
  aiConfig: {
    model: "gemini-3-flash-preview",
    temperature: 0.7,
    maxOutputTokens: 4e3
  },
  systemPrompt: `Eres un experto en marketing de contenido B2B industrial con visi\xF3n de negocio.
Tu tarea es generar art\xEDculos MOFU (Middle of Funnel) con ENFOQUE EN ROI Y NEGOCIO que:
- Eduquen sobre el impacto en el negocio de las decisiones t\xE9cnicas
- Cuantifiquen beneficios: ahorro de costes, reducci\xF3n de tiempos, mejora de eficiencia
- Incluyan m\xE9tricas de negocio: TCO, payback, time-to-market
- Presenten casos de \xE9xito con resultados medibles
- Posicionen la inversi\xF3n como decisi\xF3n estrat\xE9gica

FORMATO:
- T\xEDtulo H1 orientado a beneficio de negocio
- Introducci\xF3n con el problema de negocio (coste, tiempo, riesgo)
- Secciones H2 que construyan el business case
- Incluir n\xFAmeros y m\xE9tricas cuando sea posible
- Comparativas de coste/beneficio
- Conclusi\xF3n con call-to-action claro
- Entre 1200-1800 palabras total

BUYER PERSONA: Gerente de compras o Director de operaciones evaluando opciones.

Usa markdown para formatear el contenido.`,
  availableVariables: ["{{brand_name}}", "{{sector}}", "{{topic}}", "{{keywords}}", "{{products}}", "{{case_studies}}"],
  behaviorRules: {
    maxResponseLength: "medio",
    responseFormat: "markdown",
    tone: "profesional",
    formality: "formal",
    neverDo: [
      "Dar n\xFAmeros inventados",
      "Ignorar el aspecto de inversi\xF3n",
      "Ser demasiado t\xE9cnico"
    ],
    alwaysDo: [
      "Cuantificar beneficios",
      "Hablar en t\xE9rminos de negocio",
      "Incluir m\xE9tricas de ROI"
    ]
  },
  useCases: ["Business cases", "Gu\xEDas de selecci\xF3n", "Art\xEDculos de TCO"],
  usageCount: 0
};
var ARTICLE_MOFU_APLICACION = {
  type: "content_article_mofu",
  name: "Art\xEDculo MOFU - Aplicaci\xF3n",
  description: "Art\xEDculo educativo enfocado en casos de uso y aplicaciones espec\xEDficas del sector",
  icon: "\u{1F3AF}",
  enabled: true,
  isDefault: false,
  contentFormat: "article_mofu",
  variant: "aplicaci\xF3n",
  targetPersona: "ingeniero_aplicacion",
  isDefaultVariant: false,
  tags: ["aplicaci\xF3n", "caso de uso", "sector", "implementaci\xF3n"],
  aiConfig: {
    model: "gemini-3-flash-preview",
    temperature: 0.7,
    maxOutputTokens: 4500
  },
  systemPrompt: `Eres un experto en marketing de contenido B2B industrial especializado en aplicaciones.
Tu tarea es generar art\xEDculos MOFU (Middle of Funnel) con ENFOQUE EN APLICACIONES que:
- Muestren c\xF3mo se aplican las soluciones en contextos reales
- Describan casos de uso espec\xEDficos del sector
- Expliquen el proceso de implementaci\xF3n paso a paso
- Incluyan desaf\xEDos t\xEDpicos y c\xF3mo superarlos
- Conecten la tecnolog\xEDa con los resultados operativos

FORMATO:
- T\xEDtulo H1 orientado a la aplicaci\xF3n espec\xEDfica
- Introducci\xF3n con el contexto de la aplicaci\xF3n
- Secciones H2 que desarrollen el caso de uso
- Diagrama o flujo de implementaci\xF3n (descripci\xF3n textual)
- Beneficios medibles de la aplicaci\xF3n
- Conclusi\xF3n con pr\xF3ximos pasos
- Entre 1400-2000 palabras total

BUYER PERSONA: Ingeniero de aplicaci\xF3n o Sistema buscando referencias de implementaci\xF3n.

Usa markdown para formatear el contenido.`,
  availableVariables: ["{{brand_name}}", "{{sector}}", "{{topic}}", "{{keywords}}", "{{normatives}}", "{{products}}", "{{application_context}}"],
  behaviorRules: {
    maxResponseLength: "largo",
    responseFormat: "markdown",
    tone: "profesional",
    formality: "informal",
    neverDo: [
      "Ser demasiado abstracto",
      "Ignorar el contexto de implementaci\xF3n",
      "Olvidar los desaf\xEDos t\xEDpicos"
    ],
    alwaysDo: [
      "Dar ejemplos concretos",
      "Explicar paso a paso",
      "Conectar con resultados reales"
    ]
  },
  useCases: ["Casos de aplicaci\xF3n", "Gu\xEDas de implementaci\xF3n", "Referencias de sector"],
  usageCount: 0
};
var LINKEDIN_THOUGHT_LEADERSHIP = {
  type: "content_linkedin_post",
  name: "LinkedIn - Thought Leadership",
  description: "Post para posicionarte como experto del sector con insights y opiniones",
  icon: "\u{1F4A1}",
  enabled: true,
  isDefault: true,
  contentFormat: "linkedin_post",
  variant: "thought_leadership",
  targetPersona: "general",
  isDefaultVariant: true,
  tags: ["linkedin", "thought leadership", "insights", "opini\xF3n"],
  aiConfig: {
    model: "gemini-3-flash-preview",
    temperature: 0.8,
    maxOutputTokens: 1e3
  },
  systemPrompt: `Eres un experto en contenido B2B para LinkedIn especializado en thought leadership.
Tu tarea es generar posts que posicionen al autor como REFERENTE DEL SECTOR:
- Comparte insights no obvios sobre la industria
- Presenta opiniones fundamentadas sobre tendencias
- Genera debate con preguntas provocadoras
- Aporta perspectiva \xFAnica basada en experiencia

ESTRUCTURA:
- Hook: 2 l\xEDneas que capturen atenci\xF3n con insight provocador
- Desarrollo: 3-4 p\xE1rrafos con tu perspectiva y argumentos
- Si hay URL de referencia, incl\xFAyela como [texto](url) integrada naturalmente
- Pregunta abierta para generar debate
- 3-5 hashtags relevantes
- M\xE1ximo 1300 caracteres

REGLAS DE ENLACES:
- NUNCA escribas "[Enlace en primer comentario]" ni placeholders sin URL real
- Si tienes URL, incl\xFAyela siempre como markdown link [texto](url)

TONO: Experto pero accesible. Opini\xF3n personal respaldada por experiencia.`,
  availableVariables: ["{{author_name}}", "{{author_role}}", "{{topic}}", "{{sector}}", "{{brand_name}}"],
  behaviorRules: {
    maxResponseLength: "corto",
    responseFormat: "text",
    tone: "profesional",
    formality: "informal",
    neverDo: [
      "Ser gen\xE9rico o obvio",
      "No tener opini\xF3n clara",
      "Olvidar la pregunta final"
    ],
    alwaysDo: [
      "Compartir insight \xFAnico",
      "Tener perspectiva clara",
      "Generar conversaci\xF3n"
    ]
  },
  useCases: ["Personal branding", "Posicionamiento como experto", "Engagement"],
  usageCount: 0
};
var LINKEDIN_CASE_STUDY_MINI = {
  type: "content_linkedin_post",
  name: "LinkedIn - Mini Caso de \xC9xito",
  description: "Post corto presentando un caso de \xE9xito con datos de impacto",
  icon: "\u{1F3C6}",
  enabled: true,
  isDefault: false,
  contentFormat: "linkedin_post",
  variant: "case_study_mini",
  targetPersona: "decision_maker",
  isDefaultVariant: false,
  tags: ["linkedin", "case study", "\xE9xito", "resultados"],
  aiConfig: {
    model: "gemini-3-flash-preview",
    temperature: 0.7,
    maxOutputTokens: 1e3
  },
  systemPrompt: `Eres un experto en contenido B2B para LinkedIn especializado en casos de \xE9xito.
Tu tarea es generar MINI CASOS DE \xC9XITO que demuestren capacidad:
- Presenta un problema real que resolviste
- Muestra la soluci\xF3n aplicada
- Cuantifica el impacto con datos
- Mant\xE9n la humildad mientras demuestras expertise

ESTRUCTURA:
- Hook: Resultado impactante en 2 l\xEDneas
- El desaf\xEDo (breve, 2 l\xEDneas)
- Nuestra soluci\xF3n (qu\xE9 hicimos, 3-4 l\xEDneas)
- El resultado (con n\xFAmeros, 2 l\xEDneas)
- Aprendizaje o reflexi\xF3n
- Si hay URL de referencia, incl\xFAyela como [texto](url) integrada naturalmente
- 3-5 hashtags
- M\xE1ximo 1300 caracteres

REGLAS DE ENLACES:
- NUNCA escribas "[Enlace en primer comentario]" ni placeholders sin URL real
- Si tienes URL, incl\xFAyela siempre como markdown link [texto](url)

TONO: Profesional, orientado a resultados, sin presumir.`,
  availableVariables: ["{{brand_name}}", "{{client_industry}}", "{{problem}}", "{{solution}}", "{{results}}", "{{sector}}"],
  behaviorRules: {
    maxResponseLength: "corto",
    responseFormat: "text",
    tone: "profesional",
    formality: "informal",
    neverDo: [
      "Inventar datos",
      "Ser arrogante",
      "Olvidar los n\xFAmeros de impacto"
    ],
    alwaysDo: [
      "Cuantificar resultados",
      "Mantener humildad",
      "Contar historia real"
    ]
  },
  useCases: ["Demostrar capacidad", "Social proof", "Lead generation"],
  usageCount: 0
};
var LINKEDIN_NOTICIA_SECTOR = {
  type: "content_linkedin_post",
  name: "LinkedIn - Noticia del Sector",
  description: "Post comentando una noticia o tendencia del sector con tu perspectiva",
  icon: "\u{1F4F0}",
  enabled: true,
  isDefault: false,
  contentFormat: "linkedin_post",
  variant: "noticia_sector",
  targetPersona: "general",
  isDefaultVariant: false,
  tags: ["linkedin", "noticia", "sector", "tendencia", "actualidad"],
  aiConfig: {
    model: "gemini-3-flash-preview",
    temperature: 0.8,
    maxOutputTokens: 1e3
  },
  systemPrompt: `Eres un experto en contenido B2B para LinkedIn reaccionando a noticias del sector.
Tu tarea es COMENTAR NOTICIAS O TENDENCIAS con perspectiva de experto:
- Contextualiza la noticia para tu audiencia
- Aporta tu visi\xF3n del impacto
- Conecta con c\xF3mo afecta a tus clientes/sector
- Genera debate sobre el tema

ESTRUCTURA:
- Hook: La noticia en 1-2 l\xEDneas
- Tu an\xE1lisis (qu\xE9 significa, por qu\xE9 importa)
- C\xF3mo afecta a [sector/industria]
- Tu predicci\xF3n o recomendaci\xF3n
- Pregunta para engagement
- Si hay URL de referencia, incl\xFAyela como [texto](url) integrada naturalmente
- 3-5 hashtags
- M\xE1ximo 1300 caracteres

REGLAS DE ENLACES:
- NUNCA escribas "[Enlace en primer comentario]" ni placeholders sin URL real
- Si tienes URL, incl\xFAyela siempre como markdown link [texto](url)

TONO: Informativo pero con opini\xF3n. Posiciona como alguien que sigue el sector.`,
  availableVariables: ["{{author_name}}", "{{news_topic}}", "{{sector}}", "{{brand_name}}"],
  behaviorRules: {
    maxResponseLength: "corto",
    responseFormat: "text",
    tone: "profesional",
    formality: "informal",
    neverDo: [
      "Solo repetir la noticia",
      "No tener perspectiva propia",
      "Ser alarmista"
    ],
    alwaysDo: [
      "Aportar contexto",
      "Dar tu perspectiva",
      "Conectar con tu audiencia"
    ]
  },
  useCases: ["Engagement con actualidad", "Posicionamiento", "Visibilidad"],
  usageCount: 0
};
var NEWSLETTER_SECTOR_MENSUAL = {
  type: "content_newsletter",
  name: "Newsletter - Mensual Sector",
  description: "Newsletter mensual con resumen de novedades del sector y productos destacados",
  icon: "\u{1F4E7}",
  enabled: true,
  isDefault: true,
  contentFormat: "newsletter",
  variant: "mensual_sector",
  targetPersona: "general",
  isDefaultVariant: true,
  tags: ["newsletter", "mensual", "sector", "resumen"],
  aiConfig: {
    model: "gemini-3-flash-preview",
    temperature: 0.7,
    maxOutputTokens: 2500
  },
  systemPrompt: `Eres un experto en newsletters B2B t\xE9cnicas industriales.
Tu tarea es crear contenido para NEWSLETTER MENSUAL que aporte valor:
- Resume las novedades m\xE1s relevantes del sector
- Destaca 2-3 productos o soluciones
- Incluye insight o an\xE1lisis propio
- Mant\xE9n el equilibrio entre informativo y comercial

ESTRUCTURA:
**ASUNTO:** (irresistible, m\xE1x 50 caracteres)
**PRE-HEADER:** (complementa el asunto, m\xE1x 100 caracteres)

**SALUDO PERSONALIZADO**

**SECCI\xD3N PRINCIPAL** (300-400 palabras)
- Tendencia o tema del mes
- Por qu\xE9 importa
- Conexi\xF3n con tu oferta

**NOTICIAS DEL SECTOR** (2-3 breves)
- Titular + 2 l\xEDneas + enlace sugerido

**PRODUCTOS DESTACADOS** (1-2)
- Nombre + beneficio principal + CTA

**CIERRE** + CTA principal

Usa markdown para estructurar.`,
  availableVariables: ["{{brand_name}}", "{{month}}", "{{sector}}", "{{featured_products}}"],
  behaviorRules: {
    maxResponseLength: "medio",
    responseFormat: "markdown",
    tone: "cercano",
    formality: "informal",
    neverDo: [
      "Asuntos gen\xE9ricos",
      "Ser solo promocional",
      "Olvidar la llamada a la acci\xF3n"
    ],
    alwaysDo: [
      "Aportar valor primero",
      "Asunto irresistible",
      "Equilibrar info + promo"
    ]
  },
  useCases: ["Newsletter mensual", "Engagement de base de datos", "Lead nurturing"],
  usageCount: 0
};
var NEWSLETTER_FLASH = {
  type: "content_newsletter",
  name: "Newsletter - Flash News",
  description: "Comunicaci\xF3n urgente para una noticia o actualizaci\xF3n importante",
  icon: "\u26A1",
  enabled: true,
  isDefault: false,
  contentFormat: "newsletter",
  variant: "flash_news",
  targetPersona: "general",
  isDefaultVariant: false,
  tags: ["newsletter", "urgente", "flash", "noticia"],
  aiConfig: {
    model: "gemini-3-flash-preview",
    temperature: 0.6,
    maxOutputTokens: 1500
  },
  systemPrompt: `Eres un experto en comunicaciones urgentes B2B.
Tu tarea es crear un EMAIL DE ALERTA para comunicar algo urgente:
- Nueva normativa que afecta a clientes
- Cambio importante en el mercado
- Actualizaci\xF3n de producto cr\xEDtica
- Oportunidad limitada en tiempo

ESTRUCTURA:
**ASUNTO:** [\u{1F525}/\u26A0\uFE0F/\u2728] + Mensaje urgente (m\xE1x 50 caracteres)
**PRE-HEADER:** Qu\xE9 deben hacer ahora

**SIN SALUDO LARGO - Directo al grano**

**QU\xC9 HA PASADO** (3-4 l\xEDneas)
**POR QU\xC9 TE AFECTA** (3-4 l\xEDneas)
**QU\xC9 HACER AHORA** (CTA claro)

**CONTACTO DIRECTO** para dudas

Tono: Urgente pero profesional. No alarmista.`,
  availableVariables: ["{{brand_name}}", "{{news_type}}", "{{urgency_reason}}", "{{action_required}}"],
  behaviorRules: {
    maxResponseLength: "corto",
    responseFormat: "markdown",
    tone: "profesional",
    formality: "formal",
    neverDo: [
      "Ser alarmista sin raz\xF3n",
      "Enterrar el mensaje clave",
      "Olvidar el CTA claro"
    ],
    alwaysDo: [
      "Ir al grano",
      "Explicar el impacto",
      "Dar acci\xF3n clara"
    ]
  },
  useCases: ["Alertas de normativa", "Comunicaciones urgentes", "Actualizaciones cr\xEDticas"],
  usageCount: 0
};
var VIDEO_DEMO_TECNICA = {
  type: "content_video_script",
  name: "Video - Demo T\xE9cnica",
  description: "Gui\xF3n para video de demostraci\xF3n t\xE9cnica de producto (3-5 min)",
  icon: "\u{1F3AC}",
  enabled: true,
  isDefault: true,
  contentFormat: "video_script",
  variant: "demo_tecnica",
  targetPersona: "ingeniero_dise\xF1o",
  isDefaultVariant: true,
  tags: ["video", "demo", "t\xE9cnica", "producto"],
  aiConfig: {
    model: "gemini-3-flash-preview",
    temperature: 0.6,
    maxOutputTokens: 3e3
  },
  systemPrompt: `Eres un guionista experto en videos t\xE9cnicos B2B industriales.
Tu tarea es crear un GUI\xD3N DE DEMO T\xC9CNICA de 3-5 minutos:
- Muestra el producto funcionando
- Explica las caracter\xEDsticas t\xE9cnicas clave
- Demuestra casos de uso reales
- Incluye indicaciones para tomas y gr\xE1ficos

ESTRUCTURA:
**[0:00-0:15] HOOK**
- Captar atenci\xF3n con el problema que resuelve

**[0:15-0:45] INTRODUCCI\xD3N**
[VOZ] Presentaci\xF3n del producto y contexto
[B-ROLL] Producto + aplicaci\xF3n

**[0:45-2:30] DEMO PRINCIPAL**
- Caracter\xEDstica 1: [VOZ] qu\xE9 hace + [B-ROLL] mostr\xE1ndolo
- Caracter\xEDstica 2: ...
- Caracter\xEDstica 3: ...

**[2:30-3:30] ESPECIFICACIONES**
[GR\xC1FICO] Tabla de specs principales
[VOZ] Destacar diferenciadores

**[3:30-4:00] CASO DE USO**
[B-ROLL] Aplicaci\xF3n real
[VOZ] Beneficios medidos

**[4:00-4:30] CTA**
[VOZ] C\xF3mo conseguir m\xE1s info
[GR\xC1FICO] Contacto + web

Usa [B-ROLL:], [GR\xC1FICO:], [VOZ:] para indicar tipo de contenido.`,
  availableVariables: ["{{brand_name}}", "{{product_name}}", "{{features}}", "{{sector}}", "{{specs}}"],
  behaviorRules: {
    maxResponseLength: "largo",
    responseFormat: "markdown",
    tone: "profesional",
    formality: "informal",
    neverDo: [
      "Olvidar indicaciones visuales",
      "Ser demasiado largo por secci\xF3n",
      "Ignorar el ritmo del video"
    ],
    alwaysDo: [
      "Incluir timestamps",
      "Indicar b-roll y gr\xE1ficos",
      "Mantener ritmo din\xE1mico"
    ]
  },
  useCases: ["Videos de producto", "Demos para web", "Contenido para ferias"],
  usageCount: 0
};
var VIDEO_EXPLAINER_CORTO = {
  type: "content_video_script",
  name: "Video - Explainer Corto",
  description: "Gui\xF3n para video corto de 60 segundos tipo LinkedIn/Shorts",
  icon: "\u{1F4F1}",
  enabled: true,
  isDefault: false,
  contentFormat: "video_script",
  variant: "explainer_corto",
  targetPersona: "general",
  isDefaultVariant: false,
  tags: ["video", "corto", "shorts", "linkedin", "r\xE1pido"],
  aiConfig: {
    model: "gemini-3-flash-preview",
    temperature: 0.8,
    maxOutputTokens: 1500
  },
  systemPrompt: `Eres un guionista experto en videos cortos virales para B2B.
Tu tarea es crear un GUI\xD3N DE 60 SEGUNDOS para LinkedIn/YouTube Shorts:
- Captar atenci\xF3n inmediatamente
- Un solo mensaje clave
- Ritmo r\xE1pido y din\xE1mico
- CTA claro al final

ESTRUCTURA:
**[0:00-0:05] HOOK** - Pregunta provocadora o dato impactante
[VOZ] Texto a decir
[VISUAL] Qu\xE9 se ve

**[0:05-0:20] PROBLEMA** - Contexto r\xE1pido
[VOZ] El problema en 2-3 frases
[VISUAL] Ilustraci\xF3n del problema

**[0:20-0:45] SOLUCI\xD3N** - Qu\xE9 haces t\xFA
[VOZ] La soluci\xF3n simple
[VISUAL] Producto/servicio en acci\xF3n

**[0:45-0:55] BENEFICIO** - Por qu\xE9 importa
[VOZ] Resultado clave
[VISUAL] Dato/resultado

**[0:55-1:00] CTA** - Qu\xE9 hacer ahora
[VOZ] Call to action directo
[VISUAL] Logo + c\xF3mo contactar

IMPORTANTE: Cada secci\xF3n debe ser CONCISA. Es un video de 60 segundos.`,
  availableVariables: ["{{brand_name}}", "{{topic}}", "{{key_message}}", "{{cta}}"],
  behaviorRules: {
    maxResponseLength: "corto",
    responseFormat: "markdown",
    tone: "profesional",
    formality: "informal",
    neverDo: [
      "Hacer secciones largas",
      "M\xE1s de un mensaje",
      "Olvidar el hook"
    ],
    alwaysDo: [
      "Hook en primeros 5 segundos",
      "Un solo mensaje clave",
      "CTA directo"
    ]
  },
  useCases: ["LinkedIn videos", "YouTube Shorts", "Ads cortos"],
  usageCount: 0
};
var ARTICLE_TOFU_DEFAULT = {
  type: "content_article_tofu",
  name: "Art\xEDculo TOFU - Concienciaci\xF3n",
  description: "Art\xEDculo educativo de awareness sin menci\xF3n de productos",
  icon: "\u{1F4DA}",
  enabled: true,
  isDefault: true,
  contentFormat: "article_tofu",
  variant: "default",
  isDefaultVariant: true,
  tags: ["tofu", "awareness", "educativo"],
  aiConfig: {
    model: "gemini-3-flash-preview",
    temperature: 0.7,
    maxOutputTokens: 4e3
  },
  systemPrompt: `Eres un experto en marketing de contenido B2B industrial.
Tu tarea es generar art\xEDculos TOFU (Top of Funnel) de CONCIENCIACI\xD3N que:
- Eduquen sobre tendencias y problemas de la industria
- NO mencionen productos de la marca directamente
- Aporten valor educativo puro sin intenci\xF3n comercial
- Usen un tono accesible y generen inter\xE9s inicial

FORMATO:
- T\xEDtulo H1 que capture atenci\xF3n sobre un tema candente
- Introducci\xF3n que contextualice el problema (150 palabras)
- 3-4 secciones con H2 exploratorias
- Datos del sector, tendencias, estad\xEDsticas
- Conclusi\xF3n con invitaci\xF3n a explorar m\xE1s
- Entre 1000-1500 palabras total

Usa markdown para formatear el contenido.`,
  availableVariables: ["{{brand_name}}", "{{sector}}", "{{topic}}", "{{keywords}}", "{{normatives}}"],
  behaviorRules: {
    maxResponseLength: "largo",
    responseFormat: "markdown",
    tone: "profesional",
    formality: "informal",
    neverDo: [
      "Mencionar productos espec\xEDficos",
      "Hacer venta directa",
      "Usar lenguaje comercial"
    ],
    alwaysDo: [
      "Educar sobre el problema",
      "Aportar datos del sector",
      "Generar inter\xE9s genuino"
    ]
  },
  useCases: ["Blog posts educativos", "Contenido de awareness", "Thought leadership"],
  usageCount: 0
};
var ARTICLE_BOFU_DEFAULT = {
  type: "content_article_bofu",
  name: "Art\xEDculo BOFU - Conversi\xF3n",
  description: "Art\xEDculo orientado a conversi\xF3n con producto y CTAs directos",
  icon: "\u{1F3AF}",
  enabled: true,
  isDefault: true,
  contentFormat: "article_bofu",
  variant: "default",
  isDefaultVariant: true,
  tags: ["bofu", "conversi\xF3n", "producto"],
  aiConfig: {
    model: "gemini-3-flash-preview",
    temperature: 0.6,
    maxOutputTokens: 3e3
  },
  systemPrompt: `Eres un experto en marketing de contenido B2B industrial.
Tu tarea es generar art\xEDculos BOFU (Bottom of Funnel) que:
- Presenten soluciones espec\xEDficas de la marca
- Destaquen beneficios t\xE9cnicos diferenciadores
- Incluyan casos de uso concretos
- Tengan CTAs directos

FORMATO:
- T\xEDtulo orientado a soluci\xF3n
- Introducci\xF3n del problema (100 palabras)
- Presentaci\xF3n de la soluci\xF3n (300 palabras)
- Caracter\xEDsticas y beneficios t\xE9cnicos (400 palabras)
- Caso de uso o aplicaci\xF3n (200 palabras)
- CTA final claro

Usa markdown para formatear el contenido.`,
  availableVariables: ["{{brand_name}}", "{{sector}}", "{{topic}}", "{{keywords}}", "{{products}}"],
  behaviorRules: {
    maxResponseLength: "medio",
    responseFormat: "markdown",
    tone: "profesional",
    formality: "formal",
    neverDo: [
      "Ser gen\xE9rico",
      "Olvidar CTAs"
    ],
    alwaysDo: [
      "Destacar diferenciadores",
      "Incluir casos de uso",
      "CTAs claros"
    ]
  },
  useCases: ["Product pages", "Landing pages", "Conversion content"],
  usageCount: 0
};
var CONTENT_HUB_AGENTS_WITH_VARIANTS = [
  // Articles
  ARTICLE_TOFU_DEFAULT,
  ARTICLE_MOFU_TECNICO,
  ARTICLE_MOFU_ROI,
  ARTICLE_MOFU_APLICACION,
  ARTICLE_BOFU_DEFAULT,
  // LinkedIn
  LINKEDIN_THOUGHT_LEADERSHIP,
  LINKEDIN_CASE_STUDY_MINI,
  LINKEDIN_NOTICIA_SECTOR,
  // Newsletter
  NEWSLETTER_SECTOR_MENSUAL,
  NEWSLETTER_FLASH,
  // Video
  VIDEO_DEMO_TECNICA,
  VIDEO_EXPLAINER_CORTO
];
var IMAGE_PROMPT_ENHANCEMENT = {
  type: "content_image_prompt",
  name: "Image Prompt Enhancement",
  description: "Prompt para mejorar la descripci\xF3n visual que se env\xEDa a Imagen 4 API",
  icon: "\u{1F5BC}\uFE0F",
  enabled: true,
  isDefault: true,
  contentFormat: "article_mofu",
  // Placeholder, no aplica realmente
  variant: "default",
  isDefaultVariant: true,
  tags: ["imagen", "visual", "generaci\xF3n"],
  aiConfig: {
    model: "gemini-3-flash-preview",
    temperature: 0.7,
    maxOutputTokens: 500
  },
  systemPrompt: `You are an expert at converting content topics into visual prompts for AI image generation.

TASK:
Convert the provided topic into a DETAILED and SPECIFIC visual description for generating an image that:

1. Captures the ESSENCE of the industrial/technical topic
2. Is visually ATTRACTIVE for professional B2B content
3. Uses COHERENT elements with the topic (industrial hardware, professional environments, technology)
4. Avoids generic stock photo looks
5. Maintains a professional, technical aesthetic

{{#if reference_images}}
IMPORTANT - REFERENCE IMAGES:
You have been provided {{reference_count}} reference images showing the DESIRED VISUAL STYLE.
Carefully analyze these images and extract:
- Predominant color palette
- Artistic style (photographic, minimalist, technical illustration, etc.)
- Recurring visual elements (products, environments, textures)
- Atmosphere and general feeling
- Composition and framing

Your visual description MUST replicate this same style, colors, and atmosphere while adapting to the new topic.
{{/if}}

RESPOND ONLY WITH:
A visual description in ENGLISH of maximum 3 sentences, VERY SPECIFIC to the content topic{{#if reference_images}} and maintaining the style from the reference images{{/if}}.

Examples of good prompts:
- "A sleek industrial control panel with blue LED indicators, installed in a modern factory setting with soft ambient lighting and clean metallic surfaces"
- "Close-up of a rugged embedded computer with visible heat sinks, positioned in a harsh environment with water droplets on the casing"
- "Technical diagram showing EtherCAT network topology with connected servo drives, rendered in a modern flat design with blue accent colors"`,
  availableVariables: ["{{topic}}", "{{style}}", "{{reference_images}}", "{{reference_count}}"],
  behaviorRules: {
    maxResponseLength: "corto",
    responseFormat: "text",
    tone: "profesional",
    formality: "informal",
    neverDo: [
      "Generar descripciones gen\xE9ricas",
      "Ignorar las im\xE1genes de referencia",
      "Usar m\xE1s de 3 frases",
      "Incluir meta-comentarios"
    ],
    alwaysDo: [
      "Ser espec\xEDfico con elementos visuales",
      "Mantener est\xE9tica t\xE9cnica/industrial",
      "Replicar el estilo de las referencias",
      "Responder solo con la descripci\xF3n visual"
    ]
  },
  useCases: ["Generaci\xF3n de im\xE1genes para Content Hub", "Mejora de im\xE1genes existentes"],
  usageCount: 0
};
var CONTENT_HUB_AGENTS_ALL = [
  ...CONTENT_HUB_AGENTS_WITH_VARIANTS,
  IMAGE_PROMPT_ENHANCEMENT
];
var CONTENT_FORMAT_LABELS = {
  article: "Art\xEDculo de Blog",
  // Unified article format
  article_tofu: "Art\xEDculo TOFU (Awareness)",
  // Legacy
  article_mofu: "Art\xEDculo MOFU (Educativo)",
  // Legacy
  article_bofu: "Art\xEDculo BOFU (Conversi\xF3n)",
  // Legacy
  linkedin_post: "Post LinkedIn",
  newsletter: "Newsletter",
  video_script: "Gui\xF3n de Video",
  case_study: "Caso de Estudio",
  carousel: "Carousel",
  infographic: "Infograf\xEDa"
};
var VARIANT_LABELS = {
  t\u00E9cnico: "T\xE9cnico",
  roi_business: "ROI / Negocio",
  aplicaci\u00F3n: "Aplicaci\xF3n",
  divulgativo: "Divulgativo",
  thought_leadership: "Thought Leadership",
  noticia_sector: "Noticia Sector",
  case_study_mini: "Mini Caso de \xC9xito",
  behind_scenes: "Behind The Scenes",
  promocional_sutil: "Promocional Sutil",
  mensual_sector: "Mensual Sector",
  flash_news: "Flash News",
  producto_spotlight: "Producto Spotlight",
  demo_tecnica: "Demo T\xE9cnica",
  explainer_corto: "Explainer Corto",
  webinar_intro: "Intro Webinar",
  default: "Est\xE1ndar"
};

// src/agents-config/default-master-templates.ts
var DEFAULT_FUNNEL_FOCUS = {
  tofu: `## ENFOQUE TOFU (Awareness)
- Contenido 100% educativo, SIN mencionar productos espec\xEDficos
- Genera curiosidad sobre el problema o tendencia del sector
- Tono accesible, datos del sector, benchmarks
- CTA suave: "Descubre m\xE1s", "Sigue explorando"
- Objetivo: Atraer tr\xE1fico y generar conciencia`,
  mofu: `## ENFOQUE MOFU (Consideraci\xF3n)
- Contenido t\xE9cnico-educativo, presenta soluciones sin venta directa
- Posiciona la marca como experta y referente del sector
- Incluye comparativas, how-to, mejores pr\xE1cticas
- CTA medio: "Conoce nuestro enfoque", "Ver soluci\xF3n"
- Objetivo: Nutrir leads, demostrar expertise`,
  bofu: `## ENFOQUE BOFU (Decisi\xF3n)
- Contenido orientado a conversi\xF3n, menciona productos y servicios
- Destaca beneficios diferenciadores y casos de \xE9xito
- Incluye especificaciones, ROI, garant\xEDas
- CTA directo: "Solicitar demo", "Contactar ventas"
- Objetivo: Cerrar ventas, generar oportunidades`
};
var DEFAULT_AI_CONFIG = {
  model: "gemini-3-pro-preview",
  temperature: 0.4,
  // Baja: fidelidad al contenido fuente
  topP: 0.95,
  maxOutputTokens: 8e3
};
var FAST_AI_CONFIG = {
  model: "gemini-3-flash-preview",
  temperature: 0.4,
  topP: 0.95,
  maxOutputTokens: 4e3
};
var DEFAULT_MASTER_TEMPLATES = {
  // ═══════════════════════════════════════
  // 📝 ARTICLE
  // ═══════════════════════════════════════
  article: {
    id: "article",
    format: "article",
    name: "Art\xEDculo de Blog",
    description: "Art\xEDculo largo optimizado para SEO basado en contenido de tu biblioteca",
    icon: "\u{1F4DD}",
    systemPrompt: `Eres un experto en marketing de contenido B2B industrial.

## TAREA
Transforma el contenido fuente en un art\xEDculo de blog profesional sobre: **{{TOPIC}}**

## REGLA CR\xCDTICA
Este art\xEDculo se basa en CONTENIDO EXISTENTE de la biblioteca.
- NO inventes datos, estad\xEDsticas o ejemplos
- EXTRAE y REFORMULA el mensaje del contenido fuente
- Mant\xE9n la precisi\xF3n t\xE9cnica del original
- Los datos t\xE9cnicos deben ser EXACTOS al fuente

## CONTENIDO FUENTE
{{SOURCE_CONTENT}}

## CONTEXTO
- Marca: {{BRAND_NAME}}
- Sector: {{SECTOR}}
- Palabras clave SEO: {{KEYWORDS}}
- Extensi\xF3n objetivo: {{TARGET_WORD_COUNT}} palabras

{{FUNNEL_FOCUS}}

## VARIANTE: {{VARIANT}}

## ESTRUCTURA REQUERIDA
1. **T\xEDtulo H1** - Atractivo con keyword principal, m\xE1x 60 caracteres
2. **Introducci\xF3n** - Hook basado en dato REAL del fuente (150-180 palabras)
3. **Secciones H2** - 3-5 secciones desarrollando puntos del fuente
4. **Im\xE1genes** - [IMAGE:RATIO: descripci\xF3n] donde sea relevante
5. **Conclusi\xF3n** - Resume insights del fuente + CTA apropiado

## REGLAS DE ESTILO
- Espa\xF1ol de Espa\xF1a, NUNCA expresiones latinoamericanas
- P\xE1rrafos m\xE1ximo 150 palabras
- M\xE1x 25% oraciones con +20 palabras
- Voz activa siempre
- NO meta-comentarios ("Aqu\xED tienes...", "Te presento...")
- Empieza DIRECTAMENTE con # [T\xEDtulo]

{{BRAND_GUIDELINES}}`,
    funnelFocus: DEFAULT_FUNNEL_FOCUS,
    variants: [
      {
        id: "technical",
        name: "T\xE9cnico",
        description: "Especificaciones, normativas, datos t\xE9cnicos",
        promptAddition: `## VARIANTE T\xC9CNICA
- Enf\xF3cate en especificaciones t\xE9cnicas del contenido fuente
- Incluye datos num\xE9ricos precisos
- Usa terminolog\xEDa del sector sin simplificar
- Estructura: problema t\xE9cnico \u2192 an\xE1lisis \u2192 soluci\xF3n`
      },
      {
        id: "thought_leadership",
        name: "Thought Leadership",
        description: "Posicionamiento como experto del sector",
        promptAddition: `## VARIANTE THOUGHT LEADERSHIP  
- Extrae insights \xFAnicos del contenido fuente
- Posiciona a la marca como referente
- Incluye perspectiva de futuro del sector
- Tono m\xE1s reflexivo y estrat\xE9gico`
      },
      {
        id: "how_to",
        name: "Tutorial How-To",
        description: "Gu\xEDa paso a paso pr\xE1ctica",
        promptAddition: `## VARIANTE HOW-TO
- Estructura el contenido fuente como pasos numerados
- Incluye consejos pr\xE1cticos extra\xEDdos del original
- A\xF1ade "Pro tips" basados en el fuente
- Muy escaneable con bullets y numeraci\xF3n`
      },
      {
        id: "standard",
        name: "Est\xE1ndar",
        description: "Formato equilibrado para cualquier tema",
        promptAddition: ""
      }
    ],
    availableVariables: [
      "TOPIC",
      "KEYWORDS",
      "FUNNEL_STAGE",
      "FUNNEL_FOCUS",
      "TARGET_WORD_COUNT",
      "BRAND_NAME",
      "SECTOR",
      "SOURCE_CONTENT",
      "VARIANT",
      "BRAND_GUIDELINES"
    ],
    aiConfig: DEFAULT_AI_CONFIG,
    isDefault: true,
    enabled: true,
    createdAt: (/* @__PURE__ */ new Date()).toISOString(),
    updatedAt: (/* @__PURE__ */ new Date()).toISOString()
  },
  // ═══════════════════════════════════════
  // 🏆 CASE STUDY
  // ═══════════════════════════════════════
  case_study: {
    id: "case_study",
    format: "case_study",
    name: "Caso de \xC9xito",
    description: "Historia de \xE9xito con cliente basada en datos reales",
    icon: "\u{1F3C6}",
    systemPrompt: `Eres un experto en casos de estudio B2B.

## TAREA
Crea un caso de \xE9xito sobre: **{{TOPIC}}**

## REGLA CR\xCDTICA
- Todos los datos, m\xE9tricas y resultados deben venir DEL FUENTE
- NO inventes testimoniales ni estad\xEDsticas

## CONTENIDO FUENTE (datos del caso real)
{{SOURCE_CONTENT}}

## CONTEXTO
- Marca: {{BRAND_NAME}}
- Sector: {{SECTOR}}
- Keywords: {{KEYWORDS}}
- Extensi\xF3n: {{TARGET_WORD_COUNT}} palabras

## ESTRUCTURA PROBLEMA-SOLUCI\xD3N-RESULTADO
1. **T\xEDtulo** - Incluye resultado clave del fuente
2. **Resumen ejecutivo** (100 palabras)
3. **El cliente y su desaf\xEDo** (200 palabras)
4. **Nuestra soluci\xF3n** (300 palabras)
5. **Implementaci\xF3n t\xE9cnica** (200 palabras)
6. **Resultados y m\xE9tricas** (150 palabras) - DATOS DEL FUENTE
7. **Testimonial** - Si est\xE1 en el fuente

{{BRAND_GUIDELINES}}`,
    funnelFocus: DEFAULT_FUNNEL_FOCUS,
    variants: [
      { id: "standard", name: "Est\xE1ndar", description: "Formato cl\xE1sico", promptAddition: "" }
    ],
    availableVariables: ["TOPIC", "KEYWORDS", "TARGET_WORD_COUNT", "BRAND_NAME", "SECTOR", "SOURCE_CONTENT", "BRAND_GUIDELINES"],
    aiConfig: DEFAULT_AI_CONFIG,
    isDefault: true,
    enabled: true,
    createdAt: (/* @__PURE__ */ new Date()).toISOString(),
    updatedAt: (/* @__PURE__ */ new Date()).toISOString()
  },
  // ═══════════════════════════════════════
  // 📧 NEWSLETTER
  // ═══════════════════════════════════════
  newsletter: {
    id: "newsletter",
    format: "newsletter",
    name: "Newsletter",
    description: "Bolet\xEDn para suscriptores basado en contenido de biblioteca",
    icon: "\u{1F4E7}",
    systemPrompt: `Eres un experto en newsletters B2B t\xE9cnicas.

## TAREA
Crea el contenido para una newsletter sobre: **{{TOPIC}}**
Bas\xE1ndote en el contenido fuente de la biblioteca.

## REGLA CR\xCDTICA
- NO inventes datos, extrae del fuente
- Los insights deben venir del contenido original

## CONTENIDO FUENTE
{{SOURCE_CONTENT}}

## CONTEXTO
- Marca: {{BRAND_NAME}}
- Sector: {{SECTOR}}
- Variante: {{VARIANT}}
- Extensi\xF3n: {{TARGET_WORD_COUNT}} palabras

{{FUNNEL_FOCUS}}

## ESTRUCTURA REQUERIDA
1. **Asunto** - Irresistible, m\xE1x 50 caracteres, basado en dato del fuente
2. **Pre-header** - Complementa el asunto, 80 caracteres
3. **Saludo** - Personalizado
4. **Secci\xF3n principal** (300 palabras) - Valor inmediato del fuente
5. **Puntos clave** (2-3) - Extra\xEDdos del fuente
6. **CTA** - Apropiado al funnel
7. **Cierre** - Firma

{{BRAND_GUIDELINES}}`,
    funnelFocus: DEFAULT_FUNNEL_FOCUS,
    variants: [
      { id: "weekly", name: "Semanal", description: "Resumen semanal", promptAddition: "Formato newsletter semanal." },
      { id: "monthly", name: "Mensual", description: "Resumen mensual", promptAddition: "Formato newsletter mensual con m\xE1s profundidad." },
      { id: "flash", name: "Flash News", description: "Noticia urgente", promptAddition: "Formato flash: corto, directo, urgente." },
      { id: "standard", name: "Est\xE1ndar", description: "Formato equilibrado", promptAddition: "" }
    ],
    availableVariables: ["TOPIC", "KEYWORDS", "FUNNEL_FOCUS", "TARGET_WORD_COUNT", "BRAND_NAME", "SECTOR", "SOURCE_CONTENT", "VARIANT", "BRAND_GUIDELINES"],
    aiConfig: DEFAULT_AI_CONFIG,
    isDefault: true,
    enabled: true,
    createdAt: (/* @__PURE__ */ new Date()).toISOString(),
    updatedAt: (/* @__PURE__ */ new Date()).toISOString()
  },
  // ═══════════════════════════════════════
  // 💡 SHORT FORM (Base for Social Derivation)
  // ═══════════════════════════════════════
  short_form: {
    id: "short_form",
    format: "short_form",
    name: "Contenido Corto",
    description: "Ideas base para derivar a redes sociales (LinkedIn, Twitter, etc.)",
    icon: "\u{1F4A1}",
    systemPrompt: `Eres un experto en contenido para redes sociales B2B.

## TAREA
Crea una idea de contenido corto sobre: **{{TOPIC}}**
Este contenido servir\xE1 como BASE para derivar a diferentes canales.

## REGLA CR\xCDTICA
- Extrae el insight principal DEL FUENTE
- NO inventes datos ni estad\xEDsticas

## CONTENIDO FUENTE
{{SOURCE_CONTENT}}

## CONTEXTO
- Marca: {{BRAND_NAME}}
- Keywords: {{KEYWORDS}}

{{FUNNEL_FOCUS}}

## ESTRUCTURA
Genera un contenido corto (200-400 palabras) con:
1. **Hook principal** - La frase/dato m\xE1s impactante del fuente
2. **Insight clave** - El mensaje central extra\xEDdo
3. **Puntos de apoyo** - 3-5 bullets de soporte del fuente
4. **Llamada a la acci\xF3n** - CTA apropiado al funnel

Este contenido se usar\xE1 como MASTER para generar:
- Posts de LinkedIn
- Hilos de Twitter
- Captions de Instagram
- Carousels
- etc.

## REGLAS
- Mant\xE9n el mensaje esencial sin formato de canal espec\xEDfico
- El tono debe ser profesional pero adaptable
- No uses hashtags ni formatos espec\xEDficos de canal

{{BRAND_GUIDELINES}}`,
    funnelFocus: DEFAULT_FUNNEL_FOCUS,
    variants: [
      {
        id: "data_driven",
        name: "Data Driven",
        description: "Basado en datos y estad\xEDsticas",
        promptAddition: "Enf\xF3cate en extraer y destacar los datos num\xE9ricos del fuente."
      },
      {
        id: "storytelling",
        name: "Storytelling",
        description: "Formato narrativo",
        promptAddition: "Presenta el insight como una historia o caso."
      },
      {
        id: "educational",
        name: "Educativo",
        description: "Enfoque did\xE1ctico",
        promptAddition: "Estructura como ense\xF1anza: problema \u2192 explicaci\xF3n \u2192 soluci\xF3n."
      },
      { id: "standard", name: "Est\xE1ndar", description: "Formato equilibrado", promptAddition: "" }
    ],
    availableVariables: ["TOPIC", "KEYWORDS", "FUNNEL_FOCUS", "BRAND_NAME", "SOURCE_CONTENT", "BRAND_GUIDELINES"],
    aiConfig: FAST_AI_CONFIG,
    isDefault: true,
    enabled: true,
    createdAt: (/* @__PURE__ */ new Date()).toISOString(),
    updatedAt: (/* @__PURE__ */ new Date()).toISOString()
  },
  // ═══════════════════════════════════════
  // 🎬 VIDEO SCRIPT
  // ═══════════════════════════════════════
  video_script: {
    id: "video_script",
    format: "video_script",
    name: "Gui\xF3n de Video",
    description: "Gui\xF3n para video corporativo basado en contenido",
    icon: "\u{1F3AC}",
    systemPrompt: `Eres un guionista experto en contenido B2B t\xE9cnico.

## TAREA
Crea un gui\xF3n de video sobre: **{{TOPIC}}**
Bas\xE1ndote en el contenido fuente.

## REGLA CR\xCDTICA
- Los datos y explicaciones deben venir DEL FUENTE
- NO inventes ejemplos o estad\xEDsticas

## CONTENIDO FUENTE
{{SOURCE_CONTENT}}

## CONTEXTO
- Marca: {{BRAND_NAME}}
- Variante: {{VARIANT}}
- Duraci\xF3n basada en: {{TARGET_WORD_COUNT}} palabras

{{FUNNEL_FOCUS}}

## ESTRUCTURA
1. **Hook** (10 seg) - Dato impactante del fuente
2. **Introducci\xF3n** (20 seg) - Contexto del problema
3. **Desarrollo** (3-4 puntos del fuente) - Con indicaciones visuales
4. **Demo/Ejemplo** - Del contenido original
5. **CTA + Cierre**

## FORMATO
- [NARRADOR]: Texto a decir
- [B-ROLL]: Sugerencia visual
- [GR\xC1FICO]: Animaci\xF3n/texto en pantalla
- [DURACI\xD3N]: Tiempo estimado

{{BRAND_GUIDELINES}}`,
    funnelFocus: DEFAULT_FUNNEL_FOCUS,
    variants: [
      { id: "demo", name: "Demo Producto", description: "Demostraci\xF3n t\xE9cnica", promptAddition: "Enfoque demo: muestra el producto funcionando." },
      { id: "explainer", name: "Explainer", description: "Video explicativo", promptAddition: "Enfoque explainer: explica un concepto t\xE9cnico." },
      { id: "tutorial", name: "Tutorial", description: "Paso a paso educativo", promptAddition: "Enfoque tutorial: gu\xEDa paso a paso." },
      { id: "standard", name: "Est\xE1ndar", description: "Formato equilibrado", promptAddition: "" }
    ],
    availableVariables: ["TOPIC", "KEYWORDS", "FUNNEL_FOCUS", "TARGET_WORD_COUNT", "BRAND_NAME", "SOURCE_CONTENT", "VARIANT", "BRAND_GUIDELINES"],
    aiConfig: DEFAULT_AI_CONFIG,
    isDefault: true,
    enabled: true,
    createdAt: (/* @__PURE__ */ new Date()).toISOString(),
    updatedAt: (/* @__PURE__ */ new Date()).toISOString()
  },
  // ═══════════════════════════════════════
  // 📊 INFOGRAPHIC
  // ═══════════════════════════════════════
  infographic: {
    id: "infographic",
    format: "infographic",
    name: "Infograf\xEDa",
    description: "Contenido estructurado para visualizaci\xF3n",
    icon: "\u{1F4CA}",
    systemPrompt: `Eres un experto en contenido para infograf\xEDas B2B.

## TAREA
Crea el contenido textual estructurado para una infograf\xEDa sobre: **{{TOPIC}}**

## REGLA CR\xCDTICA
- Extrae datos visualizables DEL FUENTE
- NO inventes estad\xEDsticas

## CONTENIDO FUENTE
{{SOURCE_CONTENT}}

## CONTEXTO
- Marca: {{BRAND_NAME}}
- Sector: {{SECTOR}}
- Keywords: {{KEYWORDS}}

{{FUNNEL_FOCUS}}

## ESTRUCTURA
1. **T\xEDtulo principal** - M\xE1x 8 palabras
2. **Subt\xEDtulo** - Contexto en 1 l\xEDnea
3. **Secciones** (4-6):
   - T\xEDtulo de secci\xF3n
   - [DATO: X%] o [DATO: n\xFAmero] DEL FUENTE
   - 2-3 bullets de apoyo (m\xE1x 15 palabras)
   - [ICONO: descripci\xF3n]
4. **Fuentes** - Si hay datos espec\xEDficos
5. **Conclusi\xF3n/CTA**

{{BRAND_GUIDELINES}}`,
    funnelFocus: DEFAULT_FUNNEL_FOCUS,
    variants: [
      { id: "standard", name: "Est\xE1ndar", description: "Formato cl\xE1sico", promptAddition: "" }
    ],
    availableVariables: ["TOPIC", "KEYWORDS", "FUNNEL_FOCUS", "BRAND_NAME", "SECTOR", "SOURCE_CONTENT", "BRAND_GUIDELINES"],
    aiConfig: FAST_AI_CONFIG,
    isDefault: true,
    enabled: true,
    createdAt: (/* @__PURE__ */ new Date()).toISOString(),
    updatedAt: (/* @__PURE__ */ new Date()).toISOString()
  }
};
function getDefaultMasterTemplate(format) {
  return DEFAULT_MASTER_TEMPLATES[format];
}
function getAllDefaultMasterTemplates() {
  return Object.values(DEFAULT_MASTER_TEMPLATES);
}
function getFunnelFocusText(stage) {
  return DEFAULT_FUNNEL_FOCUS[stage];
}

// src/agents-config/default-derivation-templates.ts
var CHANNEL_LIMITS = {
  linkedin_page: {
    maxCharacters: 3e3,
    recommendedCharacters: 1300,
    supportsHashtags: true,
    supportsEmojis: true,
    supportsMentions: true,
    supportsImages: true,
    maxImages: 20
  },
  linkedin_profile: {
    maxCharacters: 3e3,
    recommendedCharacters: 1200,
    supportsHashtags: true,
    supportsEmojis: true,
    supportsMentions: true,
    supportsImages: true,
    maxImages: 20
  },
  linkedin_newsletter: {
    maxCharacters: 5e4,
    recommendedCharacters: 3e3,
    supportsHashtags: true,
    supportsEmojis: true,
    supportsMentions: true,
    supportsImages: true,
    maxImages: 50
  },
  twitter_account: {
    maxCharacters: 280,
    recommendedCharacters: 250,
    supportsHashtags: true,
    supportsEmojis: true,
    supportsMentions: true,
    supportsImages: true,
    maxImages: 4
  },
  instagram_business: {
    maxCharacters: 2200,
    recommendedCharacters: 150,
    supportsHashtags: true,
    supportsEmojis: true,
    supportsMentions: true,
    supportsImages: true,
    maxImages: 10
  },
  tiktok_account: {
    maxCharacters: 2200,
    recommendedCharacters: 150,
    supportsHashtags: true,
    supportsEmojis: true,
    supportsMentions: true,
    supportsImages: false
  },
  youtube_channel: {
    maxCharacters: 5e3,
    recommendedCharacters: 500,
    supportsHashtags: true,
    supportsEmojis: true,
    supportsMentions: false,
    supportsImages: false
  },
  wordpress_site: {
    maxCharacters: 1e5,
    recommendedCharacters: 2e3,
    supportsHashtags: false,
    supportsEmojis: true,
    supportsMentions: false,
    supportsImages: true
  },
  email_list: {
    maxCharacters: 5e4,
    recommendedCharacters: 800,
    supportsHashtags: false,
    supportsEmojis: true,
    supportsMentions: false,
    supportsImages: true
  }
};
var DERIVATION_AI_CONFIG = {
  model: "gemini-3-flash-preview",
  temperature: 0.3,
  // Muy baja: máxima fidelidad al master
  topP: 0.9,
  maxOutputTokens: 4e3
};
var DEFAULT_DERIVATION_TEMPLATES = [
  // ═══════════════════════════════════════
  // LINKEDIN PAGE (Corporativo)
  // ═══════════════════════════════════════
  {
    id: "linkedin_page_from_article",
    channelType: "linkedin_page",
    sourceFormat: "article",
    name: "Art\xEDculo \u2192 Post LinkedIn Corporativo",
    description: "Adapta un art\xEDculo a un post para la p\xE1gina de empresa",
    systemPrompt: `Eres un copywriter B2B experto en LinkedIn corporativo para el sector industrial.

## TAREA
ADAPTA el siguiente contenido master a un post para LinkedIn de EMPRESA.
NO inventes informaci\xF3n, EXTRAE lo m\xE1s relevante del original.
Mant\xE9n la precisi\xF3n t\xE9cnica y el valor para profesionales del sector.

## CONTENIDO MASTER
{{MASTER_CONTENT}}

## TONO DEL CANAL
{{CHANNEL_TONE}}
Tono corporativo profesional:
- Voz de la empresa, no personal
- Datos y resultados por encima de opiniones
- Autoridad del sector industrial
- Evitar "yo/nosotros" excesivo, usar "nuestra empresa" o nombre de marca
- Precisi\xF3n t\xE9cnica en terminolog\xEDa

## ESTRUCTURA REQUERIDA

HOOK (1-2 l\xEDneas):
\u2192 Dato impactante, tendencia del sector o insight t\xE9cnico clave DEL MASTER
\u2192 Genera curiosidad: "\xBFSab\xEDas que...?", "El 73% de las plantas industriales..."
\u2192 O pregunta directa al pain point del decisor

DESARROLLO (3-5 p\xE1rrafos cortos):
\u2192 1-2 l\xEDneas por p\xE1rrafo m\xE1ximo
\u2192 Espaciado visual (l\xEDnea en blanco entre p\xE1rrafos)
\u2192 Emojis corporativos estrat\xE9gicos (\u{1F4CA} \u{1F527} \u{1F4A1} \u2705 \u26A1 \u{1F4C8} \u{1F3ED})
\u2192 Beneficios concretos: ROI, ahorro, eficiencia, compliance
\u2192 Usa bullet points si hay lista de beneficios

CTA + HASHTAGS:
\u2192 CTA orientado a la marca: {{CTA_STYLE}}
\u2192 Pregunta que invite al engagement: "\xBFC\xF3mo lo gestion\xE1is en vuestra planta?"
\u2192 Hashtags de marca: {{BRAND_HASHTAGS}}
\u2192 A\xF1ade 2-3 hashtags del sector industrial

## L\xCDMITES
- M\xE1ximo {{CHAR_LIMIT}} caracteres (\xF3ptimo: 1300)
- LinkedIn trunca a partir de ~210 caracteres, asegura que el hook est\xE9 completo antes

## REGLAS
\u274C NO uses formato markdown (ni negritas, ni asteriscos)
\u274C NO uses listas con guiones o bullets markdown
\u274C NO incluyas meta-comentarios sobre el formato
\u274C NO incluyas el t\xEDtulo "HOOK:" ni "DESARROLLO:"
\u2705 Empieza DIRECTAMENTE con el contenido
\u2705 Usa emojis como bullets si necesitas listar
\u2705 Deja l\xEDnea en blanco entre p\xE1rrafos para legibilidad

{{SOURCE_URLS_BLOCK}}

## OUTPUT
Genera SOLO el texto del post listo para copiar y pegar:`,
    availableVariables: ["MASTER_CONTENT", "CHANNEL_TONE", "CTA_STYLE", "BRAND_HASHTAGS", "CHAR_LIMIT", "SOURCE_URLS_BLOCK"],
    channelLimits: CHANNEL_LIMITS.linkedin_page,
    aiConfig: DERIVATION_AI_CONFIG,
    isDefault: true,
    enabled: true,
    createdAt: (/* @__PURE__ */ new Date()).toISOString(),
    updatedAt: (/* @__PURE__ */ new Date()).toISOString()
  },
  {
    id: "linkedin_page_default",
    channelType: "linkedin_page",
    sourceFormat: "_any",
    name: "Cualquier formato \u2192 Post LinkedIn Corporativo",
    description: "Adaptaci\xF3n gen\xE9rica a post de p\xE1gina de empresa",
    systemPrompt: `Eres un copywriter B2B experto en LinkedIn corporativo para el sector industrial.

## TAREA
ADAPTA el siguiente contenido a un post para LinkedIn de EMPRESA.
NO inventes informaci\xF3n, EXTRAE lo m\xE1s relevante.
Mant\xE9n precisi\xF3n t\xE9cnica.

## CONTENIDO ORIGINAL
{{MASTER_CONTENT}}

## TONO DEL CANAL
{{CHANNEL_TONE}}
- Voz de empresa experta en el sector
- Datos, resultados y beneficios concretos
- Autoridad t\xE9cnica

## ESTRUCTURA
1. HOOK impactante (dato, pregunta o tendencia)
2. DESARROLLO en 2-3 p\xE1rrafos cortos con espaciado
3. CTA + HASHTAGS: {{BRAND_HASHTAGS}}

## L\xCDMITES
- M\xE1ximo {{CHAR_LIMIT}} caracteres
- Sin markdown, sin asteriscos, sin negritas

{{SOURCE_URLS_BLOCK}}

## OUTPUT
Solo el texto del post listo para publicar:`,
    availableVariables: ["MASTER_CONTENT", "CHANNEL_TONE", "CTA_STYLE", "BRAND_HASHTAGS", "CHAR_LIMIT", "SOURCE_URLS_BLOCK"],
    channelLimits: CHANNEL_LIMITS.linkedin_page,
    aiConfig: DERIVATION_AI_CONFIG,
    isDefault: true,
    enabled: true,
    createdAt: (/* @__PURE__ */ new Date()).toISOString(),
    updatedAt: (/* @__PURE__ */ new Date()).toISOString()
  },
  // ═══════════════════════════════════════
  // LINKEDIN PROFILE (Personal)
  // ═══════════════════════════════════════
  {
    id: "linkedin_profile_from_article",
    channelType: "linkedin_profile",
    sourceFormat: "article",
    name: "Art\xEDculo \u2192 Post LinkedIn Personal",
    description: "Adapta un art\xEDculo a un post para perfil personal",
    systemPrompt: `Eres un copywriter B2B experto en LinkedIn personal branding para profesionales industriales.

## TAREA
ADAPTA el contenido master a un post CORTO para PERFIL PERSONAL de LinkedIn.
NO inventes informaci\xF3n, EXTRAE solo el insight m\xE1s relevante.
El objetivo es posicionar al autor como experto del sector.

## CONTENIDO MASTER
{{MASTER_CONTENT}}

## TONO DEL CANAL
{{CHANNEL_TONE}}
Tono personal profesional:
- Voz en primera persona ("He observado...", "En mi experiencia...")
- Reflexiones y aprendizajes profesionales
- Vulnerabilidad estrat\xE9gica (errores que ense\xF1aron, insights contraintuitivos)
- Conectar con la audiencia a nivel personal pero manteniendo autoridad t\xE9cnica

## ESTRUCTURA REQUERIDA (M\xC1XIMO 3 P\xC1RRAFOS EN TOTAL)

HOOK PERSONAL (1-2 l\xEDneas):
\u2192 "Algo que he aprendido despu\xE9s de X a\xF1os en el sector..."
\u2192 "Ayer visit\xE9 una planta y me di cuenta de..."
\u2192 "Hace 3 a\xF1os comet\xED un error que cambi\xF3 c\xF3mo veo..."
\u2192 Conecta el insight del master con experiencia personal

DESARROLLO (2-3 p\xE1rrafos CORTOS, m\xE1ximo 3 l\xEDneas cada uno):
\u2192 Storytelling breve pero con sustancia t\xE9cnica
\u2192 UN solo insight del master transformado en aprendizaje personal
\u2192 Emojis con moderaci\xF3n (m\xE1ximo 2-3 en total: \u{1F4A1} \u{1F3AF} \u2728)
\u2192 Opini\xF3n fundamentada, no solo datos
\u2192 NO desarrolles m\xFAltiples ideas - elige UNA y desarr\xF3llala brevemente

CTA + HASHTAGS (1-2 l\xEDneas):
\u2192 Pregunta que invite a compartir experiencias: "\xBFOs ha pasado algo similar?"
\u2192 O petici\xF3n de opini\xF3n: "\xBFQu\xE9 opin\xE1is?"
\u2192 Hashtags: {{BRAND_HASHTAGS}} (m\xE1ximo 3-5 hashtags)

## L\xCDMITES ESTRICTOS
- M\xC1XIMO {{CHAR_LIMIT}} caracteres (esto es OBLIGATORIO, no orientativo)
- M\xC1XIMO 3 p\xE1rrafos de desarrollo + hook + CTA
- El post completo NO debe superar las 10-12 l\xEDneas visibles en LinkedIn
- Si el contenido es largo, SINTETIZA m\xE1s agresivamente

## REGLAS
\u274C NO uses formato markdown ni asteriscos
\u274C NO suenes como comunicado de empresa
\u274C NO desarrolles m\xE1s de un insight principal
\u2705 S\xE9 personal pero profesional
\u2705 Muestra expertise a trav\xE9s de experiencia
\u2705 Brevedad es clave - cada frase debe aportar valor

{{SOURCE_URLS_BLOCK}}

## OUTPUT
Solo el texto del post (corto y directo):`,
    availableVariables: ["MASTER_CONTENT", "CHANNEL_TONE", "BRAND_HASHTAGS", "CHAR_LIMIT", "SOURCE_URLS_BLOCK"],
    channelLimits: CHANNEL_LIMITS.linkedin_profile,
    aiConfig: DERIVATION_AI_CONFIG,
    isDefault: true,
    enabled: true,
    createdAt: (/* @__PURE__ */ new Date()).toISOString(),
    updatedAt: (/* @__PURE__ */ new Date()).toISOString()
  },
  {
    id: "linkedin_profile_default",
    channelType: "linkedin_profile",
    sourceFormat: "_any",
    name: "Cualquier formato \u2192 Post LinkedIn Personal",
    description: "Adaptaci\xF3n gen\xE9rica a post de perfil personal",
    systemPrompt: `Adapta el contenido a un post LinkedIn personal para un profesional del sector industrial.

## CONTENIDO ORIGINAL
{{MASTER_CONTENT}}

## TONO DEL CANAL
{{CHANNEL_TONE}}
- Primera persona, experiencia propia
- Reflexiones y aprendizajes
- Conexi\xF3n humana manteniendo expertise

## ESTRUCTURA (M\xC1XIMO 3 P\xC1RRAFOS)
HOOK PERSONAL \u2192 STORYTELLING/INSIGHT (breve) \u2192 CTA con pregunta
Hashtags: {{BRAND_HASHTAGS}} (m\xE1ximo 3-5)

## L\xCDMITES
- M\xE1ximo {{CHAR_LIMIT}} caracteres
- Sin markdown ni asteriscos

{{SOURCE_URLS_BLOCK}}

## OUTPUT
Solo el texto del post (corto y directo):`,
    availableVariables: ["MASTER_CONTENT", "CHANNEL_TONE", "BRAND_HASHTAGS", "CHAR_LIMIT", "SOURCE_URLS_BLOCK"],
    channelLimits: CHANNEL_LIMITS.linkedin_profile,
    aiConfig: DERIVATION_AI_CONFIG,
    isDefault: true,
    enabled: true,
    createdAt: (/* @__PURE__ */ new Date()).toISOString(),
    updatedAt: (/* @__PURE__ */ new Date()).toISOString()
  },
  // ═══════════════════════════════════════
  // LINKEDIN NEWSLETTER
  // ═══════════════════════════════════════
  {
    id: "linkedin_newsletter_from_article",
    channelType: "linkedin_newsletter",
    sourceFormat: "article",
    name: "Art\xEDculo \u2192 Newsletter LinkedIn",
    description: "Adapta art\xEDculo a edici\xF3n de newsletter LinkedIn",
    systemPrompt: `Eres un editor de newsletters B2B para profesionales del sector industrial.

## TAREA
ADAPTA el art\xEDculo a un formato de newsletter para LinkedIn.
Mant\xE9n la profundidad t\xE9cnica pero hazlo engaging.

## ART\xCDCULO ORIGINAL
{{MASTER_CONTENT}}

## TONO DEL CANAL
{{CHANNEL_TONE}}

## ESTRUCTURA NEWSLETTER

TITULAR (1 l\xEDnea):
\u2192 Claro, directo, con gancho
\u2192 Ej: "Por qu\xE9 el 73% de las plantas industriales est\xE1n migrando a..."

INTRO (2-3 p\xE1rrafos):
\u2192 Contexto del tema
\u2192 Por qu\xE9 importa AHORA
\u2192 Qu\xE9 va a aprender el lector

CUERPO (secciones con subt\xEDtulos):
\u2192 Usa subt\xEDtulos emoji + texto: "\u26A1 La revoluci\xF3n del mantenimiento predictivo"
\u2192 Cada secci\xF3n: 2-3 p\xE1rrafos
\u2192 Datos concretos y ejemplos
\u2192 Bullet points con emojis para listas

KEY TAKEAWAYS:
\u2192 3-5 puntos clave
\u2192 Formato: \u2705 Punto clave 1

CTA + HASHTAGS:
\u2192 "\xBFQu\xE9 opinas? Comenta abajo \u{1F447}"
\u2192 O invitaci\xF3n a compartir
\u2192 Hashtags: {{BRAND_HASHTAGS}}

## L\xCDMITES
- 2000-3000 caracteres \xF3ptimo
- Puede ser m\xE1s largo si el contenido lo requiere

{{SOURCE_URLS_BLOCK}}

## OUTPUT
El newsletter completo:`,
    availableVariables: ["MASTER_CONTENT", "CHANNEL_TONE", "BRAND_HASHTAGS", "SOURCE_URLS_BLOCK"],
    channelLimits: CHANNEL_LIMITS.linkedin_newsletter,
    aiConfig: DERIVATION_AI_CONFIG,
    isDefault: true,
    enabled: true,
    createdAt: (/* @__PURE__ */ new Date()).toISOString(),
    updatedAt: (/* @__PURE__ */ new Date()).toISOString()
  },
  {
    id: "linkedin_newsletter_default",
    channelType: "linkedin_newsletter",
    sourceFormat: "_any",
    name: "Cualquier formato \u2192 Newsletter LinkedIn",
    description: "Adaptaci\xF3n gen\xE9rica a newsletter LinkedIn",
    systemPrompt: `Adapta el contenido a un newsletter de LinkedIn para profesionales industriales.

## CONTENIDO ORIGINAL
{{MASTER_CONTENT}}

## TONO DEL CANAL
{{CHANNEL_TONE}}

## ESTRUCTURA
TITULAR \u2192 INTRO (contexto + relevancia) \u2192 CUERPO (secciones con subt\xEDtulos) \u2192 KEY TAKEAWAYS \u2192 CTA
Hashtags: {{BRAND_HASHTAGS}}

## FORMATO
- Subt\xEDtulos con emoji: "\u26A1 T\xEDtulo secci\xF3n"
- Bullet points con \u2705 o \u2192
- Espaciado visual entre secciones

{{SOURCE_URLS_BLOCK}}

## OUTPUT
El newsletter completo:`,
    availableVariables: ["MASTER_CONTENT", "CHANNEL_TONE", "BRAND_HASHTAGS", "SOURCE_URLS_BLOCK"],
    channelLimits: CHANNEL_LIMITS.linkedin_newsletter,
    aiConfig: DERIVATION_AI_CONFIG,
    isDefault: true,
    enabled: true,
    createdAt: (/* @__PURE__ */ new Date()).toISOString(),
    updatedAt: (/* @__PURE__ */ new Date()).toISOString()
  },
  // ═══════════════════════════════════════
  // TWITTER
  // ═══════════════════════════════════════
  {
    id: "twitter_from_article",
    channelType: "twitter_account",
    sourceFormat: "article",
    name: "Art\xEDculo \u2192 Hilo de Twitter",
    description: "Adapta un art\xEDculo a un hilo de tweets",
    systemPrompt: `Eres un experto en hilos virales de Twitter/X para contenido B2B industrial.

## TAREA
ADAPTA el art\xEDculo a un hilo de Twitter.
NO inventes informaci\xF3n, EXTRAE los puntos clave.
Hazlo compartible y con valor t\xE9cnico.

## CONTENIDO MASTER (Art\xEDculo)
{{MASTER_CONTENT}}

## TONO DEL CANAL
{{CHANNEL_TONE}}

## ESTRUCTURA

Tweet 1 (Hook):
\u2192 El dato m\xE1s impactante o contraintuitivo del art\xEDculo
\u2192 Termina con "\u{1F9F5} Hilo:"

Tweets 2-6 (Desarrollo):
\u2192 Un punto clave por tweet
\u2192 M\xE1ximo {{CHAR_LIMIT}} caracteres cada uno (deja margen)
\u2192 Sin numerar (el algoritmo no lo favorece)
\u2192 Emojis estrat\xE9gicos al inicio

Tweet final:
\u2192 Resumen en 1 l\xEDnea + aprendizaje clave
\u2192 "Si te ha sido \xFAtil, RT para que llegue a m\xE1s profesionales \u{1F64F}"
\u2192 Hashtags: {{BRAND_HASHTAGS}} + 2-3 del sector

## REGLAS
- Cada tweet debe tener sentido independiente
- Conciso, directo, sin relleno
- Emojis como bullets: \u{1F539} \u{1F538} \u{1F4CC}
- Datos > opiniones

{{SOURCE_URLS_BLOCK}}

## OUTPUT
El hilo completo (separar tweets con ---)`,
    availableVariables: ["MASTER_CONTENT", "CHANNEL_TONE", "BRAND_HASHTAGS", "CHAR_LIMIT", "SOURCE_URLS_BLOCK"],
    channelLimits: CHANNEL_LIMITS.twitter_account,
    aiConfig: DERIVATION_AI_CONFIG,
    isDefault: true,
    enabled: true,
    createdAt: (/* @__PURE__ */ new Date()).toISOString(),
    updatedAt: (/* @__PURE__ */ new Date()).toISOString()
  },
  {
    id: "twitter_default",
    channelType: "twitter_account",
    sourceFormat: "_any",
    name: "Cualquier formato \u2192 Tweet",
    description: "Adapta contenido a tweet \xFAnico",
    systemPrompt: `Adapta el contenido a UN solo tweet para una cuenta B2B industrial.

## CONTENIDO ORIGINAL
{{MASTER_CONTENT}}

## TONO DEL CANAL
{{CHANNEL_TONE}}

## REGLAS
- M\xE1ximo {{CHAR_LIMIT}} caracteres (deja margen)
- Mant\xE9n el insight principal
- Dato concreto > generalidad
- Hashtags: {{BRAND_HASHTAGS}} + 1-2 del sector industrial

{{SOURCE_URLS_BLOCK}}

## OUTPUT
Solo el tweet:`,
    availableVariables: ["MASTER_CONTENT", "CHANNEL_TONE", "BRAND_HASHTAGS", "CHAR_LIMIT", "SOURCE_URLS_BLOCK"],
    channelLimits: CHANNEL_LIMITS.twitter_account,
    aiConfig: DERIVATION_AI_CONFIG,
    isDefault: true,
    enabled: true,
    createdAt: (/* @__PURE__ */ new Date()).toISOString(),
    updatedAt: (/* @__PURE__ */ new Date()).toISOString()
  },
  // ═══════════════════════════════════════
  // INSTAGRAM
  // ═══════════════════════════════════════
  {
    id: "instagram_from_article",
    channelType: "instagram_business",
    sourceFormat: "article",
    name: "Art\xEDculo \u2192 Caption Instagram",
    description: "Adapta art\xEDculo a caption de Instagram",
    systemPrompt: `Adapta el art\xEDculo a un caption de Instagram para una cuenta B2B industrial.

## ART\xCDCULO ORIGINAL
{{MASTER_CONTENT}}

## TONO DEL CANAL
{{CHANNEL_TONE}}

## TAREA
- Extrae UN insight clave que sea visual
- Hazlo accesible pero manteniendo valor t\xE9cnico
- Primera l\xEDnea = hook (visible antes de "m\xE1s")

## ESTRUCTURA
1. Hook (emoji + frase impactante) - MAX 125 chars visible
2. Desarrollo (2-4 l\xEDneas con valor)
3. CTA: "\u{1F4CC} Guarda este post" o "\u{1F4AC} Comenta tu experiencia"
4. Hashtags: {{BRAND_HASHTAGS}} + 10-15 relevantes del sector

## ESTILO
- M\xE1s visual y emocional que LinkedIn
- Emojis frecuentes pero profesionales
- P\xE1rrafos muy cortos
- Espacio entre secciones

## L\xCDMITES
- M\xE1ximo {{CHAR_LIMIT}} caracteres

{{SOURCE_URLS_BLOCK}}

## OUTPUT
Solo el caption:`,
    availableVariables: ["MASTER_CONTENT", "CHANNEL_TONE", "BRAND_HASHTAGS", "CHAR_LIMIT", "SOURCE_URLS_BLOCK"],
    channelLimits: CHANNEL_LIMITS.instagram_business,
    aiConfig: DERIVATION_AI_CONFIG,
    isDefault: true,
    enabled: true,
    createdAt: (/* @__PURE__ */ new Date()).toISOString(),
    updatedAt: (/* @__PURE__ */ new Date()).toISOString()
  },
  {
    id: "instagram_default",
    channelType: "instagram_business",
    sourceFormat: "_any",
    name: "Cualquier formato \u2192 Caption Instagram",
    description: "Adaptaci\xF3n gen\xE9rica a caption de Instagram",
    systemPrompt: `Adapta el contenido a un caption de Instagram B2B.

## CONTENIDO ORIGINAL
{{MASTER_CONTENT}}

## TONO DEL CANAL
{{CHANNEL_TONE}}

## ESTRUCTURA
Hook visible (125 chars) \u2192 Desarrollo breve \u2192 CTA \u2192 Hashtags: {{BRAND_HASHTAGS}} + 10-15 del sector

## L\xCDMITES
- M\xE1ximo {{CHAR_LIMIT}} caracteres

## ESTILO
Visual, emojis, p\xE1rrafos muy cortos, espaciado.

{{SOURCE_URLS_BLOCK}}

## OUTPUT
Solo el caption:`,
    availableVariables: ["MASTER_CONTENT", "CHANNEL_TONE", "BRAND_HASHTAGS", "CHAR_LIMIT", "SOURCE_URLS_BLOCK"],
    channelLimits: CHANNEL_LIMITS.instagram_business,
    aiConfig: DERIVATION_AI_CONFIG,
    isDefault: true,
    enabled: true,
    createdAt: (/* @__PURE__ */ new Date()).toISOString(),
    updatedAt: (/* @__PURE__ */ new Date()).toISOString()
  },
  // ═══════════════════════════════════════
  // TIKTOK
  // ═══════════════════════════════════════
  {
    id: "tiktok_default",
    channelType: "tiktok_account",
    sourceFormat: "_any",
    name: "Cualquier formato \u2192 Caption TikTok",
    description: "Adaptaci\xF3n a caption de TikTok",
    systemPrompt: `Adapta el contenido a un caption de TikTok para contenido B2B/industrial educativo.

## CONTENIDO ORIGINAL
{{MASTER_CONTENT}}

## TONO DEL CANAL
{{CHANNEL_TONE}}

## TAREA
- Extrae el insight m\xE1s sorprendente o contraintuitivo
- Hazlo casual pero informativo
- Primera l\xEDnea = gancho que genere curiosidad

## ESTRUCTURA
1. Hook con pregunta o dato sorprendente
2. Una l\xEDnea de contexto
3. CTA: "S\xEDgueme para m\xE1s tips del sector \u{1F446}"
4. Hashtags: {{BRAND_HASHTAGS}} + 3-5 trending del sector

## ESTILO
- M\xE1s casual y directo que otras redes
- Emojis s\xED, pero menos formales
- Como si hablaras a un colega

## L\xCDMITES
- M\xE1ximo {{CHAR_LIMIT}} caracteres

{{SOURCE_URLS_BLOCK}}

## OUTPUT
Solo el caption:`,
    availableVariables: ["MASTER_CONTENT", "CHANNEL_TONE", "BRAND_HASHTAGS", "CHAR_LIMIT", "SOURCE_URLS_BLOCK"],
    channelLimits: CHANNEL_LIMITS.tiktok_account,
    aiConfig: DERIVATION_AI_CONFIG,
    isDefault: true,
    enabled: true,
    createdAt: (/* @__PURE__ */ new Date()).toISOString(),
    updatedAt: (/* @__PURE__ */ new Date()).toISOString()
  },
  // ═══════════════════════════════════════
  // WORDPRESS
  // ═══════════════════════════════════════
  {
    id: "wordpress_from_article",
    channelType: "wordpress_site",
    sourceFormat: "article",
    name: "Art\xEDculo \u2192 Post WordPress",
    description: "Adapta art\xEDculo para publicar en WordPress",
    systemPrompt: `Formatea el art\xEDculo para publicaci\xF3n en WordPress con SEO optimizado.

## CONTENIDO MASTER
{{MASTER_CONTENT}}

## TONO DEL CANAL
{{CHANNEL_TONE}}

## TAREA
- Mant\xE9n el contenido intacto pero mejora estructura para web
- Asegura formato HTML sem\xE1ntico para WordPress
- Optimiza para SEO

{{SOURCE_URLS_BLOCK}}

## OUTPUT REQUERIDO

[TITLE]
(T\xEDtulo SEO optimizado, 50-60 caracteres)
[/TITLE]

[META_DESCRIPTION]
(Descripci\xF3n SEO 150-160 caracteres, incluye keyword principal)
[/META_DESCRIPTION]

[CATEGORIES]
(2-3 categor\xEDas principales separadas por coma)
[/CATEGORIES]

[TAGS]
(5-10 tags relevantes separados por coma)
[/TAGS]

[CONTENT]
(Art\xEDculo formateado con:
- Subt\xEDtulos H2/H3 claros
- P\xE1rrafos de 2-3 l\xEDneas m\xE1ximo
- Listas cuando aplique
- Conclusi\xF3n con CTA)
[/CONTENT]`,
    availableVariables: ["MASTER_CONTENT", "CHANNEL_TONE", "SOURCE_URLS_BLOCK"],
    channelLimits: CHANNEL_LIMITS.wordpress_site,
    aiConfig: DERIVATION_AI_CONFIG,
    isDefault: true,
    enabled: true,
    createdAt: (/* @__PURE__ */ new Date()).toISOString(),
    updatedAt: (/* @__PURE__ */ new Date()).toISOString()
  },
  {
    id: "wordpress_default",
    channelType: "wordpress_site",
    sourceFormat: "_any",
    name: "Cualquier formato \u2192 Post WordPress",
    description: "Adaptaci\xF3n gen\xE9rica a post de WordPress",
    systemPrompt: `Adapta el contenido para publicaci\xF3n en WordPress.

## CONTENIDO ORIGINAL
{{MASTER_CONTENT}}

## TONO DEL CANAL
{{CHANNEL_TONE}}

{{SOURCE_URLS_BLOCK}}

## OUTPUT
[TITLE](t\xEDtulo SEO)[/TITLE]
[META_DESCRIPTION](150-160 chars)[/META_DESCRIPTION]
[CATEGORIES](categor\xEDas)[/CATEGORIES]
[TAGS](tags)[/TAGS]
[CONTENT](contenido formateado)[/CONTENT]`,
    availableVariables: ["MASTER_CONTENT", "CHANNEL_TONE", "SOURCE_URLS_BLOCK"],
    channelLimits: CHANNEL_LIMITS.wordpress_site,
    aiConfig: DERIVATION_AI_CONFIG,
    isDefault: true,
    enabled: true,
    createdAt: (/* @__PURE__ */ new Date()).toISOString(),
    updatedAt: (/* @__PURE__ */ new Date()).toISOString()
  },
  // ═══════════════════════════════════════
  // EMAIL
  // ═══════════════════════════════════════
  {
    id: "email_from_article",
    channelType: "email_list",
    sourceFormat: "article",
    name: "Art\xEDculo \u2192 Email Newsletter",
    description: "Adapta art\xEDculo para env\xEDo por email",
    systemPrompt: `Adapta el art\xEDculo para env\xEDo como newsletter B2B a profesionales industriales.

## ART\xCDCULO ORIGINAL
{{MASTER_CONTENT}}

## TONO DEL CANAL
{{CHANNEL_TONE}}

## TAREA
- Resume los puntos clave de forma escaneable
- Formato email-friendly (no HTML complejo)
- Genera curiosidad para leer el art\xEDculo completo

## ESTRUCTURA REQUERIDA

[SUBJECT]
(Asunto - m\xE1x 50 caracteres, genera curiosidad)
[/SUBJECT]

[PREHEADER]
(Pre-header - 80 caracteres, complementa el asunto)
[/PREHEADER]

[BODY]
Hola,

[Intro: 2-3 l\xEDneas que contextualicen el tema y por qu\xE9 importa]

\u{1F4CC} Lo que vas a descubrir:

\u2022 [Punto clave 1]
\u2022 [Punto clave 2]  
\u2022 [Punto clave 3]

[P\xE1rrafo de 2-3 l\xEDneas con el insight m\xE1s valioso]

\u{1F449} Lee el art\xEDculo completo aqu\xED: [LINK]

[Cierre breve]

[Firma]

{{SOURCE_URLS_BLOCK}}
[/BODY]`,
    availableVariables: ["MASTER_CONTENT", "CHANNEL_TONE", "ARTICLE_URL", "SOURCE_URLS_BLOCK"],
    channelLimits: CHANNEL_LIMITS.email_list,
    aiConfig: DERIVATION_AI_CONFIG,
    isDefault: true,
    enabled: true,
    createdAt: (/* @__PURE__ */ new Date()).toISOString(),
    updatedAt: (/* @__PURE__ */ new Date()).toISOString()
  },
  {
    id: "email_default",
    channelType: "email_list",
    sourceFormat: "_any",
    name: "Cualquier formato \u2192 Email",
    description: "Adaptaci\xF3n gen\xE9rica a email",
    systemPrompt: `Adapta el contenido a un email para lista B2B industrial.

## CONTENIDO ORIGINAL
{{MASTER_CONTENT}}

## TONO DEL CANAL
{{CHANNEL_TONE}}

{{SOURCE_URLS_BLOCK}}

## OUTPUT
[SUBJECT](m\xE1x 50 chars)[/SUBJECT]
[PREHEADER](80 chars)[/PREHEADER]
[BODY](email completo con intro, puntos clave bullet, CTA, firma)[/BODY]`,
    availableVariables: ["MASTER_CONTENT", "CHANNEL_TONE", "SOURCE_URLS_BLOCK"],
    channelLimits: CHANNEL_LIMITS.email_list,
    aiConfig: DERIVATION_AI_CONFIG,
    isDefault: true,
    enabled: true,
    createdAt: (/* @__PURE__ */ new Date()).toISOString(),
    updatedAt: (/* @__PURE__ */ new Date()).toISOString()
  },
  // ═══════════════════════════════════════
  // YOUTUBE
  // ═══════════════════════════════════════
  {
    id: "youtube_from_video_script",
    channelType: "youtube_channel",
    sourceFormat: "video_script",
    name: "Gui\xF3n \u2192 Descripci\xF3n YouTube",
    description: "Crea descripci\xF3n de YouTube desde gui\xF3n de video",
    systemPrompt: `Crea la descripci\xF3n del video de YouTube desde el gui\xF3n.

## GUI\xD3N ORIGINAL
{{MASTER_CONTENT}}

## TONO DEL CANAL
{{CHANNEL_TONE}}

## TAREA
Genera descripci\xF3n optimizada para YouTube y SEO.

## ESTRUCTURA

[P\xE1rrafo 1 - 2-3 l\xEDneas]:
Resumen atractivo del video (incluye keyword principal)

[Timestamps si el contenido sugiere secciones]:
00:00 - Intro
01:30 - [Tema 1]
etc.

[Puntos clave cubiertos]:
\u{1F4CC} [Punto 1]
\u{1F4CC} [Punto 2]
\u{1F4CC} [Punto 3]

[CTAs]:
\u{1F44D} Dale like si te ha sido \xFAtil
\u{1F514} Suscr\xEDbete para m\xE1s contenido del sector
\u{1F4AC} Comenta tu opini\xF3n

[Links relevantes]:
\u{1F517} Web: [URL]
\u{1F517} LinkedIn: [URL]

[Hashtags]:
{{BRAND_HASHTAGS}} #Industria #Automatizaci\xF3n #[2-3 m\xE1s]

{{SOURCE_URLS_BLOCK}}

## OUTPUT
La descripci\xF3n completa:`,
    availableVariables: ["MASTER_CONTENT", "CHANNEL_TONE", "BRAND_HASHTAGS", "SOURCE_URLS_BLOCK"],
    channelLimits: CHANNEL_LIMITS.youtube_channel,
    aiConfig: DERIVATION_AI_CONFIG,
    isDefault: true,
    enabled: true,
    createdAt: (/* @__PURE__ */ new Date()).toISOString(),
    updatedAt: (/* @__PURE__ */ new Date()).toISOString()
  },
  {
    id: "youtube_default",
    channelType: "youtube_channel",
    sourceFormat: "_any",
    name: "Cualquier formato \u2192 Descripci\xF3n YouTube",
    description: "Adaptaci\xF3n gen\xE9rica a descripci\xF3n de YouTube",
    systemPrompt: `Adapta el contenido a una descripci\xF3n de YouTube.

## CONTENIDO ORIGINAL
{{MASTER_CONTENT}}

## TONO DEL CANAL
{{CHANNEL_TONE}}

## ESTRUCTURA
Resumen (2-3 l\xEDneas) \u2192 Puntos clave (\u{1F4CC}) \u2192 CTAs \u2192 Hashtags: {{BRAND_HASHTAGS}} + 3-5 del sector

{{SOURCE_URLS_BLOCK}}

## OUTPUT
La descripci\xF3n completa:`,
    availableVariables: ["MASTER_CONTENT", "CHANNEL_TONE", "BRAND_HASHTAGS", "SOURCE_URLS_BLOCK"],
    channelLimits: CHANNEL_LIMITS.youtube_channel,
    aiConfig: DERIVATION_AI_CONFIG,
    isDefault: true,
    enabled: true,
    createdAt: (/* @__PURE__ */ new Date()).toISOString(),
    updatedAt: (/* @__PURE__ */ new Date()).toISOString()
  }
];
function getDerivationTemplate(channelType, sourceFormat) {
  const exact = DEFAULT_DERIVATION_TEMPLATES.find(
    (t) => t.channelType === channelType && t.sourceFormat === sourceFormat
  );
  if (exact) return exact;
  const fallback = DEFAULT_DERIVATION_TEMPLATES.find(
    (t) => t.channelType === channelType && t.sourceFormat === "_any"
  );
  return fallback;
}
function getChannelDerivationTemplates(channelType) {
  return DEFAULT_DERIVATION_TEMPLATES.filter((t) => t.channelType === channelType);
}
function getChannelLimits(channelType) {
  return CHANNEL_LIMITS[channelType];
}
function getAllDefaultDerivationTemplates() {
  return DEFAULT_DERIVATION_TEMPLATES;
}

// src/agents-config/unified-template.service.ts
import { Logger as Logger9 } from "@boosting/core";
var UnifiedTemplateService = class {
  constructor(db) {
    this.db = db;
  }
  // ============================================
  // MASTER TEMPLATE RESOLUTION
  // ============================================
  /**
   * Resolve master template for content creation
   * 
   * @param tenantId - Tenant ID
   * @param format - Content format (article, linkedin_post, etc.)
   * @param options - Funnel stage and variant selection
   * @returns Resolved template with final prompt
   */
  async resolveMasterTemplate(tenantId, format, options = {}) {
    const { funnelStage = "mofu", variantId, variables = {} } = options;
    let template;
    let resolution = "default_catalog";
    try {
      const tenantDoc = await this.db.collection("tenants").doc(tenantId).collection("master_templates").doc(format).get();
      if (tenantDoc.exists) {
        template = tenantDoc.data();
        resolution = "tenant_override";
      }
    } catch (error) {
      Logger9.getLogger("UnifiedTemplateService").warn(`[UnifiedTemplateService] Error fetching tenant template: ${error}`);
    }
    if (!template) {
      template = DEFAULT_MASTER_TEMPLATES[format];
    }
    if (!template) {
      template = this.createFallbackMasterTemplate(format);
    }
    const activeVariant = variantId ? template.variants.find((v) => v.id === variantId) : void 0;
    const finalPrompt = this.buildMasterPrompt(template, {
      funnelStage,
      variant: activeVariant,
      variables
    });
    return {
      template,
      finalPrompt,
      activeVariant,
      resolution
    };
  }
  /**
   * Build the final prompt with all replacements
   */
  buildMasterPrompt(template, options) {
    const { funnelStage, variant, variables } = options;
    let prompt = template.systemPrompt;
    const funnelFocus = template.funnelFocus?.[funnelStage] || getFunnelFocusText(funnelStage);
    prompt = prompt.replace(/\{\{FUNNEL_FOCUS\}\}/g, funnelFocus);
    if (variant) {
      prompt = prompt.replace(/\{\{VARIANT\}\}/g, variant.name);
      if (variant.promptAddition) {
        prompt = prompt.replace(
          /## VARIANTE: .*\n/,
          `## VARIANTE: ${variant.name}
${variant.promptAddition}
`
        );
      }
    } else {
      prompt = prompt.replace(/\{\{VARIANT\}\}/g, "est\xE1ndar");
    }
    const brandGuidelines = template.brandGuidelines || "";
    prompt = prompt.replace(/\{\{BRAND_GUIDELINES\}\}/g, brandGuidelines);
    for (const [key, value] of Object.entries(variables)) {
      const regex = new RegExp(`\\{\\{${key}\\}\\}`, "g");
      prompt = prompt.replace(regex, value || `[${key}]`);
    }
    return prompt;
  }
  /**
   * Create fallback template when none exists
   */
  createFallbackMasterTemplate(format) {
    return {
      id: format,
      format,
      name: `Template ${format}`,
      description: `Template por defecto para ${format}`,
      icon: "\u{1F4C4}",
      systemPrompt: `Genera contenido en formato ${format} sobre: {{TOPIC}}

## CONTENIDO FUENTE
{{SOURCE_CONTENT}}

## CONTEXTO
- Marca: {{BRAND_NAME}}
- Keywords: {{KEYWORDS}}

{{FUNNEL_FOCUS}}

Genera contenido profesional basado EXCLUSIVAMENTE en el contenido fuente.`,
      funnelFocus: {
        tofu: "Enfoque educativo, sin mencionar productos.",
        mofu: "Enfoque t\xE9cnico, presenta soluciones.",
        bofu: "Enfoque conversi\xF3n, menciona productos."
      },
      variants: [],
      availableVariables: ["TOPIC", "KEYWORDS", "SOURCE_CONTENT", "BRAND_NAME", "FUNNEL_FOCUS"],
      aiConfig: {
        model: "gemini-3-flash-preview",
        temperature: 0.4,
        maxOutputTokens: 4e3
      },
      isDefault: true,
      enabled: true,
      createdAt: (/* @__PURE__ */ new Date()).toISOString(),
      updatedAt: (/* @__PURE__ */ new Date()).toISOString()
    };
  }
  // ============================================
  // DERIVATION TEMPLATE RESOLUTION
  // ============================================
  /**
   * Resolve derivation template for channel adaptation
   * 
   * @param tenantId - Tenant ID
   * @param channelType - Target channel
   * @param sourceFormat - Original content format
   * @param variables - Variables to inject
   * @returns Resolved template with final prompt
   */
  async resolveDerivationTemplate(tenantId, channelType, sourceFormat, variables = {}) {
    let template;
    let channelConfig;
    let resolution = "default_catalog";
    try {
      const specificDoc = await this.db.collection("tenants").doc(tenantId).collection("derivation_templates").doc(channelType).collection("from").doc(sourceFormat).get();
      if (specificDoc.exists) {
        template = specificDoc.data();
        resolution = "channel_format_specific";
      }
    } catch (error) {
      Logger9.getLogger("UnifiedTemplateService").warn(`[UnifiedTemplateService] Error fetching specific derivation template: ${error}`);
    }
    if (!template) {
      try {
        const defaultDoc = await this.db.collection("tenants").doc(tenantId).collection("derivation_templates").doc(channelType).collection("from").doc("_any").get();
        if (defaultDoc.exists) {
          template = defaultDoc.data();
          resolution = "channel_default";
        }
      } catch (error) {
        Logger9.getLogger("UnifiedTemplateService").warn(`[UnifiedTemplateService] Error fetching default derivation template: ${error}`);
      }
    }
    if (!template) {
      template = getDerivationTemplate(channelType, sourceFormat);
    }
    if (!template) {
      template = this.createFallbackDerivationTemplate(channelType, sourceFormat);
    }
    try {
      const configDoc = await this.db.collection("tenants").doc(tenantId).collection("channel_config").doc(channelType).get();
      if (configDoc.exists) {
        channelConfig = configDoc.data();
      }
    } catch (error) {
    }
    let channelBrain;
    try {
      const channelsSnap = await this.db.collection(`tenants/${tenantId}/channels`).where("type", "==", channelType).limit(1).get();
      if (!channelsSnap.empty) {
        const channelId = channelsSnap.docs[0].id;
        const brainDoc = await this.db.doc(`tenants/${tenantId}/channels/${channelId}/brain/current`).get();
        if (brainDoc.exists) {
          const brainData = brainDoc.data();
          if (brainData.totalPostsAnalyzed > 0) {
            channelBrain = brainData;
          }
        }
      }
    } catch (error) {
      Logger9.getLogger("UnifiedTemplateService").warn(
        `[ChannelBrain] Failed to load: ${error instanceof Error ? error.message : String(error)}`
      );
    }
    const finalPrompt = this.buildDerivationPrompt(template, channelConfig, variables, channelBrain);
    return {
      template,
      finalPrompt,
      channelConfig,
      channelBrain,
      resolution
    };
  }
  /**
   * Build derivation prompt with variables
   */
  buildDerivationPrompt(template, channelConfig, variables, channelBrain) {
    let prompt = template.systemPrompt;
    const effectiveTone = channelBrain?.styleProfile.preferredTone || channelConfig?.defaultTone || "professional";
    const channelVars = {
      CHANNEL_TONE: effectiveTone,
      BRAND_HASHTAGS: channelConfig?.signature?.hashtags?.join(" ") || "",
      CTA_STYLE: channelConfig?.signature?.ctaTemplate || "Call to action apropiado",
      CHAR_LIMIT: String(template.channelLimits.recommendedCharacters)
    };
    const allVars = { ...channelVars, ...variables };
    for (const [key, value] of Object.entries(allVars)) {
      const regex = new RegExp(`\\{\\{${key}\\}\\}`, "g");
      prompt = prompt.replace(regex, value || `[${key}]`);
    }
    if (channelBrain && channelBrain.totalPostsAnalyzed > 0) {
      prompt += this.formatChannelBrainBlock(channelBrain);
    }
    prompt += `

REGLA CRITICA: SIEMPRE completa todas las frases e ideas. NUNCA cortes una frase a medias ni dejes el texto incompleto, aunque superes ligeramente el limite de caracteres recomendado. Es preferible un texto un poco mas largo pero completo que uno truncado.`;
    if (channelConfig?.promptAdditions?.trim()) {
      prompt += `

## INSTRUCCIONES ADICIONALES DEL TENANT
${channelConfig.promptAdditions.trim()}`;
    }
    return prompt;
  }
  /**
   * Format Channel Brain profile as prompt guidelines.
   */
  formatChannelBrainBlock(brain) {
    const style = brain.styleProfile;
    const lines = [
      "\n\n## ESTILO DE LA MARCA EN ESTE CANAL (Channel Brain)",
      `Basado en el an\xE1lisis de ${brain.totalPostsAnalyzed} posts publicados previamente:`,
      "",
      `- **Tono detectado**: ${style.preferredTone}`,
      `- **Longitud promedio**: ${style.avgPostLength} caracteres`,
      `- **Uso de emojis**: ${style.emojiUsage}`
    ];
    if (style.hashtagPatterns.length > 0) {
      lines.push(`- **Hashtags frecuentes**: ${style.hashtagPatterns.slice(0, 10).join(" ")}`);
    }
    if (style.ctaPatterns.length > 0) {
      lines.push(`- **Patrones de CTA**: ${style.ctaPatterns.join(", ")}`);
    }
    const examples = (brain.topExamples || []).slice(0, 3);
    if (examples.length > 0) {
      lines.push("", "### Ejemplos reales del estilo de la marca:");
      examples.forEach((ex, i) => {
        const truncated = ex.text.length > 500 ? ex.text.substring(0, 500) + "..." : ex.text;
        lines.push(`${i + 1}. "${truncated}"`);
      });
    }
    lines.push("", "IMPORTANTE: Adapta el contenido generado para que sea coherente con este estilo establecido de la marca.");
    return lines.join("\n");
  }
  /**
   * Create fallback derivation template
   */
  createFallbackDerivationTemplate(channelType, sourceFormat) {
    const limits = CHANNEL_LIMITS[channelType] || {
      maxCharacters: 3e3,
      recommendedCharacters: 1e3,
      supportsHashtags: true,
      supportsEmojis: true,
      supportsMentions: false,
      supportsImages: true
    };
    return {
      id: `${channelType}_from_${sourceFormat}`,
      channelType,
      sourceFormat,
      name: `${sourceFormat} \u2192 ${channelType}`,
      description: `Adaptaci\xF3n de ${sourceFormat} para ${channelType}`,
      systemPrompt: `Adapta el siguiente contenido para ${channelType}.

## CONTENIDO ORIGINAL
{{MASTER_CONTENT}}

## REGLAS
- NO inventes informaci\xF3n
- Adapta el tono al canal
- M\xE1ximo ${limits.recommendedCharacters} caracteres

## OUTPUT
El contenido adaptado:`,
      availableVariables: ["MASTER_CONTENT"],
      channelLimits: limits,
      aiConfig: {
        model: "gemini-3-flash-preview",
        temperature: 0.3,
        maxOutputTokens: 4e3
      },
      isDefault: true,
      enabled: true,
      createdAt: (/* @__PURE__ */ new Date()).toISOString(),
      updatedAt: (/* @__PURE__ */ new Date()).toISOString()
    };
  }
  // ============================================
  // TEMPLATE MANAGEMENT
  // ============================================
  /**
   * Sync default master templates to tenant
   */
  async syncMasterTemplates(tenantId, options = {}) {
    const { preserveCustomizations = true } = options;
    let synced = 0;
    let skipped = 0;
    const batch = this.db.batch();
    for (const format of Object.keys(DEFAULT_MASTER_TEMPLATES)) {
      const template = DEFAULT_MASTER_TEMPLATES[format];
      const docRef = this.db.collection("tenants").doc(tenantId).collection("master_templates").doc(format);
      if (preserveCustomizations) {
        const existing = await docRef.get();
        if (existing.exists && !existing.data()?.isDefault) {
          skipped++;
          continue;
        }
      }
      batch.set(docRef, {
        ...template,
        updatedAt: (/* @__PURE__ */ new Date()).toISOString()
      });
      synced++;
    }
    await batch.commit();
    return { synced, skipped };
  }
  /**
   * Sync default derivation templates to tenant
   */
  async syncDerivationTemplates(tenantId, options = {}) {
    const { preserveCustomizations = true, channelType } = options;
    let synced = 0;
    let skipped = 0;
    const templates = channelType ? DEFAULT_DERIVATION_TEMPLATES.filter((t) => t.channelType === channelType) : DEFAULT_DERIVATION_TEMPLATES;
    for (const template of templates) {
      const docRef = this.db.collection("tenants").doc(tenantId).collection("derivation_templates").doc(template.channelType).collection("from").doc(template.sourceFormat);
      if (preserveCustomizations) {
        const existing = await docRef.get();
        if (existing.exists && !existing.data()?.isDefault) {
          skipped++;
          continue;
        }
      }
      await docRef.set({
        ...template,
        updatedAt: (/* @__PURE__ */ new Date()).toISOString()
      });
      synced++;
    }
    return { synced, skipped };
  }
  /**
   * Get all master templates for tenant
   */
  async getMasterTemplates(tenantId) {
    const snapshot = await this.db.collection("tenants").doc(tenantId).collection("master_templates").get();
    if (snapshot.empty) {
      return Object.values(DEFAULT_MASTER_TEMPLATES);
    }
    return snapshot.docs.map((doc) => doc.data());
  }
  /**
   * Get all derivation templates for tenant
   */
  async getDerivationTemplates(tenantId, channelType) {
    const templates = [];
    if (channelType) {
      const snapshot = await this.db.collection("tenants").doc(tenantId).collection("derivation_templates").doc(channelType).collection("from").get();
      templates.push(...snapshot.docs.map((doc) => doc.data()));
    } else {
      const channelDocs = await this.db.collection("tenants").doc(tenantId).collection("derivation_templates").get();
      for (const channelDoc of channelDocs.docs) {
        const fromSnapshot = await channelDoc.ref.collection("from").get();
        templates.push(...fromSnapshot.docs.map((doc) => doc.data()));
      }
    }
    if (templates.length === 0) {
      return channelType ? DEFAULT_DERIVATION_TEMPLATES.filter((t) => t.channelType === channelType) : DEFAULT_DERIVATION_TEMPLATES;
    }
    return templates;
  }
  /**
   * Update master template
   */
  async updateMasterTemplate(tenantId, format, updates) {
    const docRef = this.db.collection("tenants").doc(tenantId).collection("master_templates").doc(format);
    await docRef.set({
      ...updates,
      isDefault: false,
      // Mark as customized
      updatedAt: (/* @__PURE__ */ new Date()).toISOString()
    }, { merge: true });
  }
  /**
   * Update derivation template
   */
  async updateDerivationTemplate(tenantId, channelType, sourceFormat, updates) {
    const docRef = this.db.collection("tenants").doc(tenantId).collection("derivation_templates").doc(channelType).collection("from").doc(sourceFormat);
    await docRef.set({
      ...updates,
      isDefault: false,
      // Mark as customized
      updatedAt: (/* @__PURE__ */ new Date()).toISOString()
    }, { merge: true });
  }
};
function createUnifiedTemplateService(db) {
  return new UnifiedTemplateService(db);
}

// src/chat/chat-engine.ts
import { Logger as Logger10 } from "@boosting/core";

// src/chat/chat.types.ts
var DEFAULT_CRITICAL_KEYWORDS = [
  "suicid",
  "matarme",
  "quitarme la vida",
  "acabar con todo",
  "no quiero vivir",
  "hacerme da\xF1o",
  "cortarme",
  "autolesion",
  "mejor muerto",
  "crisis",
  "emergencia",
  "no puedo m\xE1s",
  "ayuda urgente"
];
var DEFAULT_SITUATION_KEYWORDS = {
  anxiety_mild: ["nervioso", "inquieto", "intranquilo", "preocupado"],
  anxiety_moderate: ["ansioso", "ansiedad", "angustia", "agobiado", "palpitaciones"],
  stress: ["estresado", "estr\xE9s", "presi\xF3n", "tensi\xF3n", "agotado", "quemado"],
  sleep_issues: ["dormir", "sue\xF1o", "insomnio", "despertar", "noche", "descansar"],
  low_mood: ["triste", "bajo", "deprimido", "sin ganas", "vac\xEDo", "ap\xE1tico"],
  overwhelm: ["abrumado", "demasiado", "no puedo", "supera", "colapso"],
  anger: ["enfadado", "furioso", "rabia", "ira", "frustrado", "irritado"],
  focus_issues: ["concentrar", "distra\xEDdo", "enfocado", "atenci\xF3n", "productivo"],
  loneliness: ["solo", "soledad", "aislado", "nadie me entiende"],
  general_wellbeing: ["bien", "practicar", "mejorar", "rutina", "h\xE1bito"]
};
var SITUATION_LABELS = {
  anxiety_mild: "ansiedad leve",
  anxiety_moderate: "ansiedad moderada",
  stress: "estr\xE9s",
  sleep_issues: "dificultades con el sue\xF1o",
  low_mood: "estado de \xE1nimo bajo",
  overwhelm: "sensaci\xF3n de agobio",
  anger: "enfado o frustraci\xF3n",
  focus_issues: "dificultad para concentrarte",
  loneliness: "soledad",
  general_wellbeing: "bienestar general"
};
var DEFAULT_CRISIS_RESOURCES = {
  spain: {
    crisisLine: "024",
    emergency: "112",
    name: "Espa\xF1a"
  },
  mexico: {
    crisisLine: "800-290-0024",
    emergency: "911",
    name: "M\xE9xico"
  },
  argentina: {
    crisisLine: "135",
    emergency: "107",
    name: "Argentina"
  }
};

// src/chat/safety/crisis-detector.ts
var CrisisDetector = class {
  criticalKeywords;
  situationKeywords;
  severityKeywords;
  constructor(config = {}) {
    this.criticalKeywords = config.criticalKeywords ?? DEFAULT_CRITICAL_KEYWORDS;
    this.situationKeywords = config.situationKeywords ?? DEFAULT_SITUATION_KEYWORDS;
    this.severityKeywords = {
      critical: ["crisis", "urgente", "suicid", "matarme"],
      high: ["muy mal", "terrible", "insoportable", "desesperado"],
      medium: ["bastante", "mucho", "necesito ayuda"]
    };
  }
  /**
   * Analiza un mensaje en busca de señales de crisis
   */
  analyze(text) {
    const lowerText = text.toLowerCase();
    const foundKeywords = [];
    const hasCriticalKeywords = this.criticalKeywords.some((kw) => {
      if (lowerText.includes(kw)) {
        foundKeywords.push(kw);
        return true;
      }
      return false;
    });
    let detectedSituation = null;
    for (const [situation, keywords] of Object.entries(this.situationKeywords)) {
      for (const kw of keywords) {
        if (lowerText.includes(kw)) {
          detectedSituation = situation;
          if (!foundKeywords.includes(kw)) foundKeywords.push(kw);
          break;
        }
      }
      if (detectedSituation) break;
    }
    let detectedSeverity = "low";
    if (hasCriticalKeywords) {
      detectedSeverity = "critical";
    } else {
      for (const [level, keywords] of Object.entries(this.severityKeywords)) {
        if (keywords.some((kw) => lowerText.includes(kw))) {
          detectedSeverity = level;
          break;
        }
      }
    }
    const requiresEscalation = detectedSeverity === "critical" || detectedSeverity === "high";
    const escalationType = detectedSeverity === "critical" ? "crisis" : detectedSeverity === "high" ? "therapist" : void 0;
    return {
      hasCriticalKeywords,
      detectedSituation,
      detectedSeverity,
      foundKeywords,
      requiresEscalation,
      escalationType
    };
  }
  /**
   * Verifica si un mensaje contiene keywords críticas
   */
  hasCriticalContent(text) {
    const lowerText = text.toLowerCase();
    return this.criticalKeywords.some((kw) => lowerText.includes(kw));
  }
};
function createCrisisDetector(config) {
  return new CrisisDetector(config);
}

// src/chat/safety/escalation.ts
var EscalationHandler = class {
  crisisResources;
  defaultCountry;
  constructor(config = {}) {
    this.crisisResources = config.crisisResources ?? DEFAULT_CRISIS_RESOURCES;
    this.defaultCountry = config.defaultCountry ?? "spain";
  }
  /**
   * Genera una respuesta de crisis (suicidio, autolesión)
   */
  createCrisisResponse(crisisInfo) {
    const resources = this.crisisResources[this.defaultCountry];
    const crisisLine = crisisInfo?.crisisLineNumber ?? resources.crisisLine;
    const emergency = crisisInfo?.emergencyNumber ?? resources.emergency;
    return {
      type: "crisis",
      message: `Entiendo que est\xE1s pasando por un momento muy dif\xEDcil. Tu bienestar es lo m\xE1s importante.

\u{1F198} **Por favor, contacta inmediatamente con ayuda profesional:**

\u{1F4DE} **L\xEDnea de crisis:** ${crisisLine}
\u{1F6A8} **Emergencias:** ${emergency}

Estos profesionales est\xE1n preparados para ayudarte ahora mismo. No est\xE1s solo/a.`,
      practices: [],
      analysis: {
        situation: null,
        severity: "critical"
      },
      showCrisisInfo: true,
      showContactTherapist: true,
      requiresImmediateAction: true
    };
  }
  /**
   * Genera una respuesta de escalación a terapeuta (severidad alta)
   */
  createTherapistEscalationResponse(responseMessage, crisisInfo) {
    const resources = this.crisisResources[this.defaultCountry];
    const crisisLine = crisisInfo?.crisisLineNumber ?? resources.crisisLine;
    const therapistPhone = crisisInfo?.therapistPhone;
    const therapistEmail = crisisInfo?.therapistEmail;
    let contactInfo = "";
    if (therapistPhone) contactInfo += `\u{1F4F1} Tel\xE9fono: ${therapistPhone}
`;
    if (therapistEmail) contactInfo += `\u{1F4E7} Email: ${therapistEmail}
`;
    return {
      type: "escalation",
      message: `${responseMessage}

Esta situaci\xF3n merece atenci\xF3n especial de tu terapeuta. Te recomiendo contactarle.

${contactInfo}
Si es urgente y no puedes contactar con tu terapeuta:
\u{1F4DE} L\xEDnea de crisis: ${crisisLine}`,
      practices: [],
      analysis: {
        situation: null,
        severity: "high"
      },
      showCrisisInfo: true,
      showContactTherapist: true
    };
  }
  /**
   * Genera una respuesta genérica (sin configuración)
   */
  createGenericResponse() {
    const resources = this.crisisResources[this.defaultCountry];
    return {
      type: "general_support",
      message: `Gracias por compartir c\xF3mo te sientes. 

Aunque no tengo sugerencias espec\xEDficas configuradas para ti, recuerda que siempre puedes:

\u{1F4DA} Explorar tu biblioteca de pr\xE1cticas
\u{1F4AC} Contactar con tu terapeuta
\u{1F4DE} L\xEDnea de crisis: ${resources.crisisLine} (disponible 24h)

Est\xE1s dando un paso importante al buscar apoyo. \u2728`,
      practices: [],
      showCrisisInfo: true,
      showContactTherapist: true
    };
  }
  /**
   * Genera una respuesta de error
   */
  createErrorResponse() {
    const resources = this.crisisResources[this.defaultCountry];
    return {
      type: "error",
      message: `Lo siento, ha ocurrido un error. Por favor:
        
\u{1F4DA} Explora tu biblioteca de pr\xE1cticas
\u{1F4AC} Contacta con tu terapeuta si necesitas apoyo
\u{1F4DE} L\xEDnea de crisis: ${resources.crisisLine} (disponible 24h)`,
      practices: [],
      showCrisisInfo: true,
      showContactTherapist: true
    };
  }
  /**
   * Genera mensaje cuando no hay prácticas sugeridas
   */
  createNoSuggestionsResponse() {
    return `Entiendo c\xF3mo te sientes. 

En este momento no tengo pr\xE1cticas espec\xEDficas que sugerirte para esta situaci\xF3n, pero puedes:

\u{1F4DA} Explorar tu biblioteca de pr\xE1cticas guardadas
\u{1F4AC} Contactar con tu terapeuta

Recuerda que dar el paso de buscar apoyo ya es un logro importante. \u{1F49A}`;
  }
};
function createEscalationHandler(config) {
  return new EscalationHandler(config);
}

// src/chat/chat-engine.ts
var logger9 = Logger10.getLogger("ChatEngine");
var ChatEngine = class {
  geminiCaller;
  crisisDetector;
  escalationHandler;
  model;
  temperature;
  debug;
  constructor(config) {
    this.geminiCaller = config.geminiCaller;
    this.crisisDetector = createCrisisDetector(config.safetyConfig);
    this.escalationHandler = createEscalationHandler();
    this.model = config.model ?? "gemini-3-flash-preview";
    this.temperature = config.temperature ?? 0.4;
    this.debug = config.debug ?? false;
    if (this.debug) {
      logger9.info("ChatEngine initialized", { model: this.model });
    }
  }
  /**
   * Procesa un mensaje del usuario y genera una respuesta
   */
  async processMessage(request) {
    const { userId, text, semanticContext, relevantPractices, assistantConfig } = request;
    if (this.debug) {
      logger9.info("Processing message", { userId, textLength: text.length });
    }
    try {
      const safetyCheck = this.crisisDetector.analyze(text);
      if (safetyCheck.hasCriticalKeywords) {
        logger9.info("Critical safety keywords detected - immediate escalation", { userId });
        return this.escalationHandler.createCrisisResponse(assistantConfig?.crisisInfo);
      }
      if (!assistantConfig?.enabled && (!relevantPractices || relevantPractices.length === 0)) {
        logger9.warn("No config and no practices - returning generic response");
        return this.escalationHandler.createGenericResponse();
      }
      const analysis = await this.analyzeWithGemini(
        text,
        assistantConfig,
        semanticContext,
        relevantPractices
      );
      if (analysis.requiresImmediateAttention) {
        return this.escalationHandler.createTherapistEscalationResponse(
          analysis.responseMessage,
          assistantConfig?.crisisInfo
        );
      }
      if (!analysis.selectedPracticeId || analysis.selectedPracticeId === "none") {
        return {
          type: "general_support",
          message: analysis.responseMessage || this.escalationHandler.createNoSuggestionsResponse(),
          practices: [],
          analysis: {
            situation: analysis.detectedSituation,
            severity: analysis.detectedSeverity
          },
          showCrisisInfo: false,
          showContactTherapist: true,
          selectedPracticeId: null,
          reasoning: analysis.reasoning || "No encontr\xE9 una pr\xE1ctica espec\xEDfica para esta situaci\xF3n."
        };
      }
      return {
        type: "suggestions",
        message: analysis.responseMessage,
        practices: relevantPractices || [],
        analysis: {
          situation: analysis.detectedSituation,
          severity: analysis.detectedSeverity
        },
        showCrisisInfo: false,
        showContactTherapist: assistantConfig?.responseConfig?.offerContactTherapist ?? true,
        selectedPracticeId: analysis.selectedPracticeId,
        alternativePracticeId: analysis.alternativePracticeId,
        reasoning: analysis.reasoning
      };
    } catch (error) {
      logger9.error("Error processing message", error, { userId });
      return this.escalationHandler.createErrorResponse();
    }
  }
  /**
   * Analiza el mensaje usando Gemini
   */
  async analyzeWithGemini(text, config, semanticContext, relevantPractices) {
    if (this.debug) {
      logger9.info("Analyzing with Gemini", {
        hasSemantic: !!semanticContext,
        practicesCount: relevantPractices?.length ?? 0
      });
    }
    const prompt = this.buildDynamicPrompt(text, config, semanticContext, relevantPractices);
    try {
      const result = await this.geminiCaller.generateContent({
        model: this.model,
        contents: [{ role: "user", parts: [{ text: prompt }] }],
        generationConfig: {
          responseMimeType: "application/json",
          temperature: this.temperature,
          maxOutputTokens: 1500
        }
      });
      const responseText = result.response.candidates?.[0]?.content?.parts?.[0]?.text;
      if (!responseText) {
        throw new Error("Empty response from Gemini");
      }
      const parsed = JSON.parse(responseText);
      if (this.debug) {
        logger9.info("Gemini agent decision", {
          situation: parsed.detectedSituation,
          selectedPractice: parsed.selectedPracticeId,
          confidence: parsed.confidenceScore
        });
      }
      return {
        detectedSituation: parsed.detectedSituation || null,
        detectedSeverity: parsed.detectedSeverity || "low",
        keywords: parsed.keywords || [],
        suggestedApproach: parsed.suggestedApproach || "general_support",
        confidenceScore: parsed.confidenceScore || 0.5,
        requiresImmediateAttention: parsed.requiresImediateAttention || false,
        responseMessage: parsed.responseMessage || "",
        selectedPracticeId: parsed.selectedPracticeId || null,
        alternativePracticeId: parsed.alternativePracticeId || null,
        reasoning: parsed.reasoning || null
      };
    } catch (error) {
      logger9.error("Gemini analysis failed, using fallback", error);
      return this.fallbackKeywordAnalysis(text);
    }
  }
  /**
   * Construye el prompt dinámico para el agente
   */
  buildDynamicPrompt(text, config, semanticContext, relevantPractices) {
    const customPrompt = config?.customMessages?.systemPrompt || "";
    let practicesContext = "";
    if (relevantPractices && relevantPractices.length > 0) {
      practicesContext = `
PR\xC1CTICAS PRE-FILTRADAS POR RELEVANCIA (${relevantPractices.length} candidatas):

${relevantPractices.map((p, i) => {
        const instructions = p.instructions || "(ver pr\xE1ctica completa)";
        return `[${i + 1}] ID: "${p.id}"
T\xEDtulo: "${p.title}" (${p.duration} min, ${p.category})
Match: ${p.matchReasons.slice(0, 3).join(", ")}
Contenido: ${instructions.slice(0, 300)}`;
      }).join("\n\n")}

Elige selectedPracticeId (ID exacto) y opcionalmente alternativePracticeId.`;
    } else {
      practicesContext = `
NO HAY PR\xC1CTICAS RELEVANTES para esta situaci\xF3n.
selectedPracticeId = "none". Sugiere hablar con el terapeuta.`;
    }
    let semanticInfo = "";
    if (semanticContext) {
      semanticInfo = `
AN\xC1LISIS SEM\xC1NTICO:
- Conceptos detectados: ${semanticContext.detectedConcepts.join(", ") || "ninguno"}
- S\xEDntomas identificados: ${semanticContext.detectedSymptoms.join(", ") || "ninguno"}
- T\xE9cnicas recomendadas: ${semanticContext.recommendedTechniques.join(", ") || "ninguna"}`;
    }
    return `
Eres un AGENTE DE BIENESTAR MENTAL que TOMA DECISIONES sobre qu\xE9 pr\xE1ctica recomendar.

${customPrompt}

MENSAJE DEL USUARIO:
"${text}"
${semanticInfo}
${practicesContext}

TU TAREA:
1. ANALIZA el mensaje (situaci\xF3n, severidad, keywords)
2. LEE el contenido de cada pr\xE1ctica
3. DECIDE cu\xE1l recomendar (selectedPracticeId = ID exacto)
4. GENERA un responseMessage emp\xE1tico (m\xE1ximo 3-4 frases)

FORMATO DE responseMessage:
"[Validaci\xF3n emp\xE1tica]. Te recomiendo '[Nombre]' porque [raz\xF3n]. [Indicaci\xF3n concreta]. [Invitaci\xF3n]."

CRITERIOS DE ESCALACI\xD3N:
- critical: suicidio, autolesi\xF3n \u2192 suggestedApproach: "escalate_crisis"
- high: malestar intenso \u2192 suggestedApproach: "escalate_therapist"
- medium/low: suggestedApproach: "suggest_practices"

Responde SOLO JSON v\xE1lido:
{
  "detectedSituation": "anxiety_mild|stress|...|null",
  "detectedSeverity": "low|medium|high|critical",
  "keywords": ["..."],
  "suggestedApproach": "suggest_practices|escalate_therapist|escalate_crisis|general_support",
  "confidenceScore": 0.0-1.0,
  "requiresImediateAttention": false,
  "responseMessage": "...",
  "selectedPracticeId": "id-o-none",
  "alternativePracticeId": "id-opcional",
  "reasoning": "Por qu\xE9 elegiste esta pr\xE1ctica"
}`.trim();
  }
  /**
   * Análisis de fallback basado en keywords cuando Gemini falla
   */
  fallbackKeywordAnalysis(text) {
    const safetyCheck = this.crisisDetector.analyze(text);
    const situationLabel = safetyCheck.detectedSituation ? SITUATION_LABELS[safetyCheck.detectedSituation] : "algunas dificultades";
    return {
      detectedSituation: safetyCheck.detectedSituation,
      detectedSeverity: safetyCheck.detectedSeverity,
      keywords: safetyCheck.foundKeywords,
      suggestedApproach: safetyCheck.detectedSeverity === "high" ? "escalate_therapist" : "suggest_practices",
      confidenceScore: 0.4,
      requiresImmediateAttention: false,
      responseMessage: `Entiendo que est\xE1s experimentando ${situationLabel}. Gracias por compartir c\xF3mo te sientes.`
    };
  }
};
function createChatEngine(config) {
  return new ChatEngine(config);
}

// src/embeddings/embeddings.service.ts
import { Logger as Logger11 } from "@boosting/core";

// src/embeddings/embeddings.types.ts
var EMBEDDING_DIMENSIONS = {
  "text-embedding-004": 768,
  "text-embedding-005": 768,
  "textembedding-gecko@003": 768,
  "multimodalembedding@001": 1408
};
var MAX_EMBEDDING_CHARS = 1e4;
var MAX_BATCH_SIZE = 250;

// src/embeddings/embeddings.service.ts
var logger10 = Logger11.getLogger("EmbeddingsService");
var EmbeddingsService = class {
  projectId;
  location;
  model;
  onUsage;
  client;
  clientPromise = null;
  constructor(config) {
    this.projectId = config.projectId;
    this.location = config.location ?? "us-central1";
    this.model = config.model ?? "text-embedding-004";
    this.onUsage = config.onUsage;
    this.client = config.predictionClient ?? null;
  }
  /**
   * Lazy-load del cliente de predicción
   * Evita cargar el SDK pesado en cold starts
   */
  async getClient() {
    if (this.client) return this.client;
    if (this.clientPromise) return this.clientPromise;
    this.clientPromise = (async () => {
      const { PredictionServiceClient } = await import("@google-cloud/aiplatform");
      this.client = new PredictionServiceClient({
        apiEndpoint: `${this.location}-aiplatform.googleapis.com`
      });
      return this.client;
    })();
    return this.clientPromise;
  }
  /**
   * Construye el endpoint del modelo
   */
  getEndpoint() {
    return `projects/${this.projectId}/locations/${this.location}/publishers/google/models/${this.model}`;
  }
  /**
   * Trunca texto para no exceder límite de tokens
   */
  truncateText(text) {
    if (text.length <= MAX_EMBEDDING_CHARS) return text;
    return text.substring(0, MAX_EMBEDDING_CHARS);
  }
  // ============================================
  // PUBLIC METHODS
  // ============================================
  /**
   * Genera un embedding para un texto
   */
  async generate(request) {
    const { text, taskType = "RETRIEVAL_DOCUMENT", title } = typeof request === "string" ? { text: request } : request;
    const truncatedText = this.truncateText(text);
    const client = await this.getClient();
    const { helpers } = await import("@google-cloud/aiplatform");
    const instanceData = {
      content: truncatedText,
      taskType
    };
    if (title) instanceData.title = title;
    const instance = helpers.toValue(instanceData);
    const [response] = await client.predict({
      endpoint: this.getEndpoint(),
      instances: [instance]
    });
    if (!response.predictions || response.predictions.length === 0) {
      throw new Error("No predictions returned from Vertex AI");
    }
    const embeddingObj = helpers.fromValue(response.predictions[0]);
    const embedding = embeddingObj.embeddings?.values || [];
    if (!embedding || embedding.length === 0) {
      throw new Error("Empty embedding returned");
    }
    const tokensEstimated = Math.ceil(truncatedText.length / 4);
    if (this.onUsage) {
      await this.onUsage({
        task: "generateEmbedding",
        model: this.model,
        inputCharacters: truncatedText.length,
        dimensions: embedding.length,
        tokensEstimated
      });
    }
    return {
      embedding,
      model: this.model,
      dimensions: embedding.length,
      tokensEstimated
    };
  }
  /**
   * Genera embeddings para múltiples textos en batch
   */
  async generateBatch(request) {
    const { texts, taskType = "RETRIEVAL_DOCUMENT" } = request;
    if (texts.length > MAX_BATCH_SIZE) {
      throw new Error(`Maximum ${MAX_BATCH_SIZE} texts per batch`);
    }
    const truncatedTexts = texts.map((text) => this.truncateText(text));
    const client = await this.getClient();
    const { helpers } = await import("@google-cloud/aiplatform");
    const instances = truncatedTexts.map(
      (content) => helpers.toValue({ content, taskType })
    );
    const [response] = await client.predict({
      endpoint: this.getEndpoint(),
      instances
    });
    if (!response.predictions || response.predictions.length === 0) {
      throw new Error("No predictions returned from Vertex AI");
    }
    const embeddings = response.predictions.map((prediction) => {
      const embeddingObj = helpers.fromValue(prediction);
      return embeddingObj.embeddings?.values || [];
    });
    const totalCharacters = truncatedTexts.reduce((sum, text) => sum + text.length, 0);
    const totalTokensEstimated = Math.ceil(totalCharacters / 4);
    if (this.onUsage) {
      await this.onUsage({
        task: "generateBatchEmbeddings",
        model: this.model,
        inputCharacters: totalCharacters,
        dimensions: embeddings[0]?.length || EMBEDDING_DIMENSIONS[this.model],
        tokensEstimated: totalTokensEstimated
      });
    }
    return {
      embeddings,
      model: this.model,
      totalTokensEstimated,
      count: embeddings.length
    };
  }
  /**
   * Genera embedding optimizado para queries de búsqueda
   */
  async generateQueryEmbedding(query) {
    const result = await this.generate({
      text: query,
      taskType: "RETRIEVAL_QUERY"
    });
    return result.embedding;
  }
  /**
   * Busca candidatos más similares a un vector
   */
  searchSimilar(request) {
    const { queryVector, candidates, limit = 10, threshold = 0.5 } = request;
    const results = candidates.map((candidate) => ({
      id: candidate.id,
      score: this.cosineSimilarity(queryVector, candidate.vector),
      metadata: candidate.metadata
    })).filter((result) => result.score >= threshold).sort((a, b) => b.score - a.score).slice(0, limit);
    return results;
  }
  /**
   * Calcula similitud coseno entre dos vectores
   */
  cosineSimilarity(a, b) {
    if (a.length !== b.length) {
      logger10.warn("Vector length mismatch", { aLength: a.length, bLength: b.length });
      return 0;
    }
    let dotProduct = 0;
    let normA = 0;
    let normB = 0;
    for (let i = 0; i < a.length; i++) {
      dotProduct += a[i] * b[i];
      normA += a[i] * a[i];
      normB += b[i] * b[i];
    }
    const magnitude = Math.sqrt(normA) * Math.sqrt(normB);
    return magnitude === 0 ? 0 : dotProduct / magnitude;
  }
  /**
   * Obtiene las dimensiones del modelo actual
   */
  getDimensions() {
    return EMBEDDING_DIMENSIONS[this.model];
  }
  /**
   * Obtiene el modelo actual
   */
  getModel() {
    return this.model;
  }
};
function createEmbeddingsService(config) {
  return new EmbeddingsService(config);
}
function cosineSimilarity(a, b) {
  if (a.length !== b.length) return 0;
  let dotProduct = 0;
  let normA = 0;
  let normB = 0;
  for (let i = 0; i < a.length; i++) {
    dotProduct += a[i] * b[i];
    normA += a[i] * a[i];
    normB += b[i] * b[i];
  }
  const magnitude = Math.sqrt(normA) * Math.sqrt(normB);
  return magnitude === 0 ? 0 : dotProduct / magnitude;
}

// src/media/image-generator.ts
import { Logger as Logger12 } from "@boosting/core";

// src/media/media.types.ts
var IMAGE_MODEL_CONFIG = {
  /** Primary model for new image generation (best quality) */
  primaryGeneration: "imagen-4.0-generate-001",
  /** Fallback for generation */
  fallbackGeneration: "imagen-3.0-generate-002",
  /** Primary model for editing - Gemini 3 Pro Image (requires @google/genai + location: global) */
  primaryEditing: "gemini-3-pro-image-preview",
  /** Fallback for editing - Gemini 3 Flash (same SDK, location: global) */
  fallbackEditing: "gemini-3-flash-preview",
  /** Model for style customization with references */
  styleCustomization: "imagen-3.0-capability-001",
  /** Default region for image APIs (Note: Gemini 3 uses "global" instead) */
  region: "us-central1"
};
var PLACEMENT_CONFIG = {
  header: {
    aspectRatio: "16:9",
    widthHint: "full-width hero image (1200px wide minimum)",
    styleGuidance: "Panoramic, impactful, attention-grabbing composition",
    promptEnhancement: "Create a wide panoramic hero image that immediately captures attention. Dramatic lighting, professional composition."
  },
  inline: {
    aspectRatio: "4:3",
    widthHint: "medium width contextual image (600-800px wide)",
    styleGuidance: "Focused, detailed, illustrative of the specific concept",
    promptEnhancement: "Create a focused illustration that explains or demonstrates a specific concept. Clear subject, good detail."
  },
  section_break: {
    aspectRatio: "16:9",
    widthHint: "wide but shorter decorative image (800px wide)",
    styleGuidance: "Subtle, transitional, visually flowing",
    promptEnhancement: "Create a visually appealing transitional image. More artistic/abstract, subtle colors."
  },
  footer: {
    aspectRatio: "16:9",
    widthHint: "closing image (800px wide)",
    styleGuidance: "Summarizing, conclusive, memorable",
    promptEnhancement: "Create a closing summary image that leaves a lasting impression. Professional, polished."
  }
};
function getPlacementForIndex(index, totalCount) {
  if (index === 0) return "header";
  if (totalCount >= 3 && index === totalCount - 1) return "section_break";
  return "inline";
}
function getAspectRatioForPlacement(placement) {
  return PLACEMENT_CONFIG[placement].aspectRatio;
}
var STYLE_PROMPTS = {
  minimalist: "Clean minimalist design, lots of white space, simple elegant composition, soft pastel colors",
  watercolor: "Artistic watercolor painting style, soft flowing colors, painterly brushstrokes, dreamy aesthetic",
  photorealistic: "Ultra realistic photography, high resolution, professional lighting, sharp details",
  illustration: "Modern digital illustration, clean lines, vibrant colors, professional graphic design",
  abstract: "Abstract art, geometric shapes, bold colors, artistic contemporary design",
  therapeutic: "Calming soft colors, peaceful atmosphere, wellness and mindfulness aesthetic, gentle gradients",
  nature: "Beautiful natural scenery, landscapes, organic elements, serene environment",
  corporate: "Professional business aesthetic, clean modern design, corporate colors, sophisticated",
  custom: "",
  // Extended styles for B2B / Industrial content
  professional: "Professional business style, clean modern design, corporate quality, balanced composition",
  technical: "Technical illustration style, precise details, engineering aesthetic, clean diagrams",
  elegant: "Sophisticated elegant style, refined aesthetics, premium feel, luxury design",
  infographic: "Infographic style, data visualization, clean charts and icons, informative layout",
  industrial: "Industrial setting, factory environment, manufacturing aesthetic, machinery and equipment",
  tech: "High-tech style, futuristic feel, digital elements, modern technology aesthetic",
  cinematic: "Cinematic look, dramatic lighting, movie-quality composition, widescreen feel",
  sketch: "Hand-drawn sketch style, pencil strokes, artistic draft quality, conceptual",
  pop_art: "Pop art style, bold colors, graphic shapes, Andy Warhol inspiration",
  artistic: "Creative artistic style, expressive composition, unique visual approach, imaginative design"
};

// src/media/image-generator.ts
var logger11 = Logger12.getLogger("ImageGenerator");
var ImageGeneratorService = class {
  projectId;
  location;
  model;
  geminiCaller;
  client;
  helpers = null;
  initPromise = null;
  constructor(config) {
    this.projectId = config.projectId;
    this.location = config.location ?? "us-central1";
    this.model = config.model ?? "imagen-3.0-generate-002";
    this.geminiCaller = config.geminiCaller;
    this.client = config.predictionClient ?? null;
  }
  /**
   * Lazy-load del cliente de AI Platform
   */
  async ensureClient() {
    if (this.client && this.helpers) return;
    if (this.initPromise) {
      await this.initPromise;
      return;
    }
    this.initPromise = (async () => {
      const { PredictionServiceClient, helpers } = await import("@google-cloud/aiplatform");
      this.client = new PredictionServiceClient({
        apiEndpoint: `${this.location}-aiplatform.googleapis.com`
      });
      this.helpers = helpers;
    })();
    await this.initPromise;
  }
  /**
   * Construye el endpoint del modelo
   */
  getEndpoint(model) {
    const modelToUse = model ?? this.model;
    return `projects/${this.projectId}/locations/${this.location}/publishers/google/models/${modelToUse}`;
  }
  // ============================================
  // PUBLIC METHODS
  // ============================================
  /**
   * Genera una imagen
   */
  async generate(request) {
    const {
      prompt,
      style = "minimalist",
      aspectRatio = "16:9",
      referenceImages,
      enhancePrompt = true
    } = request;
    logger11.info("Starting image generation", { style, hasReferences: !!referenceImages?.length });
    await this.ensureClient();
    let visualDescription = prompt;
    if (enhancePrompt && this.geminiCaller) {
      visualDescription = await this.enhancePromptWithGemini(prompt, referenceImages);
    }
    const stylePrompt = STYLE_PROMPTS[style] || "";
    const fullPrompt = stylePrompt ? `${visualDescription}. ${stylePrompt}. High quality, balanced composition, beautiful, suitable for social media` : `${visualDescription}. High quality, balanced composition, beautiful`;
    try {
      return await this.callImagenAPI(fullPrompt, aspectRatio, visualDescription, style);
    } catch (primaryError) {
      logger11.warn("Primary model failed, trying fallback", { error: primaryError.message });
      try {
        return await this.callImagenAPI(
          fullPrompt,
          aspectRatio === "16:9" ? "1:1" : aspectRatio,
          // Fallback no soporta todos los ratios
          visualDescription,
          style,
          "imagegeneration@006"
        );
      } catch (fallbackError) {
        logger11.error("Image generation failed", fallbackError);
        throw new Error(`Image generation unavailable: ${primaryError.message}`);
      }
    }
  }
  /**
   * Mejora el prompt usando Gemini para crear una descripción visual
   */
  async enhancePromptWithGemini(prompt, referenceImages) {
    if (!this.geminiCaller) return prompt;
    const systemPrompt = this.buildEnhancePrompt(prompt, referenceImages);
    const contentParts = [{ text: systemPrompt }];
    if (referenceImages && referenceImages.length > 0) {
      for (const refImg of referenceImages) {
        if (refImg.base64 && refImg.mimeType) {
          contentParts.push({
            inlineData: {
              mimeType: refImg.mimeType,
              data: refImg.base64
            }
          });
        }
      }
      contentParts.push({
        text: "\n\nBas\xE1ndote en el texto proporcionado Y el estilo visual de las im\xE1genes de referencia anteriores, genera la descripci\xF3n visual:"
      });
    }
    try {
      const result = await this.geminiCaller.generateContent({
        model: "gemini-3-flash-preview",
        contents: [{ role: "user", parts: contentParts }]
      });
      const visualDescription = result.response.candidates?.[0]?.content?.parts?.[0]?.text?.trim();
      if (visualDescription) {
        logger11.info("Enhanced prompt generated", { length: visualDescription.length });
        return visualDescription;
      }
    } catch (error) {
      logger11.warn("Failed to enhance prompt, using original", { error: error.message });
    }
    return prompt;
  }
  /**
   * Construye el prompt para mejorar la descripción
   */
  buildEnhancePrompt(prompt, referenceImages) {
    let basePrompt = `Eres un experto en convertir textos terap\xE9uticos/psicol\xF3gicos en prompts visuales para generaci\xF3n de im\xE1genes de IA.

TEXTO DEL POST:
"${prompt}"

TAREA:
Convierte este texto en una descripci\xF3n visual DETALLADA y ESPEC\xCDFICA para generar una imagen que:

1. Capture la ESENCIA y el MENSAJE EXACTO del texto
2. Sea visualmente ATRACTIVA para redes sociales terap\xE9uticas
3. Use elementos COHERENTES con el contenido (personas solo si el texto lo requiere)
4. Prioriza ELEMENTOS SIMB\xD3LICOS: naturaleza, objetos, atm\xF3sfera, conceptos visuales
5. Si el texto menciona meditaci\xF3n/terapia/emociones espec\xEDficas, repres\xE9ntalas visualmente`;
    if (referenceImages && referenceImages.length > 0) {
      basePrompt += `

IMPORTANTE - IM\xC1GENES DE REFERENCIA:
Se proporcionan ${referenceImages.length} im\xE1genes de referencia que muestran el ESTILO VISUAL DESEADO.
Analiza cuidadosamente estas im\xE1genes y extrae:
- Paleta de colores predominante
- Estilo art\xEDstico (minimalista, acuarela, fotogr\xE1fico, etc.)
- Elementos visuales recurrentes
- Atm\xF3sfera y sensaci\xF3n general
- Composici\xF3n y encuadre

Tu descripci\xF3n visual DEBE replicar este mismo estilo, colores y atm\xF3sfera.`;
    }
    basePrompt += `

REGLAS PARA PERSONAS:
- SI el texto menciona: meditaci\xF3n, terapia, emociones humanas \u2192 Incluye persona(s) en contexto relevante
- SI el texto es conceptual/abstracto \u2192 Usa s\xEDmbolos, naturaleza, objetos (SIN personas)
- NUNCA uses rostros gen\xE9ricos sin conexi\xF3n con el texto

RESPONDE SOLO CON:
Una descripci\xF3n visual en INGL\xC9S de m\xE1ximo 3 frases, MUY ESPEC\xCDFICA al contenido del texto.`;
    return basePrompt;
  }
  /**
   * Llama a la API de Imagen
   */
  async callImagenAPI(prompt, aspectRatio, visualDescription, style, modelOverride) {
    const model = modelOverride ?? this.model;
    const endpoint = this.getEndpoint(model);
    const instance = this.helpers.toValue({ prompt });
    const parameters = this.helpers.toValue({
      sampleCount: 1,
      aspectRatio,
      safetySetting: "block_some",
      personGeneration: "allow_adult"
    });
    const [response] = await this.client.predict({
      endpoint,
      instances: [instance],
      parameters
    });
    const prediction = response.predictions?.[0];
    const imageBase64 = prediction?.structValue?.fields?.bytesBase64Encoded?.stringValue;
    if (!imageBase64) {
      throw new Error("No image data in response");
    }
    logger11.info("Image generated successfully", { model });
    return {
      imageBase64,
      mimeType: "image/png",
      prompt,
      visualDescription,
      style,
      model
    };
  }
  /**
   * Obtiene el modelo actual
   */
  getModel() {
    return this.model;
  }
};
function createImageGenerator(config) {
  return new ImageGeneratorService(config);
}

// src/media/video-generator.ts
import { Logger as Logger13 } from "@boosting/core";
var logger12 = Logger13.getLogger("VideoGeneratorService");
var VEO_3_PRICE_PER_SECOND = 0.75;
var VEO_3_FAST_PRICE_PER_SECOND = 0.3;
var VEO_MODELS = {
  VEO_3: "veo-3-generate",
  VEO_3_FAST: "veo-3-fast",
  VEO_3_1_PREVIEW: "veo-3.1-generate-preview"
};
var VideoGeneratorService = class {
  config;
  constructor(config) {
    this.config = {
      projectId: config.projectId,
      location: config.location || "us-central1",
      debug: config.debug || false
    };
  }
  /**
   * Genera un video desde un prompt de texto.
   * 
   * @param request - Parámetros de generación
   * @returns Resultado con URL del video generado
   */
  async generateFromText(request) {
    const model = VEO_MODELS.VEO_3;
    const duration = request.duration || 8;
    if (this.config.debug) {
      logger12.info("Generating video from text", {
        prompt: request.prompt.substring(0, 100) + "...",
        duration,
        model
      });
    }
    throw new Error(
      "VideoGeneratorService.generateFromText: Not implemented. Pending Veo 3 API integration. See: https://cloud.google.com/vertex-ai/docs/generative-ai/video/overview"
    );
  }
  /**
   * Genera un video animando una imagen estática.
   * 
   * @param imageUrl - URL o base64 de la imagen a animar
   * @param request - Parámetros de generación
   * @returns Resultado con URL del video generado
   */
  async generateFromImage(imageUrl, request) {
    const model = VEO_MODELS.VEO_3_1_PREVIEW;
    if (this.config.debug) {
      logger12.info("Generating video from image", {
        prompt: request.prompt.substring(0, 100) + "...",
        model
      });
    }
    throw new Error(
      "VideoGeneratorService.generateFromImage: Not implemented. Pending Veo 3.1 image-to-video API integration."
    );
  }
  /**
   * Extiende un video existente.
   * 
   * @param videoUrl - URL del video a extender
   * @param request - Parámetros de extensión
   * @returns Resultado con URL del video extendido
   */
  async extendVideo(videoUrl, request) {
    const model = VEO_MODELS.VEO_3_1_PREVIEW;
    if (this.config.debug) {
      logger12.info("Extending video", {
        videoUrl,
        prompt: request.prompt.substring(0, 100) + "...",
        model
      });
    }
    throw new Error(
      "VideoGeneratorService.extendVideo: Not implemented. Pending Veo 3.1 video extension API integration."
    );
  }
  /**
   * Obtiene el estado de una generación de video en progreso.
   * 
   * @param operationId - ID de la operación
   * @returns Estado actual de la generación
   */
  async getGenerationStatus(operationId) {
    throw new Error(
      "VideoGeneratorService.getGenerationStatus: Not implemented."
    );
  }
  /**
   * Estima el coste de una generación de video.
   * 
   * @param duration - Duración en segundos
   * @param model - Modelo a usar
   * @returns Coste estimado en USD
   */
  estimateCost(duration, model = VEO_MODELS.VEO_3) {
    const pricePerSecond = model === VEO_MODELS.VEO_3_FAST ? VEO_3_FAST_PRICE_PER_SECOND : VEO_3_PRICE_PER_SECOND;
    return duration * pricePerSecond;
  }
  /**
   * Valida los parámetros de generación.
   */
  validateRequest(request) {
    if (!request.prompt || request.prompt.trim().length === 0) {
      throw new Error("Prompt is required");
    }
    if (request.duration && ![4, 6, 8].includes(request.duration)) {
      throw new Error("Duration must be 4, 6, or 8 seconds");
    }
    if (request.aspectRatio && !["16:9", "9:16", "1:1"].includes(request.aspectRatio)) {
      throw new Error("Aspect ratio must be 16:9, 9:16, or 1:1");
    }
  }
};
function createVideoGenerator(config) {
  return new VideoGeneratorService(config);
}

// src/media/imagen-api.ts
var aiPlatformClient = null;
var aiPlatformHelpers = null;
async function getAIPlatformClient(region) {
  if (aiPlatformClient && aiPlatformHelpers) {
    return { client: aiPlatformClient, helpers: aiPlatformHelpers };
  }
  const aiplatform = await import("@google-cloud/aiplatform");
  const { PredictionServiceClient } = aiplatform.v1;
  aiPlatformClient = new PredictionServiceClient({
    apiEndpoint: `${region}-aiplatform.googleapis.com`
  });
  aiPlatformHelpers = aiplatform.helpers;
  return { client: aiPlatformClient, helpers: aiPlatformHelpers };
}
async function callImagenAPI(config) {
  const {
    prompt,
    aspectRatio,
    projectId,
    model = IMAGE_MODEL_CONFIG.primaryGeneration,
    referenceImages,
    logger: logger19
  } = config;
  const log = logger19 || console;
  const region = IMAGE_MODEL_CONFIG.region;
  const { client, helpers } = await getAIPlatformClient(region);
  const endpoint = `projects/${projectId}/locations/${region}/publishers/google/models/${model}`;
  const instanceData = { prompt };
  if (referenceImages && referenceImages.length > 0) {
    instanceData.referenceImages = referenceImages.slice(0, 4).map((refImg, idx) => ({
      referenceId: refImg.id || `ref-${idx}`,
      referenceImage: {
        bytesBase64Encoded: refImg.base64
      }
    }));
    log.info("[ai:callImagenAPI] Including reference images", {
      count: instanceData.referenceImages.length,
      model
    });
  }
  const instance = helpers.toValue(instanceData);
  const parameters = helpers.toValue({
    sampleCount: 1,
    aspectRatio,
    safetySetting: "block_some",
    personGeneration: "allow_adult"
  });
  log.info("[ai:callImagenAPI] Calling Imagen API", {
    model,
    hasReferenceImages: (referenceImages?.length || 0) > 0,
    promptLength: prompt.length
  });
  try {
    const [response] = await client.predict({
      endpoint,
      instances: [instance],
      parameters
    });
    const prediction = response.predictions?.[0];
    const imageBase64 = prediction?.structValue?.fields?.bytesBase64Encoded?.stringValue;
    if (!imageBase64) {
      throw new Error("No image data in response");
    }
    return {
      success: true,
      imageBase64,
      mimeType: "image/png",
      model
    };
  } catch (error) {
    log.error("[ai:callImagenAPI] Failed", {
      error: error.message,
      model
    });
    if (model === IMAGE_MODEL_CONFIG.primaryGeneration) {
      log.info("[ai:callImagenAPI] Trying fallback model");
      return callImagenAPI({
        ...config,
        model: IMAGE_MODEL_CONFIG.fallbackGeneration
      });
    }
    return {
      success: false,
      error: error.message,
      model
    };
  }
}
async function generateImageQuick(prompt, projectId, options) {
  const { aspectRatio = "16:9", style, logger: logger19 } = options || {};
  const fullPrompt = style ? `${prompt}. Style: ${style}. High quality, professional.` : `${prompt}. High quality, professional, balanced composition.`;
  return callImagenAPI({
    prompt: fullPrompt,
    aspectRatio,
    projectId,
    logger: logger19
  });
}

// src/media/guidelines-prompt-builder.ts
import { Logger as Logger14 } from "@boosting/core";
async function buildGuidelinesSectionAsync(guidelines, agentsService) {
  if (!guidelines) {
    return "";
  }
  const isStrictMode = guidelines.strictProductMode === true;
  if (isStrictMode && guidelines.preserveProducts && agentsService) {
    try {
      const agent = await agentsService.getAgent("image_editing_guidelines");
      if (agent && agent.systemPrompt) {
        let prompt = agent.systemPrompt;
        if (guidelines.neverModify && guidelines.neverModify.length > 0) {
          const neverModifyList = guidelines.neverModify.map((item) => `- ${item}`).join("\n");
          prompt = prompt.replace(
            /\{\{#if neverModify\}\}[\s\S]*?\{\{\/if\}\}/g,
            `\u{1F6AB} NUNCA MODIFICAR ESTOS ELEMENTOS (PROHIBICI\xD3N ABSOLUTA):
${neverModifyList}`
          );
        } else {
          prompt = prompt.replace(/\{\{#if neverModify\}\}[\s\S]*?\{\{\/if\}\}/g, "");
        }
        if (guidelines.canEnhance && guidelines.canEnhance.length > 0) {
          const canEnhanceList = guidelines.canEnhance.map((item) => `- ${item}`).join("\n");
          prompt = prompt.replace(
            /\{\{#if canEnhance\}\}[\s\S]*?\{\{\/if\}\}/g,
            `\u2705 \xDANICOS ELEMENTOS QUE PUEDES MEJORAR (solo estos, nada m\xE1s):
${canEnhanceList}`
          );
        } else {
          prompt = prompt.replace(/\{\{#if canEnhance\}\}[\s\S]*?\{\{\/if\}\}/g, "");
        }
        if (guidelines.customInstructions) {
          prompt = prompt.replace(
            /\{\{#if customInstructions\}\}[\s\S]*?\{\{\/if\}\}/g,
            `INSTRUCCIONES ADICIONALES DEL CLIENTE:
${guidelines.customInstructions}`
          );
        } else {
          prompt = prompt.replace(/\{\{#if customInstructions\}\}[\s\S]*?\{\{\/if\}\}/g, "");
        }
        prompt = prompt.replace(/\{\{#each \w+\}\}[\s\S]*?\{\{\/each\}\}/g, "");
        prompt = prompt.replace(/\{\{\w+\}\}/g, "");
        return "\n" + prompt.trim();
      }
    } catch (error) {
      Logger14.getLogger("GuidelinesPromptBuilder").warn("Failed to load agent, using fallback", { error });
    }
  }
  return buildGuidelinesSection(guidelines);
}
function buildGuidelinesSection(guidelines) {
  if (!guidelines) {
    return "";
  }
  const sections = [];
  const isStrictMode = guidelines.strictProductMode === true;
  if (isStrictMode && guidelines.preserveProducts) {
    sections.push(`
\u{1F6AB}\u{1F6AB}\u{1F6AB} MODO ESTRICTO - PRODUCTOS T\xC9CNICOS/INDUSTRIALES \u{1F6AB}\u{1F6AB}\u{1F6AB}

ESTA ES UNA IMAGEN DE PRODUCTO T\xC9CNICO/INDUSTRIAL. REGLAS ABSOLUTAS:

1. PROHIBIDO INVENTAR: NO generes, imagines, ni crees NING\xDAN producto, equipo, 
   maquinaria, componente, pantalla, interfaz, sensor, o elemento t\xE9cnico que 
   NO est\xE9 EXACTAMENTE en la imagen original.

2. PROHIBIDO MODIFICAR PRODUCTOS: Los productos mostrados son SAGRADOS.
   - NO cambies su forma, color, tama\xF1o, proporci\xF3n, o aspecto
   - NO a\xF1adas elementos que no existan (cables, pantallas, botones, puertos)
   - NO elimines ni ocultes partes del producto
   - NO "mejores" el dise\xF1o del producto

3. PROHIBIDO COMBINAR: Si hay m\xFAltiples im\xE1genes de referencia, NO combines
   elementos de una imagen con otra. Cada producto es \xFAnico y espec\xEDfico.

4. SOLO PUEDES MODIFICAR:
   - Fondo/background (hacerlo m\xE1s neutro o profesional)
   - Iluminaci\xF3n general (sin cambiar los reflejos del producto)
   - Eliminar imperfecciones del fondo (no del producto)
   - Ajustar encuadre manteniendo el producto id\xE9ntico

5. SI NO PUEDES CUMPLIR: Si la tarea requiere modificar el producto de alguna
   manera, RECHAZA la tarea y devuelve la imagen original sin cambios.

\u26A0\uFE0F ESTA ES UNA EMPRESA B2B INDUSTRIAL - Los productos t\xE9cnicos son muy 
espec\xEDficos y delicados. Inventar o modificar productos puede causar 
problemas graves de comunicaci\xF3n con clientes.`);
  } else if (guidelines.preserveProducts) {
    sections.push(`
\u26A0\uFE0F CRITICAL - PRODUCT PRESERVATION:
- If this image contains PRODUCTS, EQUIPMENT, or MACHINERY, they are SACRED
- NEVER invent, modify, or replace any product shown
- Products must remain EXACTLY as they appear in the original
- You can enhance the background, lighting, or context around products
- The product itself must be preserved pixel-perfect
- DO NOT create fictional products or combine elements from different products`);
  }
  if (guidelines.preserveLogos) {
    const logoSection = isStrictMode ? `
LOGO/MARCA - PRESERVACI\xD3N ABSOLUTA:
- Logos, marcas, textos, y elementos de identidad son INTOCABLES
- NO generes logos ficticios ni inventes marcas
- Si el logo no es legible, d\xE9jalo as\xED - NO lo "mejores"
- NO a\xF1adas logos o marcas que no est\xE9n en la imagen original` : `
LOGO PRESERVATION:
- Any logos, brand marks, or text visible in the image must remain unchanged
- Do not remove, alter, or add any branding elements`;
    sections.push(logoSection);
  }
  if (guidelines.neverModify && guidelines.neverModify.length > 0) {
    const neverModifyHeader = isStrictMode ? `
\u{1F6AB} NUNCA MODIFICAR ESTOS ELEMENTOS (PROHIBICI\xD3N ABSOLUTA):` : `
NEVER MODIFY THESE ELEMENTS:`;
    sections.push(`${neverModifyHeader}
${guidelines.neverModify.map((item) => `- ${item}`).join("\n")}`);
  }
  if (guidelines.canEnhance && guidelines.canEnhance.length > 0) {
    const canEnhanceHeader = isStrictMode ? `
\u2705 \xDANICOS ELEMENTOS QUE PUEDES MEJORAR (solo estos, nada m\xE1s):` : `
YOU MAY ENHANCE:`;
    sections.push(`${canEnhanceHeader}
${guidelines.canEnhance.map((item) => `- ${item}`).join("\n")}`);
  }
  if (guidelines.customInstructions) {
    sections.push(`
INSTRUCCIONES ADICIONALES DEL CLIENTE:
${guidelines.customInstructions}`);
  }
  return sections.length > 0 ? "\n" + sections.join("\n") : "";
}

// src/media/image-agents.types.ts
var DEFAULT_LOGO_CONFIG = {
  autoAddLogo: false,
  defaultPosition: "bottom-right",
  sizePercent: 15,
  opacity: 0.9,
  margin: 20,
  variantForLightBg: "full_color",
  variantForDarkBg: "white",
  addLogoTo: {
    infographics: true,
    carousels: true,
    videoThumbnails: true,
    articleImages: false,
    socialPosts: false
  },
  aiInstructions: ""
};
var DEFAULT_STYLE_AGENTS = [
  {
    id: "professional",
    name: "Profesional",
    description: "Im\xE1genes corporativas y profesionales",
    promptTemplate: `Professional corporate imagery with clean composition, 
high-quality lighting, modern office or industrial environment. 
Business-appropriate colors (blues, grays, whites). 
Sharp focus, professional photography style.`,
    enabled: true,
    exampleKeywords: ["corporate", "business", "office", "meeting"]
  },
  {
    id: "tech",
    name: "Tecnol\xF3gico",
    description: "Estilo futurista y tecnol\xF3gico",
    promptTemplate: `Futuristic technology aesthetic with digital elements, 
circuit patterns, data visualizations, holographic effects. 
Cool blue and cyan color palette with subtle glows. 
Clean, modern, high-tech atmosphere.`,
    enabled: true,
    exampleKeywords: ["technology", "digital", "innovation", "software"]
  },
  {
    id: "industrial",
    name: "Industrial",
    description: "Entornos industriales y manufactura",
    promptTemplate: `Industrial environment with machinery, factories, 
manufacturing equipment. Strong metallic textures, 
steel grays and safety yellows. 
Emphasize scale, precision, and engineering excellence.`,
    enabled: true,
    exampleKeywords: ["factory", "machinery", "manufacturing", "equipment"]
  },
  {
    id: "minimalist",
    name: "Minimalista",
    description: "Dise\xF1o limpio y minimalista",
    promptTemplate: `Minimalist design with clean lines, ample white space, 
simple geometric shapes. Limited color palette, 
focus on essential elements only. 
Modern, sophisticated, uncluttered aesthetic.`,
    enabled: true,
    exampleKeywords: ["simple", "clean", "elegant", "modern"]
  },
  {
    id: "corporate",
    name: "Corporativo",
    description: "Estilo empresarial formal",
    promptTemplate: `Corporate business style with formal aesthetics, 
conference rooms, handshakes, charts and graphs. 
Traditional business colors (navy, burgundy, gold accents). 
Conveys trust, stability, and professionalism.`,
    enabled: true,
    exampleKeywords: ["business", "enterprise", "formal", "executive"]
  },
  {
    id: "artistic",
    name: "Art\xEDstico",
    description: "Estilo creativo y art\xEDstico",
    promptTemplate: `Creative artistic interpretation with bold colors, 
abstract elements, dynamic compositions. 
Artistic lighting and creative angles. 
Visually striking and memorable.`,
    enabled: false,
    exampleKeywords: ["creative", "artistic", "bold", "unique"]
  }
];
var DEFAULT_INFOGRAPHIC_AGENT = {
  enabled: true,
  systemPrompt: `Create a professional B2B infographic with:
- Clear visual hierarchy
- Data-driven design elements
- Professional color scheme matching brand
- Easy to read typography
- Logical flow of information
- Icons and visual metaphors for concepts
- NO photorealistic products unless provided`,
  dataVizInstructions: `For data visualization:
- Use charts, graphs, percentages
- Large bold numbers for key stats
- Clean bar/pie/line charts
- Color-coded categories`,
  processInstructions: `For process/timeline:
- Numbered steps with icons
- Arrows showing flow direction
- Clear start and end points
- Visual progression indicators`,
  comparisonInstructions: `For comparisons:
- Side-by-side columns
- Checkmarks and X marks
- Visual balance between options
- Clear differentiators highlighted`
};
var DEFAULT_ARTICLE_IMAGE_AGENT = {
  enabled: true,
  analysisPrompt: `Analyze this section of a B2B article and create a visual prompt for an image that:
1. Directly illustrates the key concept
2. Is professional and suitable for B2B marketing
3. Avoids generic stock photo concepts
4. Is technically accurate for the industry
5. Would enhance reader understanding`,
  maxImagesPerArticle: 4,
  minSectionLength: 150
};
var DEFAULT_PLACEMENT_AGENT = {
  enabled: true,
  placementPrompt: `As a visual editor, decide optimal image placement in this content.
Consider:
- Content breaks and section transitions
- Reader attention points
- Visual balance in the document
- Information density of surrounding text`,
  placementRules: [
    "First image after first substantial paragraph",
    "Never two images consecutively",
    "Technical sections benefit from diagrams",
    "Data sections benefit from visualizations",
    "Avoid images near links or CTAs"
  ]
};
var DEFAULT_ENRICHMENT_AGENT = {
  enabled: true,
  enhancePrompt: `Enhance this image while maintaining the original subject and composition.
Improve quality, lighting, and details.
Make it more professional and suitable for B2B marketing.
Preserve all important elements from the original.`,
  inspirePrompt: `Create a professional B2B marketing image inspired by this reference.
Style: Modern, professional, suitable for industrial B2B marketing.
Maintain the essence and concept but create a fresh interpretation.
Focus on professional quality and brand appropriateness.`,
  generatePrompt: `Create a professional image for B2B marketing.
Style: Modern, professional, suitable for industrial B2B marketing.
Focus on clarity, professionalism, and visual impact.
Ensure the image communicates the intended message effectively.`,
  editingPromptTemplate: `USER REQUEST: "{{userPrompt}}"

STYLE: {{style}} aesthetic - {{styleDescription}}
{{brandingSection}}

EDITING GUIDELINES:
- Preserve the core subject and composition of the SOURCE IMAGE
- Improve visual quality: lighting, colors, sharpness
{{logoGuideline}}
- Do NOT completely replace the image with something different
{{guidelinesSection}}

Generate the image now.`,
  defaultStyle: "professional"
};
var DEFAULT_STORYBOARD_AGENT = {
  enabled: true,
  systemPromptEs: `Eres un experto director de video y guionista especializado en contenido B2B industrial.
Tu tarea es convertir art\xEDculos t\xE9cnicos en storyboards profesionales para videos.

REGLAS CR\xCDTICAS:
1. NO copies el texto literal - REESCRIBE las ideas de forma concisa y natural para narraci\xF3n de video
2. Cada narraci\xF3n debe ser de 2-4 oraciones cortas (ideal para TTS)
3. Crea una estructura dram\xE1tica: introducci\xF3n enganchante \u2192 desarrollo \u2192 cl\xEDmax \u2192 conclusi\xF3n con CTA
4. Los prompts visuales deben ser MUY espec\xEDficos y detallados para generaci\xF3n con IA
5. Asigna im\xE1genes existentes SOLO cuando sean claramente relevantes para la escena
6. Sugiere animaciones Ken Burns apropiadas para cada tipo de imagen
7. Las transiciones deben fluir naturalmente entre escenas
8. CORRELACI\xD3N IMAGEN-NARRACI\xD3N: Adapta la narraci\xF3n para que haga referencia directa a lo que muestra la imagen`,
  systemPromptEn: `You are an expert video director and scriptwriter specialized in B2B industrial content.
Your task is to convert technical articles into professional video storyboards.

CRITICAL RULES:
1. DO NOT copy text literally - REWRITE ideas concisely and naturally for video narration
2. Each narration should be 2-4 short sentences (ideal for TTS)
3. Create dramatic structure: engaging intro \u2192 development \u2192 climax \u2192 conclusion with CTA
4. Visual prompts must be VERY specific and detailed for AI generation
5. Assign existing images ONLY when clearly relevant to the scene
6. Suggest appropriate Ken Burns animations for each image type
7. Transitions should flow naturally between scenes
8. IMAGE-NARRATION CORRELATION: Adapt narration to directly reference what the image shows`,
  narrationInstructions: `NARRATION STRUCTURE:
- Intro: Provocative question or impactful fact
- Development: Key points summarized, not copied
- Closing: Clear call to action
- Keep each narration 2-4 sentences max`,
  visualPromptInstructions: `VISUAL PROMPTS:
- Be VERY specific (50-100 words per prompt)
- Include: type of image, elements, style, colors
- Avoid generic descriptions
- Consider the sector and brand context`,
  defaultStyle: "corporate"
};
var DEFAULT_VIDEO_NARRATION_AGENT = {
  enabled: true,
  systemPromptEs: `Eres un experto en crear narraciones para videos de presentaci\xF3n t\xE9cnica/profesional.

Tu objetivo es convertir el contenido de cada slide en una narraci\xF3n natural y fluida que:
1. NO repita textualmente el contenido del slide
2. EXPANDA y contextualice la informaci\xF3n
3. Use un tono conversacional pero profesional
4. Conecte cada slide con el siguiente de forma natural
5. Mantenga la atenci\xF3n del espectador

REGLAS DE FORMATO:
- Cada narraci\xF3n debe durar 8-15 segundos al leer
- Usa frases cortas y pausas naturales
- Evita jerga excesiva
- Incluye transiciones suaves entre ideas`,
  systemPromptEn: `You are an expert at creating narrations for technical/professional presentation videos.

Your goal is to convert each slide's content into a natural, flowing narration that:
1. Does NOT repeat the slide content verbatim
2. EXPANDS and contextualizes the information
3. Uses a conversational but professional tone
4. Naturally connects each slide to the next
5. Keeps the viewer engaged

FORMAT RULES:
- Each narration should take 8-15 seconds to read
- Use short sentences and natural pauses
- Avoid excessive jargon
- Include smooth transitions between ideas`,
  maxWordsPerSlide: 80,
  speakingPaceWpm: 150
};
var DEFAULT_AGENTS_CONFIG = {
  styleAgents: DEFAULT_STYLE_AGENTS,
  infographicAgent: DEFAULT_INFOGRAPHIC_AGENT,
  articleImageAgent: DEFAULT_ARTICLE_IMAGE_AGENT,
  placementAgent: DEFAULT_PLACEMENT_AGENT,
  enrichmentAgent: DEFAULT_ENRICHMENT_AGENT,
  storyboardAgent: DEFAULT_STORYBOARD_AGENT,
  videoNarrationAgent: DEFAULT_VIDEO_NARRATION_AGENT
};
var DEFAULT_IMAGE_GEN_CONFIG = {
  logoConfig: DEFAULT_LOGO_CONFIG,
  agentsConfig: DEFAULT_AGENTS_CONFIG
};
function extractLogoConfig(config) {
  if (!config) return DEFAULT_LOGO_CONFIG;
  return config.logoConfig || DEFAULT_LOGO_CONFIG;
}
function extractAgentsConfig(config) {
  if (!config) return DEFAULT_AGENTS_CONFIG;
  return config.agentsConfig || DEFAULT_AGENTS_CONFIG;
}

// src/media/gemini-editing.ts
async function editImageWithGemini(config) {
  const { referenceImage, prompt, style = "professional", projectId, guidelines, agentsService, brandingImages, brandingInstructions, editingPromptTemplate, logger: logger19 } = config;
  const log = logger19 || console;
  const modelName = IMAGE_MODEL_CONFIG.primaryEditing;
  const styleDescription = STYLE_PROMPTS[style] || STYLE_PROMPTS.professional;
  const hasBrandingInstructions = !!brandingInstructions;
  const guidelinesSection = await buildGuidelinesSectionAsync(guidelines, agentsService);
  const logoGuideline = "- Do NOT add text, logos, or watermarks to the image";
  const brandingSection = hasBrandingInstructions ? `
BRAND STYLE CONTEXT (apply these color and style preferences):
${brandingInstructions}` : "";
  const template = editingPromptTemplate || DEFAULT_ENRICHMENT_AGENT.editingPromptTemplate;
  const editPrompt = template.replace(/\{\{userPrompt\}\}/g, prompt).replace(/\{\{style\}\}/g, style).replace(/\{\{styleDescription\}\}/g, styleDescription).replace(/\{\{logoGuideline\}\}/g, logoGuideline).replace(/\{\{guidelinesSection\}\}/g, guidelinesSection).replace(/\{\{brandingSection\}\}/g, brandingSection);
  log.info("[ai:editImageWithGemini] Starting", {
    model: modelName,
    style,
    refId: referenceImage.id,
    guidelinesApplied: !!guidelines,
    hasBrandingInstructions,
    preserveProducts: guidelines?.preserveProducts ?? false,
    preserveLogos: guidelines?.preserveLogos ?? false,
    neverModifyCount: guidelines?.neverModify?.length ?? 0,
    usingCustomTemplate: !!editingPromptTemplate
  });
  const requestParts = [
    {
      inlineData: {
        mimeType: referenceImage.mimeType,
        data: referenceImage.base64
      }
    },
    { text: editPrompt }
  ];
  try {
    const { GoogleGenAI } = await import("@google/genai");
    const genai = new GoogleGenAI({
      vertexai: true,
      project: projectId,
      location: "global"
    });
    const result = await genai.models.generateContent({
      model: modelName,
      contents: [
        {
          role: "user",
          parts: requestParts
        }
      ],
      config: {
        responseModalities: ["TEXT", "IMAGE"]
      }
    });
    const responseParts = result.candidates?.[0]?.content?.parts || [];
    for (const part of responseParts) {
      if (part.inlineData?.mimeType?.startsWith("image/")) {
        log.info("[ai:editImageWithGemini] Success", {
          refId: referenceImage.id,
          model: modelName,
          hasBrandingInstructions
        });
        return {
          success: true,
          imageBase64: part.inlineData.data,
          mimeType: part.inlineData.mimeType,
          model: modelName,
          referenceImageId: referenceImage.id,
          referenceImageUrl: referenceImage.url
        };
      }
    }
    throw new Error("No image in Gemini 3 Pro response");
  } catch (error) {
    log.warn("[ai:editImageWithGemini] Gemini 3 Pro failed, trying Gemini 3 Flash fallback", {
      error: error.message,
      refId: referenceImage.id
    });
    const fallbackModel = IMAGE_MODEL_CONFIG.fallbackEditing;
    try {
      const { GoogleGenAI } = await import("@google/genai");
      const genai = new GoogleGenAI({
        vertexai: true,
        project: projectId,
        location: "global"
      });
      const result = await genai.models.generateContent({
        model: fallbackModel,
        contents: [
          {
            role: "user",
            parts: requestParts
          }
        ],
        config: {
          responseModalities: ["TEXT", "IMAGE"]
        }
      });
      const fallbackParts = result.candidates?.[0]?.content?.parts || [];
      for (const part of fallbackParts) {
        if (part.inlineData?.mimeType?.startsWith("image/")) {
          log.info("[ai:editImageWithGemini] Success with fallback", {
            refId: referenceImage.id,
            model: fallbackModel,
            hasBrandingInstructions
          });
          return {
            success: true,
            imageBase64: part.inlineData.data,
            mimeType: part.inlineData.mimeType,
            model: fallbackModel,
            referenceImageId: referenceImage.id,
            referenceImageUrl: referenceImage.url
          };
        }
      }
      throw new Error("No image in Gemini 3 Flash response");
    } catch (fallbackError) {
      log.error("[ai:editImageWithGemini] Both Gemini 3 Pro and Flash failed", {
        primaryError: error.message,
        fallbackError: fallbackError.message,
        refId: referenceImage.id
      });
    }
    return {
      success: false,
      error: error.message,
      model: modelName
    };
  }
}
async function editImageWithCustomPrompt(config) {
  const { referenceImage, customPrompt, guidelines, agentsService, projectId, logger: logger19 } = config;
  const log = logger19 || console;
  const modelName = IMAGE_MODEL_CONFIG.primaryEditing;
  const guidelinesSection = await buildGuidelinesSectionAsync(guidelines, agentsService);
  const fullPrompt = customPrompt + guidelinesSection;
  log.info("[ai:editImageWithCustomPrompt] Starting", {
    model: modelName,
    refId: referenceImage.id,
    hasGuidelines: !!guidelines
  });
  try {
    const { GoogleGenAI } = await import("@google/genai");
    const genai = new GoogleGenAI({
      vertexai: true,
      project: projectId,
      location: "global"
    });
    const result = await genai.models.generateContent({
      model: modelName,
      contents: [
        {
          role: "user",
          parts: [
            {
              inlineData: {
                mimeType: referenceImage.mimeType,
                data: referenceImage.base64
              }
            },
            { text: fullPrompt }
          ]
        }
      ],
      config: {
        responseModalities: ["TEXT", "IMAGE"]
      }
    });
    const parts = result.candidates?.[0]?.content?.parts || [];
    for (const part of parts) {
      if (part.inlineData?.mimeType?.startsWith("image/")) {
        log.info("[ai:editImageWithCustomPrompt] Success", {
          refId: referenceImage.id,
          model: modelName
        });
        return {
          success: true,
          imageBase64: part.inlineData.data,
          mimeType: part.inlineData.mimeType,
          model: modelName,
          referenceImageId: referenceImage.id,
          referenceImageUrl: referenceImage.url
        };
      }
    }
    throw new Error("No image in response");
  } catch (error) {
    log.error("[ai:editImageWithCustomPrompt] Failed", {
      error: error.message,
      refId: referenceImage.id
    });
    return {
      success: false,
      error: error.message,
      model: modelName
    };
  }
}

// src/media/prompt-enhancement.ts
var DEFAULT_IMAGE_PROMPT_SYSTEM = `You are an expert at converting content topics into visual prompts for AI image generation.

TASK:
Convert the provided topic into a DETAILED and SPECIFIC visual description for generating an image that:

1. Captures the ESSENCE of the topic
2. Is visually ATTRACTIVE for professional content
3. Uses COHERENT visual elements with the topic
4. Avoids generic stock photo looks
5. Maintains a professional aesthetic

RESPOND ONLY WITH:
A visual description in ENGLISH of maximum 3 sentences, VERY SPECIFIC to the content topic.`;
async function enhancePromptWithGemini(config) {
  const {
    prompt,
    style = "professional",
    referenceImages,
    projectId,
    systemPrompt = DEFAULT_IMAGE_PROMPT_SYSTEM,
    logger: logger19
  } = config;
  const log = logger19 || console;
  try {
    const { VertexAI: VertexAI2 } = await import("@google-cloud/vertexai");
    const vertexAI = new VertexAI2({ project: projectId, location: IMAGE_MODEL_CONFIG.region });
    const model = vertexAI.getGenerativeModel({ model: "gemini-3-flash-preview" });
    let fullSystemPrompt = systemPrompt;
    fullSystemPrompt += `

CONTENT TOPIC:
"${prompt}"

STYLE REQUIRED: ${style}`;
    if (referenceImages && referenceImages.length > 0) {
      fullSystemPrompt += `

IMPORTANT - REFERENCE IMAGES:
${referenceImages.length} reference images are provided that show the DESIRED VISUAL STYLE.
Carefully analyze these images and extract:
- Predominant color palette
- Artistic style (minimalist, watercolor, photographic, etc.)
- Recurring visual elements
- Atmosphere and general feeling
- Composition and framing

Your visual description MUST replicate this same style, colors, and atmosphere.
${referenceImages.map((img, i) => img.description ? `Image ${i + 1} description: ${img.description}` : "").filter(Boolean).join("\n")}`;
    }
    fullSystemPrompt += `

RESPOND ONLY WITH:
A visual description in ENGLISH of maximum 3 sentences, VERY SPECIFIC to the content topic${referenceImages?.length ? " and maintaining the style from the reference images" : ""}.`;
    const contentParts = [];
    if (referenceImages && referenceImages.length > 0) {
      for (const refImg of referenceImages) {
        if (refImg.base64 && refImg.mimeType) {
          contentParts.push({
            inlineData: {
              mimeType: refImg.mimeType,
              data: refImg.base64
            }
          });
        }
      }
    }
    contentParts.push({ text: fullSystemPrompt });
    if (referenceImages && referenceImages.length > 0) {
      contentParts.push({
        text: "\n\nBased on the text provided AND the visual style of the reference images above, generate the visual description:"
      });
    }
    const result = await model.generateContent({
      contents: [{ role: "user", parts: contentParts }]
    });
    const visualDescription = result.response.candidates?.[0]?.content?.parts?.[0]?.text?.trim();
    if (visualDescription) {
      log.info("[ai:enhancePrompt] Success", {
        length: visualDescription.length,
        hasReferenceImages: (referenceImages?.length || 0) > 0
      });
      return visualDescription;
    }
  } catch (error) {
    log.warn("[ai:enhancePrompt] Failed, using original", { error: error.message });
  }
  return prompt;
}
async function fetchReferenceImages(referenceImages, logger19) {
  const log = logger19 || console;
  const processedImages = [];
  for (const refImg of referenceImages.slice(0, 6)) {
    try {
      const response = await fetch(refImg.url);
      if (!response.ok) {
        log.warn("[ai:fetchReferenceImages] Failed to fetch", {
          url: refImg.url,
          status: response.status
        });
        continue;
      }
      const arrayBuffer = await response.arrayBuffer();
      const buffer = Buffer.from(arrayBuffer);
      const base64 = buffer.toString("base64");
      const mimeType = refImg.mimeType || response.headers.get("content-type") || "image/jpeg";
      processedImages.push({
        base64,
        mimeType,
        description: refImg.description,
        id: refImg.id,
        url: refImg.url
      });
      log.info("[ai:fetchReferenceImages] Processed", {
        id: refImg.id,
        description: refImg.description?.substring(0, 50)
      });
    } catch (error) {
      log.warn("[ai:fetchReferenceImages] Error", {
        url: refImg.url,
        error: error.message
      });
    }
  }
  log.info("[ai:fetchReferenceImages] Complete", {
    requested: Math.min(referenceImages.length, 6),
    processed: processedImages.length
  });
  return processedImages;
}

// src/media/media-classification-prompts.ts
var DEFAULT_MEDIA_CLASSIFICATION_PROMPTS = {
  /**
   * System Prompt for Article Classification
   * 
   * Variables:
   * - {{CATEGORY_LIST}}: Formatted list of product categories
   * - {{MARKET_LIST}}: Formatted list of markets/sectors
   * - {{TOPIC_LIST}}: Formatted list of topics/trends
   * - {{THEME_LIST}}: Formatted list of marketing themes
   */
  systemPrompt: `You are an expert content classifier for a B2B electronics distributor specializing in defense, aerospace, and industrial markets.

Your task is to classify news articles based on their relevance to our business interests AND identify if they are branded/promotional content from other companies.

## PRODUCT CATEGORIES / FAMILIES (categoryIds - assign all relevant)
These are the product families and categories we sell. Assign ALL relevant ones:

{{CATEGORY_LIST}}

## MARKETS / SECTORS (marketIds - assign all relevant)
These are the target market sectors/verticals for our products. Assign ALL applicable:

{{MARKET_LIST}}

## TOPICS / TRENDS (topicIds - assign all relevant)  
These are industry topics and trends we want to track. Assign ALL related:

{{TOPIC_LIST}}

## MARKETING THEMES (themeIds - assign all relevant)
These are specific marketing themes important for content strategy:

{{THEME_LIST}}

## CONTENT TYPE CLASSIFICATION
Classify each article into one of these content types:
- **news**: Genuine industry news, analysis, or reporting by journalists
- **product_launch**: Product announcement focused on a specific company's new product
- **press_release**: Corporate press release or PR-style content (usually promotional)
- **case_study**: Customer success story or specific use case implementation
- **sponsored**: Clearly sponsored or paid content/advertorial
- **editorial**: Opinion piece, thought leadership, or editorial content
- **research**: Industry research, reports, white papers, or technical analysis

## BRANDED CONTENT DETECTION
IMPORTANT: Identify if an article is "branded content" (promotional content about a specific company's products):

**Mark as isBrandedContent = true if:**
- Article is primarily about a specific company's product launch or announcement
- Article reads like a press release with promotional language
- Article focuses on a single vendor's solution without objective analysis
- Article uses marketing language like "innovative", "revolutionary", "industry-leading"
- Article is clearly sponsored or advertorial content
- Article is a case study promoting a vendor's solution

**Mark as isBrandedContent = false if:**
- Article is objective industry news covering multiple players
- Article is genuine journalism with balanced perspective
- Article discusses industry trends without promoting specific products
- Article is technical research or analysis
- Article covers regulatory/standards updates

**Extract mentionedBrands:**
- List ALL company/brand names mentioned prominently in the article
- Include manufacturers, vendors, contractors (e.g., "Curtiss-Wright", "Mercury Systems", "NVIDIA", "AMD", "Intel")
- Do NOT include generic terms, only actual company/brand names

**brandedContentReason (if isBrandedContent = true):**
- Briefly explain why this is branded content (e.g., "Product launch announcement for Mercury Systems HPEC module")

## RELEVANCE CRITERIA
An article IS RELEVANT if it:
- Discusses embedded computing, rugged systems, or defense electronics
- Covers AI/ML applications in embedded or edge computing
- Reports on military technology, C4ISR, or tactical systems
- Discusses high-performance computing in mobile or rugged environments
- Covers semiconductor trends, chip shortages, or supply chain affecting electronics
- Discusses industry regulations, standards (MIL-STD, DO-178, etc.)
- Reports on major defense contractors or electronics manufacturers

An article is NOT RELEVANT if it:
- Is generic business/financial news without tech focus
- Is about consumer electronics (phones, laptops, gaming)
- Is about pure software/cloud without hardware component
- Is about unrelated industries (agriculture, retail, media)
- Is press release spam without substantive content

## SCORING GUIDELINES
- 80-100: Directly relevant to our core themes (HPEC, AI for defense, military computing)
- 60-79: Industry news relevant to our markets
- 40-59: Tangentially related, might be useful
- 0-39: Not relevant, should be discarded

**NOTE on branded content scoring:**
- Branded content CAN still be relevant (score 40+) if it's about relevant products/technologies
- However, heavily promotional content with little informational value should score lower
- Objective news about a company's products can score high if relevant

Return valid JSON with the specified structure.`,
  /**
   * User Prompt Template for Batch Classification
   * 
   * Variables:
   * - {{ARTICLES}}: Formatted list of articles to classify
   * - {{ARTICLE_COUNT}}: Number of articles in batch
   */
  batchPrompt: `Classify the following {{ARTICLE_COUNT}} articles:

{{ARTICLES}}

Return a JSON with the "classifications" array containing each article's classification.`
};
function applyMediaPromptTemplate(template, variables) {
  let result = template;
  for (const [key, value] of Object.entries(variables)) {
    result = result.replace(new RegExp(`{{${key}}}`, "g"), value);
  }
  return result;
}
function getDefaultMediaClassificationPrompts() {
  return { ...DEFAULT_MEDIA_CLASSIFICATION_PROMPTS };
}
function getMediaClassificationPromptsPath(tenantId) {
  return `tenants/${tenantId}/ai_config/media_classification`;
}
function mergeWithDefaultPrompts(customPrompts) {
  const defaults = getDefaultMediaClassificationPrompts();
  return {
    ...defaults,
    ...customPrompts,
    // Only override if custom value is not empty
    systemPrompt: customPrompts.systemPrompt?.trim() || defaults.systemPrompt,
    batchPrompt: customPrompts.batchPrompt?.trim() || defaults.batchPrompt
  };
}

// src/deep-research/deep-research.service.ts
import { Logger as Logger15 } from "@boosting/core";
var DeepResearchService = class {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Firebase httpsCallable generic
  callGemini;
  defaultFocusAreas;
  pollingIntervalMs;
  maxPollingAttempts;
  constructor(config) {
    this.callGemini = config.callGemini;
    this.defaultFocusAreas = config.defaultFocusAreas || [
      "Tendencias de mercado",
      "An\xE1lisis de competencia",
      "Oportunidades de negocio",
      "Datos estad\xEDsticos"
    ];
    this.pollingIntervalMs = config.pollingIntervalMs || 5e3;
    this.maxPollingAttempts = config.maxPollingAttempts || 120;
  }
  /**
   * Start a Deep Research investigation
   * 
   * Tries the Google Deep Research Agent first, falls back to
   * Google Search Grounding if the agent is not available.
   */
  async startResearch(request) {
    const { topic, context, focusAreas } = request;
    try {
      const response = await this.callGemini({
        task: "startDeepResearch",
        data: {
          topic,
          context,
          focusAreas: focusAreas || this.defaultFocusAreas,
          systemInstruction: request.systemInstruction
        }
      });
      const result = response.data;
      return {
        id: result.interactionId,
        topic,
        status: "searching",
        progress: 10,
        report: "",
        sources: [],
        findings: [],
        generatedAt: /* @__PURE__ */ new Date()
      };
    } catch (error) {
      const err = error;
      Logger15.getLogger("DeepResearch").warn("[DeepResearch] Interactions API not available, using fallback", { message: err.message });
      return this.executeGroundingResearch(topic, context, focusAreas);
    }
  }
  /**
   * Execute synchronous research using Google Search Grounding
   * Returns results immediately (faster but less comprehensive)
   */
  async executeGroundingResearch(topic, context, focusAreas) {
    try {
      const response = await this.callGemini({
        task: "groundingResearch",
        data: {
          topic,
          context,
          focusAreas: focusAreas || this.defaultFocusAreas
        }
      });
      const data = response.data;
      const findings = this.parseReportFindings(data.report);
      return {
        id: `grounding-${Date.now()}`,
        topic,
        status: "completed",
        progress: 100,
        report: data.report,
        sources: data.sources,
        findings,
        thinkingSummaries: data.thinkingSummaries,
        generatedAt: /* @__PURE__ */ new Date(),
        durationMs: data.durationMs
      };
    } catch (error) {
      const err = error;
      Logger15.getLogger("DeepResearch").error("[DeepResearch] Grounding fallback failed", new Error(err.message));
      throw new Error(`Research failed: ${err.message}`);
    }
  }
  /**
   * Get the status of an ongoing research
   */
  async getResearchStatus(interactionId) {
    try {
      const response = await this.callGemini({
        task: "getDeepResearchStatus",
        data: { interactionId }
      });
      const data = response.data;
      let status = "searching";
      let progress = 0;
      switch (data.status) {
        case "queued":
          status = "queued";
          progress = 10;
          break;
        case "in_progress":
          status = "analyzing";
          progress = 50;
          break;
        case "completed":
          status = "completed";
          progress = 100;
          break;
        case "failed":
          status = "failed";
          progress = 0;
          break;
      }
      if (data.result) {
        const findings = this.parseReportFindings(data.result.report);
        return {
          id: interactionId,
          topic: "",
          status: "completed",
          progress: 100,
          report: data.result.report,
          sources: data.result.sources,
          findings,
          thinkingSummaries: data.result.thinkingSummaries,
          generatedAt: new Date(data.result.generatedAt),
          durationMs: data.result.durationMs
        };
      }
      return {
        id: interactionId,
        topic: "",
        status,
        progress,
        report: "",
        sources: [],
        findings: [],
        generatedAt: /* @__PURE__ */ new Date(),
        error: data.error
      };
    } catch (error) {
      const err = error;
      Logger15.getLogger("DeepResearch").error("[DeepResearch] Status check failed", new Error(err.message));
      throw new Error(`Failed to get research status: ${err.message}`);
    }
  }
  /**
   * Poll for research completion with progress callbacks
   */
  async pollForCompletion(interactionId, onProgress) {
    let attempts = 0;
    while (attempts < this.maxPollingAttempts) {
      attempts++;
      try {
        const result = await this.getResearchStatus(interactionId);
        onProgress(result);
        if (result.status === "completed" || result.status === "failed") {
          return result;
        }
        await this.sleep(this.pollingIntervalMs);
      } catch (error) {
        const err = error;
        Logger15.getLogger("DeepResearch").error("[DeepResearch] Poll attempt failed", new Error(err.message));
        if (attempts >= this.maxPollingAttempts) {
          throw error;
        }
        await this.sleep(this.pollingIntervalMs * 2);
      }
    }
    throw new Error("Research timed out after maximum attempts");
  }
  /**
   * Parse a research report into structured findings
   */
  parseReportFindings(report) {
    const findings = [];
    const trendMatches = report.match(/(?:Tendencia|Trend)[s]?:?\s*([^\n]+)/gi);
    if (trendMatches) {
      trendMatches.forEach((match) => {
        findings.push({
          category: "trend",
          content: match.replace(/^(?:Tendencia|Trend)[s]?:?\s*/i, ""),
          relevance: 85
        });
      });
    }
    const bulletPoints = report.match(/^[\-\•\*]\s+(.+)$/gm);
    if (bulletPoints) {
      bulletPoints.slice(0, 8).forEach((point) => {
        const content = point.replace(/^[\-\•\*]\s+/, "");
        let category = "technical";
        if (content.match(/oportunidad|opportunity|crecimiento|growth/i)) {
          category = "opportunity";
        } else if (content.match(/desafío|challenge|problema|issue/i)) {
          category = "pain_point";
        } else if (content.match(/tendencia|trend|emerge|future/i)) {
          category = "trend";
        } else if (content.match(/competidor|competitor|rival/i)) {
          category = "competitor";
        }
        findings.push({ category, content, relevance: 75 });
      });
    }
    return findings;
  }
  sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
};
function createDeepResearchService(callGemini, config) {
  return new DeepResearchService({
    callGemini,
    ...config
  });
}
var DEFAULT_RESEARCH_FOCUS_AREAS = {
  market: ["Tendencias de mercado", "Tama\xF1o del mercado", "Predicciones"],
  competitors: ["An\xE1lisis de competencia", "Cuota de mercado", "Estrategias"],
  technical: ["Especificaciones t\xE9cnicas", "Est\xE1ndares", "Mejores pr\xE1cticas"],
  opportunities: ["Oportunidades de negocio", "Nichos sin explotar", "Gaps"],
  general: ["Visi\xF3n general", "Tendencias", "Competidores", "Oportunidades"]
};

// src/server/deep-research-client.ts
import { Logger as Logger16 } from "@boosting/core";
var DeepResearchClient = class {
  apiKey;
  agent;
  systemInstruction;
  logger;
  client = null;
  constructor(config) {
    if (!config.apiKey) {
      throw new Error("DeepResearchClient requires a GEMINI_API_KEY");
    }
    this.apiKey = config.apiKey;
    this.agent = config.agent || "deep-research-pro-preview-12-2025";
    this.systemInstruction = config.systemInstruction || "Eres un analista de investigaci\xF3n experto. Responde siempre en espa\xF1ol. S\xE9 exhaustivo pero conciso.";
    this.logger = config.logger || {
      info: (...args) => Logger16.getLogger("DeepResearch").info("[DeepResearch]", ...args),
      error: (...args) => Logger16.getLogger("DeepResearch").error("[DeepResearch]", ...args)
    };
  }
  /**
   * Initialize the GenAI client (lazy loaded)
   */
  async getClient() {
    if (!this.client) {
      const { GoogleGenAI } = await import("@google/genai");
      this.client = new GoogleGenAI({ apiKey: this.apiKey });
    }
    return this.client;
  }
  /**
   * Start a new Deep Research investigation
   * 
   * This creates a background research task that can take several minutes.
   * Use getStatus() to poll for results.
   */
  async startResearch(request) {
    const { topic, context, focusAreas, systemInstruction } = request;
    this.logger.info("Starting Deep Research", { topic, hasFocusAreas: !!focusAreas?.length });
    let researchPrompt = `Investiga en profundidad el siguiente tema:

**${topic}**`;
    if (context) {
      researchPrompt += `

**Contexto adicional:**
${context}`;
    }
    if (focusAreas && focusAreas.length > 0) {
      researchPrompt += `

**\xC1reas de enfoque:**
${focusAreas.map((a) => `- ${a}`).join("\n")}`;
    }
    researchPrompt += `

**Requisitos del informe:**
- Proporciona un an\xE1lisis exhaustivo con datos verificables
- Incluye estad\xEDsticas y tendencias actuales cuando sea relevante
- Identifica actores clave, competidores o expertos en el tema
- Se\xF1ala oportunidades y desaf\xEDos
- Cita todas las fuentes utilizadas
- Estructura el informe con secciones claras`;
    try {
      const client = await this.getClient();
      const interactions = client.interactions;
      if (!interactions) {
        throw new Error("Interactions API not available. Make sure you are using the latest @google/genai SDK.");
      }
      const interaction = await interactions.create({
        agent: this.agent,
        input: researchPrompt,
        background: true,
        stream: false,
        system_instruction: systemInstruction || this.systemInstruction
      });
      this.logger.info("Deep Research started", {
        interactionId: interaction.id,
        status: interaction.status
      });
      return {
        interactionId: interaction.id,
        status: interaction.status,
        createdAt: interaction.created
      };
    } catch (error) {
      this.logger.error("Failed to start Deep Research", { error: error.message });
      throw error;
    }
  }
  /**
   * Start research with streaming for real-time thinking updates
   * 
   * This is like the Gemini Advanced UI - you can see the agent's
   * thinking process in real-time as it searches and reasons.
   * 
   * @example
   * ```typescript
   * await client.startResearchWithStreaming(
   *     { topic: 'B2B SaaS trends' },
   *     {
   *         onStart: (id) => console.log('Started:', id),
   *         onThought: (thought) => console.log('Thinking:', thought),
   *         onTextDelta: (text) => process.stdout.write(text),
   *         onComplete: (result) => console.log('Done! Sources:', result.sources?.length),
   *     }
   * );
   * ```
   */
  async startResearchWithStreaming(request, callbacks) {
    const { topic, context, focusAreas, systemInstruction } = request;
    this.logger.info("Starting Deep Research with streaming", { topic });
    let researchPrompt = `Investiga en profundidad el siguiente tema:

**${topic}**`;
    if (context) {
      researchPrompt += `

**Contexto adicional:**
${context}`;
    }
    if (focusAreas && focusAreas.length > 0) {
      researchPrompt += `

**\xC1reas de enfoque:**
${focusAreas.map((a) => `- ${a}`).join("\n")}`;
    }
    researchPrompt += `

**Requisitos del informe:**
- Proporciona un an\xE1lisis exhaustivo con datos verificables
- Incluye estad\xEDsticas y tendencias actuales cuando sea relevante
- Identifica actores clave, competidores o expertos en el tema
- Se\xF1ala oportunidades y desaf\xEDos
- Cita todas las fuentes utilizadas
- Estructura el informe con secciones claras`;
    try {
      const client = await this.getClient();
      const interactions = client.interactions;
      if (!interactions) {
        throw new Error("Interactions API not available");
      }
      const stream = await interactions.create({
        agent: this.agent,
        input: researchPrompt,
        background: true,
        stream: true,
        // Enable streaming!
        agent_config: {
          type: "deep-research",
          thinking_summaries: "auto"
          // Include thinking summaries
        },
        system_instruction: systemInstruction || this.systemInstruction
      });
      let interactionId = "";
      let fullText = "";
      const thoughts = [];
      for await (const chunk of stream) {
        if (chunk.event_type === "interaction.start") {
          interactionId = chunk.interaction.id;
          if (callbacks.onStart) {
            callbacks.onStart(interactionId);
          }
        }
        if (chunk.event_type === "content.delta") {
          if (chunk.delta?.type === "text") {
            const text = chunk.delta.text || "";
            fullText += text;
            if (callbacks.onTextDelta) {
              callbacks.onTextDelta(text);
            }
          } else if (chunk.delta?.type === "thought_summary") {
            const thought = chunk.delta.content?.text || "";
            thoughts.push(thought);
            if (callbacks.onThought) {
              callbacks.onThought(thought);
            }
          }
        }
        if (chunk.event_type === "interaction.complete") {
          this.logger.info("Streaming research completed", { interactionId });
        }
      }
      const finalStatus = await this.getStatus(interactionId);
      finalStatus.thinkingSummaries = thoughts;
      if (callbacks.onComplete) {
        callbacks.onComplete(finalStatus);
      }
      return finalStatus;
    } catch (error) {
      this.logger.error("Streaming research failed", { error: error.message });
      if (callbacks.onError) {
        callbacks.onError(error);
      }
      throw error;
    }
  }
  /**
   * Get the status of a research task
   */
  async getStatus(interactionId) {
    try {
      const client = await this.getClient();
      const interactions = client.interactions;
      const interaction = await interactions.get(interactionId);
      let progress = 0;
      let report;
      let sources = [];
      let thinkingSummaries = [];
      switch (interaction.status) {
        case "queued":
          progress = 5;
          break;
        case "in_progress":
          progress = 50;
          break;
        case "completed":
          progress = 100;
          if (interaction.outputs && interaction.outputs.length > 0) {
            const lastOutput = interaction.outputs[interaction.outputs.length - 1];
            report = lastOutput.text || lastOutput.content;
            if (lastOutput.annotations && Array.isArray(lastOutput.annotations)) {
              const seenUrls = /* @__PURE__ */ new Set();
              sources = lastOutput.annotations.filter((a) => a.source && !seenUrls.has(a.source)).map((a) => {
                seenUrls.add(a.source);
                return {
                  url: a.source,
                  // Extract title from URL if possible
                  title: this.extractTitleFromUrl(a.source),
                  startIndex: a.start_index,
                  endIndex: a.end_index
                };
              });
            }
          }
          if (sources.length === 0 && interaction.grounding_metadata?.grounding_chunks) {
            sources = interaction.grounding_metadata.grounding_chunks.filter((c) => c.web?.uri).map((c) => ({
              url: c.web.uri,
              title: c.web.title || c.web.domain || "Unknown"
            }));
          }
          if (interaction.thinking_summaries) {
            thinkingSummaries = interaction.thinking_summaries;
          }
          break;
        case "failed":
          progress = 0;
          break;
      }
      return {
        interactionId: interaction.id,
        status: interaction.status,
        progress,
        report,
        sources,
        thinkingSummaries,
        error: interaction.error,
        updatedAt: interaction.updated,
        // Include usage info if available
        ...interaction.usage && {
          usage: {
            inputTokens: interaction.usage.total_input_tokens,
            outputTokens: interaction.usage.total_output_tokens,
            thoughtTokens: interaction.usage.total_thought_tokens,
            totalTokens: interaction.usage.total_tokens
          }
        }
      };
    } catch (error) {
      this.logger.error("Failed to get research status", { interactionId, error: error.message });
      throw error;
    }
  }
  /**
   * Extract a readable title from a Google grounding redirect URL
   */
  extractTitleFromUrl(url) {
    try {
      if (url.includes("vertexaisearch.cloud.google.com")) {
        return "Web Source";
      }
      const urlObj = new URL(url);
      return urlObj.hostname.replace("www.", "");
    } catch {
      return "Unknown Source";
    }
  }
  /**
   * Poll for research results until completion or timeout
   * 
   * @param interactionId - The interaction ID from startResearch
   * @param options - Polling options
   * @returns The final research result
   */
  async waitForCompletion(interactionId, options) {
    const pollInterval = options?.pollIntervalMs || 1e4;
    const maxWait = options?.maxWaitMs || 6e5;
    const startTime = Date.now();
    this.logger.info("Waiting for research completion", { interactionId, pollInterval, maxWait });
    while (Date.now() - startTime < maxWait) {
      const status = await this.getStatus(interactionId);
      if (options?.onProgress) {
        options.onProgress(status);
      }
      if (status.status === "completed") {
        this.logger.info("Research completed", {
          interactionId,
          reportLength: status.report?.length,
          sourcesCount: status.sources?.length
        });
        return status;
      }
      if (status.status === "failed") {
        this.logger.error("Research failed", { interactionId, error: status.error });
        throw new Error(`Deep Research failed: ${status.error}`);
      }
      await new Promise((resolve) => setTimeout(resolve, pollInterval));
    }
    throw new Error(`Deep Research timed out after ${maxWait}ms`);
  }
};
function createDeepResearchClient(config) {
  return new DeepResearchClient(config);
}

// src/document-processing/document-processing.service.ts
import { Logger as Logger17 } from "@boosting/core";
var logger13 = Logger17.getLogger("DocumentProcessingService");
var DEFAULT_DOCUMENT_PROCESSING_CONFIG = {
  doclingUrl: process.env.DOCLING_URL || "http://localhost:5001",
  doclingApiKey: process.env.DOCLING_API_KEY,
  ocrEngine: "easyocr",
  ocrLanguages: ["es", "en", "de", "fr"],
  forceOCR: false,
  accurateTables: true,
  includeImages: true,
  pipeline: "standard",
  pdfBackend: "dlparse_v2",
  timeout: 3e5
};
var PROCESSING_PRESETS = {
  /** Para fichas técnicas / datasheets - máxima extracción de tablas */
  datasheet: {
    ocrEngine: "easyocr",
    // easyocr para mejor multilenguaje
    accurateTables: true,
    includeImages: true,
    pipeline: "standard",
    forceOCR: false
  },
  /** Para facturas - OCR forzado, sin imágenes */
  invoice: {
    ocrEngine: "easyocr",
    accurateTables: true,
    includeImages: false,
    pipeline: "standard",
    forceOCR: true
  },
  /** Para documentos escaneados - VLM para mejor comprensión */
  scanned: {
    ocrEngine: "easyocr",
    // easyocr por compatibilidad
    accurateTables: true,
    includeImages: true,
    pipeline: "vlm",
    // VLM para scans complejos
    forceOCR: true
  },
  /** Procesamiento rápido - modo legacy, sin imágenes */
  quick: {
    ocrEngine: "rapidocr",
    // rapidocr es el más rápido
    accurateTables: false,
    includeImages: false,
    pipeline: "legacy",
    // legacy es más simple
    forceOCR: false
  }
};
var FetchHttpClient = class {
  constructor(timeout = 6e4) {
    this.timeout = timeout;
  }
  async post(url, body, headers) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...headers },
        body: JSON.stringify(body),
        signal: controller.signal
      });
      if (!response.ok) {
        let errorDetail = response.statusText || "unknown";
        try {
          const errorBody = await response.text();
          if (errorBody) {
            errorDetail = errorBody.substring(0, 500);
          }
        } catch {
        }
        throw new Error(`HTTP error: ${response.status} - ${errorDetail}`);
      }
      return await response.json();
    } finally {
      clearTimeout(timeoutId);
    }
  }
  async get(url, headers) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);
    try {
      const response = await fetch(url, {
        method: "GET",
        headers,
        signal: controller.signal
      });
      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status} ${response.statusText}`);
      }
      return await response.json();
    } finally {
      clearTimeout(timeoutId);
    }
  }
};
var DocumentProcessingService = class _DocumentProcessingService {
  config;
  httpClient;
  constructor(config, httpClient) {
    this.config = { ...DEFAULT_DOCUMENT_PROCESSING_CONFIG, ...config };
    this.httpClient = httpClient || new FetchHttpClient(this.config.timeout);
  }
  async processFromUrl(documentUrl, options) {
    const startTime = Date.now();
    logger13.info("Processing document from URL", { url: documentUrl });
    const mergedOptions = this.mergeOptions(options);
    try {
      const response = await this.callDocling({
        sources: [{ kind: "http", url: documentUrl }],
        options: this.buildDoclingOptions(mergedOptions)
      });
      const document = this.parseResponse(response, documentUrl, startTime, mergedOptions);
      logger13.info("Document processed", { filename: document.filename, pages: document.pageCount });
      return document;
    } catch (err) {
      logger13.error("Document processing failed", err, { source: documentUrl });
      throw err;
    }
  }
  async processFromBuffer(content, filename, options) {
    const startTime = Date.now();
    logger13.info("Processing document from buffer", { filename });
    const mergedOptions = this.mergeOptions(options);
    const base64Content = Buffer.isBuffer(content) ? content.toString("base64") : content;
    try {
      const response = await this.callDocling({
        sources: [{
          kind: "file",
          filename,
          base64_string: base64Content
        }],
        options: this.buildDoclingOptions(mergedOptions)
      });
      const document = this.parseResponse(response, filename, startTime, mergedOptions);
      logger13.info("Document processed", { filename: document.filename, pages: document.pageCount });
      return document;
    } catch (err) {
      logger13.error("Document processing failed", err, { source: filename });
      throw err;
    }
  }
  withPreset(preset) {
    return new _DocumentProcessingService(
      { ...this.config, ...PROCESSING_PRESETS[preset] },
      this.httpClient
    );
  }
  extractSpecifications(markdown) {
    const specs = [];
    const pattern = /^([A-Za-z][A-Za-z0-9\s\-\/]+)[:=]\s*([^\n]+)/gm;
    let match;
    while ((match = pattern.exec(markdown)) !== null) {
      const name = match[1].trim();
      const rawValue = match[2].trim();
      if (!name || !rawValue || name.length > 50) continue;
      const unitMatch = rawValue.match(/^([\d.,]+)\s*([A-Za-z°%/²³]+)$/);
      specs.push({ name, value: unitMatch ? unitMatch[1] : rawValue, unit: unitMatch?.[2] });
    }
    return this.deduplicateSpecs(specs);
  }
  // ========================================
  // PRIVATE METHODS
  // ========================================
  mergeOptions(options) {
    return {
      forceOCR: options?.forceOCR ?? this.config.forceOCR,
      languages: options?.languages ?? this.config.ocrLanguages,
      pageRange: options?.pageRange,
      ocrEngine: options?.ocrEngine ?? this.config.ocrEngine,
      pipeline: options?.pipeline ?? this.config.pipeline,
      accurateTables: this.config.accurateTables,
      includeImages: this.config.includeImages
    };
  }
  buildDoclingOptions(opts) {
    const options = {
      to_formats: ["md", "json"],
      do_ocr: true,
      force_ocr: opts.forceOCR,
      ocr_engine: opts.ocrEngine,
      ocr_lang: opts.languages,
      pdf_backend: this.config.pdfBackend,
      pipeline: opts.pipeline,
      do_table_structure: true,
      table_mode: opts.accurateTables ? "accurate" : "fast",
      include_images: opts.includeImages,
      images_scale: 2,
      image_export_mode: "embedded",
      abort_on_error: false,
      document_timeout: Math.floor(this.config.timeout / 1e3)
    };
    logger13.info("Built Docling options", {
      includeImages: opts.includeImages,
      accurateTables: opts.accurateTables,
      pipeline: opts.pipeline,
      imageExportMode: "embedded"
    });
    return options;
  }
  async callDocling(request) {
    const headers = {};
    if (this.config.doclingApiKey) {
      headers["X-Api-Key"] = this.config.doclingApiKey;
    }
    logger13.info("Calling Docling sync endpoint", {
      url: this.config.doclingUrl,
      timeout: this.config.timeout
    });
    const result = await this.httpClient.post(
      `${this.config.doclingUrl}/v1/convert/source`,
      request,
      headers
    );
    logger13.info("Docling processing complete", {
      hasDocument: !!result.document,
      mdContentLength: result.document?.md_content?.length || 0
    });
    return result;
  }
  async pollForResult(taskId, headers) {
    const maxRetries = 30;
    const waitSeconds = 30;
    for (let i = 0; i < maxRetries; i++) {
      const statusResponse = await this.httpClient.get(`${this.config.doclingUrl}/v1/status/poll/${taskId}?wait=${waitSeconds}`, headers);
      logger13.info("Polling task status", {
        taskId,
        status: statusResponse.task_status,
        poll: i + 1,
        maxRetries
      });
      if (statusResponse.task_status === "success") {
        const result = await this.httpClient.get(
          `${this.config.doclingUrl}/v1/result/${taskId}`,
          headers
        );
        return result;
      } else if (statusResponse.task_status === "failure") {
        let errorDetail = "";
        try {
          const errorResult = await this.httpClient.get(
            `${this.config.doclingUrl}/v1/result/${taskId}`,
            headers
          );
          if (errorResult.errors?.length) {
            errorDetail = `: ${errorResult.errors.join(", ")}`;
          }
        } catch {
        }
        throw new Error(`Docling processing failed for task ${taskId}${errorDetail}`);
      }
    }
    throw new Error(`Docling processing timeout after ${maxRetries * waitSeconds} seconds`);
  }
  parseResponse(response, source, startTime, options) {
    const doc = response.document || {};
    const markdown = doc.md_content || "";
    let pageCount = 1;
    if (doc.json_content) {
      const jsonContent = typeof doc.json_content === "string" ? JSON.parse(doc.json_content) : doc.json_content;
      if (jsonContent.pages && typeof jsonContent.pages === "object") {
        const pageKeys = Object.keys(jsonContent.pages);
        pageCount = pageKeys.length || 1;
        logger13.info("Extracted page count from json_content", { pageCount });
      }
    } else if (typeof doc.pages === "number") {
      pageCount = doc.pages;
    }
    return {
      id: `doc_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
      filename: source.split("/").pop() || "document",
      mimeType: source.endsWith(".pdf") ? "application/pdf" : "application/octet-stream",
      pageCount,
      markdown,
      plainText: this.stripMarkdown(markdown),
      sections: this.extractSections(markdown),
      tables: (doc.tables || []).map((t, i) => ({
        id: t.id || `table_${i}`,
        pageNumber: t.page || 1,
        headers: t.headers,
        rows: t.data,
        title: t.title,
        confidence: 0.9
      })),
      // Extract images from doc.images (only those with base64) + embedded base64 in markdown
      // Combine both sources to ensure we capture all images
      images: this.extractAllImages(doc.images, markdown),
      specifications: this.extractSpecifications(markdown),
      metadata: {
        title: doc.metadata?.title,
        author: doc.metadata?.author,
        createdAt: doc.metadata?.created ? new Date(doc.metadata.created) : void 0,
        modifiedAt: doc.metadata?.modified ? new Date(doc.metadata.modified) : void 0,
        language: doc.metadata?.language
      },
      processing: {
        ocrEngine: options.ocrEngine,
        pipeline: options.pipeline,
        duration: Date.now() - startTime,
        ocrApplied: options.forceOCR,
        timestamp: /* @__PURE__ */ new Date()
      }
    };
  }
  extractSections(markdown) {
    const sections = [];
    const lines = markdown.split("\n");
    let current = null;
    let content = [];
    let count = 0;
    for (const line of lines) {
      const match = line.match(/^(#{1,6})\s+(.+)$/);
      if (match) {
        if (current?.title) {
          current.content = content.join("\n").trim();
          sections.push(current);
        }
        count++;
        current = { id: `section_${count}`, title: match[2].trim(), level: match[1].length, pageStart: 1, pageEnd: 1 };
        content = [];
      } else if (current) {
        content.push(line);
      }
    }
    if (current?.title) {
      current.content = content.join("\n").trim();
      sections.push(current);
    }
    return sections;
  }
  stripMarkdown(markdown) {
    return markdown.replace(/#{1,6}\s+/g, "").replace(/\*\*([^*]+)\*\*/g, "$1").replace(/\*([^*]+)\*/g, "$1").replace(/\[([^\]]+)\]\([^)]+\)/g, "$1").replace(/`([^`]+)`/g, "$1").replace(/!\[([^\]]*)\]\([^)]+\)/g, "").replace(/\|[^|\n]+\|/g, "").replace(/-{3,}/g, "").trim();
  }
  normalizeImageType(type) {
    if (!type) return "other";
    const t = type.toLowerCase();
    if (t.includes("diagram")) return "diagram";
    if (t.includes("photo") || t.includes("picture")) return "photo";
    if (t.includes("chart") || t.includes("graph")) return "chart";
    if (t.includes("logo")) return "logo";
    if (t.includes("icon")) return "icon";
    return "other";
  }
  deduplicateSpecs(specs) {
    const seen = /* @__PURE__ */ new Map();
    for (const spec of specs) {
      const key = spec.name.toLowerCase();
      if (!seen.has(key)) seen.set(key, spec);
    }
    return Array.from(seen.values());
  }
  /**
   * Extract all images from both doc.images and embedded markdown
   * Prioritizes doc.images if they have base64, then supplements with markdown images
   */
  extractAllImages(docImages, markdown) {
    const images = [];
    const seenBase64Hashes = /* @__PURE__ */ new Set();
    if (docImages && docImages.length > 0) {
      for (let i = 0; i < docImages.length; i++) {
        const img = docImages[i];
        if (img.base64) {
          const hash = img.base64.substring(0, 100);
          if (!seenBase64Hashes.has(hash)) {
            seenBase64Hashes.add(hash);
            images.push({
              id: img.id || `image_${i}`,
              pageNumber: img.page || 1,
              type: this.normalizeImageType(img.type),
              description: img.description,
              base64: img.base64,
              dimensions: img.width && img.height ? { width: img.width, height: img.height } : void 0
            });
          }
        }
      }
    }
    const markdownImages = this.extractImagesFromMarkdown(markdown);
    for (const mdImg of markdownImages) {
      if (mdImg.base64) {
        const hash = mdImg.base64.substring(0, 100);
        if (!seenBase64Hashes.has(hash)) {
          seenBase64Hashes.add(hash);
          images.push({
            ...mdImg,
            id: `image_${images.length}`
          });
        }
      }
    }
    if (images.length > 0) {
      logger13.info("Extracted all images", {
        fromDocImages: docImages?.filter((i) => !!i.base64).length || 0,
        fromMarkdown: markdownImages.length,
        totalUnique: images.length
      });
    }
    return images;
  }
  /**
   * Extract base64 images embedded in markdown
   * Docling embeds images as: ![Image](data:image/png;base64,...)
   */
  extractImagesFromMarkdown(markdown) {
    const images = [];
    const regex = /!\[([^\]]*)\]\((data:image\/([^;]+);base64,([^)]+))\)/g;
    let match;
    let count = 0;
    while ((match = regex.exec(markdown)) !== null && count < 50) {
      const [, altText, fullDataUrl, mimeSubtype, base64Data] = match;
      images.push({
        id: `image_${count}`,
        pageNumber: 1,
        // Docling doesn't provide page info in embedded images
        type: this.guessImageType(altText, base64Data),
        description: altText || void 0,
        base64: base64Data
      });
      count++;
    }
    if (images.length > 0) {
      logger13.info("Extracted images from markdown", { count: images.length });
    }
    return images;
  }
  /**
   * Guess image type based on alt text or content
   */
  guessImageType(altText, _base64) {
    const alt = (altText || "").toLowerCase();
    if (alt.includes("logo")) return "logo";
    if (alt.includes("chart") || alt.includes("graph")) return "chart";
    if (alt.includes("diagram") || alt.includes("flow")) return "diagram";
    if (alt.includes("photo") || alt.includes("picture")) return "photo";
    if (alt.includes("icon")) return "icon";
    return "other";
  }
};
function createDocumentProcessingService(config, httpClient) {
  return new DocumentProcessingService(config, httpClient);
}

// src/table-refinement/table-refinement.service.ts
import { Logger as Logger18 } from "@boosting/core";

// src/table-refinement/table-refinement.types.ts
var DEFAULT_TABLE_REFINEMENT_CONFIG = {
  model: "gemini-3.1-pro-preview",
  temperature: 0.1,
  maxOutputTokens: 8192,
  timeout: 6e4,
  debug: false,
  minConfidenceThreshold: 0.7,
  maxCellsPerTable: 500
};

// src/table-refinement/table-refinement.service.ts
var logger14 = Logger18.getLogger("TableRefinementService");
var TABLE_REFINEMENT_SYSTEM_PROMPT = `Eres un experto en an\xE1lisis de tablas de datasheets t\xE9cnicos de componentes electr\xF3nicos.

Tu tarea es comparar una tabla extra\xEDda autom\xE1ticamente (puede contener errores de OCR) con la imagen original de esa tabla, y producir una versi\xF3n corregida y mejorada.

INSTRUCCIONES:
1. Analiza cuidadosamente la imagen de la tabla
2. Compara cada celda con el valor extra\xEDdo autom\xE1ticamente
3. Corrige errores de OCR (ej: "0" confundido con "O", "1" con "l", etc.)
4. Detecta celdas vac\xEDas que deber\xEDan tener valor
5. Identifica el tipo correcto de tabla (specs, pinout, electrical, mechanical, pricing, dimensions)
6. Detecta headers si no fueron identificados correctamente

FORMATO DE RESPUESTA (JSON estricto):
{
  "refinedTable": {
    "headers": ["Header1", "Header2", ...],
    "rows": [
      [{"value": "celda1", "confidence": 0.95, "wasRefined": false}, ...],
      ...
    ],
    "tableType": "specs|pinout|electrical|mechanical|pricing|dimensions|other",
    "confidence": 0.0-1.0
  },
  "corrections": [
    {
      "type": "ocr_error|missing_cell|wrong_alignment|truncated_text|merged_cells|header_detection",
      "row": 0,
      "col": 0,
      "original": "valor original",
      "corrected": "valor corregido",
      "confidence": 0.9
    }
  ],
  "summary": "Breve descripci\xF3n de las correcciones aplicadas"
}

REGLAS:
- Si el valor extra\xEDdo es correcto, pon wasRefined: false
- Confidence de celda: 1.0 si est\xE1s seguro, menor si hay incertidumbre
- Solo reporta corrections para celdas que realmente modificaste
- Si la tabla est\xE1 bien extra\xEDda, devuelve la misma con wasRefined: false en todas las celdas`;
var TableRefinementService = class {
  config;
  geminiClient;
  constructor(geminiClient, config) {
    this.geminiClient = geminiClient;
    this.config = { ...DEFAULT_TABLE_REFINEMENT_CONFIG, ...config };
  }
  /**
   * Refina una tabla usando Gemini Vision
   */
  async refineTable(request) {
    const startTime = Date.now();
    logger14.info("Starting table refinement", {
      tableId: request.tableId,
      originalRows: request.extractedTable.rows.length,
      hasHeaders: !!request.extractedTable.headers?.length
    });
    try {
      const prompt = this.buildRefinementPrompt(request);
      const response = await this.geminiClient.generateWithImage({
        prompt,
        imageBase64: request.imageBase64,
        mimeType: request.mimeType,
        temperature: this.config.temperature,
        maxOutputTokens: this.config.maxOutputTokens || 8192
      });
      const parsed = this.parseResponse(response.text, request.extractedTable);
      const processingDurationMs = Date.now() - startTime;
      const metrics = this.calculateMetrics(request.extractedTable, parsed.refinedTable, parsed.corrections);
      logger14.info("Table refinement complete", {
        tableId: request.tableId,
        cellsModified: metrics.cellsModified,
        improvementPercentage: metrics.improvementPercentage,
        durationMs: processingDurationMs
      });
      return {
        refinedTable: {
          id: request.tableId,
          pageNumber: request.extractedTable.pageNumber,
          ...parsed.refinedTable,
          refinementApplied: metrics.cellsModified > 0
        },
        metrics,
        modelUsed: response.model,
        processingDurationMs
      };
    } catch (error) {
      logger14.error("Table refinement failed", error, { tableId: request.tableId });
      return this.createFallbackResponse(request, Date.now() - startTime, error);
    }
  }
  /**
   * Refina múltiples tablas en paralelo (con límite de concurrencia)
   */
  async refineTables(requests, concurrency = 3) {
    const results = [];
    for (let i = 0; i < requests.length; i += concurrency) {
      const batch = requests.slice(i, i + concurrency);
      const batchResults = await Promise.all(batch.map((req) => this.refineTable(req)));
      results.push(...batchResults);
    }
    return results;
  }
  // ========================================
  // PRIVATE METHODS
  // ========================================
  buildRefinementPrompt(request) {
    const { extractedTable, documentContext } = request;
    const tableJson = JSON.stringify({
      headers: extractedTable.headers || [],
      rows: extractedTable.rows,
      detectedType: extractedTable.tableType || "unknown"
    }, null, 2);
    let contextInfo = "";
    if (documentContext) {
      contextInfo = `
CONTEXTO DEL DOCUMENTO:
- Archivo: ${documentContext.filename || "desconocido"}
- P\xE1ginas: ${documentContext.pageCount || "desconocido"}
- Tipo de producto: ${documentContext.productType || "componente electr\xF3nico"}
`;
    }
    return `${TABLE_REFINEMENT_SYSTEM_PROMPT}

${contextInfo}

TABLA EXTRA\xCDDA AUTOM\xC1TICAMENTE (puede contener errores):
\`\`\`json
${tableJson}
\`\`\`

Por favor, analiza la imagen de la tabla y proporciona la versi\xF3n corregida en formato JSON:`;
  }
  parseResponse(responseText, originalTable) {
    try {
      let cleanJson = responseText.trim();
      cleanJson = cleanJson.replace(/```json\s*/gi, "").replace(/```\s*/gi, "");
      const jsonMatch = cleanJson.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        throw new Error("No JSON found in response");
      }
      const parsed = JSON.parse(jsonMatch[0]);
      const rows = (parsed.refinedTable?.rows || []).map(
        (row) => row.map((cell) => {
          if (typeof cell === "object" && cell !== null) {
            const cellObj = cell;
            return {
              value: String(cellObj.value || ""),
              confidence: cellObj.confidence || 0.9,
              wasRefined: cellObj.wasRefined || false
            };
          }
          return { value: String(cell || ""), confidence: 0.9, wasRefined: false };
        })
      );
      const corrections = (parsed.corrections || []).map((c) => ({
        type: c.type || "ocr_error",
        cellLocation: c.row !== void 0 && c.col !== void 0 ? { row: c.row, col: c.col } : void 0,
        originalValue: c.original,
        correctedValue: c.corrected,
        confidence: c.confidence || 0.8
      }));
      return {
        refinedTable: {
          headers: parsed.refinedTable?.headers || originalTable.headers || [],
          rows,
          tableType: parsed.refinedTable?.tableType || originalTable.tableType || "other",
          confidence: parsed.refinedTable?.confidence || 0.8
        },
        corrections
      };
    } catch (parseError) {
      logger14.warn("Failed to parse Gemini response, using original", { error: parseError.message });
      return {
        refinedTable: {
          headers: originalTable.headers || [],
          rows: originalTable.rows.map(
            (row) => row.map((cell) => ({ value: cell, confidence: 0.7, wasRefined: false }))
          ),
          tableType: originalTable.tableType || "other",
          confidence: 0.5
        },
        corrections: []
      };
    }
  }
  calculateMetrics(original, refined, corrections) {
    const totalCells = refined.rows.reduce((sum, row) => sum + row.length, 0);
    const cellsModified = refined.rows.reduce(
      (sum, row) => sum + row.filter((cell) => cell.wasRefined).length,
      0
    );
    const originalConfidence = 0.7;
    const refinedConfidence = refined.confidence;
    const improvementPercentage = totalCells > 0 ? Math.round(cellsModified / totalCells * 100) : 0;
    const tableTypeChanged = original.tableType !== refined.tableType;
    const headersRefined = corrections.some((c) => c.type === "header_detection");
    return {
      totalCells,
      cellsModified,
      improvementPercentage,
      originalConfidence,
      refinedConfidence,
      errorsDetected: corrections,
      tableTypeChanged,
      headersRefined
    };
  }
  createFallbackResponse(request, durationMs, error) {
    const { extractedTable } = request;
    return {
      refinedTable: {
        id: request.tableId,
        pageNumber: extractedTable.pageNumber,
        headers: extractedTable.headers || [],
        rows: extractedTable.rows.map(
          (row) => row.map((cell) => ({ value: cell, confidence: 0.5, wasRefined: false }))
        ),
        tableType: extractedTable.tableType || "other",
        confidence: 0.5,
        refinementApplied: false
      },
      metrics: {
        totalCells: extractedTable.rows.reduce((sum, row) => sum + row.length, 0),
        cellsModified: 0,
        improvementPercentage: 0,
        originalConfidence: 0.7,
        refinedConfidence: 0.5,
        errorsDetected: [],
        tableTypeChanged: false,
        headersRefined: false
      },
      modelUsed: "fallback",
      processingDurationMs: durationMs
    };
  }
};
function createTableRefinementService(geminiClient, config) {
  return new TableRefinementService(geminiClient, config);
}

// src/server/gemini-client.ts
import { VertexAI } from "@google-cloud/vertexai";
import { Logger as Logger19 } from "@boosting/core";
var GEMINI3_MODELS2 = {
  /** Flash: Fast and economical, ideal for most tasks */
  FLASH: "gemini-3-flash-preview",
  /** Pro: Most powerful, ideal for complex reasoning (3.1 replaces deprecated 3-pro) */
  PRO: "gemini-3.1-pro-preview"
};
var LEGACY_MODELS2 = {
  FLASH_2_5: "gemini-2.5-flash",
  PRO_2_5: "gemini-2.5-pro"
};
var AVAILABLE_MODELS = {
  ...LEGACY_MODELS2
};
var EMBEDDING_MODELS = {
  TEXT_EMBEDDING_004: "text-embedding-004",
  MULTIMODAL: "multimodalembedding"
};
function isGemini3Model(modelName) {
  return modelName.startsWith("gemini-3");
}
function isModernGeminiModel(modelName) {
  return modelName.startsWith("gemini-3") || modelName.startsWith("gemini-2.5") || modelName.includes("2.5");
}
function normalizeModelName(modelName) {
  if (modelName.includes("/")) return modelName;
  const aliases = {
    "gemini-3-flash": "gemini-3-flash-preview",
    "gemini-3-pro": "gemini-3.1-pro-preview",
    // 3-pro redirects to 3.1
    "gemini-3.1-pro": "gemini-3.1-pro-preview",
    "gemini-3-pro-preview": "gemini-3.1-pro-preview",
    // deprecated March 9 → 3.1
    "gemini-2.0-flash": "gemini-3-flash-preview",
    // deprecated → 3-flash
    "gemini-2.0-flash-001": "gemini-3-flash-preview",
    // deprecated → 3-flash
    "gemini-2.0-flash-exp": "gemini-3-flash-preview",
    // deprecated → 3-flash
    "gemini-flash": "gemini-3-flash-preview",
    // deprecated → 3-flash
    "gemini-2.5-flash": "gemini-3-flash-preview",
    // deprecated → 3-flash
    "gemini-pro": "gemini-2.5-pro"
  };
  return aliases[modelName] || modelName;
}
var MAX_RETRIES = 3;
var BASE_RETRY_DELAY_MS = 2e3;
function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
var GeminiClient = class {
  config;
  vertexAIClient = null;
  gemini3Client = null;
  logger;
  constructor(config, logger19) {
    this.config = {
      projectId: config.projectId,
      legacyLocation: config.legacyLocation || "us-central1",
      gemini3Location: config.gemini3Location || "global"
    };
    const defaultLogger = Logger19.getLogger("GeminiClient");
    this.logger = logger19 || {
      info: (...args) => defaultLogger.info(String(args[0]), args.length > 1 ? args[1] : void 0),
      error: (...args) => defaultLogger.error(String(args[0]), args.length > 1 ? args[1] : void 0)
    };
  }
  /**
   * Gets the VertexAI client for legacy models
   */
  getVertexAIClient() {
    if (!this.vertexAIClient) {
      this.vertexAIClient = new VertexAI({
        project: this.config.projectId,
        location: this.config.legacyLocation
      });
    }
    return this.vertexAIClient;
  }
  /**
   * Gets the Gemini 3 client (@google/genai)
   */
  async getGemini3Client() {
    if (!this.gemini3Client) {
      const { GoogleGenAI } = await import("@google/genai");
      this.gemini3Client = new GoogleGenAI({
        vertexai: true,
        project: this.config.projectId,
        location: this.config.gemini3Location
      });
    }
    return this.gemini3Client;
  }
  /**
   * Calls a Gemini model using the correct SDK based on version.
   * Includes automatic retry with exponential backoff for 429/RESOURCE_EXHAUSTED errors.
   */
  async generate(options) {
    const model = normalizeModelName(options.model);
    const fallbackModel = options.fallbackModel ? normalizeModelName(options.fallbackModel) : void 0;
    const startTime = Date.now();
    let lastError;
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        if (attempt > 0) {
          const delayMs = BASE_RETRY_DELAY_MS * Math.pow(2, attempt - 1);
          this.logger.info(
            `[GeminiClient] Retry attempt ${attempt}/${MAX_RETRIES} for ${model} after ${delayMs}ms backoff`
          );
          await sleep(delayMs);
        }
        return await this.callModel(model, options, startTime);
      } catch (error) {
        lastError = error;
        if (this.isRateLimitError(error) && attempt < MAX_RETRIES) {
          this.logger.info(
            `[GeminiClient] ${model} rate limited (attempt ${attempt + 1}/${MAX_RETRIES + 1}), will retry...`
          );
          continue;
        }
        if (fallbackModel && fallbackModel !== model && this.isRetryableError(error)) {
          this.logger.info(
            `[GeminiClient] ${model} failed (${this.extractErrorCode(error)}), falling back to ${fallbackModel}`
          );
          try {
            return await this.callModel(fallbackModel, options, Date.now());
          } catch (fallbackError) {
            this.logger.error(
              `[GeminiClient] Fallback ${fallbackModel} also failed after ${Date.now() - startTime}ms:`,
              fallbackError.message
            );
            throw fallbackError;
          }
        }
        break;
      }
    }
    if (fallbackModel && fallbackModel !== model && this.isRateLimitError(lastError)) {
      this.logger.info(
        `[GeminiClient] ${model} exhausted ${MAX_RETRIES} retries, last resort fallback to ${fallbackModel}`
      );
      try {
        return await this.callModel(fallbackModel, options, Date.now());
      } catch (fallbackError) {
        this.logger.error(
          `[GeminiClient] Fallback ${fallbackModel} also failed after ${Date.now() - startTime}ms:`,
          fallbackError.message
        );
        throw fallbackError;
      }
    }
    const durationMs = Date.now() - startTime;
    this.logger.error(`[GeminiClient] ${model} failed after ${durationMs}ms:`, lastError.message);
    throw lastError;
  }
  async callModel(model, options, startTime) {
    let result;
    if (isModernGeminiModel(model)) {
      result = await this.callGemini3(model, options);
    } else {
      result = await this.callVertexAI(model, options);
    }
    const durationMs = Date.now() - startTime;
    this.logger.info(`[GeminiClient] ${model} responded in ${durationMs}ms`);
    return {
      text: result.text,
      model,
      durationMs,
      groundingMetadata: result.groundingMetadata
    };
  }
  /**
   * Checks if an error is specifically a rate limit error (429 / RESOURCE_EXHAUSTED)
   */
  isRateLimitError(error) {
    const message = String(error?.message || "");
    return message.includes("429") || message.includes("RESOURCE_EXHAUSTED");
  }
  isRetryableError(error) {
    const message = String(error?.message || "");
    return message.includes("429") || message.includes("RESOURCE_EXHAUSTED") || message.includes("503") || message.includes("500") || message.includes("UNAVAILABLE") || message.includes("INTERNAL");
  }
  extractErrorCode(error) {
    const message = String(error?.message || "");
    if (message.includes("429") || message.includes("RESOURCE_EXHAUSTED")) return "429";
    if (message.includes("503") || message.includes("UNAVAILABLE")) return "503";
    if (message.includes("500") || message.includes("INTERNAL")) return "500";
    return "unknown";
  }
  /**
   * Gemini 3 implementation using @google/genai
   */
  async callGemini3(model, options) {
    const client = await this.getGemini3Client();
    const parts = [{ text: options.prompt }];
    if (options.images) {
      for (const img of options.images) {
        parts.push({ inlineData: { mimeType: img.mimeType || "image/jpeg", data: img.base64 } });
      }
    } else if (options.imageBase64) {
      parts.push({ inlineData: { mimeType: options.mimeType || "image/jpeg", data: options.imageBase64 } });
    }
    const config = {
      temperature: options.temperature ?? 0.7,
      maxOutputTokens: options.maxOutputTokens ?? 8e3
    };
    if (options.systemInstruction) {
      config.systemInstruction = options.systemInstruction;
    }
    if (options.tools && options.tools.length > 0) {
      config.tools = options.tools;
    }
    if (options.responseFormat === "json") {
      config.responseMimeType = "application/json";
    }
    if (options.responseSchema) {
      config.responseSchema = options.responseSchema;
      config.responseMimeType = "application/json";
    }
    const response = await client.models.generateContent({
      model,
      contents: [{ role: "user", parts }],
      config
    });
    const groundingMetadata = response.candidates?.[0]?.groundingMetadata;
    if (options.tools?.some((t) => t.googleSearch)) {
      this.logger.info(`[GeminiClient] Grounding response - has candidates: ${!!response.candidates}, candidate count: ${response.candidates?.length || 0}`);
      if (response.candidates?.[0]) {
        const candidateKeys = Object.keys(response.candidates[0]);
        this.logger.info(`[GeminiClient] Candidate keys: ${candidateKeys.join(", ")}`);
      }
      this.logger.info(`[GeminiClient] Grounding metadata exists: ${!!groundingMetadata}, chunks count: ${groundingMetadata?.groundingChunks?.length || 0}`);
    }
    return {
      text: response.text || "",
      groundingMetadata
    };
  }
  /**
   * Legacy VertexAI implementation for Gemini 2.x and earlier
   */
  async callVertexAI(model, options) {
    const client = this.getVertexAIClient();
    const generationConfig = {
      temperature: options.temperature ?? 0.7,
      maxOutputTokens: options.maxOutputTokens ?? 8e3
    };
    if (options.responseFormat === "json") {
      generationConfig.responseMimeType = "application/json";
    }
    if (options.responseSchema) {
      generationConfig.responseSchema = options.responseSchema;
      generationConfig.responseMimeType = "application/json";
    }
    const modelConfig = {
      model,
      generationConfig
    };
    if (options.systemInstruction) {
      modelConfig.systemInstruction = { parts: [{ text: options.systemInstruction }] };
    }
    if (options.tools && options.tools.length > 0) {
      modelConfig.tools = options.tools;
    }
    const generativeModel = client.getGenerativeModel(modelConfig);
    const parts = [{ text: options.prompt }];
    if (options.images) {
      for (const img of options.images) {
        parts.push({ inlineData: { mimeType: img.mimeType || "image/jpeg", data: img.base64 } });
      }
    } else if (options.imageBase64) {
      parts.push({ inlineData: { mimeType: options.mimeType || "image/jpeg", data: options.imageBase64 } });
    }
    const result = await generativeModel.generateContent({
      contents: [{ role: "user", parts }]
    });
    const response = result.response;
    return {
      text: response.candidates?.[0]?.content?.parts?.[0]?.text || "",
      groundingMetadata: response.candidates?.[0]?.groundingMetadata
    };
  }
  /**
   * Generate text embedding
   */
  async embed(text, model = EMBEDDING_MODELS.TEXT_EMBEDDING_004) {
    const startTime = Date.now();
    try {
      const client = await this.getGemini3Client();
      const response = await client.models.embedContent({
        model,
        contents: [{ parts: [{ text }] }]
      });
      const embedding = response.embeddings?.[0]?.values;
      const durationMs = Date.now() - startTime;
      this.logger.info(`[GeminiClient] Embedding generated in ${durationMs}ms`);
      if (!embedding) {
        throw new Error("No embedding returned");
      }
      return embedding;
    } catch (error) {
      const durationMs = Date.now() - startTime;
      this.logger.error(`[GeminiClient] Embedding failed after ${durationMs}ms:`, error.message);
      throw error;
    }
  }
  // ============================================
  // CONVENIENCE METHODS
  // ============================================
  /**
   * Generate text with Gemini 3 Flash Preview
   */
  async generateWithFlash(prompt, options) {
    const response = await this.generate({
      model: GEMINI3_MODELS2.FLASH,
      prompt,
      ...options
    });
    return response.text;
  }
  /**
   * Generate text with Gemini 3.1 Pro Preview
   */
  async generateWithPro(prompt, options) {
    const response = await this.generate({
      model: GEMINI3_MODELS2.PRO,
      prompt,
      ...options
    });
    return response.text;
  }
  /**
   * Generate JSON with Gemini 3 Flash Preview
   */
  async generateJsonWithFlash(prompt, options) {
    const response = await this.generate({
      model: GEMINI3_MODELS2.FLASH,
      prompt,
      responseFormat: "json",
      ...options
    });
    return JSON.parse(response.text);
  }
  /**
   * Generate content with Google Search grounding
   */
  async generateWithGrounding(prompt, options) {
    return this.generate({
      model: GEMINI3_MODELS2.FLASH,
      prompt,
      tools: [{ googleSearch: {} }],
      ...options
    });
  }
};
function createGeminiClient(config, logger19) {
  return new GeminiClient(config, logger19);
}

// src/server/models-config.ts
var DEFAULT_MODEL_CONFIGS = {
  // === AGENTES CENTRALIZADOS ===
  deepResearch: {
    primary: "gemini-3-flash-preview",
    // Flash con grounding
    fallback: "gemini-2.5-pro",
    temperature: 0.3,
    maxOutputTokens: 8192,
    requiresGrounding: true,
    description: "Investigaci\xF3n profunda con grounding de Google Search"
  },
  seoConsultant: {
    primary: "gemini-3.1-pro-preview",
    // PRO para mejor análisis SEO
    fallback: "gemini-3-flash-preview",
    temperature: 0.7,
    maxOutputTokens: 4096,
    description: "Consultor SEO conversacional"
  },
  contentGenerator: {
    primary: "gemini-3.1-pro-preview",
    // PRO para mejor calidad de contenido
    fallback: "gemini-3-flash-preview",
    temperature: 0.7,
    maxOutputTokens: 4096,
    description: "Generaci\xF3n de contenido gen\xE9rico"
  },
  // === PROCESAMIENTO DE DOCUMENTOS ===
  processPDF: {
    primary: "gemini-3.1-pro-preview",
    // PRO para mejor extracción de documentos
    fallback: "gemini-3-flash-preview",
    temperature: 0.3,
    maxOutputTokens: 8192,
    description: "Extracci\xF3n de texto de PDF"
  },
  processImageOCR: {
    primary: "gemini-3-flash-preview",
    // Flash suficiente para OCR simple
    fallback: "gemini-2.5-pro",
    temperature: 0.1,
    maxOutputTokens: 4096,
    description: "OCR de im\xE1genes"
  },
  transcribeAudio: {
    primary: "gemini-3.1-pro-preview",
    // PRO para mejor transcripción
    fallback: "gemini-3-flash-preview",
    temperature: 0.3,
    maxOutputTokens: 8192,
    description: "Transcripci\xF3n de audio"
  },
  // === GENERACIÓN DE CONTENIDO ===
  generateText: {
    primary: "gemini-3.1-pro-preview",
    // PRO para mejor calidad de texto
    fallback: "gemini-3-flash-preview",
    temperature: 0.7,
    maxOutputTokens: 4096,
    description: "Generaci\xF3n de texto gen\xE9rico"
  },
  generateHashtags: {
    primary: "gemini-3-flash-preview",
    // Flash suficiente para hashtags
    fallback: "gemini-2.5-pro",
    temperature: 0.6,
    maxOutputTokens: 500,
    description: "Generaci\xF3n de hashtags"
  },
  improveText: {
    primary: "gemini-3.1-pro-preview",
    // PRO para mejor refinamiento
    fallback: "gemini-3-flash-preview",
    temperature: 0.5,
    maxOutputTokens: 4096,
    description: "Mejora de texto"
  },
  generateArticle: {
    primary: "gemini-3.1-pro-preview",
    fallback: "gemini-3-flash-preview",
    temperature: 0.7,
    maxOutputTokens: 8192,
    description: "Generaci\xF3n de art\xEDculos largos"
  },
  optimizeSEO: {
    primary: "gemini-3.1-pro-preview",
    fallback: "gemini-3-flash-preview",
    temperature: 0.4,
    maxOutputTokens: 4096,
    description: "Optimizaci\xF3n SEO de texto"
  },
  // === ANÁLISIS ===
  analyzeWritingStyle: {
    primary: "gemini-3.1-pro-preview",
    fallback: "gemini-3-flash-preview",
    temperature: 0.3,
    maxOutputTokens: 2048,
    description: "An\xE1lisis de estilo de escritura"
  },
  classifyIntent: {
    primary: "gemini-3-flash-preview",
    fallback: "gemini-2.5-pro",
    temperature: 0.1,
    maxOutputTokens: 100,
    description: "Clasificaci\xF3n de intenci\xF3n (A2K pattern)"
  },
  queryRewriter: {
    primary: "gemini-3-flash-preview",
    fallback: "gemini-2.5-pro",
    temperature: 0.3,
    maxOutputTokens: 200,
    description: "Reescritura de queries (A2K pattern)"
  },
  // === CHAT/ASISTENTES ===
  aiChat: {
    primary: "gemini-3.1-pro-preview",
    // 3.1 Pro — mejor razonamiento empático, respuestas más concisas
    fallback: "gemini-3-flash-preview",
    temperature: 0.5,
    maxOutputTokens: 600,
    // Chat no necesita más de 600 tokens → latencia 3-4x menor
    description: "Chat asistente paciente \u2014 respuesta r\xE1pida y emp\xE1tica"
  },
  therapistAssistant: {
    primary: "gemini-3.1-pro-preview",
    fallback: "gemini-3-flash-preview",
    temperature: 0.6,
    maxOutputTokens: 4096,
    description: "Asistente de terapeuta (espec\xEDfico appmor)"
  },
  // === MEDIA ===
  generateImage: {
    primary: "gemini-3.1-pro-preview",
    fallback: "gemini-2.5-pro",
    temperature: 0.7,
    maxOutputTokens: 2048,
    description: "Generaci\xF3n de im\xE1genes"
  },
  // === APPMOR: PRÁCTICAS ===
  generatePracticesFromIdea: { primary: "gemini-3-flash-preview", fallback: "gemini-2.5-pro", temperature: 0.7, description: "Generar pr\xE1cticas desde idea" },
  generatePracticesFromText: { primary: "gemini-3-flash-preview", fallback: "gemini-2.5-pro", temperature: 0.7, description: "Generar pr\xE1cticas desde texto" },
  generatePracticesFromPDF: { primary: "gemini-3-flash-preview", fallback: "gemini-2.5-pro", temperature: 0.5, description: "Generar pr\xE1cticas desde PDF" },
  generatePracticesFromDocument: { primary: "gemini-3-flash-preview", fallback: "gemini-2.5-pro", temperature: 0.5, description: "Generar pr\xE1cticas desde documento" },
  extractPracticesFromNotes: { primary: "gemini-3-flash-preview", fallback: "gemini-2.5-pro", temperature: 0.3, description: "Extraer pr\xE1cticas exactas de apuntes" },
  generatePracticesFromNotesInspiration: { primary: "gemini-3-flash-preview", fallback: "gemini-2.5-pro", temperature: 0.8, description: "Generar pr\xE1cticas inspiradas en apuntes" },
  professionalDiscovery: {
    primary: "gemini-3-flash-preview",
    // Flash con grounding
    fallback: "gemini-2.5-pro",
    temperature: 0.2,
    requiresGrounding: true,
    description: "B\xFAsqueda de profesionales con Google Grounding"
  },
  // === APPMOR: TEXTO/CONTENIDO ===
  optimizeText: { primary: "gemini-3-flash-preview", fallback: "gemini-2.5-pro", temperature: 0.5, description: "Optimizar texto" },
  improveTextFormat: { primary: "gemini-3-flash-preview", fallback: "gemini-2.5-pro", temperature: 0.5, description: "Mejorar formato texto" },
  // === APPMOR: ARTÍCULOS ===
  improveArticle: { primary: "gemini-3.1-pro-preview", fallback: "gemini-3-flash-preview", temperature: 0.6, description: "Mejorar art\xEDculo" },
  generateArticleTitle: { primary: "gemini-3-flash-preview", fallback: "gemini-2.5-pro", temperature: 0.8, description: "Generar t\xEDtulo art\xEDculo" },
  generateExcerpt: { primary: "gemini-3-flash-preview", fallback: "gemini-2.5-pro", temperature: 0.5, description: "Generar resumen" },
  optimizeArticleSEO: { primary: "gemini-3.1-pro-preview", fallback: "gemini-3-flash-preview", temperature: 0.4, description: "Optimizar SEO art\xEDculo" },
  // === APPMOR: MEDIA/HERRAMIENTAS ===
  generateGenogramFromText: { primary: "gemini-3.1-pro-preview", fallback: "gemini-3-flash-preview", temperature: 0.5, description: "Generar genograma" },
  generateYoutubeDescription: { primary: "gemini-3-flash-preview", fallback: "gemini-2.5-pro", temperature: 0.6, description: "Generar descripci\xF3n YouTube" },
  therapistPatientAssistant: { primary: "gemini-3.1-pro-preview", fallback: "gemini-3-flash-preview", temperature: 0.6, description: "Asistente terapeuta-paciente" },
  // === EVA PLATFORM: PROCUREMENT CLASSIFICATION ===
  classifyProcurement: {
    primary: "gemini-3-flash-preview",
    fallback: "gemini-2.5-pro",
    temperature: 0.1,
    // Muy determinístico para clasificación consistente
    maxOutputTokens: 4096,
    // Más tokens para múltiples categorías sin límite
    description: "Clasificaci\xF3n estructurada de licitaciones p\xFAblicas con ontolog\xEDa"
  },
  refineTableExtraction: {
    primary: "gemini-3.1-pro-preview",
    // Pro para máxima precisión en análisis visual
    fallback: "gemini-3-flash-preview",
    temperature: 0.1,
    // Muy bajo para precisión en corrección de datos
    maxOutputTokens: 8192,
    // Alto para tablas grandes
    description: "Refinamiento A2K de tablas extra\xEDdas usando visi\xF3n multimodal"
  },
  classifyContent: {
    primary: "gemini-3-flash-preview",
    fallback: "gemini-2.5-pro",
    temperature: 0.1,
    // Muy determinístico para clasificación consistente
    maxOutputTokens: 2048,
    description: "Clasificaci\xF3n de contenido seg\xFAn ontolog\xEDa de categor\xEDas"
  },
  classifyProducts: {
    primary: "gemini-3.1-pro-preview",
    fallback: "gemini-3-flash-preview",
    temperature: 0.1,
    maxOutputTokens: 8192,
    description: "Clasificaci\xF3n de productos por datasheet en ontolog\xEDa del tenant (Pro for max quality)"
  },
  benchmarkOntology: {
    primary: "gemini-3-flash-preview",
    // Flash con grounding
    fallback: "gemini-2.5-pro",
    temperature: 0.2,
    maxOutputTokens: 4096,
    requiresGrounding: true,
    description: "Benchmarking competitivo de taxonom\xEDas con Google Search grounding"
  },
  generateMarketSeeds: {
    primary: "gemini-3.1-pro-preview",
    // PRO para mejor generación de ideas
    fallback: "gemini-3-flash-preview",
    temperature: 0.7,
    maxOutputTokens: 4096,
    description: "Generaci\xF3n de seeds para inteligencia de mercado"
  },
  // === CONTENT GENERATION (GENÉRICO) ===
  generateLongFormContent: {
    primary: "gemini-3.1-pro-preview",
    fallback: "gemini-3-flash-preview",
    temperature: 0.7,
    maxOutputTokens: 8192,
    description: "Generaci\xF3n de contenido largo (art\xEDculos, whitepapers)"
  },
  generateShortFormContent: {
    primary: "gemini-3.1-pro-preview",
    // PRO incluso para contenido corto
    fallback: "gemini-3-flash-preview",
    temperature: 0.8,
    maxOutputTokens: 2048,
    description: "Contenido corto (posts, emails, social media)"
  },
  correlateEntities: {
    primary: "gemini-3-flash-preview",
    // Flash: más rápido y mayor cuota (Pro tiene límites muy bajos)
    fallback: "gemini-2.5-pro",
    temperature: 0.2,
    // Muy determinístico para correlación precisa
    maxOutputTokens: 4096,
    description: "Correlaci\xF3n de entidades por contexto y embeddings"
  },
  validateTextQuality: {
    primary: "gemini-3-flash-preview",
    fallback: "gemini-2.5-pro",
    temperature: 0.1,
    maxOutputTokens: 2048,
    description: "Validaci\xF3n de calidad de texto (SEO, legibilidad)"
  },
  extractKeywords: {
    primary: "gemini-3-flash-preview",
    fallback: "gemini-2.5-pro",
    temperature: 0.3,
    maxOutputTokens: 1024,
    description: "Extracci\xF3n de keywords desde contenido"
  },
  linkWordPressArticles: {
    primary: "gemini-3-flash-preview",
    // Flash para velocidad en linking
    fallback: "gemini-2.5-pro",
    temperature: 0.4,
    // Algo de creatividad para anchor text natural
    maxOutputTokens: 4096,
    description: "Linking org\xE1nico de art\xEDculos WordPress relacionados en contenido generado"
  },
  productLinking: {
    primary: "gemini-3.1-pro-preview",
    // PRO para mejor correlación semántica
    fallback: "gemini-3-flash-preview",
    temperature: 0.3,
    maxOutputTokens: 4096,
    description: "Linking de productos a contenido por similitud sem\xE1ntica"
  },
  generateEnrichmentSchema: {
    primary: "gemini-3.1-pro-preview",
    // PRO para mejor comprensión de consultas complejas
    fallback: "gemini-3-flash-preview",
    temperature: 0.4,
    maxOutputTokens: 4096,
    description: "Generaci\xF3n de esquema de enriquecimiento desde descripci\xF3n en lenguaje natural"
  },
  enrichmentExecution: {
    primary: "gemini-3-flash-preview",
    // Flash para ejecución rápida de esquemas
    fallback: "gemini-2.5-pro",
    temperature: 0.2,
    maxOutputTokens: 4096,
    description: "Ejecuci\xF3n de esquema de enriquecimiento sobre items de contenido"
  },
  // === ADVERTISING ===
  adFormatRecommendation: {
    primary: "gemini-3-flash-preview",
    // Flash para clasificación rápida
    fallback: "gemini-2.5-pro",
    temperature: 0.2,
    maxOutputTokens: 500,
    description: "Recomendaci\xF3n de formato publicitario \xF3ptimo para v\xEDdeos"
  },
  adCreativeGeneration: {
    primary: "gemini-3.1-pro-preview",
    // PRO para creatividades de calidad
    fallback: "gemini-3-flash-preview",
    temperature: 0.7,
    maxOutputTokens: 4096,
    description: "Generaci\xF3n de creatividades, t\xEDtulos y keywords publicitarios"
  },
  videoPromotionAnalysis: {
    primary: "gemini-3-flash-preview",
    // Flash para análisis rápido
    fallback: "gemini-2.5-pro",
    temperature: 0.3,
    maxOutputTokens: 2048,
    description: "An\xE1lisis de v\xEDdeo para recomendaciones publicitarias"
  },
  adDiscoveryAnalysis: {
    primary: "gemini-3.1-pro-preview",
    // PRO para análisis profundo de oportunidades
    fallback: "gemini-3-flash-preview",
    temperature: 0.5,
    maxOutputTokens: 4096,
    description: "Descubrimiento de oportunidades publicitarias en categor\xEDas de producto"
  },
  // === PRODUCT KEYWORD INTELLIGENCE ===
  generateProductKeywordSeeds: {
    primary: "gemini-3-flash-preview",
    fallback: "gemini-2.5-pro",
    temperature: 0.3,
    maxOutputTokens: 4096,
    description: "Generate keyword seeds from product specs and competitor URLs"
  },
  classifyKeywordClusters: {
    primary: "gemini-3-flash-preview",
    fallback: "gemini-2.5-pro",
    temperature: 0.1,
    maxOutputTokens: 2048,
    description: "Name keyword clusters and classify search intent"
  },
  generateCannibalizationRecommendations: {
    primary: "gemini-3.1-pro-preview",
    fallback: "gemini-3-flash-preview",
    temperature: 0.5,
    maxOutputTokens: 4096,
    description: "Generate keyword cannibalization resolution recommendations"
  },
  generateKeywordExplainability: {
    primary: "gemini-3-flash-preview",
    fallback: "gemini-2.5-pro",
    temperature: 0.3,
    maxOutputTokens: 32768,
    description: "Explain every keyword in plain language with priorities and guidance"
  },
  analyzeUniverseClusters: {
    primary: "gemini-3-flash-preview",
    fallback: "gemini-2.5-pro",
    temperature: 0.3,
    maxOutputTokens: 16384,
    description: "Analyze universe cluster relevance with AI-generated verdicts"
  },
  // === EMPLOYEE ADVOCACY (STYLE LEARNING) ===
  adaptContentToStyle: {
    primary: "gemini-3.1-pro-preview",
    // PRO para calidad en adaptación de estilo
    fallback: "gemini-3-flash-preview",
    temperature: 0.5,
    maxOutputTokens: 65536,
    // High limit: thinking tokens consume ~5-10K, need headroom for long posts
    description: "Adaptar contenido al estilo de escritura personal del comercial"
  },
  analyzeStyleChanges: {
    primary: "gemini-3-flash-preview",
    // Flash para extracción JSON estructurada
    fallback: "gemini-2.5-pro",
    temperature: 0.3,
    maxOutputTokens: 2e3,
    description: "Analizar diferencias entre texto original y correcci\xF3n del usuario"
  },
  synthesizeTendencies: {
    primary: "gemini-3-flash-preview",
    // Flash para síntesis rápida
    fallback: "gemini-2.5-pro",
    temperature: 0.3,
    maxOutputTokens: 1500,
    description: "Sintetizar tendencias de edici\xF3n desde historial de correcciones"
  },
  // === CONTENT HUB (DERIVATION & PROCESSING) ===
  generateDerivedContent: {
    primary: "gemini-3.1-pro-preview",
    // PRO para calidad en derivación de contenido
    fallback: "gemini-3-flash-preview",
    temperature: 0.3,
    maxOutputTokens: 4096,
    description: "Generar contenido derivado adaptado a canal espec\xEDfico"
  },
  analyzeFreshness: {
    primary: "gemini-3-flash-preview",
    fallback: "gemini-2.5-pro",
    temperature: 0.2,
    maxOutputTokens: 2048,
    description: "Analizar frescura y relevancia temporal de contenido"
  },
  streamContentGeneration: {
    primary: "gemini-3.1-pro-preview",
    fallback: "gemini-3-flash-preview",
    temperature: 0.7,
    maxOutputTokens: 8192,
    description: "Generaci\xF3n de contenido en modo streaming (SSE)"
  },
  groundedSearch: {
    primary: "gemini-3-flash-preview",
    // Flash con grounding
    fallback: "gemini-2.5-pro",
    temperature: 0.3,
    maxOutputTokens: 4096,
    requiresGrounding: true,
    description: "B\xFAsqueda fundamentada con Google Search integration"
  },
  // === EMBEDDINGS ===
  generateEmbeddings: {
    primary: "text-embedding-004",
    fallback: "text-embedding-005",
    temperature: 0,
    // No aplica para embeddings, pero se mantiene por consistencia
    maxOutputTokens: 768,
    // Dimensiones del embedding
    description: "Generaci\xF3n de embeddings vectoriales para b\xFAsqueda sem\xE1ntica"
  },
  // === TTS (Text-to-Speech) ===
  ttsNarration: {
    primary: "google-tts",
    // Google Cloud TTS
    fallback: "elevenlabs",
    temperature: 0,
    description: "Text-to-Speech para narraciones de video"
  },
  // === VIDEO STORYBOARD ===
  generateVideoStoryboard: {
    primary: "gemini-3.1-pro-preview",
    fallback: "gemini-3-flash-preview",
    temperature: 0.7,
    maxOutputTokens: 4096,
    description: "Generaci\xF3n de storyboard (texto \u2192 escenas JSON)"
  },
  // === VIDEO GENERATION (VEO 3) — Contexto/Entorno ===
  generateVideoFromText: {
    primary: "veo-3-generate",
    fallback: "veo-3-fast",
    temperature: 0.7,
    maxOutputTokens: 1024,
    description: "Generaci\xF3n de video desde texto (1080p, 4-8s) \u2014 ideal para clips de contexto/entorno"
  },
  generateVideoFromImage: {
    primary: "veo-3.1-generate-preview",
    fallback: "veo-3-generate",
    temperature: 0.6,
    maxOutputTokens: 1024,
    description: "Animar imagen est\xE1tica a video \u2014 Veo 3.1 con ref images"
  },
  extendVideo: {
    primary: "veo-3.1-generate-preview",
    fallback: "veo-3-generate",
    temperature: 0.5,
    maxOutputTokens: 1024,
    description: "Extender video existente"
  },
  // === VIDEO GENERATION (KLING 3.0 PRO) — Producto ===
  generateProductVideo: {
    primary: "kling-3.0-pro",
    fallback: "veo-3.1-generate-preview",
    temperature: 0.5,
    maxOutputTokens: 1024,
    description: "Video de producto con Kling 3.0 Pro \u2014 preserva texto, logos, labels y detalles de superficie. Hasta 7 ref images, 15s, 1080p/4K"
  },
  generateProductVideoFromText: {
    primary: "kling-3.0-pro",
    fallback: "veo-3-generate",
    temperature: 0.6,
    maxOutputTokens: 1024,
    description: "Video de producto desde texto con Kling 3.0 Pro"
  },
  // === VIDEO GENERATION (RUNWAY GEN-4 TURBO) — Drafts/Previews ===
  generateDraftVideo: {
    primary: "gen4.5",
    fallback: "veo-3-fast",
    temperature: 0.7,
    maxOutputTokens: 1024,
    description: "Draft r\xE1pido con Runway Gen-4 Turbo (~60s generaci\xF3n, $0.05/s) \u2014 sin garant\xEDas de fidelidad de producto"
  },
  generatePreviewVideo: {
    primary: "gen4.5",
    fallback: "veo-3-fast",
    temperature: 0.6,
    maxOutputTokens: 1024,
    description: "Preview r\xE1pido antes de generar versi\xF3n final con Kling/Veo"
  },
  // === VIDEO GENERATION (OPENAI SORA) — Cinematic / Reality ===
  generateCinematicVideo: {
    primary: "sora-2-pro",
    fallback: "sora-2",
    temperature: 0.6,
    maxOutputTokens: 1024,
    description: "Video de muy alta fidelidad y f\xEDsicas perfectas \u2014 $0.30-$0.50/s"
  },
  generateSoraVideo: {
    primary: "sora-2",
    fallback: "veo-3-generate",
    temperature: 0.7,
    maxOutputTokens: 1024,
    description: "Generador de video est\xE1ndar OpenAI \u2014 $0.10/s"
  },
  // === VIDEO GENERATION (SEEDANCE 2.0) — Futuro ===
  generateBrandedVideo: {
    primary: "seedance-2.0",
    fallback: "kling-3.0-pro",
    temperature: 0.5,
    maxOutputTokens: 1024,
    description: "Video con control granular de marca (Seedance 2.0) \u2014 9 ref images con tags por rol, preserva logos/colores. API pendiente."
  },
  // === YOUTUBE TRANSCRIPTION ===
  transcribeYouTube: {
    primary: "gemini-3-flash-preview",
    fallback: "gemini-2.5-pro",
    temperature: 0.1,
    maxOutputTokens: 8192,
    description: "Transcripci\xF3n de videos YouTube con Gemini"
  },
  // === QUANT ACADEMY ===
  quantTechnicalAnalysis: {
    primary: "gemini-3.1-pro-preview",
    fallback: "gemini-3-flash-preview",
    temperature: 0.3,
    maxOutputTokens: 4096,
    description: "An\xE1lisis t\xE9cnico cuantitativo de trading"
  },
  quantRiskAnalysis: {
    primary: "gemini-3.1-pro-preview",
    fallback: "gemini-3-flash-preview",
    temperature: 0.2,
    maxOutputTokens: 4096,
    description: "An\xE1lisis de riesgo cuantitativo"
  },
  quantMentorChat: {
    primary: "gemini-3.1-pro-preview",
    fallback: "gemini-3-flash-preview",
    temperature: 0.7,
    maxOutputTokens: 2048,
    description: "Chat con mentor de trading AI"
  },
  quantQuantumAnalysis: {
    primary: "gemini-3.1-pro-preview",
    fallback: "gemini-3-flash-preview",
    temperature: 0.3,
    maxOutputTokens: 4096,
    description: "An\xE1lisis cu\xE1ntico avanzado (GARCH, Monte Carlo)"
  },
  quantNewsClassification: {
    primary: "gemini-3-flash-preview",
    // Flash: fast, cheap, great for batch structured JSON classification
    fallback: "gemini-3.1-pro-preview",
    // Pro as fallback for complex articles
    temperature: 0.1,
    maxOutputTokens: 4096,
    description: "Clasificaci\xF3n institucional de noticias con eventos estructurados (Flash para batch)"
  },
  quantDashboardExplainer: {
    primary: "gemini-3.1-pro-preview",
    fallback: "gemini-3-flash-preview",
    temperature: 0.6,
    maxOutputTokens: 65536,
    description: "Briefing diario completo con an\xE1lisis de negocio + fundamento t\xE9cnico educativo"
  },
  quantStrategyGenerator: {
    primary: "gemini-3.1-pro-preview",
    fallback: "gemini-3-flash-preview",
    temperature: 0.5,
    maxOutputTokens: 4096,
    description: "Generaci\xF3n de estrategias de trading desde lenguaje natural"
  },
  quantStrategyReviewer: {
    primary: "gemini-3.1-pro-preview",
    fallback: "gemini-3-flash-preview",
    temperature: 0.3,
    maxOutputTokens: 4096,
    description: "Review de estrategias y explicaci\xF3n de backtests"
  },
  // === DEFAULT ===
  default: {
    primary: "gemini-3.1-pro-preview",
    // PRO por defecto para máxima calidad
    fallback: "gemini-3-flash-preview",
    temperature: 0.7,
    maxOutputTokens: 4096,
    description: "Configuraci\xF3n por defecto - usa PRO para mejor calidad"
  }
};
function getModelConfig(taskType) {
  return DEFAULT_MODEL_CONFIGS[taskType] || DEFAULT_MODEL_CONFIGS.default;
}
function getModelForTask(taskType) {
  return getModelConfig(taskType).primary;
}
function getFallbackModel(taskType) {
  return getModelConfig(taskType).fallback;
}
function getAvailableTaskTypes() {
  return Object.keys(DEFAULT_MODEL_CONFIGS);
}
function taskRequiresGrounding(taskType) {
  return getModelConfig(taskType).requiresGrounding === true;
}
var TASK_CATEGORIES = [
  {
    id: "content_generation",
    label: "Generaci\xF3n de Contenido",
    icon: "\u{1F4DD}",
    tasks: [
      "generateText",
      "generateArticle",
      "generateLongFormContent",
      "generateShortFormContent",
      "generateDerivedContent",
      "streamContentGeneration",
      "improveText",
      "generateHashtags",
      "extractKeywords",
      "linkWordPressArticles",
      "productLinking",
      "adaptContentToStyle",
      "generateEnrichmentSchema",
      "enrichmentExecution"
    ]
  },
  {
    id: "classification",
    label: "Clasificaci\xF3n",
    icon: "\u{1F4CA}",
    tasks: [
      "classifyContent",
      "classifyProducts",
      "classifyProcurement",
      "classifyIntent"
    ]
  },
  {
    id: "analysis",
    label: "An\xE1lisis",
    icon: "\u{1F50D}",
    tasks: [
      "optimizeSEO",
      "analyzeWritingStyle",
      "analyzeFreshness",
      "analyzeStyleChanges",
      "synthesizeTendencies",
      "validateTextQuality",
      "correlateEntities",
      "groundedSearch",
      "benchmarkOntology",
      "queryRewriter",
      "generateMarketSeeds"
    ]
  },
  {
    id: "embeddings",
    label: "Embeddings",
    icon: "\u{1F9EC}",
    tasks: ["generateEmbeddings"]
  },
  {
    id: "image",
    label: "Imagen",
    icon: "\u{1F3A8}",
    tasks: ["generateImage"]
  },
  {
    id: "video",
    label: "Video",
    icon: "\u{1F3AC}",
    tasks: [
      "generateVideoStoryboard",
      "generateVideoFromText",
      "generateVideoFromImage",
      "extendVideo",
      "ttsNarration",
      "transcribeYouTube",
      // External video providers
      "generateProductVideo",
      "generateProductVideoFromText",
      "generateDraftVideo",
      "generatePreviewVideo",
      "generateBrandedVideo"
    ]
  },
  {
    id: "agents",
    label: "Agentes IA",
    icon: "\u{1F916}",
    tasks: [
      "deepResearch",
      "seoConsultant",
      "contentGenerator",
      "refineTableExtraction"
    ]
  },
  {
    id: "chat",
    label: "Chat / Asistentes",
    icon: "\u{1F4AC}",
    tasks: ["aiChat", "therapistAssistant", "therapistPatientAssistant"]
  },
  {
    id: "documents",
    label: "Documentos",
    icon: "\u{1F4C4}",
    tasks: ["processPDF", "processImageOCR", "transcribeAudio"]
  },
  {
    id: "advertising",
    label: "Publicidad",
    icon: "\u{1F4E2}",
    tasks: [
      "adFormatRecommendation",
      "adCreativeGeneration",
      "videoPromotionAnalysis",
      "adDiscoveryAnalysis",
      "generateProductKeywordSeeds",
      "classifyKeywordClusters",
      "generateCannibalizationRecommendations",
      "generateKeywordExplainability",
      "analyzeUniverseClusters"
    ]
  },
  {
    id: "quant",
    label: "Quant Intelligence (trAIding)",
    icon: "\u{1F4C8}",
    tasks: [
      "quantTechnicalAnalysis",
      "quantRiskAnalysis",
      "quantMentorChat",
      "quantQuantumAnalysis",
      "quantNewsClassification",
      "quantDashboardExplainer",
      "quantStrategyGenerator",
      "quantStrategyReviewer"
    ]
  }
];
var _dynamicConfigCache = null;
var _dynamicCacheTimestamp = 0;
var DYNAMIC_CACHE_TTL_MS = 5 * 60 * 1e3;
async function getDynamicModelConfig(taskType, firestore) {
  const defaultConfig = DEFAULT_MODEL_CONFIGS[taskType] || DEFAULT_MODEL_CONFIGS.default;
  if (!firestore) return defaultConfig;
  if (_dynamicConfigCache && Date.now() - _dynamicCacheTimestamp < DYNAMIC_CACHE_TTL_MS) {
    const cached = _dynamicConfigCache[taskType];
    if (cached) {
      return { ...defaultConfig, ...cached };
    }
    return defaultConfig;
  }
  try {
    const doc = await firestore.doc("platform/config").get();
    if (doc.exists) {
      const data = doc.data();
      const taskConfigs = data?.aiModels?.taskConfigs;
      if (taskConfigs) {
        _dynamicConfigCache = taskConfigs;
        _dynamicCacheTimestamp = Date.now();
        const cached = taskConfigs[taskType];
        if (cached) {
          return { ...defaultConfig, ...cached };
        }
      }
    }
  } catch {
  }
  return defaultConfig;
}
async function getDynamicModelForTask(taskType, firestore) {
  const config = await getDynamicModelConfig(taskType, firestore);
  return config.primary;
}
function invalidateModelConfigCache() {
  _dynamicConfigCache = null;
  _dynamicCacheTimestamp = 0;
}
var _globalFirestore = null;
async function initializeModelConfigSystem(firestore) {
  _globalFirestore = firestore;
  await getDynamicModelConfig("default", firestore);
}
async function getAllModelConfigs(firestore) {
  let overrides = {};
  if (firestore) {
    try {
      const doc = await firestore.doc("platform/config").get();
      if (doc.exists) {
        const data = doc.data();
        overrides = data?.aiModels?.taskConfigs || {};
      }
    } catch {
    }
  }
  const result = {};
  for (const [task, config] of Object.entries(DEFAULT_MODEL_CONFIGS)) {
    const override = overrides[task];
    result[task] = {
      ...config,
      ...override || {},
      isCustomized: !!override
    };
  }
  return result;
}
var IMAGEN_MODELS = {
  /** Imagen 4 - Latest model, 2K resolution, best quality */
  IMAGEN_4: "imagen-4.0-generate-001",
  /** Imagen 4 Fast - Faster variant, optimized for speed */
  IMAGEN_4_FAST: "imagen-4.0-fast-generate-001",
  /** Imagen 4 Ultra - Highest quality variant */
  IMAGEN_4_ULTRA: "imagen-4.0-ultra-generate-001",
  /** Imagen 3 - Stable fallback */
  IMAGEN_3: "imagen-3.0-generate-002",
  /** Legacy imagegeneration model */
  LEGACY: "imagegeneration@006"
};
var IMAGE_GENERATION_CONFIG = {
  /** Modelo primario - máxima calidad, 2K resolution */
  primary: IMAGEN_MODELS.IMAGEN_4,
  /** Modelo de fallback - más rápido si el primario falla */
  fallback: IMAGEN_MODELS.IMAGEN_4_FAST,
  /** Fallback legacy para compatibilidad */
  legacy: IMAGEN_MODELS.IMAGEN_3,
  /** Endpoint region (us-central1 para Imagen) */
  region: "us-central1"
};
var VEO_MODELS2 = {
  /** Veo 3 - Standard video generation */
  VEO_3: "veo-3-generate",
  /** Veo 3 Fast - Faster generation */
  VEO_3_FAST: "veo-3-fast",
  /** Veo 3.1 Preview - Latest with image-to-video */
  VEO_3_1: "veo-3.1-generate-preview"
};
var KLING_MODELS = {
  /** Kling 3.0 Pro - Image-to-video de producto con fidelidad máxima */
  KLING_3_PRO: "kling-3.0-pro",
  /** Kling 2.0 - Versión anterior, menor calidad */
  KLING_2: "kling-2.0"
};
var RUNWAY_MODELS = {
  /** Gen-4.5 - Mejor calidad y velocidad (reemplaza gen4_turbo) */
  GEN4_5: "gen4.5",
  /** Gen-3a Turbo - Legacy, más barato */
  GEN3A_TURBO: "gen3a_turbo"
  // Runway API también ofrece veo3/veo3.1/veo3.1_fast pero son de Google vía Runway
  // No los incluimos aquí porque ya los cubrimos directamente con Veo
};
var SORA_MODELS = {
  /** Sora 2 - Calidad estándar, rápida — $0.10/s */
  SORA_2: "sora-2",
  /** Sora 2 Pro - Alta resolución y precisión cinemática — $0.30-$0.50/s */
  SORA_2_PRO: "sora-2-pro"
};
var SEEDANCE_MODELS = {
  /** Seedance 2.0 - Control granular de marca (API pendiente) */
  SEEDANCE_2: "seedance-2.0"
};
var VIDEO_PROVIDERS = {
  // --- TIER 1: Production Ready ---
  veo: {
    provider: "veo",
    name: "Google Veo 3.1",
    tier: "tier1",
    models: Object.values(VEO_MODELS2),
    defaultModel: VEO_MODELS2.VEO_3,
    bestFor: "Clips de contexto/entorno, text-to-video general",
    costPerSecond: 0.3,
    maxDurationSeconds: 8,
    maxReferenceImages: 3,
    maxResolution: "1080p",
    preservesTextAndLogos: false,
    approxGenerationTimeSec: 180,
    apiAvailable: true,
    apiType: "vertex-ai",
    speedDescription: "~3 min por clip"
  },
  kling: {
    provider: "kling",
    name: "Kling 3.0 Pro",
    tier: "tier1",
    models: Object.values(KLING_MODELS),
    defaultModel: KLING_MODELS.KLING_3_PRO,
    bestFor: "Clips de producto (image-to-video) \u2014 preserva texto, logos, labels",
    costPerSecond: 0.15,
    maxDurationSeconds: 15,
    maxReferenceImages: 7,
    maxResolution: "4k",
    preservesTextAndLogos: true,
    approxGenerationTimeSec: 120,
    apiAvailable: true,
    apiType: "fal.ai",
    speedDescription: "~2 min por clip"
  },
  runway: {
    provider: "runway",
    name: "Runway Gen-4 Turbo",
    tier: "tier1",
    models: Object.values(RUNWAY_MODELS),
    defaultModel: RUNWAY_MODELS.GEN4_5,
    bestFor: "Drafts r\xE1pidos y previews antes de versi\xF3n final",
    costPerSecond: 0.05,
    maxDurationSeconds: 10,
    maxReferenceImages: 1,
    maxResolution: "1080p",
    preservesTextAndLogos: false,
    approxGenerationTimeSec: 60,
    apiAvailable: true,
    apiType: "rest",
    speedDescription: "~60s por clip (el m\xE1s r\xE1pido)"
  },
  sora: {
    provider: "sora",
    name: "OpenAI Sora 2.0",
    tier: "tier1",
    models: Object.values(SORA_MODELS),
    defaultModel: SORA_MODELS.SORA_2_PRO,
    bestFor: "Calidad extrema, f\xEDsica realista, cinem\xE1tica y text-to-video en HD",
    costPerSecond: 0.3,
    maxDurationSeconds: 12,
    maxReferenceImages: 1,
    maxResolution: "1080p",
    preservesTextAndLogos: true,
    approxGenerationTimeSec: 240,
    // Polling needed
    apiAvailable: true,
    apiType: "rest",
    speedDescription: "Calidad superior, tiempo de espera moderado"
  },
  // --- TIER 2: Monitoring / Future ---
  seedance: {
    provider: "seedance",
    name: "Seedance 2.0 (ByteDance)",
    tier: "tier2",
    models: Object.values(SEEDANCE_MODELS),
    defaultModel: SEEDANCE_MODELS.SEEDANCE_2,
    bestFor: "Video con control granular de marca \u2014 9 ref images con tags por rol",
    costPerSecond: 0.12,
    // Estimated
    maxDurationSeconds: 10,
    maxReferenceImages: 9,
    maxResolution: "1080p",
    preservesTextAndLogos: true,
    approxGenerationTimeSec: 150,
    apiAvailable: false,
    // API retrasada
    apiType: "not-available",
    speedDescription: "API pendiente"
  }
};
function getVideoProvidersByTier(tier) {
  return Object.values(VIDEO_PROVIDERS).filter((p) => p.tier === tier);
}
function getVideoProviderForUseCase(useCase) {
  switch (useCase) {
    case "product":
      return VIDEO_PROVIDERS.kling;
    case "context":
      return VIDEO_PROVIDERS.veo;
    case "draft":
      return VIDEO_PROVIDERS.runway;
    case "branded":
      return VIDEO_PROVIDERS.seedance.apiAvailable ? VIDEO_PROVIDERS.seedance : VIDEO_PROVIDERS.kling;
    case "cinematic":
      return VIDEO_PROVIDERS.sora;
    default:
      return VIDEO_PROVIDERS.veo;
  }
}
function getAvailableVideoProviders() {
  return Object.values(VIDEO_PROVIDERS).filter((p) => p.apiAvailable);
}
function estimateVideoGenerationCost(provider, durationSeconds) {
  const providerInfo = VIDEO_PROVIDERS[provider];
  if (!providerInfo) return 0;
  return +(durationSeconds * providerInfo.costPerSecond).toFixed(4);
}

// src/server/image-generation.ts
var STYLE_PROMPTS2 = {
  minimalist: "Clean minimalist design with soft neutral colors, simple elegant composition, professional aesthetic, high-end quality",
  watercolor: "Soft watercolor artistic style with gentle color blending, flowing organic textures, artistic quality",
  digital_art: "Modern digital art style with vibrant colors, clean lines, contemporary aesthetic",
  photography: "Professional photography style, realistic, high quality, natural lighting",
  illustration: "Clean illustration style, vector-like quality, balanced composition",
  abstract: "Abstract artistic style, creative interpretation, unique visual elements",
  warm: "Warm inviting atmosphere, soft golden light, comfortable and welcoming",
  therapeutic: "Calming therapeutic atmosphere, soft pastel colors, peaceful and serene",
  professional: "Professional business style, clean modern design, corporate quality",
  // Extended styles for Content Hub
  photorealistic: "Ultra-realistic photography, sharp focus, professional lighting",
  technical: "Technical illustration, precise details, engineering aesthetic",
  elegant: "Sophisticated elegant style, refined aesthetics, premium feel",
  infographic: "Infographic style, data visualization, clean charts and icons, informative layout",
  industrial: "Industrial setting, factory environment, manufacturing aesthetic, machinery and equipment",
  tech: "High-tech style, futuristic feel, digital elements, modern technology aesthetic",
  cinematic: "Cinematic look, dramatic lighting, movie-quality composition",
  sketch: "Hand-drawn sketch style, pencil strokes, artistic draft quality",
  pop_art: "Pop art style, bold colors, graphic shapes, Andy Warhol inspiration"
};
var GEMINI_IMAGE_MODEL = "gemini-3-pro-image-preview";
var DEFAULT_PROJECT_ID = process.env.GCLOUD_PROJECT || process.env.GCP_PROJECT || process.env.GOOGLE_CLOUD_PROJECT || "appmor-25e91";
var GEMINI_LOCATION = "global";
var IMAGEN_LOCATION = "us-central1";
var IMAGEN_MODEL = "imagen-3.0-generate-002";
async function generateImage(config, logger19) {
  const log = logger19 || console;
  const projectId = config.projectId || DEFAULT_PROJECT_ID;
  const maxReferenceImages = config.maxReferenceImages || 6;
  log.info("[ai-server:generateImage] Starting image generation", {
    style: config.style,
    hasReferenceImages: (config.referenceImages?.length || 0) > 0,
    aspectRatio: config.aspectRatio,
    projectId
    // Log which project is being used
  });
  const stylePrompt = STYLE_PROMPTS2[config.style || "minimalist"];
  const fullPrompt = `${config.prompt}. ${stylePrompt}. High quality, balanced composition, beautiful, suitable for social media`;
  try {
    const result = await generateWithGemini3(
      projectId,
      fullPrompt,
      config,
      maxReferenceImages,
      log
    );
    if (result.success && result.imageBase64) {
      return result;
    }
    log.warn("[ai-server:generateImage] Gemini 3 failed, trying Imagen 3.0 fallback");
    return await generateWithImagen(projectId, fullPrompt, config, log);
  } catch (error) {
    log.error("[ai-server:generateImage] Error:", error.message);
    try {
      log.info("[ai-server:generateImage] Attempting Imagen 3.0 fallback after error");
      return await generateWithImagen(projectId, fullPrompt, config, log);
    } catch (fallbackError) {
      return {
        success: false,
        error: `All image generation methods failed. Gemini: ${error.message}. Imagen: ${fallbackError.message}`,
        model: "none"
      };
    }
  }
}
async function generateWithGemini3(projectId, fullPrompt, config, maxReferenceImages, log) {
  log.info("[ai-server:Gemini3] Using @google/genai SDK for image generation");
  const { GoogleGenAI } = await import("@google/genai");
  const client = new GoogleGenAI({
    vertexai: true,
    project: projectId,
    location: GEMINI_LOCATION
  });
  let contentParts = [{ text: fullPrompt }];
  if (config.referenceImages && config.referenceImages.length > 0) {
    for (const refImg of config.referenceImages.slice(0, maxReferenceImages)) {
      if (refImg.base64 && refImg.mimeType) {
        contentParts.push({
          inlineData: {
            mimeType: refImg.mimeType,
            data: refImg.base64
          }
        });
      }
    }
    contentParts.push({
      text: "Generate an image following the style and colors of the reference images above."
    });
  }
  log.info("[ai-server:Gemini3] Sending request", {
    referenceImagesCount: config.referenceImages?.length || 0
  });
  const models = client.models;
  const result = await models.generateContent({
    model: GEMINI_IMAGE_MODEL,
    contents: contentParts,
    config: {
      responseModalities: ["TEXT", "IMAGE"],
      imageConfig: {
        aspectRatio: config.aspectRatio || "16:9",
        imageSize: config.imageSize || "1K"
      }
    }
  });
  const candidates = result.candidates || [];
  const parts = candidates[0]?.content?.parts || [];
  let imageBase64 = null;
  let textResponse = null;
  let mimeType = "image/png";
  for (const part of parts) {
    if (part.text) {
      textResponse = part.text;
      log.info("[ai-server:Gemini3] Text response:", textResponse?.substring(0, 100));
    } else if (part.inlineData) {
      imageBase64 = part.inlineData.data;
      mimeType = part.inlineData.mimeType || "image/png";
    }
  }
  if (!imageBase64) {
    log.warn("[ai-server:Gemini3] No image in response");
    return {
      success: false,
      textResponse: textResponse || void 0,
      error: "No image data in Gemini 3 Pro Image response",
      model: GEMINI_IMAGE_MODEL
    };
  }
  log.info("[ai-server:Gemini3] SUCCESS", {
    base64Length: imageBase64.length,
    mimeType
  });
  return {
    success: true,
    imageBase64,
    mimeType,
    textResponse: textResponse || void 0,
    model: GEMINI_IMAGE_MODEL,
    fallbackUsed: false
  };
}
async function generateWithImagen(projectId, fullPrompt, config, log) {
  log.info("[ai-server:Imagen] Using Imagen 3.0 fallback");
  const { PredictionServiceClient } = await import("@google-cloud/aiplatform");
  const predictionClient = new PredictionServiceClient({
    apiEndpoint: `${IMAGEN_LOCATION}-aiplatform.googleapis.com`
  });
  const endpoint = `projects/${projectId}/locations/${IMAGEN_LOCATION}/publishers/google/models/${IMAGEN_MODEL}`;
  const aspectRatioMap = {
    "1:1": "1:1",
    "16:9": "16:9",
    "9:16": "9:16",
    "4:3": "4:3",
    "3:4": "3:4"
  };
  const instanceValue = {
    prompt: fullPrompt
  };
  const parametersValue = {
    sampleCount: 1,
    aspectRatio: aspectRatioMap[config.aspectRatio || "16:9"] || "16:9",
    personGeneration: "dont_allow",
    safetyFilterLevel: "block_some"
  };
  try {
    const [response] = await predictionClient.predict({
      endpoint,
      instances: [{ structValue: { fields: toProtoValue(instanceValue) } }],
      parameters: { structValue: { fields: toProtoValue(parametersValue) } }
    });
    const predictions = response.predictions || [];
    if (predictions.length === 0) {
      return {
        success: false,
        error: "No predictions returned from Imagen",
        model: IMAGEN_MODEL,
        fallbackUsed: true
      };
    }
    const prediction = predictions[0];
    const fields = prediction.structValue?.fields;
    const imageBase64 = fields?.bytesBase64Encoded?.stringValue;
    const mimeType = fields?.mimeType?.stringValue || "image/png";
    if (!imageBase64) {
      return {
        success: false,
        error: "No image data in Imagen response",
        model: IMAGEN_MODEL,
        fallbackUsed: true
      };
    }
    log.info("[ai-server:Imagen] SUCCESS", {
      base64Length: imageBase64.length
    });
    return {
      success: true,
      imageBase64,
      mimeType,
      model: IMAGEN_MODEL,
      fallbackUsed: true
    };
  } catch (error) {
    log.error("[ai-server:Imagen] Error:", error.message);
    return {
      success: false,
      error: error.message,
      model: IMAGEN_MODEL,
      fallbackUsed: true
    };
  }
}
function toProtoValue(obj) {
  const result = {};
  for (const [key, value] of Object.entries(obj)) {
    if (typeof value === "string") {
      result[key] = { stringValue: value };
    } else if (typeof value === "number") {
      result[key] = { numberValue: value };
    } else if (typeof value === "boolean") {
      result[key] = { boolValue: value };
    } else if (Array.isArray(value)) {
      result[key] = {
        listValue: {
          values: value.map((v) => ({ stringValue: String(v) }))
        }
      };
    } else if (typeof value === "object" && value !== null) {
      result[key] = { structValue: { fields: toProtoValue(value) } };
    }
  }
  return result;
}
async function editImage(config, logger19) {
  const log = logger19 || console;
  const projectId = config.projectId || DEFAULT_PROJECT_ID;
  const styleDescription = STYLE_PROMPTS2[config.style || "professional"];
  const editPrompt = `Enhance and improve this image.

TASK: Create an enhanced version that:
1. PRESERVES the core subject, composition, and key elements
2. IMPROVES visual quality: better lighting, enhanced colors, sharper details
3. APPLIES ${config.style || "professional"} aesthetic: ${styleDescription}
4. MAKES it suitable for premium marketing materials

CONTEXT: ${config.prompt}

IMPORTANT:
- Keep the same subject and layout
- Do NOT add text, logos, or watermarks
- Enhance and refine, don't replace

Generate the enhanced image now.`;
  log.info("[ai-server:editImage] Starting image edit", {
    style: config.style,
    refId: config.referenceImage.id
  });
  try {
    const { GoogleGenAI } = await import("@google/genai");
    const client = new GoogleGenAI({
      vertexai: true,
      project: projectId,
      location: GEMINI_LOCATION
    });
    const result = await client.models.generateContent({
      model: GEMINI_IMAGE_MODEL,
      contents: [
        {
          role: "user",
          parts: [
            {
              inlineData: {
                mimeType: config.referenceImage.mimeType,
                data: config.referenceImage.base64
              }
            },
            { text: editPrompt }
          ]
        }
      ],
      config: {
        responseModalities: ["TEXT", "IMAGE"]
      }
    });
    const parts = result.candidates?.[0]?.content?.parts || [];
    for (const part of parts) {
      if (part.inlineData?.mimeType?.startsWith("image/")) {
        log.info("[ai-server:editImage] SUCCESS", {
          refId: config.referenceImage.id,
          model: GEMINI_IMAGE_MODEL
        });
        return {
          success: true,
          imageBase64: part.inlineData.data,
          mimeType: part.inlineData.mimeType,
          model: GEMINI_IMAGE_MODEL,
          referenceImageId: config.referenceImage.id,
          referenceImageUrl: config.referenceImage.url
        };
      }
    }
    return {
      success: false,
      error: "No image in Gemini response",
      model: GEMINI_IMAGE_MODEL
    };
  } catch (error) {
    log.error("[ai-server:editImage] Error:", error.message);
    return {
      success: false,
      error: error.message,
      model: GEMINI_IMAGE_MODEL
    };
  }
}

// src/server/utils/pricing.ts
var VISION_PRICING = {
  // Document Text Detection (OCR) - precio por 1000 unidades
  "document-text-detection": {
    pricePerUnit: 1.5 / 1e3,
    displayName: "Document Text Detection (OCR)"
  },
  "text-detection": {
    pricePerUnit: 1.5 / 1e3,
    displayName: "Text Detection"
  },
  "label-detection": {
    pricePerUnit: 1.5 / 1e3,
    displayName: "Label Detection"
  },
  "face-detection": {
    pricePerUnit: 1.5 / 1e3,
    displayName: "Face Detection"
  },
  "logo-detection": {
    pricePerUnit: 1.5 / 1e3,
    displayName: "Logo Detection"
  },
  "safe-search": {
    pricePerUnit: 1.5 / 1e3,
    displayName: "Safe Search Detection"
  }
};

// src/server/utils/json.ts
var JsonParseError = class extends Error {
  code = "internal";
  constructor(message) {
    super(message);
    this.name = "JsonParseError";
  }
};
var safelyParseJsonResponse = (textOrResult) => {
  let text = "";
  if (textOrResult?.response?.candidates?.[0]?.content?.parts?.[0]?.text) {
    text = textOrResult.response.candidates[0].content.parts[0].text;
  } else if (typeof textOrResult === "string") {
    text = textOrResult;
  } else {
    throw new Error("Invalid input for safelyParseJsonResponse");
  }
  try {
    return JSON.parse(text);
  } catch {
    const jsonBlockMatch = text.match(/```json\s*([\s\S]*?)\s*```/);
    if (jsonBlockMatch) {
      return JSON.parse(jsonBlockMatch[1]);
    }
    const jsonMatch = text.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      return JSON.parse(jsonMatch[0]);
    }
    const jsonArrayMatch = text.match(/\[[\s\S]*\]/);
    if (jsonArrayMatch) {
      return JSON.parse(jsonArrayMatch[0]);
    }
    throw new JsonParseError(
      "La respuesta de la IA no ten\xEDa el formato JSON esperado."
    );
  }
};

// ../../node_modules/zod/v3/external.js
var external_exports = {};
__export(external_exports, {
  BRAND: () => BRAND,
  DIRTY: () => DIRTY,
  EMPTY_PATH: () => EMPTY_PATH,
  INVALID: () => INVALID,
  NEVER: () => NEVER,
  OK: () => OK,
  ParseStatus: () => ParseStatus,
  Schema: () => ZodType,
  ZodAny: () => ZodAny,
  ZodArray: () => ZodArray,
  ZodBigInt: () => ZodBigInt,
  ZodBoolean: () => ZodBoolean,
  ZodBranded: () => ZodBranded,
  ZodCatch: () => ZodCatch,
  ZodDate: () => ZodDate,
  ZodDefault: () => ZodDefault,
  ZodDiscriminatedUnion: () => ZodDiscriminatedUnion,
  ZodEffects: () => ZodEffects,
  ZodEnum: () => ZodEnum,
  ZodError: () => ZodError,
  ZodFirstPartyTypeKind: () => ZodFirstPartyTypeKind,
  ZodFunction: () => ZodFunction,
  ZodIntersection: () => ZodIntersection,
  ZodIssueCode: () => ZodIssueCode,
  ZodLazy: () => ZodLazy,
  ZodLiteral: () => ZodLiteral,
  ZodMap: () => ZodMap,
  ZodNaN: () => ZodNaN,
  ZodNativeEnum: () => ZodNativeEnum,
  ZodNever: () => ZodNever,
  ZodNull: () => ZodNull,
  ZodNullable: () => ZodNullable,
  ZodNumber: () => ZodNumber,
  ZodObject: () => ZodObject,
  ZodOptional: () => ZodOptional,
  ZodParsedType: () => ZodParsedType,
  ZodPipeline: () => ZodPipeline,
  ZodPromise: () => ZodPromise,
  ZodReadonly: () => ZodReadonly,
  ZodRecord: () => ZodRecord,
  ZodSchema: () => ZodType,
  ZodSet: () => ZodSet,
  ZodString: () => ZodString,
  ZodSymbol: () => ZodSymbol,
  ZodTransformer: () => ZodEffects,
  ZodTuple: () => ZodTuple,
  ZodType: () => ZodType,
  ZodUndefined: () => ZodUndefined,
  ZodUnion: () => ZodUnion,
  ZodUnknown: () => ZodUnknown,
  ZodVoid: () => ZodVoid,
  addIssueToContext: () => addIssueToContext,
  any: () => anyType,
  array: () => arrayType,
  bigint: () => bigIntType,
  boolean: () => booleanType,
  coerce: () => coerce,
  custom: () => custom,
  date: () => dateType,
  datetimeRegex: () => datetimeRegex,
  defaultErrorMap: () => en_default,
  discriminatedUnion: () => discriminatedUnionType,
  effect: () => effectsType,
  enum: () => enumType,
  function: () => functionType,
  getErrorMap: () => getErrorMap,
  getParsedType: () => getParsedType,
  instanceof: () => instanceOfType,
  intersection: () => intersectionType,
  isAborted: () => isAborted,
  isAsync: () => isAsync,
  isDirty: () => isDirty,
  isValid: () => isValid,
  late: () => late,
  lazy: () => lazyType,
  literal: () => literalType,
  makeIssue: () => makeIssue,
  map: () => mapType,
  nan: () => nanType,
  nativeEnum: () => nativeEnumType,
  never: () => neverType,
  null: () => nullType,
  nullable: () => nullableType,
  number: () => numberType,
  object: () => objectType,
  objectUtil: () => objectUtil,
  oboolean: () => oboolean,
  onumber: () => onumber,
  optional: () => optionalType,
  ostring: () => ostring,
  pipeline: () => pipelineType,
  preprocess: () => preprocessType,
  promise: () => promiseType,
  quotelessJson: () => quotelessJson,
  record: () => recordType,
  set: () => setType,
  setErrorMap: () => setErrorMap,
  strictObject: () => strictObjectType,
  string: () => stringType,
  symbol: () => symbolType,
  transformer: () => effectsType,
  tuple: () => tupleType,
  undefined: () => undefinedType,
  union: () => unionType,
  unknown: () => unknownType,
  util: () => util,
  void: () => voidType
});

// ../../node_modules/zod/v3/helpers/util.js
var util;
(function(util2) {
  util2.assertEqual = (_) => {
  };
  function assertIs(_arg) {
  }
  util2.assertIs = assertIs;
  function assertNever(_x) {
    throw new Error();
  }
  util2.assertNever = assertNever;
  util2.arrayToEnum = (items) => {
    const obj = {};
    for (const item of items) {
      obj[item] = item;
    }
    return obj;
  };
  util2.getValidEnumValues = (obj) => {
    const validKeys = util2.objectKeys(obj).filter((k) => typeof obj[obj[k]] !== "number");
    const filtered = {};
    for (const k of validKeys) {
      filtered[k] = obj[k];
    }
    return util2.objectValues(filtered);
  };
  util2.objectValues = (obj) => {
    return util2.objectKeys(obj).map(function(e) {
      return obj[e];
    });
  };
  util2.objectKeys = typeof Object.keys === "function" ? (obj) => Object.keys(obj) : (object) => {
    const keys = [];
    for (const key in object) {
      if (Object.prototype.hasOwnProperty.call(object, key)) {
        keys.push(key);
      }
    }
    return keys;
  };
  util2.find = (arr, checker) => {
    for (const item of arr) {
      if (checker(item))
        return item;
    }
    return void 0;
  };
  util2.isInteger = typeof Number.isInteger === "function" ? (val) => Number.isInteger(val) : (val) => typeof val === "number" && Number.isFinite(val) && Math.floor(val) === val;
  function joinValues(array, separator = " | ") {
    return array.map((val) => typeof val === "string" ? `'${val}'` : val).join(separator);
  }
  util2.joinValues = joinValues;
  util2.jsonStringifyReplacer = (_, value) => {
    if (typeof value === "bigint") {
      return value.toString();
    }
    return value;
  };
})(util || (util = {}));
var objectUtil;
(function(objectUtil2) {
  objectUtil2.mergeShapes = (first, second) => {
    return {
      ...first,
      ...second
      // second overwrites first
    };
  };
})(objectUtil || (objectUtil = {}));
var ZodParsedType = util.arrayToEnum([
  "string",
  "nan",
  "number",
  "integer",
  "float",
  "boolean",
  "date",
  "bigint",
  "symbol",
  "function",
  "undefined",
  "null",
  "array",
  "object",
  "unknown",
  "promise",
  "void",
  "never",
  "map",
  "set"
]);
var getParsedType = (data) => {
  const t = typeof data;
  switch (t) {
    case "undefined":
      return ZodParsedType.undefined;
    case "string":
      return ZodParsedType.string;
    case "number":
      return Number.isNaN(data) ? ZodParsedType.nan : ZodParsedType.number;
    case "boolean":
      return ZodParsedType.boolean;
    case "function":
      return ZodParsedType.function;
    case "bigint":
      return ZodParsedType.bigint;
    case "symbol":
      return ZodParsedType.symbol;
    case "object":
      if (Array.isArray(data)) {
        return ZodParsedType.array;
      }
      if (data === null) {
        return ZodParsedType.null;
      }
      if (data.then && typeof data.then === "function" && data.catch && typeof data.catch === "function") {
        return ZodParsedType.promise;
      }
      if (typeof Map !== "undefined" && data instanceof Map) {
        return ZodParsedType.map;
      }
      if (typeof Set !== "undefined" && data instanceof Set) {
        return ZodParsedType.set;
      }
      if (typeof Date !== "undefined" && data instanceof Date) {
        return ZodParsedType.date;
      }
      return ZodParsedType.object;
    default:
      return ZodParsedType.unknown;
  }
};

// ../../node_modules/zod/v3/ZodError.js
var ZodIssueCode = util.arrayToEnum([
  "invalid_type",
  "invalid_literal",
  "custom",
  "invalid_union",
  "invalid_union_discriminator",
  "invalid_enum_value",
  "unrecognized_keys",
  "invalid_arguments",
  "invalid_return_type",
  "invalid_date",
  "invalid_string",
  "too_small",
  "too_big",
  "invalid_intersection_types",
  "not_multiple_of",
  "not_finite"
]);
var quotelessJson = (obj) => {
  const json = JSON.stringify(obj, null, 2);
  return json.replace(/"([^"]+)":/g, "$1:");
};
var ZodError = class _ZodError extends Error {
  get errors() {
    return this.issues;
  }
  constructor(issues) {
    super();
    this.issues = [];
    this.addIssue = (sub) => {
      this.issues = [...this.issues, sub];
    };
    this.addIssues = (subs = []) => {
      this.issues = [...this.issues, ...subs];
    };
    const actualProto = new.target.prototype;
    if (Object.setPrototypeOf) {
      Object.setPrototypeOf(this, actualProto);
    } else {
      this.__proto__ = actualProto;
    }
    this.name = "ZodError";
    this.issues = issues;
  }
  format(_mapper) {
    const mapper = _mapper || function(issue) {
      return issue.message;
    };
    const fieldErrors = { _errors: [] };
    const processError = (error) => {
      for (const issue of error.issues) {
        if (issue.code === "invalid_union") {
          issue.unionErrors.map(processError);
        } else if (issue.code === "invalid_return_type") {
          processError(issue.returnTypeError);
        } else if (issue.code === "invalid_arguments") {
          processError(issue.argumentsError);
        } else if (issue.path.length === 0) {
          fieldErrors._errors.push(mapper(issue));
        } else {
          let curr = fieldErrors;
          let i = 0;
          while (i < issue.path.length) {
            const el = issue.path[i];
            const terminal = i === issue.path.length - 1;
            if (!terminal) {
              curr[el] = curr[el] || { _errors: [] };
            } else {
              curr[el] = curr[el] || { _errors: [] };
              curr[el]._errors.push(mapper(issue));
            }
            curr = curr[el];
            i++;
          }
        }
      }
    };
    processError(this);
    return fieldErrors;
  }
  static assert(value) {
    if (!(value instanceof _ZodError)) {
      throw new Error(`Not a ZodError: ${value}`);
    }
  }
  toString() {
    return this.message;
  }
  get message() {
    return JSON.stringify(this.issues, util.jsonStringifyReplacer, 2);
  }
  get isEmpty() {
    return this.issues.length === 0;
  }
  flatten(mapper = (issue) => issue.message) {
    const fieldErrors = {};
    const formErrors = [];
    for (const sub of this.issues) {
      if (sub.path.length > 0) {
        const firstEl = sub.path[0];
        fieldErrors[firstEl] = fieldErrors[firstEl] || [];
        fieldErrors[firstEl].push(mapper(sub));
      } else {
        formErrors.push(mapper(sub));
      }
    }
    return { formErrors, fieldErrors };
  }
  get formErrors() {
    return this.flatten();
  }
};
ZodError.create = (issues) => {
  const error = new ZodError(issues);
  return error;
};

// ../../node_modules/zod/v3/locales/en.js
var errorMap = (issue, _ctx) => {
  let message;
  switch (issue.code) {
    case ZodIssueCode.invalid_type:
      if (issue.received === ZodParsedType.undefined) {
        message = "Required";
      } else {
        message = `Expected ${issue.expected}, received ${issue.received}`;
      }
      break;
    case ZodIssueCode.invalid_literal:
      message = `Invalid literal value, expected ${JSON.stringify(issue.expected, util.jsonStringifyReplacer)}`;
      break;
    case ZodIssueCode.unrecognized_keys:
      message = `Unrecognized key(s) in object: ${util.joinValues(issue.keys, ", ")}`;
      break;
    case ZodIssueCode.invalid_union:
      message = `Invalid input`;
      break;
    case ZodIssueCode.invalid_union_discriminator:
      message = `Invalid discriminator value. Expected ${util.joinValues(issue.options)}`;
      break;
    case ZodIssueCode.invalid_enum_value:
      message = `Invalid enum value. Expected ${util.joinValues(issue.options)}, received '${issue.received}'`;
      break;
    case ZodIssueCode.invalid_arguments:
      message = `Invalid function arguments`;
      break;
    case ZodIssueCode.invalid_return_type:
      message = `Invalid function return type`;
      break;
    case ZodIssueCode.invalid_date:
      message = `Invalid date`;
      break;
    case ZodIssueCode.invalid_string:
      if (typeof issue.validation === "object") {
        if ("includes" in issue.validation) {
          message = `Invalid input: must include "${issue.validation.includes}"`;
          if (typeof issue.validation.position === "number") {
            message = `${message} at one or more positions greater than or equal to ${issue.validation.position}`;
          }
        } else if ("startsWith" in issue.validation) {
          message = `Invalid input: must start with "${issue.validation.startsWith}"`;
        } else if ("endsWith" in issue.validation) {
          message = `Invalid input: must end with "${issue.validation.endsWith}"`;
        } else {
          util.assertNever(issue.validation);
        }
      } else if (issue.validation !== "regex") {
        message = `Invalid ${issue.validation}`;
      } else {
        message = "Invalid";
      }
      break;
    case ZodIssueCode.too_small:
      if (issue.type === "array")
        message = `Array must contain ${issue.exact ? "exactly" : issue.inclusive ? `at least` : `more than`} ${issue.minimum} element(s)`;
      else if (issue.type === "string")
        message = `String must contain ${issue.exact ? "exactly" : issue.inclusive ? `at least` : `over`} ${issue.minimum} character(s)`;
      else if (issue.type === "number")
        message = `Number must be ${issue.exact ? `exactly equal to ` : issue.inclusive ? `greater than or equal to ` : `greater than `}${issue.minimum}`;
      else if (issue.type === "bigint")
        message = `Number must be ${issue.exact ? `exactly equal to ` : issue.inclusive ? `greater than or equal to ` : `greater than `}${issue.minimum}`;
      else if (issue.type === "date")
        message = `Date must be ${issue.exact ? `exactly equal to ` : issue.inclusive ? `greater than or equal to ` : `greater than `}${new Date(Number(issue.minimum))}`;
      else
        message = "Invalid input";
      break;
    case ZodIssueCode.too_big:
      if (issue.type === "array")
        message = `Array must contain ${issue.exact ? `exactly` : issue.inclusive ? `at most` : `less than`} ${issue.maximum} element(s)`;
      else if (issue.type === "string")
        message = `String must contain ${issue.exact ? `exactly` : issue.inclusive ? `at most` : `under`} ${issue.maximum} character(s)`;
      else if (issue.type === "number")
        message = `Number must be ${issue.exact ? `exactly` : issue.inclusive ? `less than or equal to` : `less than`} ${issue.maximum}`;
      else if (issue.type === "bigint")
        message = `BigInt must be ${issue.exact ? `exactly` : issue.inclusive ? `less than or equal to` : `less than`} ${issue.maximum}`;
      else if (issue.type === "date")
        message = `Date must be ${issue.exact ? `exactly` : issue.inclusive ? `smaller than or equal to` : `smaller than`} ${new Date(Number(issue.maximum))}`;
      else
        message = "Invalid input";
      break;
    case ZodIssueCode.custom:
      message = `Invalid input`;
      break;
    case ZodIssueCode.invalid_intersection_types:
      message = `Intersection results could not be merged`;
      break;
    case ZodIssueCode.not_multiple_of:
      message = `Number must be a multiple of ${issue.multipleOf}`;
      break;
    case ZodIssueCode.not_finite:
      message = "Number must be finite";
      break;
    default:
      message = _ctx.defaultError;
      util.assertNever(issue);
  }
  return { message };
};
var en_default = errorMap;

// ../../node_modules/zod/v3/errors.js
var overrideErrorMap = en_default;
function setErrorMap(map) {
  overrideErrorMap = map;
}
function getErrorMap() {
  return overrideErrorMap;
}

// ../../node_modules/zod/v3/helpers/parseUtil.js
var makeIssue = (params) => {
  const { data, path, errorMaps, issueData } = params;
  const fullPath = [...path, ...issueData.path || []];
  const fullIssue = {
    ...issueData,
    path: fullPath
  };
  if (issueData.message !== void 0) {
    return {
      ...issueData,
      path: fullPath,
      message: issueData.message
    };
  }
  let errorMessage = "";
  const maps = errorMaps.filter((m) => !!m).slice().reverse();
  for (const map of maps) {
    errorMessage = map(fullIssue, { data, defaultError: errorMessage }).message;
  }
  return {
    ...issueData,
    path: fullPath,
    message: errorMessage
  };
};
var EMPTY_PATH = [];
function addIssueToContext(ctx, issueData) {
  const overrideMap = getErrorMap();
  const issue = makeIssue({
    issueData,
    data: ctx.data,
    path: ctx.path,
    errorMaps: [
      ctx.common.contextualErrorMap,
      // contextual error map is first priority
      ctx.schemaErrorMap,
      // then schema-bound map if available
      overrideMap,
      // then global override map
      overrideMap === en_default ? void 0 : en_default
      // then global default map
    ].filter((x) => !!x)
  });
  ctx.common.issues.push(issue);
}
var ParseStatus = class _ParseStatus {
  constructor() {
    this.value = "valid";
  }
  dirty() {
    if (this.value === "valid")
      this.value = "dirty";
  }
  abort() {
    if (this.value !== "aborted")
      this.value = "aborted";
  }
  static mergeArray(status, results) {
    const arrayValue = [];
    for (const s of results) {
      if (s.status === "aborted")
        return INVALID;
      if (s.status === "dirty")
        status.dirty();
      arrayValue.push(s.value);
    }
    return { status: status.value, value: arrayValue };
  }
  static async mergeObjectAsync(status, pairs) {
    const syncPairs = [];
    for (const pair of pairs) {
      const key = await pair.key;
      const value = await pair.value;
      syncPairs.push({
        key,
        value
      });
    }
    return _ParseStatus.mergeObjectSync(status, syncPairs);
  }
  static mergeObjectSync(status, pairs) {
    const finalObject = {};
    for (const pair of pairs) {
      const { key, value } = pair;
      if (key.status === "aborted")
        return INVALID;
      if (value.status === "aborted")
        return INVALID;
      if (key.status === "dirty")
        status.dirty();
      if (value.status === "dirty")
        status.dirty();
      if (key.value !== "__proto__" && (typeof value.value !== "undefined" || pair.alwaysSet)) {
        finalObject[key.value] = value.value;
      }
    }
    return { status: status.value, value: finalObject };
  }
};
var INVALID = Object.freeze({
  status: "aborted"
});
var DIRTY = (value) => ({ status: "dirty", value });
var OK = (value) => ({ status: "valid", value });
var isAborted = (x) => x.status === "aborted";
var isDirty = (x) => x.status === "dirty";
var isValid = (x) => x.status === "valid";
var isAsync = (x) => typeof Promise !== "undefined" && x instanceof Promise;

// ../../node_modules/zod/v3/helpers/errorUtil.js
var errorUtil;
(function(errorUtil2) {
  errorUtil2.errToObj = (message) => typeof message === "string" ? { message } : message || {};
  errorUtil2.toString = (message) => typeof message === "string" ? message : message?.message;
})(errorUtil || (errorUtil = {}));

// ../../node_modules/zod/v3/types.js
var ParseInputLazyPath = class {
  constructor(parent, value, path, key) {
    this._cachedPath = [];
    this.parent = parent;
    this.data = value;
    this._path = path;
    this._key = key;
  }
  get path() {
    if (!this._cachedPath.length) {
      if (Array.isArray(this._key)) {
        this._cachedPath.push(...this._path, ...this._key);
      } else {
        this._cachedPath.push(...this._path, this._key);
      }
    }
    return this._cachedPath;
  }
};
var handleResult = (ctx, result) => {
  if (isValid(result)) {
    return { success: true, data: result.value };
  } else {
    if (!ctx.common.issues.length) {
      throw new Error("Validation failed but no issues detected.");
    }
    return {
      success: false,
      get error() {
        if (this._error)
          return this._error;
        const error = new ZodError(ctx.common.issues);
        this._error = error;
        return this._error;
      }
    };
  }
};
function processCreateParams(params) {
  if (!params)
    return {};
  const { errorMap: errorMap2, invalid_type_error, required_error, description } = params;
  if (errorMap2 && (invalid_type_error || required_error)) {
    throw new Error(`Can't use "invalid_type_error" or "required_error" in conjunction with custom error map.`);
  }
  if (errorMap2)
    return { errorMap: errorMap2, description };
  const customMap = (iss, ctx) => {
    const { message } = params;
    if (iss.code === "invalid_enum_value") {
      return { message: message ?? ctx.defaultError };
    }
    if (typeof ctx.data === "undefined") {
      return { message: message ?? required_error ?? ctx.defaultError };
    }
    if (iss.code !== "invalid_type")
      return { message: ctx.defaultError };
    return { message: message ?? invalid_type_error ?? ctx.defaultError };
  };
  return { errorMap: customMap, description };
}
var ZodType = class {
  get description() {
    return this._def.description;
  }
  _getType(input) {
    return getParsedType(input.data);
  }
  _getOrReturnCtx(input, ctx) {
    return ctx || {
      common: input.parent.common,
      data: input.data,
      parsedType: getParsedType(input.data),
      schemaErrorMap: this._def.errorMap,
      path: input.path,
      parent: input.parent
    };
  }
  _processInputParams(input) {
    return {
      status: new ParseStatus(),
      ctx: {
        common: input.parent.common,
        data: input.data,
        parsedType: getParsedType(input.data),
        schemaErrorMap: this._def.errorMap,
        path: input.path,
        parent: input.parent
      }
    };
  }
  _parseSync(input) {
    const result = this._parse(input);
    if (isAsync(result)) {
      throw new Error("Synchronous parse encountered promise.");
    }
    return result;
  }
  _parseAsync(input) {
    const result = this._parse(input);
    return Promise.resolve(result);
  }
  parse(data, params) {
    const result = this.safeParse(data, params);
    if (result.success)
      return result.data;
    throw result.error;
  }
  safeParse(data, params) {
    const ctx = {
      common: {
        issues: [],
        async: params?.async ?? false,
        contextualErrorMap: params?.errorMap
      },
      path: params?.path || [],
      schemaErrorMap: this._def.errorMap,
      parent: null,
      data,
      parsedType: getParsedType(data)
    };
    const result = this._parseSync({ data, path: ctx.path, parent: ctx });
    return handleResult(ctx, result);
  }
  "~validate"(data) {
    const ctx = {
      common: {
        issues: [],
        async: !!this["~standard"].async
      },
      path: [],
      schemaErrorMap: this._def.errorMap,
      parent: null,
      data,
      parsedType: getParsedType(data)
    };
    if (!this["~standard"].async) {
      try {
        const result = this._parseSync({ data, path: [], parent: ctx });
        return isValid(result) ? {
          value: result.value
        } : {
          issues: ctx.common.issues
        };
      } catch (err) {
        if (err?.message?.toLowerCase()?.includes("encountered")) {
          this["~standard"].async = true;
        }
        ctx.common = {
          issues: [],
          async: true
        };
      }
    }
    return this._parseAsync({ data, path: [], parent: ctx }).then((result) => isValid(result) ? {
      value: result.value
    } : {
      issues: ctx.common.issues
    });
  }
  async parseAsync(data, params) {
    const result = await this.safeParseAsync(data, params);
    if (result.success)
      return result.data;
    throw result.error;
  }
  async safeParseAsync(data, params) {
    const ctx = {
      common: {
        issues: [],
        contextualErrorMap: params?.errorMap,
        async: true
      },
      path: params?.path || [],
      schemaErrorMap: this._def.errorMap,
      parent: null,
      data,
      parsedType: getParsedType(data)
    };
    const maybeAsyncResult = this._parse({ data, path: ctx.path, parent: ctx });
    const result = await (isAsync(maybeAsyncResult) ? maybeAsyncResult : Promise.resolve(maybeAsyncResult));
    return handleResult(ctx, result);
  }
  refine(check, message) {
    const getIssueProperties = (val) => {
      if (typeof message === "string" || typeof message === "undefined") {
        return { message };
      } else if (typeof message === "function") {
        return message(val);
      } else {
        return message;
      }
    };
    return this._refinement((val, ctx) => {
      const result = check(val);
      const setError = () => ctx.addIssue({
        code: ZodIssueCode.custom,
        ...getIssueProperties(val)
      });
      if (typeof Promise !== "undefined" && result instanceof Promise) {
        return result.then((data) => {
          if (!data) {
            setError();
            return false;
          } else {
            return true;
          }
        });
      }
      if (!result) {
        setError();
        return false;
      } else {
        return true;
      }
    });
  }
  refinement(check, refinementData) {
    return this._refinement((val, ctx) => {
      if (!check(val)) {
        ctx.addIssue(typeof refinementData === "function" ? refinementData(val, ctx) : refinementData);
        return false;
      } else {
        return true;
      }
    });
  }
  _refinement(refinement) {
    return new ZodEffects({
      schema: this,
      typeName: ZodFirstPartyTypeKind.ZodEffects,
      effect: { type: "refinement", refinement }
    });
  }
  superRefine(refinement) {
    return this._refinement(refinement);
  }
  constructor(def) {
    this.spa = this.safeParseAsync;
    this._def = def;
    this.parse = this.parse.bind(this);
    this.safeParse = this.safeParse.bind(this);
    this.parseAsync = this.parseAsync.bind(this);
    this.safeParseAsync = this.safeParseAsync.bind(this);
    this.spa = this.spa.bind(this);
    this.refine = this.refine.bind(this);
    this.refinement = this.refinement.bind(this);
    this.superRefine = this.superRefine.bind(this);
    this.optional = this.optional.bind(this);
    this.nullable = this.nullable.bind(this);
    this.nullish = this.nullish.bind(this);
    this.array = this.array.bind(this);
    this.promise = this.promise.bind(this);
    this.or = this.or.bind(this);
    this.and = this.and.bind(this);
    this.transform = this.transform.bind(this);
    this.brand = this.brand.bind(this);
    this.default = this.default.bind(this);
    this.catch = this.catch.bind(this);
    this.describe = this.describe.bind(this);
    this.pipe = this.pipe.bind(this);
    this.readonly = this.readonly.bind(this);
    this.isNullable = this.isNullable.bind(this);
    this.isOptional = this.isOptional.bind(this);
    this["~standard"] = {
      version: 1,
      vendor: "zod",
      validate: (data) => this["~validate"](data)
    };
  }
  optional() {
    return ZodOptional.create(this, this._def);
  }
  nullable() {
    return ZodNullable.create(this, this._def);
  }
  nullish() {
    return this.nullable().optional();
  }
  array() {
    return ZodArray.create(this);
  }
  promise() {
    return ZodPromise.create(this, this._def);
  }
  or(option) {
    return ZodUnion.create([this, option], this._def);
  }
  and(incoming) {
    return ZodIntersection.create(this, incoming, this._def);
  }
  transform(transform) {
    return new ZodEffects({
      ...processCreateParams(this._def),
      schema: this,
      typeName: ZodFirstPartyTypeKind.ZodEffects,
      effect: { type: "transform", transform }
    });
  }
  default(def) {
    const defaultValueFunc = typeof def === "function" ? def : () => def;
    return new ZodDefault({
      ...processCreateParams(this._def),
      innerType: this,
      defaultValue: defaultValueFunc,
      typeName: ZodFirstPartyTypeKind.ZodDefault
    });
  }
  brand() {
    return new ZodBranded({
      typeName: ZodFirstPartyTypeKind.ZodBranded,
      type: this,
      ...processCreateParams(this._def)
    });
  }
  catch(def) {
    const catchValueFunc = typeof def === "function" ? def : () => def;
    return new ZodCatch({
      ...processCreateParams(this._def),
      innerType: this,
      catchValue: catchValueFunc,
      typeName: ZodFirstPartyTypeKind.ZodCatch
    });
  }
  describe(description) {
    const This = this.constructor;
    return new This({
      ...this._def,
      description
    });
  }
  pipe(target) {
    return ZodPipeline.create(this, target);
  }
  readonly() {
    return ZodReadonly.create(this);
  }
  isOptional() {
    return this.safeParse(void 0).success;
  }
  isNullable() {
    return this.safeParse(null).success;
  }
};
var cuidRegex = /^c[^\s-]{8,}$/i;
var cuid2Regex = /^[0-9a-z]+$/;
var ulidRegex = /^[0-9A-HJKMNP-TV-Z]{26}$/i;
var uuidRegex = /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/i;
var nanoidRegex = /^[a-z0-9_-]{21}$/i;
var jwtRegex = /^[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+\.[A-Za-z0-9-_]*$/;
var durationRegex = /^[-+]?P(?!$)(?:(?:[-+]?\d+Y)|(?:[-+]?\d+[.,]\d+Y$))?(?:(?:[-+]?\d+M)|(?:[-+]?\d+[.,]\d+M$))?(?:(?:[-+]?\d+W)|(?:[-+]?\d+[.,]\d+W$))?(?:(?:[-+]?\d+D)|(?:[-+]?\d+[.,]\d+D$))?(?:T(?=[\d+-])(?:(?:[-+]?\d+H)|(?:[-+]?\d+[.,]\d+H$))?(?:(?:[-+]?\d+M)|(?:[-+]?\d+[.,]\d+M$))?(?:[-+]?\d+(?:[.,]\d+)?S)?)??$/;
var emailRegex = /^(?!\.)(?!.*\.\.)([A-Z0-9_'+\-\.]*)[A-Z0-9_+-]@([A-Z0-9][A-Z0-9\-]*\.)+[A-Z]{2,}$/i;
var _emojiRegex = `^(\\p{Extended_Pictographic}|\\p{Emoji_Component})+$`;
var emojiRegex;
var ipv4Regex = /^(?:(?:25[0-5]|2[0-4][0-9]|1[0-9][0-9]|[1-9][0-9]|[0-9])\.){3}(?:25[0-5]|2[0-4][0-9]|1[0-9][0-9]|[1-9][0-9]|[0-9])$/;
var ipv4CidrRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|1[0-9][0-9]|[1-9][0-9]|[0-9])\.){3}(?:25[0-5]|2[0-4][0-9]|1[0-9][0-9]|[1-9][0-9]|[0-9])\/(3[0-2]|[12]?[0-9])$/;
var ipv6Regex = /^(([0-9a-fA-F]{1,4}:){7,7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:((:[0-9a-fA-F]{1,4}){1,6})|:((:[0-9a-fA-F]{1,4}){1,7}|:)|fe80:(:[0-9a-fA-F]{0,4}){0,4}%[0-9a-zA-Z]{1,}|::(ffff(:0{1,4}){0,1}:){0,1}((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])|([0-9a-fA-F]{1,4}:){1,4}:((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9]))$/;
var ipv6CidrRegex = /^(([0-9a-fA-F]{1,4}:){7,7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:((:[0-9a-fA-F]{1,4}){1,6})|:((:[0-9a-fA-F]{1,4}){1,7}|:)|fe80:(:[0-9a-fA-F]{0,4}){0,4}%[0-9a-zA-Z]{1,}|::(ffff(:0{1,4}){0,1}:){0,1}((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])|([0-9a-fA-F]{1,4}:){1,4}:((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9]))\/(12[0-8]|1[01][0-9]|[1-9]?[0-9])$/;
var base64Regex = /^([0-9a-zA-Z+/]{4})*(([0-9a-zA-Z+/]{2}==)|([0-9a-zA-Z+/]{3}=))?$/;
var base64urlRegex = /^([0-9a-zA-Z-_]{4})*(([0-9a-zA-Z-_]{2}(==)?)|([0-9a-zA-Z-_]{3}(=)?))?$/;
var dateRegexSource = `((\\d\\d[2468][048]|\\d\\d[13579][26]|\\d\\d0[48]|[02468][048]00|[13579][26]00)-02-29|\\d{4}-((0[13578]|1[02])-(0[1-9]|[12]\\d|3[01])|(0[469]|11)-(0[1-9]|[12]\\d|30)|(02)-(0[1-9]|1\\d|2[0-8])))`;
var dateRegex = new RegExp(`^${dateRegexSource}$`);
function timeRegexSource(args) {
  let secondsRegexSource = `[0-5]\\d`;
  if (args.precision) {
    secondsRegexSource = `${secondsRegexSource}\\.\\d{${args.precision}}`;
  } else if (args.precision == null) {
    secondsRegexSource = `${secondsRegexSource}(\\.\\d+)?`;
  }
  const secondsQuantifier = args.precision ? "+" : "?";
  return `([01]\\d|2[0-3]):[0-5]\\d(:${secondsRegexSource})${secondsQuantifier}`;
}
function timeRegex(args) {
  return new RegExp(`^${timeRegexSource(args)}$`);
}
function datetimeRegex(args) {
  let regex = `${dateRegexSource}T${timeRegexSource(args)}`;
  const opts = [];
  opts.push(args.local ? `Z?` : `Z`);
  if (args.offset)
    opts.push(`([+-]\\d{2}:?\\d{2})`);
  regex = `${regex}(${opts.join("|")})`;
  return new RegExp(`^${regex}$`);
}
function isValidIP(ip, version) {
  if ((version === "v4" || !version) && ipv4Regex.test(ip)) {
    return true;
  }
  if ((version === "v6" || !version) && ipv6Regex.test(ip)) {
    return true;
  }
  return false;
}
function isValidJWT(jwt, alg) {
  if (!jwtRegex.test(jwt))
    return false;
  try {
    const [header] = jwt.split(".");
    if (!header)
      return false;
    const base64 = header.replace(/-/g, "+").replace(/_/g, "/").padEnd(header.length + (4 - header.length % 4) % 4, "=");
    const decoded = JSON.parse(atob(base64));
    if (typeof decoded !== "object" || decoded === null)
      return false;
    if ("typ" in decoded && decoded?.typ !== "JWT")
      return false;
    if (!decoded.alg)
      return false;
    if (alg && decoded.alg !== alg)
      return false;
    return true;
  } catch {
    return false;
  }
}
function isValidCidr(ip, version) {
  if ((version === "v4" || !version) && ipv4CidrRegex.test(ip)) {
    return true;
  }
  if ((version === "v6" || !version) && ipv6CidrRegex.test(ip)) {
    return true;
  }
  return false;
}
var ZodString = class _ZodString extends ZodType {
  _parse(input) {
    if (this._def.coerce) {
      input.data = String(input.data);
    }
    const parsedType = this._getType(input);
    if (parsedType !== ZodParsedType.string) {
      const ctx2 = this._getOrReturnCtx(input);
      addIssueToContext(ctx2, {
        code: ZodIssueCode.invalid_type,
        expected: ZodParsedType.string,
        received: ctx2.parsedType
      });
      return INVALID;
    }
    const status = new ParseStatus();
    let ctx = void 0;
    for (const check of this._def.checks) {
      if (check.kind === "min") {
        if (input.data.length < check.value) {
          ctx = this._getOrReturnCtx(input, ctx);
          addIssueToContext(ctx, {
            code: ZodIssueCode.too_small,
            minimum: check.value,
            type: "string",
            inclusive: true,
            exact: false,
            message: check.message
          });
          status.dirty();
        }
      } else if (check.kind === "max") {
        if (input.data.length > check.value) {
          ctx = this._getOrReturnCtx(input, ctx);
          addIssueToContext(ctx, {
            code: ZodIssueCode.too_big,
            maximum: check.value,
            type: "string",
            inclusive: true,
            exact: false,
            message: check.message
          });
          status.dirty();
        }
      } else if (check.kind === "length") {
        const tooBig = input.data.length > check.value;
        const tooSmall = input.data.length < check.value;
        if (tooBig || tooSmall) {
          ctx = this._getOrReturnCtx(input, ctx);
          if (tooBig) {
            addIssueToContext(ctx, {
              code: ZodIssueCode.too_big,
              maximum: check.value,
              type: "string",
              inclusive: true,
              exact: true,
              message: check.message
            });
          } else if (tooSmall) {
            addIssueToContext(ctx, {
              code: ZodIssueCode.too_small,
              minimum: check.value,
              type: "string",
              inclusive: true,
              exact: true,
              message: check.message
            });
          }
          status.dirty();
        }
      } else if (check.kind === "email") {
        if (!emailRegex.test(input.data)) {
          ctx = this._getOrReturnCtx(input, ctx);
          addIssueToContext(ctx, {
            validation: "email",
            code: ZodIssueCode.invalid_string,
            message: check.message
          });
          status.dirty();
        }
      } else if (check.kind === "emoji") {
        if (!emojiRegex) {
          emojiRegex = new RegExp(_emojiRegex, "u");
        }
        if (!emojiRegex.test(input.data)) {
          ctx = this._getOrReturnCtx(input, ctx);
          addIssueToContext(ctx, {
            validation: "emoji",
            code: ZodIssueCode.invalid_string,
            message: check.message
          });
          status.dirty();
        }
      } else if (check.kind === "uuid") {
        if (!uuidRegex.test(input.data)) {
          ctx = this._getOrReturnCtx(input, ctx);
          addIssueToContext(ctx, {
            validation: "uuid",
            code: ZodIssueCode.invalid_string,
            message: check.message
          });
          status.dirty();
        }
      } else if (check.kind === "nanoid") {
        if (!nanoidRegex.test(input.data)) {
          ctx = this._getOrReturnCtx(input, ctx);
          addIssueToContext(ctx, {
            validation: "nanoid",
            code: ZodIssueCode.invalid_string,
            message: check.message
          });
          status.dirty();
        }
      } else if (check.kind === "cuid") {
        if (!cuidRegex.test(input.data)) {
          ctx = this._getOrReturnCtx(input, ctx);
          addIssueToContext(ctx, {
            validation: "cuid",
            code: ZodIssueCode.invalid_string,
            message: check.message
          });
          status.dirty();
        }
      } else if (check.kind === "cuid2") {
        if (!cuid2Regex.test(input.data)) {
          ctx = this._getOrReturnCtx(input, ctx);
          addIssueToContext(ctx, {
            validation: "cuid2",
            code: ZodIssueCode.invalid_string,
            message: check.message
          });
          status.dirty();
        }
      } else if (check.kind === "ulid") {
        if (!ulidRegex.test(input.data)) {
          ctx = this._getOrReturnCtx(input, ctx);
          addIssueToContext(ctx, {
            validation: "ulid",
            code: ZodIssueCode.invalid_string,
            message: check.message
          });
          status.dirty();
        }
      } else if (check.kind === "url") {
        try {
          new URL(input.data);
        } catch {
          ctx = this._getOrReturnCtx(input, ctx);
          addIssueToContext(ctx, {
            validation: "url",
            code: ZodIssueCode.invalid_string,
            message: check.message
          });
          status.dirty();
        }
      } else if (check.kind === "regex") {
        check.regex.lastIndex = 0;
        const testResult = check.regex.test(input.data);
        if (!testResult) {
          ctx = this._getOrReturnCtx(input, ctx);
          addIssueToContext(ctx, {
            validation: "regex",
            code: ZodIssueCode.invalid_string,
            message: check.message
          });
          status.dirty();
        }
      } else if (check.kind === "trim") {
        input.data = input.data.trim();
      } else if (check.kind === "includes") {
        if (!input.data.includes(check.value, check.position)) {
          ctx = this._getOrReturnCtx(input, ctx);
          addIssueToContext(ctx, {
            code: ZodIssueCode.invalid_string,
            validation: { includes: check.value, position: check.position },
            message: check.message
          });
          status.dirty();
        }
      } else if (check.kind === "toLowerCase") {
        input.data = input.data.toLowerCase();
      } else if (check.kind === "toUpperCase") {
        input.data = input.data.toUpperCase();
      } else if (check.kind === "startsWith") {
        if (!input.data.startsWith(check.value)) {
          ctx = this._getOrReturnCtx(input, ctx);
          addIssueToContext(ctx, {
            code: ZodIssueCode.invalid_string,
            validation: { startsWith: check.value },
            message: check.message
          });
          status.dirty();
        }
      } else if (check.kind === "endsWith") {
        if (!input.data.endsWith(check.value)) {
          ctx = this._getOrReturnCtx(input, ctx);
          addIssueToContext(ctx, {
            code: ZodIssueCode.invalid_string,
            validation: { endsWith: check.value },
            message: check.message
          });
          status.dirty();
        }
      } else if (check.kind === "datetime") {
        const regex = datetimeRegex(check);
        if (!regex.test(input.data)) {
          ctx = this._getOrReturnCtx(input, ctx);
          addIssueToContext(ctx, {
            code: ZodIssueCode.invalid_string,
            validation: "datetime",
            message: check.message
          });
          status.dirty();
        }
      } else if (check.kind === "date") {
        const regex = dateRegex;
        if (!regex.test(input.data)) {
          ctx = this._getOrReturnCtx(input, ctx);
          addIssueToContext(ctx, {
            code: ZodIssueCode.invalid_string,
            validation: "date",
            message: check.message
          });
          status.dirty();
        }
      } else if (check.kind === "time") {
        const regex = timeRegex(check);
        if (!regex.test(input.data)) {
          ctx = this._getOrReturnCtx(input, ctx);
          addIssueToContext(ctx, {
            code: ZodIssueCode.invalid_string,
            validation: "time",
            message: check.message
          });
          status.dirty();
        }
      } else if (check.kind === "duration") {
        if (!durationRegex.test(input.data)) {
          ctx = this._getOrReturnCtx(input, ctx);
          addIssueToContext(ctx, {
            validation: "duration",
            code: ZodIssueCode.invalid_string,
            message: check.message
          });
          status.dirty();
        }
      } else if (check.kind === "ip") {
        if (!isValidIP(input.data, check.version)) {
          ctx = this._getOrReturnCtx(input, ctx);
          addIssueToContext(ctx, {
            validation: "ip",
            code: ZodIssueCode.invalid_string,
            message: check.message
          });
          status.dirty();
        }
      } else if (check.kind === "jwt") {
        if (!isValidJWT(input.data, check.alg)) {
          ctx = this._getOrReturnCtx(input, ctx);
          addIssueToContext(ctx, {
            validation: "jwt",
            code: ZodIssueCode.invalid_string,
            message: check.message
          });
          status.dirty();
        }
      } else if (check.kind === "cidr") {
        if (!isValidCidr(input.data, check.version)) {
          ctx = this._getOrReturnCtx(input, ctx);
          addIssueToContext(ctx, {
            validation: "cidr",
            code: ZodIssueCode.invalid_string,
            message: check.message
          });
          status.dirty();
        }
      } else if (check.kind === "base64") {
        if (!base64Regex.test(input.data)) {
          ctx = this._getOrReturnCtx(input, ctx);
          addIssueToContext(ctx, {
            validation: "base64",
            code: ZodIssueCode.invalid_string,
            message: check.message
          });
          status.dirty();
        }
      } else if (check.kind === "base64url") {
        if (!base64urlRegex.test(input.data)) {
          ctx = this._getOrReturnCtx(input, ctx);
          addIssueToContext(ctx, {
            validation: "base64url",
            code: ZodIssueCode.invalid_string,
            message: check.message
          });
          status.dirty();
        }
      } else {
        util.assertNever(check);
      }
    }
    return { status: status.value, value: input.data };
  }
  _regex(regex, validation, message) {
    return this.refinement((data) => regex.test(data), {
      validation,
      code: ZodIssueCode.invalid_string,
      ...errorUtil.errToObj(message)
    });
  }
  _addCheck(check) {
    return new _ZodString({
      ...this._def,
      checks: [...this._def.checks, check]
    });
  }
  email(message) {
    return this._addCheck({ kind: "email", ...errorUtil.errToObj(message) });
  }
  url(message) {
    return this._addCheck({ kind: "url", ...errorUtil.errToObj(message) });
  }
  emoji(message) {
    return this._addCheck({ kind: "emoji", ...errorUtil.errToObj(message) });
  }
  uuid(message) {
    return this._addCheck({ kind: "uuid", ...errorUtil.errToObj(message) });
  }
  nanoid(message) {
    return this._addCheck({ kind: "nanoid", ...errorUtil.errToObj(message) });
  }
  cuid(message) {
    return this._addCheck({ kind: "cuid", ...errorUtil.errToObj(message) });
  }
  cuid2(message) {
    return this._addCheck({ kind: "cuid2", ...errorUtil.errToObj(message) });
  }
  ulid(message) {
    return this._addCheck({ kind: "ulid", ...errorUtil.errToObj(message) });
  }
  base64(message) {
    return this._addCheck({ kind: "base64", ...errorUtil.errToObj(message) });
  }
  base64url(message) {
    return this._addCheck({
      kind: "base64url",
      ...errorUtil.errToObj(message)
    });
  }
  jwt(options) {
    return this._addCheck({ kind: "jwt", ...errorUtil.errToObj(options) });
  }
  ip(options) {
    return this._addCheck({ kind: "ip", ...errorUtil.errToObj(options) });
  }
  cidr(options) {
    return this._addCheck({ kind: "cidr", ...errorUtil.errToObj(options) });
  }
  datetime(options) {
    if (typeof options === "string") {
      return this._addCheck({
        kind: "datetime",
        precision: null,
        offset: false,
        local: false,
        message: options
      });
    }
    return this._addCheck({
      kind: "datetime",
      precision: typeof options?.precision === "undefined" ? null : options?.precision,
      offset: options?.offset ?? false,
      local: options?.local ?? false,
      ...errorUtil.errToObj(options?.message)
    });
  }
  date(message) {
    return this._addCheck({ kind: "date", message });
  }
  time(options) {
    if (typeof options === "string") {
      return this._addCheck({
        kind: "time",
        precision: null,
        message: options
      });
    }
    return this._addCheck({
      kind: "time",
      precision: typeof options?.precision === "undefined" ? null : options?.precision,
      ...errorUtil.errToObj(options?.message)
    });
  }
  duration(message) {
    return this._addCheck({ kind: "duration", ...errorUtil.errToObj(message) });
  }
  regex(regex, message) {
    return this._addCheck({
      kind: "regex",
      regex,
      ...errorUtil.errToObj(message)
    });
  }
  includes(value, options) {
    return this._addCheck({
      kind: "includes",
      value,
      position: options?.position,
      ...errorUtil.errToObj(options?.message)
    });
  }
  startsWith(value, message) {
    return this._addCheck({
      kind: "startsWith",
      value,
      ...errorUtil.errToObj(message)
    });
  }
  endsWith(value, message) {
    return this._addCheck({
      kind: "endsWith",
      value,
      ...errorUtil.errToObj(message)
    });
  }
  min(minLength, message) {
    return this._addCheck({
      kind: "min",
      value: minLength,
      ...errorUtil.errToObj(message)
    });
  }
  max(maxLength, message) {
    return this._addCheck({
      kind: "max",
      value: maxLength,
      ...errorUtil.errToObj(message)
    });
  }
  length(len, message) {
    return this._addCheck({
      kind: "length",
      value: len,
      ...errorUtil.errToObj(message)
    });
  }
  /**
   * Equivalent to `.min(1)`
   */
  nonempty(message) {
    return this.min(1, errorUtil.errToObj(message));
  }
  trim() {
    return new _ZodString({
      ...this._def,
      checks: [...this._def.checks, { kind: "trim" }]
    });
  }
  toLowerCase() {
    return new _ZodString({
      ...this._def,
      checks: [...this._def.checks, { kind: "toLowerCase" }]
    });
  }
  toUpperCase() {
    return new _ZodString({
      ...this._def,
      checks: [...this._def.checks, { kind: "toUpperCase" }]
    });
  }
  get isDatetime() {
    return !!this._def.checks.find((ch) => ch.kind === "datetime");
  }
  get isDate() {
    return !!this._def.checks.find((ch) => ch.kind === "date");
  }
  get isTime() {
    return !!this._def.checks.find((ch) => ch.kind === "time");
  }
  get isDuration() {
    return !!this._def.checks.find((ch) => ch.kind === "duration");
  }
  get isEmail() {
    return !!this._def.checks.find((ch) => ch.kind === "email");
  }
  get isURL() {
    return !!this._def.checks.find((ch) => ch.kind === "url");
  }
  get isEmoji() {
    return !!this._def.checks.find((ch) => ch.kind === "emoji");
  }
  get isUUID() {
    return !!this._def.checks.find((ch) => ch.kind === "uuid");
  }
  get isNANOID() {
    return !!this._def.checks.find((ch) => ch.kind === "nanoid");
  }
  get isCUID() {
    return !!this._def.checks.find((ch) => ch.kind === "cuid");
  }
  get isCUID2() {
    return !!this._def.checks.find((ch) => ch.kind === "cuid2");
  }
  get isULID() {
    return !!this._def.checks.find((ch) => ch.kind === "ulid");
  }
  get isIP() {
    return !!this._def.checks.find((ch) => ch.kind === "ip");
  }
  get isCIDR() {
    return !!this._def.checks.find((ch) => ch.kind === "cidr");
  }
  get isBase64() {
    return !!this._def.checks.find((ch) => ch.kind === "base64");
  }
  get isBase64url() {
    return !!this._def.checks.find((ch) => ch.kind === "base64url");
  }
  get minLength() {
    let min = null;
    for (const ch of this._def.checks) {
      if (ch.kind === "min") {
        if (min === null || ch.value > min)
          min = ch.value;
      }
    }
    return min;
  }
  get maxLength() {
    let max = null;
    for (const ch of this._def.checks) {
      if (ch.kind === "max") {
        if (max === null || ch.value < max)
          max = ch.value;
      }
    }
    return max;
  }
};
ZodString.create = (params) => {
  return new ZodString({
    checks: [],
    typeName: ZodFirstPartyTypeKind.ZodString,
    coerce: params?.coerce ?? false,
    ...processCreateParams(params)
  });
};
function floatSafeRemainder(val, step) {
  const valDecCount = (val.toString().split(".")[1] || "").length;
  const stepDecCount = (step.toString().split(".")[1] || "").length;
  const decCount = valDecCount > stepDecCount ? valDecCount : stepDecCount;
  const valInt = Number.parseInt(val.toFixed(decCount).replace(".", ""));
  const stepInt = Number.parseInt(step.toFixed(decCount).replace(".", ""));
  return valInt % stepInt / 10 ** decCount;
}
var ZodNumber = class _ZodNumber extends ZodType {
  constructor() {
    super(...arguments);
    this.min = this.gte;
    this.max = this.lte;
    this.step = this.multipleOf;
  }
  _parse(input) {
    if (this._def.coerce) {
      input.data = Number(input.data);
    }
    const parsedType = this._getType(input);
    if (parsedType !== ZodParsedType.number) {
      const ctx2 = this._getOrReturnCtx(input);
      addIssueToContext(ctx2, {
        code: ZodIssueCode.invalid_type,
        expected: ZodParsedType.number,
        received: ctx2.parsedType
      });
      return INVALID;
    }
    let ctx = void 0;
    const status = new ParseStatus();
    for (const check of this._def.checks) {
      if (check.kind === "int") {
        if (!util.isInteger(input.data)) {
          ctx = this._getOrReturnCtx(input, ctx);
          addIssueToContext(ctx, {
            code: ZodIssueCode.invalid_type,
            expected: "integer",
            received: "float",
            message: check.message
          });
          status.dirty();
        }
      } else if (check.kind === "min") {
        const tooSmall = check.inclusive ? input.data < check.value : input.data <= check.value;
        if (tooSmall) {
          ctx = this._getOrReturnCtx(input, ctx);
          addIssueToContext(ctx, {
            code: ZodIssueCode.too_small,
            minimum: check.value,
            type: "number",
            inclusive: check.inclusive,
            exact: false,
            message: check.message
          });
          status.dirty();
        }
      } else if (check.kind === "max") {
        const tooBig = check.inclusive ? input.data > check.value : input.data >= check.value;
        if (tooBig) {
          ctx = this._getOrReturnCtx(input, ctx);
          addIssueToContext(ctx, {
            code: ZodIssueCode.too_big,
            maximum: check.value,
            type: "number",
            inclusive: check.inclusive,
            exact: false,
            message: check.message
          });
          status.dirty();
        }
      } else if (check.kind === "multipleOf") {
        if (floatSafeRemainder(input.data, check.value) !== 0) {
          ctx = this._getOrReturnCtx(input, ctx);
          addIssueToContext(ctx, {
            code: ZodIssueCode.not_multiple_of,
            multipleOf: check.value,
            message: check.message
          });
          status.dirty();
        }
      } else if (check.kind === "finite") {
        if (!Number.isFinite(input.data)) {
          ctx = this._getOrReturnCtx(input, ctx);
          addIssueToContext(ctx, {
            code: ZodIssueCode.not_finite,
            message: check.message
          });
          status.dirty();
        }
      } else {
        util.assertNever(check);
      }
    }
    return { status: status.value, value: input.data };
  }
  gte(value, message) {
    return this.setLimit("min", value, true, errorUtil.toString(message));
  }
  gt(value, message) {
    return this.setLimit("min", value, false, errorUtil.toString(message));
  }
  lte(value, message) {
    return this.setLimit("max", value, true, errorUtil.toString(message));
  }
  lt(value, message) {
    return this.setLimit("max", value, false, errorUtil.toString(message));
  }
  setLimit(kind, value, inclusive, message) {
    return new _ZodNumber({
      ...this._def,
      checks: [
        ...this._def.checks,
        {
          kind,
          value,
          inclusive,
          message: errorUtil.toString(message)
        }
      ]
    });
  }
  _addCheck(check) {
    return new _ZodNumber({
      ...this._def,
      checks: [...this._def.checks, check]
    });
  }
  int(message) {
    return this._addCheck({
      kind: "int",
      message: errorUtil.toString(message)
    });
  }
  positive(message) {
    return this._addCheck({
      kind: "min",
      value: 0,
      inclusive: false,
      message: errorUtil.toString(message)
    });
  }
  negative(message) {
    return this._addCheck({
      kind: "max",
      value: 0,
      inclusive: false,
      message: errorUtil.toString(message)
    });
  }
  nonpositive(message) {
    return this._addCheck({
      kind: "max",
      value: 0,
      inclusive: true,
      message: errorUtil.toString(message)
    });
  }
  nonnegative(message) {
    return this._addCheck({
      kind: "min",
      value: 0,
      inclusive: true,
      message: errorUtil.toString(message)
    });
  }
  multipleOf(value, message) {
    return this._addCheck({
      kind: "multipleOf",
      value,
      message: errorUtil.toString(message)
    });
  }
  finite(message) {
    return this._addCheck({
      kind: "finite",
      message: errorUtil.toString(message)
    });
  }
  safe(message) {
    return this._addCheck({
      kind: "min",
      inclusive: true,
      value: Number.MIN_SAFE_INTEGER,
      message: errorUtil.toString(message)
    })._addCheck({
      kind: "max",
      inclusive: true,
      value: Number.MAX_SAFE_INTEGER,
      message: errorUtil.toString(message)
    });
  }
  get minValue() {
    let min = null;
    for (const ch of this._def.checks) {
      if (ch.kind === "min") {
        if (min === null || ch.value > min)
          min = ch.value;
      }
    }
    return min;
  }
  get maxValue() {
    let max = null;
    for (const ch of this._def.checks) {
      if (ch.kind === "max") {
        if (max === null || ch.value < max)
          max = ch.value;
      }
    }
    return max;
  }
  get isInt() {
    return !!this._def.checks.find((ch) => ch.kind === "int" || ch.kind === "multipleOf" && util.isInteger(ch.value));
  }
  get isFinite() {
    let max = null;
    let min = null;
    for (const ch of this._def.checks) {
      if (ch.kind === "finite" || ch.kind === "int" || ch.kind === "multipleOf") {
        return true;
      } else if (ch.kind === "min") {
        if (min === null || ch.value > min)
          min = ch.value;
      } else if (ch.kind === "max") {
        if (max === null || ch.value < max)
          max = ch.value;
      }
    }
    return Number.isFinite(min) && Number.isFinite(max);
  }
};
ZodNumber.create = (params) => {
  return new ZodNumber({
    checks: [],
    typeName: ZodFirstPartyTypeKind.ZodNumber,
    coerce: params?.coerce || false,
    ...processCreateParams(params)
  });
};
var ZodBigInt = class _ZodBigInt extends ZodType {
  constructor() {
    super(...arguments);
    this.min = this.gte;
    this.max = this.lte;
  }
  _parse(input) {
    if (this._def.coerce) {
      try {
        input.data = BigInt(input.data);
      } catch {
        return this._getInvalidInput(input);
      }
    }
    const parsedType = this._getType(input);
    if (parsedType !== ZodParsedType.bigint) {
      return this._getInvalidInput(input);
    }
    let ctx = void 0;
    const status = new ParseStatus();
    for (const check of this._def.checks) {
      if (check.kind === "min") {
        const tooSmall = check.inclusive ? input.data < check.value : input.data <= check.value;
        if (tooSmall) {
          ctx = this._getOrReturnCtx(input, ctx);
          addIssueToContext(ctx, {
            code: ZodIssueCode.too_small,
            type: "bigint",
            minimum: check.value,
            inclusive: check.inclusive,
            message: check.message
          });
          status.dirty();
        }
      } else if (check.kind === "max") {
        const tooBig = check.inclusive ? input.data > check.value : input.data >= check.value;
        if (tooBig) {
          ctx = this._getOrReturnCtx(input, ctx);
          addIssueToContext(ctx, {
            code: ZodIssueCode.too_big,
            type: "bigint",
            maximum: check.value,
            inclusive: check.inclusive,
            message: check.message
          });
          status.dirty();
        }
      } else if (check.kind === "multipleOf") {
        if (input.data % check.value !== BigInt(0)) {
          ctx = this._getOrReturnCtx(input, ctx);
          addIssueToContext(ctx, {
            code: ZodIssueCode.not_multiple_of,
            multipleOf: check.value,
            message: check.message
          });
          status.dirty();
        }
      } else {
        util.assertNever(check);
      }
    }
    return { status: status.value, value: input.data };
  }
  _getInvalidInput(input) {
    const ctx = this._getOrReturnCtx(input);
    addIssueToContext(ctx, {
      code: ZodIssueCode.invalid_type,
      expected: ZodParsedType.bigint,
      received: ctx.parsedType
    });
    return INVALID;
  }
  gte(value, message) {
    return this.setLimit("min", value, true, errorUtil.toString(message));
  }
  gt(value, message) {
    return this.setLimit("min", value, false, errorUtil.toString(message));
  }
  lte(value, message) {
    return this.setLimit("max", value, true, errorUtil.toString(message));
  }
  lt(value, message) {
    return this.setLimit("max", value, false, errorUtil.toString(message));
  }
  setLimit(kind, value, inclusive, message) {
    return new _ZodBigInt({
      ...this._def,
      checks: [
        ...this._def.checks,
        {
          kind,
          value,
          inclusive,
          message: errorUtil.toString(message)
        }
      ]
    });
  }
  _addCheck(check) {
    return new _ZodBigInt({
      ...this._def,
      checks: [...this._def.checks, check]
    });
  }
  positive(message) {
    return this._addCheck({
      kind: "min",
      value: BigInt(0),
      inclusive: false,
      message: errorUtil.toString(message)
    });
  }
  negative(message) {
    return this._addCheck({
      kind: "max",
      value: BigInt(0),
      inclusive: false,
      message: errorUtil.toString(message)
    });
  }
  nonpositive(message) {
    return this._addCheck({
      kind: "max",
      value: BigInt(0),
      inclusive: true,
      message: errorUtil.toString(message)
    });
  }
  nonnegative(message) {
    return this._addCheck({
      kind: "min",
      value: BigInt(0),
      inclusive: true,
      message: errorUtil.toString(message)
    });
  }
  multipleOf(value, message) {
    return this._addCheck({
      kind: "multipleOf",
      value,
      message: errorUtil.toString(message)
    });
  }
  get minValue() {
    let min = null;
    for (const ch of this._def.checks) {
      if (ch.kind === "min") {
        if (min === null || ch.value > min)
          min = ch.value;
      }
    }
    return min;
  }
  get maxValue() {
    let max = null;
    for (const ch of this._def.checks) {
      if (ch.kind === "max") {
        if (max === null || ch.value < max)
          max = ch.value;
      }
    }
    return max;
  }
};
ZodBigInt.create = (params) => {
  return new ZodBigInt({
    checks: [],
    typeName: ZodFirstPartyTypeKind.ZodBigInt,
    coerce: params?.coerce ?? false,
    ...processCreateParams(params)
  });
};
var ZodBoolean = class extends ZodType {
  _parse(input) {
    if (this._def.coerce) {
      input.data = Boolean(input.data);
    }
    const parsedType = this._getType(input);
    if (parsedType !== ZodParsedType.boolean) {
      const ctx = this._getOrReturnCtx(input);
      addIssueToContext(ctx, {
        code: ZodIssueCode.invalid_type,
        expected: ZodParsedType.boolean,
        received: ctx.parsedType
      });
      return INVALID;
    }
    return OK(input.data);
  }
};
ZodBoolean.create = (params) => {
  return new ZodBoolean({
    typeName: ZodFirstPartyTypeKind.ZodBoolean,
    coerce: params?.coerce || false,
    ...processCreateParams(params)
  });
};
var ZodDate = class _ZodDate extends ZodType {
  _parse(input) {
    if (this._def.coerce) {
      input.data = new Date(input.data);
    }
    const parsedType = this._getType(input);
    if (parsedType !== ZodParsedType.date) {
      const ctx2 = this._getOrReturnCtx(input);
      addIssueToContext(ctx2, {
        code: ZodIssueCode.invalid_type,
        expected: ZodParsedType.date,
        received: ctx2.parsedType
      });
      return INVALID;
    }
    if (Number.isNaN(input.data.getTime())) {
      const ctx2 = this._getOrReturnCtx(input);
      addIssueToContext(ctx2, {
        code: ZodIssueCode.invalid_date
      });
      return INVALID;
    }
    const status = new ParseStatus();
    let ctx = void 0;
    for (const check of this._def.checks) {
      if (check.kind === "min") {
        if (input.data.getTime() < check.value) {
          ctx = this._getOrReturnCtx(input, ctx);
          addIssueToContext(ctx, {
            code: ZodIssueCode.too_small,
            message: check.message,
            inclusive: true,
            exact: false,
            minimum: check.value,
            type: "date"
          });
          status.dirty();
        }
      } else if (check.kind === "max") {
        if (input.data.getTime() > check.value) {
          ctx = this._getOrReturnCtx(input, ctx);
          addIssueToContext(ctx, {
            code: ZodIssueCode.too_big,
            message: check.message,
            inclusive: true,
            exact: false,
            maximum: check.value,
            type: "date"
          });
          status.dirty();
        }
      } else {
        util.assertNever(check);
      }
    }
    return {
      status: status.value,
      value: new Date(input.data.getTime())
    };
  }
  _addCheck(check) {
    return new _ZodDate({
      ...this._def,
      checks: [...this._def.checks, check]
    });
  }
  min(minDate, message) {
    return this._addCheck({
      kind: "min",
      value: minDate.getTime(),
      message: errorUtil.toString(message)
    });
  }
  max(maxDate, message) {
    return this._addCheck({
      kind: "max",
      value: maxDate.getTime(),
      message: errorUtil.toString(message)
    });
  }
  get minDate() {
    let min = null;
    for (const ch of this._def.checks) {
      if (ch.kind === "min") {
        if (min === null || ch.value > min)
          min = ch.value;
      }
    }
    return min != null ? new Date(min) : null;
  }
  get maxDate() {
    let max = null;
    for (const ch of this._def.checks) {
      if (ch.kind === "max") {
        if (max === null || ch.value < max)
          max = ch.value;
      }
    }
    return max != null ? new Date(max) : null;
  }
};
ZodDate.create = (params) => {
  return new ZodDate({
    checks: [],
    coerce: params?.coerce || false,
    typeName: ZodFirstPartyTypeKind.ZodDate,
    ...processCreateParams(params)
  });
};
var ZodSymbol = class extends ZodType {
  _parse(input) {
    const parsedType = this._getType(input);
    if (parsedType !== ZodParsedType.symbol) {
      const ctx = this._getOrReturnCtx(input);
      addIssueToContext(ctx, {
        code: ZodIssueCode.invalid_type,
        expected: ZodParsedType.symbol,
        received: ctx.parsedType
      });
      return INVALID;
    }
    return OK(input.data);
  }
};
ZodSymbol.create = (params) => {
  return new ZodSymbol({
    typeName: ZodFirstPartyTypeKind.ZodSymbol,
    ...processCreateParams(params)
  });
};
var ZodUndefined = class extends ZodType {
  _parse(input) {
    const parsedType = this._getType(input);
    if (parsedType !== ZodParsedType.undefined) {
      const ctx = this._getOrReturnCtx(input);
      addIssueToContext(ctx, {
        code: ZodIssueCode.invalid_type,
        expected: ZodParsedType.undefined,
        received: ctx.parsedType
      });
      return INVALID;
    }
    return OK(input.data);
  }
};
ZodUndefined.create = (params) => {
  return new ZodUndefined({
    typeName: ZodFirstPartyTypeKind.ZodUndefined,
    ...processCreateParams(params)
  });
};
var ZodNull = class extends ZodType {
  _parse(input) {
    const parsedType = this._getType(input);
    if (parsedType !== ZodParsedType.null) {
      const ctx = this._getOrReturnCtx(input);
      addIssueToContext(ctx, {
        code: ZodIssueCode.invalid_type,
        expected: ZodParsedType.null,
        received: ctx.parsedType
      });
      return INVALID;
    }
    return OK(input.data);
  }
};
ZodNull.create = (params) => {
  return new ZodNull({
    typeName: ZodFirstPartyTypeKind.ZodNull,
    ...processCreateParams(params)
  });
};
var ZodAny = class extends ZodType {
  constructor() {
    super(...arguments);
    this._any = true;
  }
  _parse(input) {
    return OK(input.data);
  }
};
ZodAny.create = (params) => {
  return new ZodAny({
    typeName: ZodFirstPartyTypeKind.ZodAny,
    ...processCreateParams(params)
  });
};
var ZodUnknown = class extends ZodType {
  constructor() {
    super(...arguments);
    this._unknown = true;
  }
  _parse(input) {
    return OK(input.data);
  }
};
ZodUnknown.create = (params) => {
  return new ZodUnknown({
    typeName: ZodFirstPartyTypeKind.ZodUnknown,
    ...processCreateParams(params)
  });
};
var ZodNever = class extends ZodType {
  _parse(input) {
    const ctx = this._getOrReturnCtx(input);
    addIssueToContext(ctx, {
      code: ZodIssueCode.invalid_type,
      expected: ZodParsedType.never,
      received: ctx.parsedType
    });
    return INVALID;
  }
};
ZodNever.create = (params) => {
  return new ZodNever({
    typeName: ZodFirstPartyTypeKind.ZodNever,
    ...processCreateParams(params)
  });
};
var ZodVoid = class extends ZodType {
  _parse(input) {
    const parsedType = this._getType(input);
    if (parsedType !== ZodParsedType.undefined) {
      const ctx = this._getOrReturnCtx(input);
      addIssueToContext(ctx, {
        code: ZodIssueCode.invalid_type,
        expected: ZodParsedType.void,
        received: ctx.parsedType
      });
      return INVALID;
    }
    return OK(input.data);
  }
};
ZodVoid.create = (params) => {
  return new ZodVoid({
    typeName: ZodFirstPartyTypeKind.ZodVoid,
    ...processCreateParams(params)
  });
};
var ZodArray = class _ZodArray extends ZodType {
  _parse(input) {
    const { ctx, status } = this._processInputParams(input);
    const def = this._def;
    if (ctx.parsedType !== ZodParsedType.array) {
      addIssueToContext(ctx, {
        code: ZodIssueCode.invalid_type,
        expected: ZodParsedType.array,
        received: ctx.parsedType
      });
      return INVALID;
    }
    if (def.exactLength !== null) {
      const tooBig = ctx.data.length > def.exactLength.value;
      const tooSmall = ctx.data.length < def.exactLength.value;
      if (tooBig || tooSmall) {
        addIssueToContext(ctx, {
          code: tooBig ? ZodIssueCode.too_big : ZodIssueCode.too_small,
          minimum: tooSmall ? def.exactLength.value : void 0,
          maximum: tooBig ? def.exactLength.value : void 0,
          type: "array",
          inclusive: true,
          exact: true,
          message: def.exactLength.message
        });
        status.dirty();
      }
    }
    if (def.minLength !== null) {
      if (ctx.data.length < def.minLength.value) {
        addIssueToContext(ctx, {
          code: ZodIssueCode.too_small,
          minimum: def.minLength.value,
          type: "array",
          inclusive: true,
          exact: false,
          message: def.minLength.message
        });
        status.dirty();
      }
    }
    if (def.maxLength !== null) {
      if (ctx.data.length > def.maxLength.value) {
        addIssueToContext(ctx, {
          code: ZodIssueCode.too_big,
          maximum: def.maxLength.value,
          type: "array",
          inclusive: true,
          exact: false,
          message: def.maxLength.message
        });
        status.dirty();
      }
    }
    if (ctx.common.async) {
      return Promise.all([...ctx.data].map((item, i) => {
        return def.type._parseAsync(new ParseInputLazyPath(ctx, item, ctx.path, i));
      })).then((result2) => {
        return ParseStatus.mergeArray(status, result2);
      });
    }
    const result = [...ctx.data].map((item, i) => {
      return def.type._parseSync(new ParseInputLazyPath(ctx, item, ctx.path, i));
    });
    return ParseStatus.mergeArray(status, result);
  }
  get element() {
    return this._def.type;
  }
  min(minLength, message) {
    return new _ZodArray({
      ...this._def,
      minLength: { value: minLength, message: errorUtil.toString(message) }
    });
  }
  max(maxLength, message) {
    return new _ZodArray({
      ...this._def,
      maxLength: { value: maxLength, message: errorUtil.toString(message) }
    });
  }
  length(len, message) {
    return new _ZodArray({
      ...this._def,
      exactLength: { value: len, message: errorUtil.toString(message) }
    });
  }
  nonempty(message) {
    return this.min(1, message);
  }
};
ZodArray.create = (schema, params) => {
  return new ZodArray({
    type: schema,
    minLength: null,
    maxLength: null,
    exactLength: null,
    typeName: ZodFirstPartyTypeKind.ZodArray,
    ...processCreateParams(params)
  });
};
function deepPartialify(schema) {
  if (schema instanceof ZodObject) {
    const newShape = {};
    for (const key in schema.shape) {
      const fieldSchema = schema.shape[key];
      newShape[key] = ZodOptional.create(deepPartialify(fieldSchema));
    }
    return new ZodObject({
      ...schema._def,
      shape: () => newShape
    });
  } else if (schema instanceof ZodArray) {
    return new ZodArray({
      ...schema._def,
      type: deepPartialify(schema.element)
    });
  } else if (schema instanceof ZodOptional) {
    return ZodOptional.create(deepPartialify(schema.unwrap()));
  } else if (schema instanceof ZodNullable) {
    return ZodNullable.create(deepPartialify(schema.unwrap()));
  } else if (schema instanceof ZodTuple) {
    return ZodTuple.create(schema.items.map((item) => deepPartialify(item)));
  } else {
    return schema;
  }
}
var ZodObject = class _ZodObject extends ZodType {
  constructor() {
    super(...arguments);
    this._cached = null;
    this.nonstrict = this.passthrough;
    this.augment = this.extend;
  }
  _getCached() {
    if (this._cached !== null)
      return this._cached;
    const shape = this._def.shape();
    const keys = util.objectKeys(shape);
    this._cached = { shape, keys };
    return this._cached;
  }
  _parse(input) {
    const parsedType = this._getType(input);
    if (parsedType !== ZodParsedType.object) {
      const ctx2 = this._getOrReturnCtx(input);
      addIssueToContext(ctx2, {
        code: ZodIssueCode.invalid_type,
        expected: ZodParsedType.object,
        received: ctx2.parsedType
      });
      return INVALID;
    }
    const { status, ctx } = this._processInputParams(input);
    const { shape, keys: shapeKeys } = this._getCached();
    const extraKeys = [];
    if (!(this._def.catchall instanceof ZodNever && this._def.unknownKeys === "strip")) {
      for (const key in ctx.data) {
        if (!shapeKeys.includes(key)) {
          extraKeys.push(key);
        }
      }
    }
    const pairs = [];
    for (const key of shapeKeys) {
      const keyValidator = shape[key];
      const value = ctx.data[key];
      pairs.push({
        key: { status: "valid", value: key },
        value: keyValidator._parse(new ParseInputLazyPath(ctx, value, ctx.path, key)),
        alwaysSet: key in ctx.data
      });
    }
    if (this._def.catchall instanceof ZodNever) {
      const unknownKeys = this._def.unknownKeys;
      if (unknownKeys === "passthrough") {
        for (const key of extraKeys) {
          pairs.push({
            key: { status: "valid", value: key },
            value: { status: "valid", value: ctx.data[key] }
          });
        }
      } else if (unknownKeys === "strict") {
        if (extraKeys.length > 0) {
          addIssueToContext(ctx, {
            code: ZodIssueCode.unrecognized_keys,
            keys: extraKeys
          });
          status.dirty();
        }
      } else if (unknownKeys === "strip") {
      } else {
        throw new Error(`Internal ZodObject error: invalid unknownKeys value.`);
      }
    } else {
      const catchall = this._def.catchall;
      for (const key of extraKeys) {
        const value = ctx.data[key];
        pairs.push({
          key: { status: "valid", value: key },
          value: catchall._parse(
            new ParseInputLazyPath(ctx, value, ctx.path, key)
            //, ctx.child(key), value, getParsedType(value)
          ),
          alwaysSet: key in ctx.data
        });
      }
    }
    if (ctx.common.async) {
      return Promise.resolve().then(async () => {
        const syncPairs = [];
        for (const pair of pairs) {
          const key = await pair.key;
          const value = await pair.value;
          syncPairs.push({
            key,
            value,
            alwaysSet: pair.alwaysSet
          });
        }
        return syncPairs;
      }).then((syncPairs) => {
        return ParseStatus.mergeObjectSync(status, syncPairs);
      });
    } else {
      return ParseStatus.mergeObjectSync(status, pairs);
    }
  }
  get shape() {
    return this._def.shape();
  }
  strict(message) {
    errorUtil.errToObj;
    return new _ZodObject({
      ...this._def,
      unknownKeys: "strict",
      ...message !== void 0 ? {
        errorMap: (issue, ctx) => {
          const defaultError = this._def.errorMap?.(issue, ctx).message ?? ctx.defaultError;
          if (issue.code === "unrecognized_keys")
            return {
              message: errorUtil.errToObj(message).message ?? defaultError
            };
          return {
            message: defaultError
          };
        }
      } : {}
    });
  }
  strip() {
    return new _ZodObject({
      ...this._def,
      unknownKeys: "strip"
    });
  }
  passthrough() {
    return new _ZodObject({
      ...this._def,
      unknownKeys: "passthrough"
    });
  }
  // const AugmentFactory =
  //   <Def extends ZodObjectDef>(def: Def) =>
  //   <Augmentation extends ZodRawShape>(
  //     augmentation: Augmentation
  //   ): ZodObject<
  //     extendShape<ReturnType<Def["shape"]>, Augmentation>,
  //     Def["unknownKeys"],
  //     Def["catchall"]
  //   > => {
  //     return new ZodObject({
  //       ...def,
  //       shape: () => ({
  //         ...def.shape(),
  //         ...augmentation,
  //       }),
  //     }) as any;
  //   };
  extend(augmentation) {
    return new _ZodObject({
      ...this._def,
      shape: () => ({
        ...this._def.shape(),
        ...augmentation
      })
    });
  }
  /**
   * Prior to zod@1.0.12 there was a bug in the
   * inferred type of merged objects. Please
   * upgrade if you are experiencing issues.
   */
  merge(merging) {
    const merged = new _ZodObject({
      unknownKeys: merging._def.unknownKeys,
      catchall: merging._def.catchall,
      shape: () => ({
        ...this._def.shape(),
        ...merging._def.shape()
      }),
      typeName: ZodFirstPartyTypeKind.ZodObject
    });
    return merged;
  }
  // merge<
  //   Incoming extends AnyZodObject,
  //   Augmentation extends Incoming["shape"],
  //   NewOutput extends {
  //     [k in keyof Augmentation | keyof Output]: k extends keyof Augmentation
  //       ? Augmentation[k]["_output"]
  //       : k extends keyof Output
  //       ? Output[k]
  //       : never;
  //   },
  //   NewInput extends {
  //     [k in keyof Augmentation | keyof Input]: k extends keyof Augmentation
  //       ? Augmentation[k]["_input"]
  //       : k extends keyof Input
  //       ? Input[k]
  //       : never;
  //   }
  // >(
  //   merging: Incoming
  // ): ZodObject<
  //   extendShape<T, ReturnType<Incoming["_def"]["shape"]>>,
  //   Incoming["_def"]["unknownKeys"],
  //   Incoming["_def"]["catchall"],
  //   NewOutput,
  //   NewInput
  // > {
  //   const merged: any = new ZodObject({
  //     unknownKeys: merging._def.unknownKeys,
  //     catchall: merging._def.catchall,
  //     shape: () =>
  //       objectUtil.mergeShapes(this._def.shape(), merging._def.shape()),
  //     typeName: ZodFirstPartyTypeKind.ZodObject,
  //   }) as any;
  //   return merged;
  // }
  setKey(key, schema) {
    return this.augment({ [key]: schema });
  }
  // merge<Incoming extends AnyZodObject>(
  //   merging: Incoming
  // ): //ZodObject<T & Incoming["_shape"], UnknownKeys, Catchall> = (merging) => {
  // ZodObject<
  //   extendShape<T, ReturnType<Incoming["_def"]["shape"]>>,
  //   Incoming["_def"]["unknownKeys"],
  //   Incoming["_def"]["catchall"]
  // > {
  //   // const mergedShape = objectUtil.mergeShapes(
  //   //   this._def.shape(),
  //   //   merging._def.shape()
  //   // );
  //   const merged: any = new ZodObject({
  //     unknownKeys: merging._def.unknownKeys,
  //     catchall: merging._def.catchall,
  //     shape: () =>
  //       objectUtil.mergeShapes(this._def.shape(), merging._def.shape()),
  //     typeName: ZodFirstPartyTypeKind.ZodObject,
  //   }) as any;
  //   return merged;
  // }
  catchall(index) {
    return new _ZodObject({
      ...this._def,
      catchall: index
    });
  }
  pick(mask) {
    const shape = {};
    for (const key of util.objectKeys(mask)) {
      if (mask[key] && this.shape[key]) {
        shape[key] = this.shape[key];
      }
    }
    return new _ZodObject({
      ...this._def,
      shape: () => shape
    });
  }
  omit(mask) {
    const shape = {};
    for (const key of util.objectKeys(this.shape)) {
      if (!mask[key]) {
        shape[key] = this.shape[key];
      }
    }
    return new _ZodObject({
      ...this._def,
      shape: () => shape
    });
  }
  /**
   * @deprecated
   */
  deepPartial() {
    return deepPartialify(this);
  }
  partial(mask) {
    const newShape = {};
    for (const key of util.objectKeys(this.shape)) {
      const fieldSchema = this.shape[key];
      if (mask && !mask[key]) {
        newShape[key] = fieldSchema;
      } else {
        newShape[key] = fieldSchema.optional();
      }
    }
    return new _ZodObject({
      ...this._def,
      shape: () => newShape
    });
  }
  required(mask) {
    const newShape = {};
    for (const key of util.objectKeys(this.shape)) {
      if (mask && !mask[key]) {
        newShape[key] = this.shape[key];
      } else {
        const fieldSchema = this.shape[key];
        let newField = fieldSchema;
        while (newField instanceof ZodOptional) {
          newField = newField._def.innerType;
        }
        newShape[key] = newField;
      }
    }
    return new _ZodObject({
      ...this._def,
      shape: () => newShape
    });
  }
  keyof() {
    return createZodEnum(util.objectKeys(this.shape));
  }
};
ZodObject.create = (shape, params) => {
  return new ZodObject({
    shape: () => shape,
    unknownKeys: "strip",
    catchall: ZodNever.create(),
    typeName: ZodFirstPartyTypeKind.ZodObject,
    ...processCreateParams(params)
  });
};
ZodObject.strictCreate = (shape, params) => {
  return new ZodObject({
    shape: () => shape,
    unknownKeys: "strict",
    catchall: ZodNever.create(),
    typeName: ZodFirstPartyTypeKind.ZodObject,
    ...processCreateParams(params)
  });
};
ZodObject.lazycreate = (shape, params) => {
  return new ZodObject({
    shape,
    unknownKeys: "strip",
    catchall: ZodNever.create(),
    typeName: ZodFirstPartyTypeKind.ZodObject,
    ...processCreateParams(params)
  });
};
var ZodUnion = class extends ZodType {
  _parse(input) {
    const { ctx } = this._processInputParams(input);
    const options = this._def.options;
    function handleResults(results) {
      for (const result of results) {
        if (result.result.status === "valid") {
          return result.result;
        }
      }
      for (const result of results) {
        if (result.result.status === "dirty") {
          ctx.common.issues.push(...result.ctx.common.issues);
          return result.result;
        }
      }
      const unionErrors = results.map((result) => new ZodError(result.ctx.common.issues));
      addIssueToContext(ctx, {
        code: ZodIssueCode.invalid_union,
        unionErrors
      });
      return INVALID;
    }
    if (ctx.common.async) {
      return Promise.all(options.map(async (option) => {
        const childCtx = {
          ...ctx,
          common: {
            ...ctx.common,
            issues: []
          },
          parent: null
        };
        return {
          result: await option._parseAsync({
            data: ctx.data,
            path: ctx.path,
            parent: childCtx
          }),
          ctx: childCtx
        };
      })).then(handleResults);
    } else {
      let dirty = void 0;
      const issues = [];
      for (const option of options) {
        const childCtx = {
          ...ctx,
          common: {
            ...ctx.common,
            issues: []
          },
          parent: null
        };
        const result = option._parseSync({
          data: ctx.data,
          path: ctx.path,
          parent: childCtx
        });
        if (result.status === "valid") {
          return result;
        } else if (result.status === "dirty" && !dirty) {
          dirty = { result, ctx: childCtx };
        }
        if (childCtx.common.issues.length) {
          issues.push(childCtx.common.issues);
        }
      }
      if (dirty) {
        ctx.common.issues.push(...dirty.ctx.common.issues);
        return dirty.result;
      }
      const unionErrors = issues.map((issues2) => new ZodError(issues2));
      addIssueToContext(ctx, {
        code: ZodIssueCode.invalid_union,
        unionErrors
      });
      return INVALID;
    }
  }
  get options() {
    return this._def.options;
  }
};
ZodUnion.create = (types, params) => {
  return new ZodUnion({
    options: types,
    typeName: ZodFirstPartyTypeKind.ZodUnion,
    ...processCreateParams(params)
  });
};
var getDiscriminator = (type) => {
  if (type instanceof ZodLazy) {
    return getDiscriminator(type.schema);
  } else if (type instanceof ZodEffects) {
    return getDiscriminator(type.innerType());
  } else if (type instanceof ZodLiteral) {
    return [type.value];
  } else if (type instanceof ZodEnum) {
    return type.options;
  } else if (type instanceof ZodNativeEnum) {
    return util.objectValues(type.enum);
  } else if (type instanceof ZodDefault) {
    return getDiscriminator(type._def.innerType);
  } else if (type instanceof ZodUndefined) {
    return [void 0];
  } else if (type instanceof ZodNull) {
    return [null];
  } else if (type instanceof ZodOptional) {
    return [void 0, ...getDiscriminator(type.unwrap())];
  } else if (type instanceof ZodNullable) {
    return [null, ...getDiscriminator(type.unwrap())];
  } else if (type instanceof ZodBranded) {
    return getDiscriminator(type.unwrap());
  } else if (type instanceof ZodReadonly) {
    return getDiscriminator(type.unwrap());
  } else if (type instanceof ZodCatch) {
    return getDiscriminator(type._def.innerType);
  } else {
    return [];
  }
};
var ZodDiscriminatedUnion = class _ZodDiscriminatedUnion extends ZodType {
  _parse(input) {
    const { ctx } = this._processInputParams(input);
    if (ctx.parsedType !== ZodParsedType.object) {
      addIssueToContext(ctx, {
        code: ZodIssueCode.invalid_type,
        expected: ZodParsedType.object,
        received: ctx.parsedType
      });
      return INVALID;
    }
    const discriminator = this.discriminator;
    const discriminatorValue = ctx.data[discriminator];
    const option = this.optionsMap.get(discriminatorValue);
    if (!option) {
      addIssueToContext(ctx, {
        code: ZodIssueCode.invalid_union_discriminator,
        options: Array.from(this.optionsMap.keys()),
        path: [discriminator]
      });
      return INVALID;
    }
    if (ctx.common.async) {
      return option._parseAsync({
        data: ctx.data,
        path: ctx.path,
        parent: ctx
      });
    } else {
      return option._parseSync({
        data: ctx.data,
        path: ctx.path,
        parent: ctx
      });
    }
  }
  get discriminator() {
    return this._def.discriminator;
  }
  get options() {
    return this._def.options;
  }
  get optionsMap() {
    return this._def.optionsMap;
  }
  /**
   * The constructor of the discriminated union schema. Its behaviour is very similar to that of the normal z.union() constructor.
   * However, it only allows a union of objects, all of which need to share a discriminator property. This property must
   * have a different value for each object in the union.
   * @param discriminator the name of the discriminator property
   * @param types an array of object schemas
   * @param params
   */
  static create(discriminator, options, params) {
    const optionsMap = /* @__PURE__ */ new Map();
    for (const type of options) {
      const discriminatorValues = getDiscriminator(type.shape[discriminator]);
      if (!discriminatorValues.length) {
        throw new Error(`A discriminator value for key \`${discriminator}\` could not be extracted from all schema options`);
      }
      for (const value of discriminatorValues) {
        if (optionsMap.has(value)) {
          throw new Error(`Discriminator property ${String(discriminator)} has duplicate value ${String(value)}`);
        }
        optionsMap.set(value, type);
      }
    }
    return new _ZodDiscriminatedUnion({
      typeName: ZodFirstPartyTypeKind.ZodDiscriminatedUnion,
      discriminator,
      options,
      optionsMap,
      ...processCreateParams(params)
    });
  }
};
function mergeValues(a, b) {
  const aType = getParsedType(a);
  const bType = getParsedType(b);
  if (a === b) {
    return { valid: true, data: a };
  } else if (aType === ZodParsedType.object && bType === ZodParsedType.object) {
    const bKeys = util.objectKeys(b);
    const sharedKeys = util.objectKeys(a).filter((key) => bKeys.indexOf(key) !== -1);
    const newObj = { ...a, ...b };
    for (const key of sharedKeys) {
      const sharedValue = mergeValues(a[key], b[key]);
      if (!sharedValue.valid) {
        return { valid: false };
      }
      newObj[key] = sharedValue.data;
    }
    return { valid: true, data: newObj };
  } else if (aType === ZodParsedType.array && bType === ZodParsedType.array) {
    if (a.length !== b.length) {
      return { valid: false };
    }
    const newArray = [];
    for (let index = 0; index < a.length; index++) {
      const itemA = a[index];
      const itemB = b[index];
      const sharedValue = mergeValues(itemA, itemB);
      if (!sharedValue.valid) {
        return { valid: false };
      }
      newArray.push(sharedValue.data);
    }
    return { valid: true, data: newArray };
  } else if (aType === ZodParsedType.date && bType === ZodParsedType.date && +a === +b) {
    return { valid: true, data: a };
  } else {
    return { valid: false };
  }
}
var ZodIntersection = class extends ZodType {
  _parse(input) {
    const { status, ctx } = this._processInputParams(input);
    const handleParsed = (parsedLeft, parsedRight) => {
      if (isAborted(parsedLeft) || isAborted(parsedRight)) {
        return INVALID;
      }
      const merged = mergeValues(parsedLeft.value, parsedRight.value);
      if (!merged.valid) {
        addIssueToContext(ctx, {
          code: ZodIssueCode.invalid_intersection_types
        });
        return INVALID;
      }
      if (isDirty(parsedLeft) || isDirty(parsedRight)) {
        status.dirty();
      }
      return { status: status.value, value: merged.data };
    };
    if (ctx.common.async) {
      return Promise.all([
        this._def.left._parseAsync({
          data: ctx.data,
          path: ctx.path,
          parent: ctx
        }),
        this._def.right._parseAsync({
          data: ctx.data,
          path: ctx.path,
          parent: ctx
        })
      ]).then(([left, right]) => handleParsed(left, right));
    } else {
      return handleParsed(this._def.left._parseSync({
        data: ctx.data,
        path: ctx.path,
        parent: ctx
      }), this._def.right._parseSync({
        data: ctx.data,
        path: ctx.path,
        parent: ctx
      }));
    }
  }
};
ZodIntersection.create = (left, right, params) => {
  return new ZodIntersection({
    left,
    right,
    typeName: ZodFirstPartyTypeKind.ZodIntersection,
    ...processCreateParams(params)
  });
};
var ZodTuple = class _ZodTuple extends ZodType {
  _parse(input) {
    const { status, ctx } = this._processInputParams(input);
    if (ctx.parsedType !== ZodParsedType.array) {
      addIssueToContext(ctx, {
        code: ZodIssueCode.invalid_type,
        expected: ZodParsedType.array,
        received: ctx.parsedType
      });
      return INVALID;
    }
    if (ctx.data.length < this._def.items.length) {
      addIssueToContext(ctx, {
        code: ZodIssueCode.too_small,
        minimum: this._def.items.length,
        inclusive: true,
        exact: false,
        type: "array"
      });
      return INVALID;
    }
    const rest = this._def.rest;
    if (!rest && ctx.data.length > this._def.items.length) {
      addIssueToContext(ctx, {
        code: ZodIssueCode.too_big,
        maximum: this._def.items.length,
        inclusive: true,
        exact: false,
        type: "array"
      });
      status.dirty();
    }
    const items = [...ctx.data].map((item, itemIndex) => {
      const schema = this._def.items[itemIndex] || this._def.rest;
      if (!schema)
        return null;
      return schema._parse(new ParseInputLazyPath(ctx, item, ctx.path, itemIndex));
    }).filter((x) => !!x);
    if (ctx.common.async) {
      return Promise.all(items).then((results) => {
        return ParseStatus.mergeArray(status, results);
      });
    } else {
      return ParseStatus.mergeArray(status, items);
    }
  }
  get items() {
    return this._def.items;
  }
  rest(rest) {
    return new _ZodTuple({
      ...this._def,
      rest
    });
  }
};
ZodTuple.create = (schemas, params) => {
  if (!Array.isArray(schemas)) {
    throw new Error("You must pass an array of schemas to z.tuple([ ... ])");
  }
  return new ZodTuple({
    items: schemas,
    typeName: ZodFirstPartyTypeKind.ZodTuple,
    rest: null,
    ...processCreateParams(params)
  });
};
var ZodRecord = class _ZodRecord extends ZodType {
  get keySchema() {
    return this._def.keyType;
  }
  get valueSchema() {
    return this._def.valueType;
  }
  _parse(input) {
    const { status, ctx } = this._processInputParams(input);
    if (ctx.parsedType !== ZodParsedType.object) {
      addIssueToContext(ctx, {
        code: ZodIssueCode.invalid_type,
        expected: ZodParsedType.object,
        received: ctx.parsedType
      });
      return INVALID;
    }
    const pairs = [];
    const keyType = this._def.keyType;
    const valueType = this._def.valueType;
    for (const key in ctx.data) {
      pairs.push({
        key: keyType._parse(new ParseInputLazyPath(ctx, key, ctx.path, key)),
        value: valueType._parse(new ParseInputLazyPath(ctx, ctx.data[key], ctx.path, key)),
        alwaysSet: key in ctx.data
      });
    }
    if (ctx.common.async) {
      return ParseStatus.mergeObjectAsync(status, pairs);
    } else {
      return ParseStatus.mergeObjectSync(status, pairs);
    }
  }
  get element() {
    return this._def.valueType;
  }
  static create(first, second, third) {
    if (second instanceof ZodType) {
      return new _ZodRecord({
        keyType: first,
        valueType: second,
        typeName: ZodFirstPartyTypeKind.ZodRecord,
        ...processCreateParams(third)
      });
    }
    return new _ZodRecord({
      keyType: ZodString.create(),
      valueType: first,
      typeName: ZodFirstPartyTypeKind.ZodRecord,
      ...processCreateParams(second)
    });
  }
};
var ZodMap = class extends ZodType {
  get keySchema() {
    return this._def.keyType;
  }
  get valueSchema() {
    return this._def.valueType;
  }
  _parse(input) {
    const { status, ctx } = this._processInputParams(input);
    if (ctx.parsedType !== ZodParsedType.map) {
      addIssueToContext(ctx, {
        code: ZodIssueCode.invalid_type,
        expected: ZodParsedType.map,
        received: ctx.parsedType
      });
      return INVALID;
    }
    const keyType = this._def.keyType;
    const valueType = this._def.valueType;
    const pairs = [...ctx.data.entries()].map(([key, value], index) => {
      return {
        key: keyType._parse(new ParseInputLazyPath(ctx, key, ctx.path, [index, "key"])),
        value: valueType._parse(new ParseInputLazyPath(ctx, value, ctx.path, [index, "value"]))
      };
    });
    if (ctx.common.async) {
      const finalMap = /* @__PURE__ */ new Map();
      return Promise.resolve().then(async () => {
        for (const pair of pairs) {
          const key = await pair.key;
          const value = await pair.value;
          if (key.status === "aborted" || value.status === "aborted") {
            return INVALID;
          }
          if (key.status === "dirty" || value.status === "dirty") {
            status.dirty();
          }
          finalMap.set(key.value, value.value);
        }
        return { status: status.value, value: finalMap };
      });
    } else {
      const finalMap = /* @__PURE__ */ new Map();
      for (const pair of pairs) {
        const key = pair.key;
        const value = pair.value;
        if (key.status === "aborted" || value.status === "aborted") {
          return INVALID;
        }
        if (key.status === "dirty" || value.status === "dirty") {
          status.dirty();
        }
        finalMap.set(key.value, value.value);
      }
      return { status: status.value, value: finalMap };
    }
  }
};
ZodMap.create = (keyType, valueType, params) => {
  return new ZodMap({
    valueType,
    keyType,
    typeName: ZodFirstPartyTypeKind.ZodMap,
    ...processCreateParams(params)
  });
};
var ZodSet = class _ZodSet extends ZodType {
  _parse(input) {
    const { status, ctx } = this._processInputParams(input);
    if (ctx.parsedType !== ZodParsedType.set) {
      addIssueToContext(ctx, {
        code: ZodIssueCode.invalid_type,
        expected: ZodParsedType.set,
        received: ctx.parsedType
      });
      return INVALID;
    }
    const def = this._def;
    if (def.minSize !== null) {
      if (ctx.data.size < def.minSize.value) {
        addIssueToContext(ctx, {
          code: ZodIssueCode.too_small,
          minimum: def.minSize.value,
          type: "set",
          inclusive: true,
          exact: false,
          message: def.minSize.message
        });
        status.dirty();
      }
    }
    if (def.maxSize !== null) {
      if (ctx.data.size > def.maxSize.value) {
        addIssueToContext(ctx, {
          code: ZodIssueCode.too_big,
          maximum: def.maxSize.value,
          type: "set",
          inclusive: true,
          exact: false,
          message: def.maxSize.message
        });
        status.dirty();
      }
    }
    const valueType = this._def.valueType;
    function finalizeSet(elements2) {
      const parsedSet = /* @__PURE__ */ new Set();
      for (const element of elements2) {
        if (element.status === "aborted")
          return INVALID;
        if (element.status === "dirty")
          status.dirty();
        parsedSet.add(element.value);
      }
      return { status: status.value, value: parsedSet };
    }
    const elements = [...ctx.data.values()].map((item, i) => valueType._parse(new ParseInputLazyPath(ctx, item, ctx.path, i)));
    if (ctx.common.async) {
      return Promise.all(elements).then((elements2) => finalizeSet(elements2));
    } else {
      return finalizeSet(elements);
    }
  }
  min(minSize, message) {
    return new _ZodSet({
      ...this._def,
      minSize: { value: minSize, message: errorUtil.toString(message) }
    });
  }
  max(maxSize, message) {
    return new _ZodSet({
      ...this._def,
      maxSize: { value: maxSize, message: errorUtil.toString(message) }
    });
  }
  size(size, message) {
    return this.min(size, message).max(size, message);
  }
  nonempty(message) {
    return this.min(1, message);
  }
};
ZodSet.create = (valueType, params) => {
  return new ZodSet({
    valueType,
    minSize: null,
    maxSize: null,
    typeName: ZodFirstPartyTypeKind.ZodSet,
    ...processCreateParams(params)
  });
};
var ZodFunction = class _ZodFunction extends ZodType {
  constructor() {
    super(...arguments);
    this.validate = this.implement;
  }
  _parse(input) {
    const { ctx } = this._processInputParams(input);
    if (ctx.parsedType !== ZodParsedType.function) {
      addIssueToContext(ctx, {
        code: ZodIssueCode.invalid_type,
        expected: ZodParsedType.function,
        received: ctx.parsedType
      });
      return INVALID;
    }
    function makeArgsIssue(args, error) {
      return makeIssue({
        data: args,
        path: ctx.path,
        errorMaps: [ctx.common.contextualErrorMap, ctx.schemaErrorMap, getErrorMap(), en_default].filter((x) => !!x),
        issueData: {
          code: ZodIssueCode.invalid_arguments,
          argumentsError: error
        }
      });
    }
    function makeReturnsIssue(returns, error) {
      return makeIssue({
        data: returns,
        path: ctx.path,
        errorMaps: [ctx.common.contextualErrorMap, ctx.schemaErrorMap, getErrorMap(), en_default].filter((x) => !!x),
        issueData: {
          code: ZodIssueCode.invalid_return_type,
          returnTypeError: error
        }
      });
    }
    const params = { errorMap: ctx.common.contextualErrorMap };
    const fn = ctx.data;
    if (this._def.returns instanceof ZodPromise) {
      const me = this;
      return OK(async function(...args) {
        const error = new ZodError([]);
        const parsedArgs = await me._def.args.parseAsync(args, params).catch((e) => {
          error.addIssue(makeArgsIssue(args, e));
          throw error;
        });
        const result = await Reflect.apply(fn, this, parsedArgs);
        const parsedReturns = await me._def.returns._def.type.parseAsync(result, params).catch((e) => {
          error.addIssue(makeReturnsIssue(result, e));
          throw error;
        });
        return parsedReturns;
      });
    } else {
      const me = this;
      return OK(function(...args) {
        const parsedArgs = me._def.args.safeParse(args, params);
        if (!parsedArgs.success) {
          throw new ZodError([makeArgsIssue(args, parsedArgs.error)]);
        }
        const result = Reflect.apply(fn, this, parsedArgs.data);
        const parsedReturns = me._def.returns.safeParse(result, params);
        if (!parsedReturns.success) {
          throw new ZodError([makeReturnsIssue(result, parsedReturns.error)]);
        }
        return parsedReturns.data;
      });
    }
  }
  parameters() {
    return this._def.args;
  }
  returnType() {
    return this._def.returns;
  }
  args(...items) {
    return new _ZodFunction({
      ...this._def,
      args: ZodTuple.create(items).rest(ZodUnknown.create())
    });
  }
  returns(returnType) {
    return new _ZodFunction({
      ...this._def,
      returns: returnType
    });
  }
  implement(func) {
    const validatedFunc = this.parse(func);
    return validatedFunc;
  }
  strictImplement(func) {
    const validatedFunc = this.parse(func);
    return validatedFunc;
  }
  static create(args, returns, params) {
    return new _ZodFunction({
      args: args ? args : ZodTuple.create([]).rest(ZodUnknown.create()),
      returns: returns || ZodUnknown.create(),
      typeName: ZodFirstPartyTypeKind.ZodFunction,
      ...processCreateParams(params)
    });
  }
};
var ZodLazy = class extends ZodType {
  get schema() {
    return this._def.getter();
  }
  _parse(input) {
    const { ctx } = this._processInputParams(input);
    const lazySchema = this._def.getter();
    return lazySchema._parse({ data: ctx.data, path: ctx.path, parent: ctx });
  }
};
ZodLazy.create = (getter, params) => {
  return new ZodLazy({
    getter,
    typeName: ZodFirstPartyTypeKind.ZodLazy,
    ...processCreateParams(params)
  });
};
var ZodLiteral = class extends ZodType {
  _parse(input) {
    if (input.data !== this._def.value) {
      const ctx = this._getOrReturnCtx(input);
      addIssueToContext(ctx, {
        received: ctx.data,
        code: ZodIssueCode.invalid_literal,
        expected: this._def.value
      });
      return INVALID;
    }
    return { status: "valid", value: input.data };
  }
  get value() {
    return this._def.value;
  }
};
ZodLiteral.create = (value, params) => {
  return new ZodLiteral({
    value,
    typeName: ZodFirstPartyTypeKind.ZodLiteral,
    ...processCreateParams(params)
  });
};
function createZodEnum(values, params) {
  return new ZodEnum({
    values,
    typeName: ZodFirstPartyTypeKind.ZodEnum,
    ...processCreateParams(params)
  });
}
var ZodEnum = class _ZodEnum extends ZodType {
  _parse(input) {
    if (typeof input.data !== "string") {
      const ctx = this._getOrReturnCtx(input);
      const expectedValues = this._def.values;
      addIssueToContext(ctx, {
        expected: util.joinValues(expectedValues),
        received: ctx.parsedType,
        code: ZodIssueCode.invalid_type
      });
      return INVALID;
    }
    if (!this._cache) {
      this._cache = new Set(this._def.values);
    }
    if (!this._cache.has(input.data)) {
      const ctx = this._getOrReturnCtx(input);
      const expectedValues = this._def.values;
      addIssueToContext(ctx, {
        received: ctx.data,
        code: ZodIssueCode.invalid_enum_value,
        options: expectedValues
      });
      return INVALID;
    }
    return OK(input.data);
  }
  get options() {
    return this._def.values;
  }
  get enum() {
    const enumValues = {};
    for (const val of this._def.values) {
      enumValues[val] = val;
    }
    return enumValues;
  }
  get Values() {
    const enumValues = {};
    for (const val of this._def.values) {
      enumValues[val] = val;
    }
    return enumValues;
  }
  get Enum() {
    const enumValues = {};
    for (const val of this._def.values) {
      enumValues[val] = val;
    }
    return enumValues;
  }
  extract(values, newDef = this._def) {
    return _ZodEnum.create(values, {
      ...this._def,
      ...newDef
    });
  }
  exclude(values, newDef = this._def) {
    return _ZodEnum.create(this.options.filter((opt) => !values.includes(opt)), {
      ...this._def,
      ...newDef
    });
  }
};
ZodEnum.create = createZodEnum;
var ZodNativeEnum = class extends ZodType {
  _parse(input) {
    const nativeEnumValues = util.getValidEnumValues(this._def.values);
    const ctx = this._getOrReturnCtx(input);
    if (ctx.parsedType !== ZodParsedType.string && ctx.parsedType !== ZodParsedType.number) {
      const expectedValues = util.objectValues(nativeEnumValues);
      addIssueToContext(ctx, {
        expected: util.joinValues(expectedValues),
        received: ctx.parsedType,
        code: ZodIssueCode.invalid_type
      });
      return INVALID;
    }
    if (!this._cache) {
      this._cache = new Set(util.getValidEnumValues(this._def.values));
    }
    if (!this._cache.has(input.data)) {
      const expectedValues = util.objectValues(nativeEnumValues);
      addIssueToContext(ctx, {
        received: ctx.data,
        code: ZodIssueCode.invalid_enum_value,
        options: expectedValues
      });
      return INVALID;
    }
    return OK(input.data);
  }
  get enum() {
    return this._def.values;
  }
};
ZodNativeEnum.create = (values, params) => {
  return new ZodNativeEnum({
    values,
    typeName: ZodFirstPartyTypeKind.ZodNativeEnum,
    ...processCreateParams(params)
  });
};
var ZodPromise = class extends ZodType {
  unwrap() {
    return this._def.type;
  }
  _parse(input) {
    const { ctx } = this._processInputParams(input);
    if (ctx.parsedType !== ZodParsedType.promise && ctx.common.async === false) {
      addIssueToContext(ctx, {
        code: ZodIssueCode.invalid_type,
        expected: ZodParsedType.promise,
        received: ctx.parsedType
      });
      return INVALID;
    }
    const promisified = ctx.parsedType === ZodParsedType.promise ? ctx.data : Promise.resolve(ctx.data);
    return OK(promisified.then((data) => {
      return this._def.type.parseAsync(data, {
        path: ctx.path,
        errorMap: ctx.common.contextualErrorMap
      });
    }));
  }
};
ZodPromise.create = (schema, params) => {
  return new ZodPromise({
    type: schema,
    typeName: ZodFirstPartyTypeKind.ZodPromise,
    ...processCreateParams(params)
  });
};
var ZodEffects = class extends ZodType {
  innerType() {
    return this._def.schema;
  }
  sourceType() {
    return this._def.schema._def.typeName === ZodFirstPartyTypeKind.ZodEffects ? this._def.schema.sourceType() : this._def.schema;
  }
  _parse(input) {
    const { status, ctx } = this._processInputParams(input);
    const effect = this._def.effect || null;
    const checkCtx = {
      addIssue: (arg) => {
        addIssueToContext(ctx, arg);
        if (arg.fatal) {
          status.abort();
        } else {
          status.dirty();
        }
      },
      get path() {
        return ctx.path;
      }
    };
    checkCtx.addIssue = checkCtx.addIssue.bind(checkCtx);
    if (effect.type === "preprocess") {
      const processed = effect.transform(ctx.data, checkCtx);
      if (ctx.common.async) {
        return Promise.resolve(processed).then(async (processed2) => {
          if (status.value === "aborted")
            return INVALID;
          const result = await this._def.schema._parseAsync({
            data: processed2,
            path: ctx.path,
            parent: ctx
          });
          if (result.status === "aborted")
            return INVALID;
          if (result.status === "dirty")
            return DIRTY(result.value);
          if (status.value === "dirty")
            return DIRTY(result.value);
          return result;
        });
      } else {
        if (status.value === "aborted")
          return INVALID;
        const result = this._def.schema._parseSync({
          data: processed,
          path: ctx.path,
          parent: ctx
        });
        if (result.status === "aborted")
          return INVALID;
        if (result.status === "dirty")
          return DIRTY(result.value);
        if (status.value === "dirty")
          return DIRTY(result.value);
        return result;
      }
    }
    if (effect.type === "refinement") {
      const executeRefinement = (acc) => {
        const result = effect.refinement(acc, checkCtx);
        if (ctx.common.async) {
          return Promise.resolve(result);
        }
        if (result instanceof Promise) {
          throw new Error("Async refinement encountered during synchronous parse operation. Use .parseAsync instead.");
        }
        return acc;
      };
      if (ctx.common.async === false) {
        const inner = this._def.schema._parseSync({
          data: ctx.data,
          path: ctx.path,
          parent: ctx
        });
        if (inner.status === "aborted")
          return INVALID;
        if (inner.status === "dirty")
          status.dirty();
        executeRefinement(inner.value);
        return { status: status.value, value: inner.value };
      } else {
        return this._def.schema._parseAsync({ data: ctx.data, path: ctx.path, parent: ctx }).then((inner) => {
          if (inner.status === "aborted")
            return INVALID;
          if (inner.status === "dirty")
            status.dirty();
          return executeRefinement(inner.value).then(() => {
            return { status: status.value, value: inner.value };
          });
        });
      }
    }
    if (effect.type === "transform") {
      if (ctx.common.async === false) {
        const base = this._def.schema._parseSync({
          data: ctx.data,
          path: ctx.path,
          parent: ctx
        });
        if (!isValid(base))
          return INVALID;
        const result = effect.transform(base.value, checkCtx);
        if (result instanceof Promise) {
          throw new Error(`Asynchronous transform encountered during synchronous parse operation. Use .parseAsync instead.`);
        }
        return { status: status.value, value: result };
      } else {
        return this._def.schema._parseAsync({ data: ctx.data, path: ctx.path, parent: ctx }).then((base) => {
          if (!isValid(base))
            return INVALID;
          return Promise.resolve(effect.transform(base.value, checkCtx)).then((result) => ({
            status: status.value,
            value: result
          }));
        });
      }
    }
    util.assertNever(effect);
  }
};
ZodEffects.create = (schema, effect, params) => {
  return new ZodEffects({
    schema,
    typeName: ZodFirstPartyTypeKind.ZodEffects,
    effect,
    ...processCreateParams(params)
  });
};
ZodEffects.createWithPreprocess = (preprocess, schema, params) => {
  return new ZodEffects({
    schema,
    effect: { type: "preprocess", transform: preprocess },
    typeName: ZodFirstPartyTypeKind.ZodEffects,
    ...processCreateParams(params)
  });
};
var ZodOptional = class extends ZodType {
  _parse(input) {
    const parsedType = this._getType(input);
    if (parsedType === ZodParsedType.undefined) {
      return OK(void 0);
    }
    return this._def.innerType._parse(input);
  }
  unwrap() {
    return this._def.innerType;
  }
};
ZodOptional.create = (type, params) => {
  return new ZodOptional({
    innerType: type,
    typeName: ZodFirstPartyTypeKind.ZodOptional,
    ...processCreateParams(params)
  });
};
var ZodNullable = class extends ZodType {
  _parse(input) {
    const parsedType = this._getType(input);
    if (parsedType === ZodParsedType.null) {
      return OK(null);
    }
    return this._def.innerType._parse(input);
  }
  unwrap() {
    return this._def.innerType;
  }
};
ZodNullable.create = (type, params) => {
  return new ZodNullable({
    innerType: type,
    typeName: ZodFirstPartyTypeKind.ZodNullable,
    ...processCreateParams(params)
  });
};
var ZodDefault = class extends ZodType {
  _parse(input) {
    const { ctx } = this._processInputParams(input);
    let data = ctx.data;
    if (ctx.parsedType === ZodParsedType.undefined) {
      data = this._def.defaultValue();
    }
    return this._def.innerType._parse({
      data,
      path: ctx.path,
      parent: ctx
    });
  }
  removeDefault() {
    return this._def.innerType;
  }
};
ZodDefault.create = (type, params) => {
  return new ZodDefault({
    innerType: type,
    typeName: ZodFirstPartyTypeKind.ZodDefault,
    defaultValue: typeof params.default === "function" ? params.default : () => params.default,
    ...processCreateParams(params)
  });
};
var ZodCatch = class extends ZodType {
  _parse(input) {
    const { ctx } = this._processInputParams(input);
    const newCtx = {
      ...ctx,
      common: {
        ...ctx.common,
        issues: []
      }
    };
    const result = this._def.innerType._parse({
      data: newCtx.data,
      path: newCtx.path,
      parent: {
        ...newCtx
      }
    });
    if (isAsync(result)) {
      return result.then((result2) => {
        return {
          status: "valid",
          value: result2.status === "valid" ? result2.value : this._def.catchValue({
            get error() {
              return new ZodError(newCtx.common.issues);
            },
            input: newCtx.data
          })
        };
      });
    } else {
      return {
        status: "valid",
        value: result.status === "valid" ? result.value : this._def.catchValue({
          get error() {
            return new ZodError(newCtx.common.issues);
          },
          input: newCtx.data
        })
      };
    }
  }
  removeCatch() {
    return this._def.innerType;
  }
};
ZodCatch.create = (type, params) => {
  return new ZodCatch({
    innerType: type,
    typeName: ZodFirstPartyTypeKind.ZodCatch,
    catchValue: typeof params.catch === "function" ? params.catch : () => params.catch,
    ...processCreateParams(params)
  });
};
var ZodNaN = class extends ZodType {
  _parse(input) {
    const parsedType = this._getType(input);
    if (parsedType !== ZodParsedType.nan) {
      const ctx = this._getOrReturnCtx(input);
      addIssueToContext(ctx, {
        code: ZodIssueCode.invalid_type,
        expected: ZodParsedType.nan,
        received: ctx.parsedType
      });
      return INVALID;
    }
    return { status: "valid", value: input.data };
  }
};
ZodNaN.create = (params) => {
  return new ZodNaN({
    typeName: ZodFirstPartyTypeKind.ZodNaN,
    ...processCreateParams(params)
  });
};
var BRAND = /* @__PURE__ */ Symbol("zod_brand");
var ZodBranded = class extends ZodType {
  _parse(input) {
    const { ctx } = this._processInputParams(input);
    const data = ctx.data;
    return this._def.type._parse({
      data,
      path: ctx.path,
      parent: ctx
    });
  }
  unwrap() {
    return this._def.type;
  }
};
var ZodPipeline = class _ZodPipeline extends ZodType {
  _parse(input) {
    const { status, ctx } = this._processInputParams(input);
    if (ctx.common.async) {
      const handleAsync = async () => {
        const inResult = await this._def.in._parseAsync({
          data: ctx.data,
          path: ctx.path,
          parent: ctx
        });
        if (inResult.status === "aborted")
          return INVALID;
        if (inResult.status === "dirty") {
          status.dirty();
          return DIRTY(inResult.value);
        } else {
          return this._def.out._parseAsync({
            data: inResult.value,
            path: ctx.path,
            parent: ctx
          });
        }
      };
      return handleAsync();
    } else {
      const inResult = this._def.in._parseSync({
        data: ctx.data,
        path: ctx.path,
        parent: ctx
      });
      if (inResult.status === "aborted")
        return INVALID;
      if (inResult.status === "dirty") {
        status.dirty();
        return {
          status: "dirty",
          value: inResult.value
        };
      } else {
        return this._def.out._parseSync({
          data: inResult.value,
          path: ctx.path,
          parent: ctx
        });
      }
    }
  }
  static create(a, b) {
    return new _ZodPipeline({
      in: a,
      out: b,
      typeName: ZodFirstPartyTypeKind.ZodPipeline
    });
  }
};
var ZodReadonly = class extends ZodType {
  _parse(input) {
    const result = this._def.innerType._parse(input);
    const freeze = (data) => {
      if (isValid(data)) {
        data.value = Object.freeze(data.value);
      }
      return data;
    };
    return isAsync(result) ? result.then((data) => freeze(data)) : freeze(result);
  }
  unwrap() {
    return this._def.innerType;
  }
};
ZodReadonly.create = (type, params) => {
  return new ZodReadonly({
    innerType: type,
    typeName: ZodFirstPartyTypeKind.ZodReadonly,
    ...processCreateParams(params)
  });
};
function cleanParams(params, data) {
  const p = typeof params === "function" ? params(data) : typeof params === "string" ? { message: params } : params;
  const p2 = typeof p === "string" ? { message: p } : p;
  return p2;
}
function custom(check, _params = {}, fatal) {
  if (check)
    return ZodAny.create().superRefine((data, ctx) => {
      const r = check(data);
      if (r instanceof Promise) {
        return r.then((r2) => {
          if (!r2) {
            const params = cleanParams(_params, data);
            const _fatal = params.fatal ?? fatal ?? true;
            ctx.addIssue({ code: "custom", ...params, fatal: _fatal });
          }
        });
      }
      if (!r) {
        const params = cleanParams(_params, data);
        const _fatal = params.fatal ?? fatal ?? true;
        ctx.addIssue({ code: "custom", ...params, fatal: _fatal });
      }
      return;
    });
  return ZodAny.create();
}
var late = {
  object: ZodObject.lazycreate
};
var ZodFirstPartyTypeKind;
(function(ZodFirstPartyTypeKind2) {
  ZodFirstPartyTypeKind2["ZodString"] = "ZodString";
  ZodFirstPartyTypeKind2["ZodNumber"] = "ZodNumber";
  ZodFirstPartyTypeKind2["ZodNaN"] = "ZodNaN";
  ZodFirstPartyTypeKind2["ZodBigInt"] = "ZodBigInt";
  ZodFirstPartyTypeKind2["ZodBoolean"] = "ZodBoolean";
  ZodFirstPartyTypeKind2["ZodDate"] = "ZodDate";
  ZodFirstPartyTypeKind2["ZodSymbol"] = "ZodSymbol";
  ZodFirstPartyTypeKind2["ZodUndefined"] = "ZodUndefined";
  ZodFirstPartyTypeKind2["ZodNull"] = "ZodNull";
  ZodFirstPartyTypeKind2["ZodAny"] = "ZodAny";
  ZodFirstPartyTypeKind2["ZodUnknown"] = "ZodUnknown";
  ZodFirstPartyTypeKind2["ZodNever"] = "ZodNever";
  ZodFirstPartyTypeKind2["ZodVoid"] = "ZodVoid";
  ZodFirstPartyTypeKind2["ZodArray"] = "ZodArray";
  ZodFirstPartyTypeKind2["ZodObject"] = "ZodObject";
  ZodFirstPartyTypeKind2["ZodUnion"] = "ZodUnion";
  ZodFirstPartyTypeKind2["ZodDiscriminatedUnion"] = "ZodDiscriminatedUnion";
  ZodFirstPartyTypeKind2["ZodIntersection"] = "ZodIntersection";
  ZodFirstPartyTypeKind2["ZodTuple"] = "ZodTuple";
  ZodFirstPartyTypeKind2["ZodRecord"] = "ZodRecord";
  ZodFirstPartyTypeKind2["ZodMap"] = "ZodMap";
  ZodFirstPartyTypeKind2["ZodSet"] = "ZodSet";
  ZodFirstPartyTypeKind2["ZodFunction"] = "ZodFunction";
  ZodFirstPartyTypeKind2["ZodLazy"] = "ZodLazy";
  ZodFirstPartyTypeKind2["ZodLiteral"] = "ZodLiteral";
  ZodFirstPartyTypeKind2["ZodEnum"] = "ZodEnum";
  ZodFirstPartyTypeKind2["ZodEffects"] = "ZodEffects";
  ZodFirstPartyTypeKind2["ZodNativeEnum"] = "ZodNativeEnum";
  ZodFirstPartyTypeKind2["ZodOptional"] = "ZodOptional";
  ZodFirstPartyTypeKind2["ZodNullable"] = "ZodNullable";
  ZodFirstPartyTypeKind2["ZodDefault"] = "ZodDefault";
  ZodFirstPartyTypeKind2["ZodCatch"] = "ZodCatch";
  ZodFirstPartyTypeKind2["ZodPromise"] = "ZodPromise";
  ZodFirstPartyTypeKind2["ZodBranded"] = "ZodBranded";
  ZodFirstPartyTypeKind2["ZodPipeline"] = "ZodPipeline";
  ZodFirstPartyTypeKind2["ZodReadonly"] = "ZodReadonly";
})(ZodFirstPartyTypeKind || (ZodFirstPartyTypeKind = {}));
var instanceOfType = (cls, params = {
  message: `Input not instance of ${cls.name}`
}) => custom((data) => data instanceof cls, params);
var stringType = ZodString.create;
var numberType = ZodNumber.create;
var nanType = ZodNaN.create;
var bigIntType = ZodBigInt.create;
var booleanType = ZodBoolean.create;
var dateType = ZodDate.create;
var symbolType = ZodSymbol.create;
var undefinedType = ZodUndefined.create;
var nullType = ZodNull.create;
var anyType = ZodAny.create;
var unknownType = ZodUnknown.create;
var neverType = ZodNever.create;
var voidType = ZodVoid.create;
var arrayType = ZodArray.create;
var objectType = ZodObject.create;
var strictObjectType = ZodObject.strictCreate;
var unionType = ZodUnion.create;
var discriminatedUnionType = ZodDiscriminatedUnion.create;
var intersectionType = ZodIntersection.create;
var tupleType = ZodTuple.create;
var recordType = ZodRecord.create;
var mapType = ZodMap.create;
var setType = ZodSet.create;
var functionType = ZodFunction.create;
var lazyType = ZodLazy.create;
var literalType = ZodLiteral.create;
var enumType = ZodEnum.create;
var nativeEnumType = ZodNativeEnum.create;
var promiseType = ZodPromise.create;
var effectsType = ZodEffects.create;
var optionalType = ZodOptional.create;
var nullableType = ZodNullable.create;
var preprocessType = ZodEffects.createWithPreprocess;
var pipelineType = ZodPipeline.create;
var ostring = () => stringType().optional();
var onumber = () => numberType().optional();
var oboolean = () => booleanType().optional();
var coerce = {
  string: ((arg) => ZodString.create({ ...arg, coerce: true })),
  number: ((arg) => ZodNumber.create({ ...arg, coerce: true })),
  boolean: ((arg) => ZodBoolean.create({
    ...arg,
    coerce: true
  })),
  bigint: ((arg) => ZodBigInt.create({ ...arg, coerce: true })),
  date: ((arg) => ZodDate.create({ ...arg, coerce: true }))
};
var NEVER = INVALID;

// src/server/schemas/procurement-classification.schema.ts
var ProcurementClassificationSchema = external_exports.object({
  // Categorías de producto - SIN LÍMITE
  categoryIds: external_exports.array(external_exports.string()).describe(
    "IDs de TODAS las categor\xEDas de producto relevantes de la ontolog\xEDa. Sin l\xEDmite."
  ),
  categoryConfidence: external_exports.number().min(0).max(1).describe(
    "Confianza promedio en las asignaciones de categor\xEDas (0-1)"
  ),
  // Mercados/Verticales - SIN LÍMITE
  marketIds: external_exports.array(external_exports.string()).describe(
    "IDs de TODOS los mercados/verticales industriales aplicables. Sin l\xEDmite."
  ),
  marketConfidence: external_exports.number().min(0).max(1).describe(
    "Confianza promedio en las asignaciones de mercados (0-1)"
  ),
  // Topics/Tendencias - SIN LÍMITE
  topicIds: external_exports.array(external_exports.string()).describe(
    "IDs de TODOS los temas/tendencias tecnol\xF3gicas relacionados. Sin l\xEDmite."
  ),
  topicConfidence: external_exports.number().min(0).max(1).describe(
    "Confianza promedio en las asignaciones de topics (0-1)"
  ),
  // Análisis de relevancia
  isRelevant: external_exports.boolean().describe(
    "Si la licitaci\xF3n es relevante para el distribuidor seg\xFAn los criterios definidos"
  ),
  relevanceReason: external_exports.string().describe(
    "Explicaci\xF3n clara de por qu\xE9 la licitaci\xF3n es o no es relevante"
  ),
  relevanceConfidence: external_exports.number().min(0).max(1).describe(
    "Confianza en la determinaci\xF3n de relevancia (0-1)"
  ),
  // Análisis de valor y urgencia
  estimatedValue: external_exports.enum(["very-high", "high", "medium", "low", "unknown"]).describe(
    "Valor econ\xF3mico estimado del contrato basado en el texto del anuncio"
  ),
  urgency: external_exports.enum(["urgent", "normal", "low"]).describe(
    "Urgencia basada en plazos de presentaci\xF3n y tipo de procedimiento"
  ),
  complexity: external_exports.enum(["complex", "standard", "simple"]).describe(
    "Complejidad del suministro: complex (integraci\xF3n, multi-producto), standard, simple"
  ),
  // Keywords técnicas extraídas
  extractedKeywords: external_exports.array(external_exports.string()).describe(
    "Palabras clave t\xE9cnicas relevantes extra\xEDdas del anuncio (m\xE1x 10)"
  )
});
var BatchClassificationResultSchema = external_exports.object({
  classifications: external_exports.array(external_exports.object({
    sourceId: external_exports.string().describe("ID \xFAnico de la licitaci\xF3n (sourceId del documento)"),
    classification: ProcurementClassificationSchema
  }))
});
var ClassifierConfigSchema = external_exports.object({
  id: external_exports.string(),
  configType: external_exports.literal("classifier"),
  // Modelo configurable
  model: external_exports.string().default("gemini-3-flash-preview"),
  fallbackModel: external_exports.string().default("gemini-3-flash-preview"),
  temperature: external_exports.number().min(0).max(1).default(0.1),
  // Procesamiento
  batchSize: external_exports.number().min(1).max(50).default(10),
  maxNoticesPerRun: external_exports.number().min(1).max(1e3).default(500),
  autoClassifyOnSync: external_exports.boolean().default(true),
  // Umbrales
  minConfidenceThreshold: external_exports.number().min(0).max(1).default(0.6),
  minRelevanceScore: external_exports.number().min(0).max(1).default(0.3),
  // Custom prompts (IDs de documentos en agent_prompts)
  customSystemPromptId: external_exports.string().nullable().default(null),
  customRelevanceCriteriaId: external_exports.string().nullable().default(null),
  // Metadata
  lastClassifiedAt: external_exports.date().nullable().optional(),
  totalClassified: external_exports.number().default(0),
  updatedAt: external_exports.date().optional(),
  updatedBy: external_exports.string().optional()
});
var PromptVariableSchema = external_exports.object({
  key: external_exports.string(),
  name: external_exports.string(),
  description: external_exports.string(),
  type: external_exports.enum(["string", "number", "boolean", "array"]),
  required: external_exports.boolean()
});
var StoredPromptSchema = external_exports.object({
  id: external_exports.string(),
  agentType: external_exports.string(),
  promptKey: external_exports.string(),
  name: external_exports.string(),
  description: external_exports.string().optional(),
  template: external_exports.string(),
  availableVariables: external_exports.array(PromptVariableSchema),
  version: external_exports.number(),
  isActive: external_exports.boolean(),
  createdAt: external_exports.any(),
  // Firestore Timestamp
  updatedAt: external_exports.any(),
  // Firestore Timestamp
  createdBy: external_exports.string()
});
var ClassifyProcurementRequestSchema = external_exports.object({
  tenantId: external_exports.string().min(1, "tenantId es requerido"),
  noticeIds: external_exports.array(external_exports.string()).optional().describe(
    "IDs espec\xEDficas de licitaciones a clasificar. Si no se provee, se clasifican las pendientes."
  ),
  limit: external_exports.number().min(1).max(1e3).default(100).describe(
    "N\xFAmero m\xE1ximo de licitaciones a clasificar en esta ejecuci\xF3n"
  ),
  modelOverride: external_exports.string().optional().describe(
    "Modelo espec\xEDfico a usar, sobreescribe la configuraci\xF3n del tenant"
  ),
  forceReclassify: external_exports.boolean().default(false).describe(
    "Si es true, reclasifica incluso las que ya tienen clasificaci\xF3n"
  )
});
var ClassifyProcurementResponseSchema = external_exports.object({
  success: external_exports.boolean(),
  classified: external_exports.number().describe("N\xFAmero de licitaciones clasificadas exitosamente"),
  errors: external_exports.number().describe("N\xFAmero de errores durante la clasificaci\xF3n"),
  skipped: external_exports.number().describe("N\xFAmero de licitaciones omitidas (ya clasificadas o bajo umbral)"),
  durationMs: external_exports.number().describe("Duraci\xF3n total en milisegundos"),
  modelUsed: external_exports.string().describe("Modelo usado para la clasificaci\xF3n"),
  promptVersion: external_exports.number().optional().describe("Versi\xF3n del prompt usado")
});
function getClassificationJsonSchema() {
  return {
    type: "object",
    properties: {
      classifications: {
        type: "array",
        items: {
          type: "object",
          properties: {
            sourceId: { type: "string" },
            classification: {
              type: "object",
              properties: {
                categoryIds: { type: "array", items: { type: "string" } },
                categoryConfidence: { type: "number" },
                marketIds: { type: "array", items: { type: "string" } },
                marketConfidence: { type: "number" },
                topicIds: { type: "array", items: { type: "string" } },
                topicConfidence: { type: "number" },
                isRelevant: { type: "boolean" },
                relevanceReason: { type: "string" },
                relevanceConfidence: { type: "number" },
                estimatedValue: {
                  type: "string",
                  enum: ["very-high", "high", "medium", "low", "unknown"]
                },
                urgency: { type: "string", enum: ["urgent", "normal", "low"] },
                complexity: { type: "string", enum: ["complex", "standard", "simple"] },
                extractedKeywords: { type: "array", items: { type: "string" } }
              },
              required: [
                "categoryIds",
                "categoryConfidence",
                "marketIds",
                "marketConfidence",
                "topicIds",
                "topicConfidence",
                "isRelevant",
                "relevanceReason",
                "relevanceConfidence",
                "estimatedValue",
                "urgency",
                "complexity",
                "extractedKeywords"
              ]
            }
          },
          required: ["sourceId", "classification"]
        }
      }
    },
    required: ["classifications"]
  };
}

// src/server/kling-client.ts
import { Logger as Logger20 } from "@boosting/core";
var logger15 = Logger20.getLogger("KlingClient");
var FAL_API_BASE = "https://queue.fal.run";
var FAL_QUEUE_BASE = "https://queue.fal.run";
var FAL_RESULT_BASE = "https://queue.fal.run";
var KLING_I2V_MODEL = "fal-ai/kling-video/v3/pro/image-to-video";
var KLING_T2V_MODEL = "fal-ai/kling-video/v3/pro/text-to-video";
var KlingClient = class {
  apiKey;
  timeoutMs;
  webhookUrl;
  constructor(config) {
    if (!config.apiKey) {
      throw new Error("KlingClient: apiKey (FAL_KEY) is required");
    }
    this.apiKey = config.apiKey;
    this.timeoutMs = config.timeoutMs ?? 3e5;
    this.webhookUrl = config.webhookUrl;
  }
  // ============================================
  // PUBLIC API
  // ============================================
  /**
   * Generate video from a product image (image-to-video).
   * This is the primary use case — preserves text, logos, labels.
   */
  async imageToVideo(request) {
    const startTime = Date.now();
    const duration = String(request.duration ?? "5");
    try {
      logger15.info("[Kling] Starting image-to-video generation", {
        duration,
        aspectRatio: request.aspectRatio ?? "16:9",
        hasElements: !!request.elements?.length,
        hasEndImage: !!request.endImageUrl
      });
      const input = {
        start_image_url: request.imageUrl,
        prompt: request.prompt,
        duration,
        aspect_ratio: request.aspectRatio ?? "16:9",
        generate_audio: request.generateAudio ?? false,
        negative_prompt: request.negativePrompt ?? "blur, distort, and low quality",
        cfg_scale: request.cfgScale ?? 0.5
      };
      if (request.endImageUrl) {
        input.end_image_url = request.endImageUrl;
      }
      if (request.elements?.length) {
        input.elements = request.elements;
      }
      if (request.multiPrompt?.length) {
        input.multi_prompt = request.multiPrompt;
        input.shot_type = "customize";
        delete input.prompt;
      }
      const result = await this.submitAndWait(KLING_I2V_MODEL, input);
      const durationSeconds = parseInt(duration, 10);
      const estimatedCost = durationSeconds * (KLING_PRICING.video["kling-3.0-pro"] || 0.15);
      return {
        success: true,
        video: result.video,
        requestId: result.requestId,
        durationSeconds,
        estimatedCost: +estimatedCost.toFixed(4),
        model: KLING_MODELS.KLING_3_PRO,
        generationTimeMs: Date.now() - startTime
      };
    } catch (error) {
      logger15.error("[Kling] Image-to-video generation failed", error);
      return {
        success: false,
        model: KLING_MODELS.KLING_3_PRO,
        error: error instanceof Error ? error.message : String(error),
        generationTimeMs: Date.now() - startTime
      };
    }
  }
  /**
   * Generate video from text prompt (text-to-video).
   */
  async textToVideo(request) {
    const startTime = Date.now();
    const duration = String(request.duration ?? "5");
    try {
      logger15.info("[Kling] Starting text-to-video generation", {
        duration,
        aspectRatio: request.aspectRatio ?? "16:9"
      });
      const input = {
        prompt: request.prompt,
        duration,
        aspect_ratio: request.aspectRatio ?? "16:9",
        generate_audio: request.generateAudio ?? false,
        negative_prompt: request.negativePrompt ?? "blur, distort, and low quality",
        cfg_scale: request.cfgScale ?? 0.5
      };
      const result = await this.submitAndWait(KLING_T2V_MODEL, input);
      const durationSeconds = parseInt(duration, 10);
      const estimatedCost = durationSeconds * (KLING_PRICING.video["kling-3.0-pro"] || 0.15);
      return {
        success: true,
        video: result.video,
        requestId: result.requestId,
        durationSeconds,
        estimatedCost: +estimatedCost.toFixed(4),
        model: KLING_MODELS.KLING_3_PRO,
        generationTimeMs: Date.now() - startTime
      };
    } catch (error) {
      logger15.error("[Kling] Text-to-video generation failed", error);
      return {
        success: false,
        model: KLING_MODELS.KLING_3_PRO,
        error: error instanceof Error ? error.message : String(error),
        generationTimeMs: Date.now() - startTime
      };
    }
  }
  /**
   * Submit a request to the queue without waiting for completion.
   * Returns the requestId for later polling.
   */
  async submitToQueue(mode, input) {
    const model = mode === "image-to-video" ? KLING_I2V_MODEL : KLING_T2V_MODEL;
    const url = `${FAL_QUEUE_BASE}/${model}`;
    const body = { input };
    if (this.webhookUrl) {
      body.webhook_url = this.webhookUrl;
    }
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Authorization": `Key ${this.apiKey}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify(body)
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`fal.ai queue submit failed (${response.status}): ${errorText}`);
    }
    const data = await response.json();
    return { requestId: data.request_id };
  }
  /**
   * Check the status of a queued request.
   */
  async getQueueStatus(model, requestId) {
    const url = `${FAL_QUEUE_BASE}/${model}/requests/${requestId}/status`;
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Authorization": `Key ${this.apiKey}`
      }
    });
    if (!response.ok) {
      throw new Error(`fal.ai status check failed (${response.status})`);
    }
    const data = await response.json();
    return {
      status: data.status,
      requestId,
      queuePosition: data.queue_position,
      logs: data.logs?.map((l) => l.message)
    };
  }
  /**
   * Get the result of a completed request.
   */
  async getQueueResult(model, requestId) {
    const url = `${FAL_RESULT_BASE}/${model}/requests/${requestId}`;
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Authorization": `Key ${this.apiKey}`
      }
    });
    if (!response.ok) {
      throw new Error(`fal.ai result fetch failed (${response.status})`);
    }
    return await response.json();
  }
  /**
   * Estimate the cost for a video generation request.
   */
  estimateCost(durationSeconds) {
    return +(durationSeconds * (KLING_PRICING.video["kling-3.0-pro"] || 0.15)).toFixed(4);
  }
  // ============================================
  // INTERNAL
  // ============================================
  /**
   * Submit to fal.ai queue and poll until completion.
   */
  async submitAndWait(model, input) {
    const submitUrl = `${FAL_API_BASE}/${model}`;
    const submitResponse = await fetch(submitUrl, {
      method: "POST",
      headers: {
        "Authorization": `Key ${this.apiKey}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify(input)
    });
    if (!submitResponse.ok) {
      const errorText = await submitResponse.text();
      throw new Error(`fal.ai submit failed (${submitResponse.status}): ${errorText}`);
    }
    const submitData = await submitResponse.json();
    const requestId = submitData.request_id;
    logger15.info("[Kling] Request submitted", { requestId, model });
    const startTime = Date.now();
    const pollInterval = 5e3;
    while (Date.now() - startTime < this.timeoutMs) {
      await this.sleep(pollInterval);
      const status = await this.getQueueStatus(model, requestId);
      if (status.logs?.length) {
        logger15.debug("[Kling] Progress", { requestId, logs: status.logs.slice(-2) });
      }
      if (status.status === "COMPLETED") {
        const result = await this.getQueueResult(model, requestId);
        return { ...result, requestId };
      }
      if (status.status === "FAILED") {
        throw new Error(`Kling generation failed (requestId: ${requestId})`);
      }
    }
    throw new Error(`Kling generation timed out after ${this.timeoutMs}ms (requestId: ${requestId})`);
  }
  sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
};
function createKlingClient(config) {
  return new KlingClient(config);
}

// src/server/runway-client.ts
import { Logger as Logger21 } from "@boosting/core";
var logger16 = Logger21.getLogger("RunwayClient");
var RUNWAY_API_BASE = "https://api.dev.runwayml.com/v1";
var RunwayClient = class {
  apiKey;
  apiVersion;
  timeoutMs;
  constructor(config) {
    if (!config.apiKey) {
      throw new Error("RunwayClient: apiKey (RUNWAYML_API_SECRET) is required");
    }
    this.apiKey = config.apiKey;
    this.apiVersion = config.apiVersion ?? "2024-11-06";
    this.timeoutMs = config.timeoutMs ?? 18e4;
  }
  // ============================================
  // PUBLIC API
  // ============================================
  /**
   * Generate video from an image (image-to-video).
   * Uses Gen-4 Turbo for fastest generation (~60s).
   */
  async imageToVideo(request) {
    const startTime = Date.now();
    const model = request.model ?? "gen4.5";
    const duration = request.duration ?? 5;
    try {
      logger16.info("[Runway] Starting image-to-video generation", {
        model,
        duration,
        ratio: request.ratio ?? "1280:720"
      });
      const body = {
        model,
        promptImage: request.imageUrl,
        promptText: request.prompt,
        ratio: request.ratio ?? "1280:720",
        duration
      };
      if (request.seed !== void 0) {
        body.seed = request.seed;
      }
      if (request.watermark !== void 0) {
        body.watermark = request.watermark;
      }
      const task = await this.createTask("image_to_video", body);
      const completedTask = await this.waitForTask(task.id);
      if (completedTask.status !== "SUCCEEDED" || !completedTask.output?.length) {
        throw new Error(
          completedTask.failure || `Task failed with status: ${completedTask.status}`
        );
      }
      const pricePerSecond = model === "gen3a_turbo" ? 0.025 : 0.05;
      const estimatedCost = +(duration * pricePerSecond).toFixed(4);
      return {
        success: true,
        videoUrl: completedTask.output[0],
        taskId: task.id,
        durationSeconds: duration,
        estimatedCost,
        model: RUNWAY_MODELS.GEN4_5,
        generationTimeMs: Date.now() - startTime
      };
    } catch (error) {
      logger16.error("[Runway] Image-to-video generation failed", error);
      return {
        success: false,
        model: RUNWAY_MODELS.GEN4_5,
        error: error instanceof Error ? error.message : String(error),
        generationTimeMs: Date.now() - startTime
      };
    }
  }
  /**
   * Generate video from text only (text-to-video).
   * Supports reference images with at-mention tags.
   */
  async textToVideo(request) {
    const startTime = Date.now();
    const model = request.model ?? "gen4.5";
    const duration = request.duration ?? 5;
    try {
      logger16.info("[Runway] Starting text-to-video generation", {
        model,
        duration,
        ratio: request.ratio ?? "1280:720",
        referenceImagesCount: request.referenceImages?.length ?? 0
      });
      const body = {
        model,
        promptText: request.prompt,
        ratio: request.ratio ?? "1280:720",
        duration
      };
      if (request.referenceImages?.length) {
        body.referenceImages = request.referenceImages;
      }
      if (request.seed !== void 0) {
        body.seed = request.seed;
      }
      if (request.watermark !== void 0) {
        body.watermark = request.watermark;
      }
      const task = await this.createTask("image_to_video", body);
      const completedTask = await this.waitForTask(task.id);
      if (completedTask.status !== "SUCCEEDED" || !completedTask.output?.length) {
        throw new Error(
          completedTask.failure || `Task failed with status: ${completedTask.status}`
        );
      }
      const pricePerSecond = model === "gen3a_turbo" ? 0.025 : 0.05;
      const estimatedCost = +(duration * pricePerSecond).toFixed(4);
      return {
        success: true,
        videoUrl: completedTask.output[0],
        taskId: task.id,
        durationSeconds: duration,
        estimatedCost,
        model: RUNWAY_MODELS.GEN4_5,
        generationTimeMs: Date.now() - startTime
      };
    } catch (error) {
      logger16.error("[Runway] Text-to-video generation failed", error);
      return {
        success: false,
        model: RUNWAY_MODELS.GEN4_5,
        error: error instanceof Error ? error.message : String(error),
        generationTimeMs: Date.now() - startTime
      };
    }
  }
  /**
   * Get the status of a task.
   */
  async getTaskStatus(taskId) {
    return this.getTask(taskId);
  }
  /**
   * Cancel a running task.
   */
  async cancelTask(taskId) {
    const url = `${RUNWAY_API_BASE}/tasks/${taskId}/cancel`;
    const response = await fetch(url, {
      method: "POST",
      headers: this.getHeaders()
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Runway cancel failed (${response.status}): ${errorText}`);
    }
    logger16.info("[Runway] Task cancelled", { taskId });
  }
  /**
   * Estimate the cost for a video generation request.
   */
  estimateCost(durationSeconds, model = "gen4.5") {
    const pricePerSecond = model === "gen3a_turbo" ? 0.025 : 0.05;
    return +(durationSeconds * pricePerSecond).toFixed(4);
  }
  // ============================================
  // INTERNAL
  // ============================================
  getHeaders() {
    return {
      "Authorization": `Bearer ${this.apiKey}`,
      "Content-Type": "application/json",
      "X-Runway-Version": this.apiVersion
    };
  }
  /**
   * Create a new generation task.
   */
  async createTask(endpoint, body) {
    const url = `${RUNWAY_API_BASE}/${endpoint}`;
    const response = await fetch(url, {
      method: "POST",
      headers: this.getHeaders(),
      body: JSON.stringify(body)
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Runway task creation failed (${response.status}): ${errorText}`);
    }
    const data = await response.json();
    logger16.info("[Runway] Task created", { taskId: data.id });
    return data;
  }
  /**
   * Get task details.
   */
  async getTask(taskId) {
    const url = `${RUNWAY_API_BASE}/tasks/${taskId}`;
    const response = await fetch(url, {
      method: "GET",
      headers: this.getHeaders()
    });
    if (!response.ok) {
      throw new Error(`Runway task fetch failed (${response.status})`);
    }
    return await response.json();
  }
  /**
   * Poll for task completion.
   */
  async waitForTask(taskId) {
    const startTime = Date.now();
    const pollInterval = 3e3;
    while (Date.now() - startTime < this.timeoutMs) {
      await this.sleep(pollInterval);
      const task = await this.getTask(taskId);
      if (task.progress !== void 0) {
        logger16.debug("[Runway] Progress", { taskId, progress: task.progress, status: task.status });
      }
      switch (task.status) {
        case "SUCCEEDED":
          return task;
        case "FAILED":
        case "CANCELLED":
          return task;
        case "PENDING":
        case "THROTTLED":
        case "RUNNING":
          break;
        default:
          logger16.warn("[Runway] Unknown status", { taskId, status: task.status });
      }
    }
    throw new Error(`Runway generation timed out after ${this.timeoutMs}ms (taskId: ${taskId})`);
  }
  sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
};
function createRunwayClient(config) {
  return new RunwayClient(config);
}

// src/server/sora-client.ts
import { Logger as Logger22 } from "@boosting/core";
var logger17 = Logger22.getLogger("SoraClient");
var SORA_PRICING2 = {
  "sora-2": 0.1,
  // $0.10 per second
  "sora-2-pro": {
    standard: 0.3,
    // up to 720p
    high_res: 0.5
    // above 720p (like 1080p or 1792)
  }
};
var SORA_API_BASE = "https://api.openai.com/v1";
var SoraClient = class {
  apiKey;
  baseUrl;
  timeoutMs;
  constructor(config) {
    if (!config.apiKey) {
      throw new Error("SoraClient: apiKey (OPENAI_API_KEY) is required");
    }
    this.apiKey = config.apiKey;
    this.baseUrl = config.baseUrl ?? SORA_API_BASE;
    this.timeoutMs = config.timeoutMs ?? 3e5;
  }
  // ============================================
  // PUBLIC API
  // ============================================
  /**
   * Generate video from text only (text-to-video).
   */
  async textToVideo(request) {
    const startTime = Date.now();
    const model = request.model ?? "sora-2";
    const durationSeconds = request.duration ?? 4;
    const size = request.size ?? "1280x720";
    try {
      logger17.info("[Sora] Starting text-to-video generation", {
        model,
        durationSeconds,
        size
      });
      const validSeconds = [4, 8, 12].includes(durationSeconds) ? String(durationSeconds) : "4";
      const body = {
        model,
        prompt: request.prompt,
        size,
        seconds: validSeconds
      };
      const job = await this.createJob(body);
      const completedJob = await this.waitForJob(job.id);
      if (completedJob.status !== "completed") {
        throw new Error(
          completedJob.error?.message || `Task failed with status: ${completedJob.status}`
        );
      }
      const estimatedCost = this.estimateCost(durationSeconds, size, model);
      const videoUrl = `${this.baseUrl}/videos/${job.id}/content`;
      return {
        success: true,
        videoUrl,
        jobId: job.id,
        durationSeconds,
        estimatedCost,
        model,
        generationTimeMs: Date.now() - startTime
      };
    } catch (error) {
      logger17.error("[Sora] Text-to-video generation failed", error);
      return {
        success: false,
        model,
        error: error instanceof Error ? error.message : String(error),
        generationTimeMs: Date.now() - startTime
      };
    }
  }
  /**
   * Estimate cost based on duration, dimensions, and model
   */
  estimateCost(seconds, size, model) {
    if (model === "sora-2") {
      return +(seconds * SORA_PRICING2["sora-2"]).toFixed(4);
    }
    const isHighRes = ["1080x1920", "1920x1080", "1024x1792", "1792x1024"].includes(size);
    const costPerSec = isHighRes ? SORA_PRICING2["sora-2-pro"].high_res : SORA_PRICING2["sora-2-pro"].standard;
    return +(seconds * costPerSec).toFixed(4);
  }
  // ============================================
  // INTERNAL
  // ============================================
  getHeaders() {
    return {
      "Authorization": `Bearer ${this.apiKey}`,
      "Content-Type": "application/json"
    };
  }
  /**
   * Create a new generation job.
   */
  async createJob(body) {
    const url = `${this.baseUrl}/videos`;
    const response = await fetch(url, {
      method: "POST",
      headers: this.getHeaders(),
      body: JSON.stringify(body)
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Sora job creation failed (${response.status}): ${errorText}`);
    }
    const data = await response.json();
    logger17.info("[Sora] Job created", { jobId: data.id });
    return data;
  }
  /**
   * Get job details
   */
  async getJobStatus(jobId) {
    const url = `${this.baseUrl}/videos/${jobId}`;
    const response = await fetch(url, {
      method: "GET",
      headers: this.getHeaders()
    });
    if (!response.ok) {
      throw new Error(`Sora job status fetch failed (${response.status})`);
    }
    return await response.json();
  }
  /**
   * Poll for job completion
   */
  async waitForJob(jobId) {
    const startTime = Date.now();
    const pollInterval = 1e4;
    while (Date.now() - startTime < this.timeoutMs) {
      await this.sleep(pollInterval);
      const job = await this.getJobStatus(jobId);
      logger17.debug("[Sora] Progress", { jobId, status: job.status });
      switch (job.status) {
        case "completed":
          return job;
        case "failed":
        case "cancelled":
          return job;
        case "queued":
        case "in_progress":
          break;
        default:
          logger17.warn("[Sora] Unknown status", { jobId, status: job.status });
      }
    }
    throw new Error(`Sora generation timed out after ${this.timeoutMs}ms (jobId: ${jobId})`);
  }
  /**
   * Download the final video binary.
   * Use this if you want to buffer directly instead of exposing the raw API URL.
   */
  async downloadVideoContent(jobId) {
    const url = `${this.baseUrl}/videos/${jobId}/content`;
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Authorization": `Bearer ${this.apiKey}`
      }
    });
    if (!response.ok) {
      throw new Error(`Sora video content fetch failed (${response.status})`);
    }
    return await response.arrayBuffer();
  }
  sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
};
function createSoraClient(config) {
  return new SoraClient(config);
}

// src/server/video-generation.types.ts
var VEO_PRICING = {
  "veo-3.1-generate-preview": 0.3,
  "veo-3.0-generate-001": 0.25,
  "veo-3.0-fast-generate-001": 0.1
};
var VIDEO_MODEL_PRICING = {
  // Google Veo
  "veo-3.1-generate-preview": 0.3,
  "veo-3.0-generate-001": 0.25,
  "veo-3.0-fast-generate-001": 0.1,
  "veo-3-generate": 0.25,
  "veo-3-fast": 0.1,
  // OpenAI Sora — Alta calidad cinemática
  "sora-2": 0.1,
  // Standard ($0.10/s)
  "sora-2-pro": 0.3,
  // High quality, ≤720p
  "sora-2-pro-highres": 0.5,
  // High quality, >720p (1080p, 1792)
  // Kling (Kuaishou) — Fidelidad de producto
  "kling-3.0-pro": 0.15,
  // Mejor relación calidad/precio para producto
  "kling-2.0": 0.08,
  // Runway — Drafts rápidos y alta fidelidad I2V
  "runway-gen4-turbo": 0.05,
  // El más barato, mejor fidelidad I2V
  "runway-gen4": 0.1,
  // Seedance (ByteDance) — Futuro
  "seedance-2.0": 0.12
  // Estimado, API pendiente
};
var TTS_PRICING = {
  google: 16e-6,
  // Chirp
  elevenlabs: 3e-4
};
function estimateSceneCost(scene) {
  if (scene.visualType !== "ai_video") {
    return 0;
  }
  const model = scene.veoConfig?.model;
  if (!model) return 0;
  const pricePerSecond = VIDEO_MODEL_PRICING[model] ?? VEO_PRICING[model] ?? 0.25;
  return scene.content.duration * pricePerSecond;
}
function estimateProjectVideoCost(scenes) {
  return scenes.reduce((total, scene) => total + estimateSceneCost(scene), 0);
}
function estimateVideoCostByModel(model, durationSeconds) {
  const pricePerSecond = VIDEO_MODEL_PRICING[model] ?? 0.25;
  return +(durationSeconds * pricePerSecond).toFixed(4);
}

// src/server/video-generation.ts
var VEO_LOCATION = "us-central1";
var DEFAULT_PROJECT_ID2 = "eva-platform-prod";
var VEO_MODELS3 = {
  PRIMARY: "veo-3.1-generate-preview",
  FALLBACK_1: "veo-3.0-generate-001",
  FALLBACK_2: "veo-3.0-fast-generate-001"
};
var DURATION_SECONDS = {
  short: 4,
  medium: 6,
  long: 8
};
var VIDEO_STYLE_PROMPTS = {
  cinematic: "Cinematic look, dramatic lighting, professional color grading",
  corporate: "Clean professional corporate style, well-lit, trustworthy",
  documentary: "Documentary style, authentic, informative natural lighting",
  animated: "Smooth animated motion graphics, clean transitions",
  professional: "High-end professional quality, polished production value",
  dynamic: "Dynamic movement, engaging transitions, energetic pacing",
  minimal: "Minimalist aesthetic, clean design, subtle motion",
  industrial: "Industrial B2B, factory environment, manufacturing",
  tech: "High-tech futuristic, digital elements, modern technology"
};
var ANIMATION_PROMPTS = {
  pan: "Slow cinematic pan, revealing details smoothly",
  zoom: "Gradual dolly zoom from wide to detailed close-up",
  parallax: "Parallax motion with depth separation, 2.5D effect",
  morph: "Subtle morphing and organic movement within elements",
  natural: "Natural realistic motion, elements moving authentically"
};
async function generateSceneVideo(scene, branding, logger19) {
  const log = logger19 || console;
  if (scene.visualType !== "ai_video") {
    return {
      success: false,
      error: "Scene does not require AI video generation"
    };
  }
  log.info("[ai:generateSceneVideo] Starting", {
    sceneId: scene.id,
    sceneIndex: scene.index,
    duration: scene.content.duration
  });
  if (scene.referenceImage?.base64) {
    return generateVideoFromImage({
      referenceImage: {
        ...scene.referenceImage,
        base64: scene.referenceImage.base64
      },
      prompt: buildScenePrompt(scene, branding),
      animationType: scene.veoConfig?.animationType || "natural",
      duration: getDurationCategory(scene.content.duration)
    }, log);
  }
  return generateVideoFromText({
    prompt: buildScenePrompt(scene, branding),
    style: branding?.preferredVideoStyle || "professional",
    duration: getDurationCategory(scene.content.duration)
  }, log);
}
async function generateVideoFromText(config, logger19) {
  const log = logger19 || console;
  const projectId = config.projectId || DEFAULT_PROJECT_ID2;
  log.info("[ai:generateVideoFromText] Starting", {
    style: config.style,
    aspectRatio: config.aspectRatio,
    duration: config.duration
  });
  const stylePrompt = VIDEO_STYLE_PROMPTS[config.style || "professional"];
  const fullPrompt = buildFullPrompt(config.prompt, stylePrompt, config.negativePrompt);
  if (config.provider === "runway" || Object.values(RUNWAY_MODELS).includes(config.model)) {
    log.info("[ai:generateVideoFromText] Routing to Runway", { model: config.model });
    return executeRunwayTextToVideo(config, fullPrompt, log);
  }
  if (config.provider === "kling" || Object.values(KLING_MODELS).includes(config.model)) {
    log.info("[ai:generateVideoFromText] Routing to Kling", { model: config.model });
    return executeKlingTextToVideo(config, fullPrompt, log);
  }
  if (config.provider === "sora" || Object.values(SORA_MODELS).includes(config.model)) {
    log.info("[ai:generateVideoFromText] Routing to Sora", { model: config.model });
    return executeSoraTextToVideo(config, fullPrompt, log);
  }
  const requestedModel = config.model && Object.values(VEO_MODELS3).includes(config.model) ? config.model : VEO_MODELS3.PRIMARY;
  const models = requestedModel === VEO_MODELS3.PRIMARY ? [VEO_MODELS3.PRIMARY, VEO_MODELS3.FALLBACK_1, VEO_MODELS3.FALLBACK_2] : [requestedModel];
  for (let i = 0; i < models.length; i++) {
    const model = models[i];
    const isFallback = i > 0;
    try {
      log.info("[ai:generateVideoFromText] Trying model", { model, isFallback });
      const result = await executeVeoGeneration(
        projectId,
        model,
        { prompt: fullPrompt },
        config,
        log
      );
      if (result.success) {
        return { ...result, fallbackUsed: isFallback };
      }
      log.warn("[ai:generateVideoFromText] Model failed", {
        model,
        error: result.error
      });
    } catch (error) {
      log.warn("[ai:generateVideoFromText] Error", {
        model,
        error: error.message
      });
      if (i === models.length - 1) {
        return {
          success: false,
          error: `All models failed. Last: ${error.message}`,
          model
        };
      }
    }
  }
  return { success: false, error: "All video models failed", model: VEO_MODELS3.FALLBACK_2 };
}
async function generateVideoFromImage(config, logger19) {
  const log = logger19 || console;
  const projectId = config.projectId || DEFAULT_PROJECT_ID2;
  log.info("[ai:generateVideoFromImage] Starting", {
    animationType: config.animationType,
    aspectRatio: config.aspectRatio
  });
  const animPrompt = ANIMATION_PROMPTS[config.animationType || "natural"];
  const fullPrompt = `${config.prompt}. ${animPrompt}`;
  if (config.provider === "runway" || Object.values(RUNWAY_MODELS).includes(config.model)) {
    log.info("[ai:generateVideoFromImage] Routing to Runway", { model: config.model });
    return executeRunwayImageToVideo(config, fullPrompt, log);
  }
  if (config.provider === "kling" || Object.values(KLING_MODELS).includes(config.model)) {
    log.info("[ai:generateVideoFromImage] Routing to Kling", { model: config.model });
    return executeKlingImageToVideo(config, fullPrompt, log);
  }
  if (config.provider === "sora" || Object.values(SORA_MODELS).includes(config.model)) {
    log.info("[ai:generateVideoFromImage] Routing to Sora (text-to-video fallback, Sora I2V limited)", { model: config.model });
    return executeSoraTextToVideo(
      { ...config, prompt: config.prompt },
      fullPrompt,
      log
    );
  }
  const requestedModel = config.model && Object.values(VEO_MODELS3).includes(config.model) ? config.model : VEO_MODELS3.PRIMARY;
  const models = requestedModel === VEO_MODELS3.PRIMARY ? [VEO_MODELS3.PRIMARY, VEO_MODELS3.FALLBACK_1, VEO_MODELS3.FALLBACK_2] : [requestedModel];
  for (let i = 0; i < models.length; i++) {
    const model = models[i];
    const isFallback = i > 0;
    try {
      log.info("[ai:generateVideoFromImage] Trying model", { model, isFallback });
      const result = await executeVeoGeneration(
        projectId,
        model,
        {
          prompt: fullPrompt,
          image: {
            imageBytes: config.referenceImage.base64,
            mimeType: config.referenceImage.mimeType || "image/jpeg"
          }
        },
        config,
        log
      );
      if (result.success) {
        return { ...result, fallbackUsed: isFallback };
      }
    } catch (error) {
      log.warn("[ai:generateVideoFromImage] Error", {
        model,
        error: error.message
      });
      if (i === models.length - 1) {
        return {
          success: false,
          error: `All models failed. Last: ${error.message}`,
          model
        };
      }
    }
  }
  return { success: false, error: "All video models failed", model: VEO_MODELS3.FALLBACK_2 };
}
async function generateVideoQuick(prompt, style = "professional", logger19) {
  return generateVideoFromText({ prompt, style }, logger19);
}
async function executeVeoGeneration(projectId, model, source, config, log) {
  const { GoogleGenAI } = await import("@google/genai");
  const client = new GoogleGenAI({
    vertexai: true,
    project: projectId,
    location: VEO_LOCATION
  });
  const durationSeconds = DURATION_SECONDS[config.duration || "medium"] || 6;
  log.info("[ai:executeVeoGeneration] Calling API", {
    model,
    durationSeconds,
    aspectRatio: config.aspectRatio
  });
  const operation = await client.models.generateVideos({
    model,
    source,
    config: {
      numberOfVideos: config.numberOfVideos || 1,
      aspectRatio: config.aspectRatio || "16:9",
      durationSeconds,
      personGeneration: config.personGeneration || "dont_allow"
    }
  });
  log.info("[ai:executeVeoGeneration] Operation started", {
    operationName: operation.name
  });
  return await pollVeoOperation(client, operation, model, log);
}
async function pollVeoOperation(client, operation, model, log, maxWaitMs = 3e5) {
  const startTime = Date.now();
  let currentOp = operation;
  let pollCount = 0;
  while (!currentOp.done && Date.now() - startTime < maxWaitMs) {
    await new Promise((resolve) => setTimeout(resolve, 1e4));
    pollCount++;
    currentOp = await client.operations.getVideosOperation({
      operation: currentOp
    });
    log.info("[ai:pollVeoOperation] Polling", {
      done: currentOp.done,
      pollCount,
      elapsedMs: Date.now() - startTime
    });
  }
  if (!currentOp.done) {
    return {
      success: false,
      error: "Video generation timed out (5 min)",
      operationName: operation.name,
      model
    };
  }
  if (currentOp.error) {
    return {
      success: false,
      error: currentOp.error.message || JSON.stringify(currentOp.error),
      operationName: operation.name,
      model
    };
  }
  const generatedVideo = currentOp.response?.generatedVideos?.[0];
  if (!generatedVideo?.video?.uri) {
    return {
      success: false,
      error: "No video in response",
      operationName: operation.name,
      model
    };
  }
  log.info("[ai:pollVeoOperation] SUCCESS", {
    videoUri: generatedVideo.video.uri,
    durationSeconds: generatedVideo.video.durationSeconds,
    model
  });
  return {
    success: true,
    videoUri: generatedVideo.video.uri,
    mimeType: generatedVideo.video.mimeType || "video/mp4",
    durationSeconds: generatedVideo.video.durationSeconds,
    model,
    operationName: operation.name
  };
}
function buildScenePrompt(scene, branding) {
  const parts = [];
  parts.push(scene.content.visualDescription);
  if (scene.veoConfig?.prompt) {
    parts.push(scene.veoConfig.prompt);
  }
  if (branding?.preferredVideoStyle) {
    parts.push(VIDEO_STYLE_PROMPTS[branding.preferredVideoStyle]);
  }
  return parts.join(". ");
}
function buildFullPrompt(base, style, negative) {
  let prompt = `${base}. ${style}`;
  if (negative) {
    prompt += `. Avoid: ${negative}`;
  }
  return prompt;
}
function getDurationCategory(seconds) {
  if (seconds <= 4) return "short";
  if (seconds <= 6) return "medium";
  return "long";
}
function mapToRunwayRatio(ratio) {
  switch (ratio) {
    case "9:16":
      return "720:1280";
    case "1:1":
      return "1024:1024";
    case "16:9":
    default:
      return "1280:720";
  }
}
async function executeRunwayTextToVideo(config, fullPrompt, log) {
  if (!config.runwayKey) {
    return { success: false, error: "Runway API key not provided in config", model: config.model || RUNWAY_MODELS.GEN4_5 };
  }
  const client = createRunwayClient({ apiKey: config.runwayKey });
  const durationSeconds = DURATION_SECONDS[config.duration || "medium"] || 5;
  log.info("[ai:executeRunwayTextToVideo] Calling API", {
    model: config.model,
    durationSeconds
  });
  const result = await client.textToVideo({
    prompt: fullPrompt,
    model: config.model || "gen4.5",
    duration: durationSeconds >= 10 ? 10 : 5,
    ratio: mapToRunwayRatio(config.aspectRatio || "16:9")
  });
  if (!result.success || !result.videoUrl) {
    return {
      success: false,
      error: result.error || "Failed to generate Runway video",
      model: result.model
    };
  }
  return {
    success: true,
    videoUri: result.videoUrl,
    mimeType: "video/mp4",
    durationSeconds: result.durationSeconds || 5,
    model: result.model,
    operationName: result.taskId
  };
}
async function executeRunwayImageToVideo(config, fullPrompt, log) {
  if (!config.runwayKey) {
    return { success: false, error: "Runway API key not provided in config", model: config.model || RUNWAY_MODELS.GEN4_5 };
  }
  const client = createRunwayClient({ apiKey: config.runwayKey });
  const durationSeconds = DURATION_SECONDS[config.duration || "medium"] || 5;
  log.info("[ai:executeRunwayImageToVideo] Calling API", {
    model: config.model,
    durationSeconds
  });
  const prefix = config.referenceImage.mimeType === "image/png" ? "data:image/png;base64," : "data:image/jpeg;base64,";
  const imageUrl = config.referenceImage.base64.startsWith("http") || config.referenceImage.base64.startsWith("data:") ? config.referenceImage.base64 : `${prefix}${config.referenceImage.base64}`;
  const result = await client.imageToVideo({
    imageUrl,
    prompt: fullPrompt,
    model: config.model || "gen4.5",
    duration: durationSeconds >= 10 ? 10 : 5,
    ratio: mapToRunwayRatio(config.aspectRatio || "16:9")
  });
  if (!result.success || !result.videoUrl) {
    return {
      success: false,
      error: result.error || "Failed to generate Runway video",
      model: result.model
    };
  }
  return {
    success: true,
    videoUri: result.videoUrl,
    mimeType: "video/mp4",
    durationSeconds: result.durationSeconds || 5,
    model: result.model,
    operationName: result.taskId
  };
}
async function executeKlingTextToVideo(config, fullPrompt, log) {
  if (!config.falKey) {
    return { success: false, error: "Kling/Fal API key not provided in config", model: config.model || KLING_MODELS.KLING_3_PRO };
  }
  const client = createKlingClient({ apiKey: config.falKey });
  const durationSeconds = DURATION_SECONDS[config.duration || "medium"] || 5;
  log.info("[ai:executeKlingTextToVideo] Calling API", {
    model: config.model,
    durationSeconds
  });
  const result = await client.textToVideo({
    prompt: fullPrompt,
    duration: durationSeconds,
    aspectRatio: config.aspectRatio || "16:9"
  });
  if (!result.success || !result.video?.url) {
    return {
      success: false,
      error: result.error || "Failed to generate Kling video",
      model: result.model
    };
  }
  return {
    success: true,
    videoUri: result.video.url,
    mimeType: result.video.content_type || "video/mp4",
    durationSeconds: result.durationSeconds || durationSeconds,
    model: result.model,
    operationName: result.requestId
  };
}
async function executeKlingImageToVideo(config, fullPrompt, log) {
  if (!config.falKey) {
    return { success: false, error: "Kling/Fal API key not provided in config", model: config.model || KLING_MODELS.KLING_3_PRO };
  }
  const client = createKlingClient({ apiKey: config.falKey });
  const durationSeconds = DURATION_SECONDS[config.duration || "medium"] || 5;
  log.info("[ai:executeKlingImageToVideo] Calling API", {
    model: config.model,
    durationSeconds
  });
  const prefix = config.referenceImage.mimeType === "image/png" ? "data:image/png;base64," : "data:image/jpeg;base64,";
  const imageUrl = config.referenceImage.base64.startsWith("http") || config.referenceImage.base64.startsWith("data:") ? config.referenceImage.base64 : `${prefix}${config.referenceImage.base64}`;
  const result = await client.imageToVideo({
    imageUrl,
    prompt: fullPrompt,
    duration: durationSeconds,
    aspectRatio: config.aspectRatio || "16:9"
  });
  if (!result.success || !result.video?.url) {
    return {
      success: false,
      error: result.error || "Failed to generate Kling video",
      model: result.model
    };
  }
  return {
    success: true,
    videoUri: result.video.url,
    mimeType: result.video.content_type || "video/mp4",
    durationSeconds: result.durationSeconds || durationSeconds,
    model: result.model,
    operationName: result.requestId
  };
}
function mapToSoraSize(ratio, resolution = "720p") {
  const isPro = resolution === "1080p" || resolution === "4k";
  switch (ratio) {
    case "9:16":
      return isPro ? "1080x1920" : "720x1280";
    case "1:1":
      return "1080x1080";
    case "16:9":
    default:
      return isPro ? "1920x1080" : "1280x720";
  }
}
async function executeSoraTextToVideo(config, fullPrompt, log) {
  if (!config.soraKey) {
    return { success: false, error: "Sora/OpenAI API key not provided in config", model: config.model || SORA_MODELS.SORA_2_PRO };
  }
  const client = createSoraClient({ apiKey: config.soraKey });
  const durationSeconds = DURATION_SECONDS[config.duration || "medium"] || 8;
  log.info("[ai:executeSoraTextToVideo] Calling API", {
    model: config.model,
    durationSeconds
  });
  const result = await client.textToVideo({
    prompt: fullPrompt,
    model: config.model || "sora-2-pro",
    duration: durationSeconds,
    size: mapToSoraSize(config.aspectRatio || "16:9", config.resolution || "720p")
  });
  if (!result.success || !result.videoUrl) {
    return {
      success: false,
      error: result.error || "Failed to generate Sora video",
      model: result.model
    };
  }
  return {
    success: true,
    videoUri: result.videoUrl,
    mimeType: "video/mp4",
    durationSeconds: result.durationSeconds || durationSeconds,
    model: result.model,
    operationName: result.jobId
  };
}
var VIDEO_GENERATION_LOCATION = VEO_LOCATION;

// src/server/tts.ts
var DEFAULT_PROJECT_ID3 = "appmor-25e91";
var DEFAULT_VOICES = {
  "es-ES": "es-ES-Chirp3-HD-Charon",
  "en-US": "en-US-Chirp3-HD-Charon"
};
var DEFAULT_PRONUNCIATION_DICT = {
  "PoE": "P.o.E.",
  "PoE+": "P.o.E. plus",
  "Gbps": "gigabits per second",
  "Mbps": "megabits per second",
  "SFP": "S.F.P.",
  "SFP+": "S.F.P. plus",
  "VLAN": "V.LAN",
  "QoS": "Q.o.S.",
  "IoT": "I.o.T.",
  "IP": "I.P.",
  "CPU": "C.P.U.",
  "GPU": "G.P.U.",
  "API": "A.P.I.",
  "SaaS": "sas",
  "B2B": "B. to B.",
  "AI": "A.I.",
  "ML": "M.L.",
  "NAS": "nas",
  "RAID": "raid",
  "USB": "U.S.B.",
  "HDMI": "H.D.M.I.",
  "LED": "led",
  "LCD": "L.C.D.",
  "UPS": "U.P.S.",
  "VPN": "V.P.N."
};
function applyPronunciationDictionary(text, customDictionary) {
  const dictionary = { ...DEFAULT_PRONUNCIATION_DICT, ...customDictionary };
  let result = text;
  const terms = Object.keys(dictionary).sort((a, b) => b.length - a.length);
  for (const term of terms) {
    const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`\\b${escaped}\\b`, "g");
    result = result.replace(regex, dictionary[term]);
  }
  return result;
}
async function generateNarration(config, logger19) {
  const log = logger19 || console;
  const provider = config.provider || "google";
  log.info("[ai:generateNarration] Starting", {
    textLength: config.text.length,
    provider,
    languageCode: config.languageCode
  });
  if (!config.text || config.text.trim().length === 0) {
    return { success: false, error: "Text is empty" };
  }
  const processedConfig = { ...config };
  if (config.pronunciationDictionary !== void 0) {
    processedConfig.text = applyPronunciationDictionary(config.text, config.pronunciationDictionary);
    log.info("[ai:generateNarration] Applied pronunciation dictionary", {
      originalLength: config.text.length,
      processedLength: processedConfig.text.length
    });
  }
  if (provider === "google") {
    return generateWithGoogle(processedConfig, log);
  }
  if (provider === "elevenlabs") {
    return generateWithElevenLabs(processedConfig, log);
  }
  return { success: false, error: `Unknown provider: ${provider}` };
}
async function generateNarrationBatch(batchConfig, logger19) {
  const log = logger19 || console;
  const results = /* @__PURE__ */ new Map();
  let totalCost = 0;
  let totalDuration = 0;
  log.info("[ai:generateNarrationBatch] Starting", {
    count: batchConfig.texts.length
  });
  for (const item of batchConfig.texts) {
    const result = await generateNarration({
      text: item.text,
      ...batchConfig.config
    }, log);
    results.set(item.id, result);
    if (result.success) {
      totalCost += result.cost || 0;
      totalDuration += result.durationSeconds || 0;
    }
  }
  log.info("[ai:generateNarrationBatch] Completed", {
    successCount: Array.from(results.values()).filter((r) => r.success).length,
    totalCost,
    totalDuration
  });
  return {
    success: Array.from(results.values()).every((r) => r.success),
    results,
    totalCost,
    totalDurationSeconds: totalDuration
  };
}
function estimateTTSCost(text, provider = "google") {
  const chars = text.length;
  if (provider === "google") {
    return chars * 16e-6;
  }
  if (provider === "elevenlabs") {
    return chars * 18e-5;
  }
  return 0;
}
function estimateAudioDuration(text, speakingRate = 1) {
  const words = text.split(/\s+/).length;
  const baseDuration = words / 2.5;
  return baseDuration / speakingRate;
}
async function generateWithGoogle(config, log) {
  const projectId = config.projectId || DEFAULT_PROJECT_ID3;
  const languageCode = config.languageCode || "es-ES";
  const voiceId = config.voiceId || DEFAULT_VOICES[languageCode] || "es-ES-Chirp3-HD-Charon";
  log.info("[ai:generateWithGoogle] Calling API", {
    voiceId,
    languageCode,
    textLength: config.text.length
  });
  try {
    const textToSpeech = await import("@google-cloud/text-to-speech");
    const client = new textToSpeech.TextToSpeechClient({ projectId });
    const audioEncoding = config.audioFormat === "wav" ? "LINEAR16" : config.audioFormat === "ogg" ? "OGG_OPUS" : "MP3";
    const [response] = await client.synthesizeSpeech({
      input: { text: config.text },
      voice: {
        languageCode,
        name: voiceId
      },
      audioConfig: {
        audioEncoding,
        speakingRate: config.speakingRate || 1,
        pitch: config.pitch || 0
      }
    });
    if (!response.audioContent) {
      return { success: false, error: "No audio content in response" };
    }
    const audioBase64 = typeof response.audioContent === "string" ? response.audioContent : Buffer.from(response.audioContent).toString("base64");
    const durationSeconds = estimateAudioDuration(config.text, config.speakingRate || 1);
    const cost = estimateTTSCost(config.text, "google");
    log.info("[ai:generateWithGoogle] SUCCESS", {
      durationSeconds,
      cost
    });
    return {
      success: true,
      audioBase64,
      durationSeconds,
      mimeType: audioEncoding === "MP3" ? "audio/mpeg" : audioEncoding === "LINEAR16" ? "audio/wav" : "audio/ogg",
      cost,
      provider: "google"
    };
  } catch (error) {
    log.error("[ai:generateWithGoogle] Error", {
      error: error.message,
      code: error.code,
      details: error.details,
      stack: error.stack?.substring?.(0, 500)
    });
    return {
      success: false,
      error: `Google TTS Error: ${error.message || "Unknown error"}`,
      provider: "google"
    };
  }
}
async function generateWithElevenLabs(config, log) {
  log.info("[ai:generateWithElevenLabs] Calling API", {
    voiceId: config.voiceId,
    textLength: config.text.length
  });
  try {
    const apiKey = process.env.ELEVENLABS_API_KEY;
    if (!apiKey) {
      return { success: false, error: "ELEVENLABS_API_KEY not configured" };
    }
    const voiceId = config.voiceId || "onwK4e9ZLuTAKqWW03F9";
    const voiceSettings = {
      stability: config.elevenLabsSettings?.stability ?? 0.5,
      similarity_boost: config.elevenLabsSettings?.similarity_boost ?? 0.75,
      ...config.elevenLabsSettings?.style !== void 0 && { style: config.elevenLabsSettings.style },
      ...config.elevenLabsSettings?.use_speaker_boost !== void 0 && { use_speaker_boost: config.elevenLabsSettings.use_speaker_boost }
    };
    const response = await fetch(`https://api.elevenlabs.io/v1/text-to-speech/${voiceId}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "xi-api-key": apiKey
      },
      body: JSON.stringify({
        text: config.text,
        model_id: "eleven_multilingual_v2",
        voice_settings: voiceSettings
      })
    });
    if (!response.ok) {
      const errorText = await response.text();
      return { success: false, error: `ElevenLabs API error: ${errorText}` };
    }
    const audioBuffer = await response.arrayBuffer();
    const audioBase64 = Buffer.from(audioBuffer).toString("base64");
    const durationSeconds = estimateAudioDuration(config.text, config.speakingRate || 1);
    const cost = estimateTTSCost(config.text, "elevenlabs");
    log.info("[ai:generateWithElevenLabs] SUCCESS", {
      durationSeconds,
      cost
    });
    return {
      success: true,
      audioBase64,
      durationSeconds,
      mimeType: "audio/mpeg",
      cost,
      provider: "elevenlabs"
    };
  } catch (error) {
    log.error("[ai:generateWithElevenLabs] Error", {
      error: error.message
    });
    return {
      success: false,
      error: error.message,
      provider: "elevenlabs"
    };
  }
}
function chunkTextBySentence(text, maxChunkChars = 500) {
  const sentences = text.match(/[^.!?]+[.!?]+/g) || [text];
  const chunks = [];
  let currentChunk = "";
  for (const sentence of sentences) {
    const trimmed = sentence.trim();
    if (!trimmed) continue;
    if (currentChunk.length + trimmed.length > maxChunkChars && currentChunk) {
      chunks.push(currentChunk.trim());
      currentChunk = trimmed;
    } else {
      currentChunk += (currentChunk ? " " : "") + trimmed;
    }
  }
  if (currentChunk.trim()) {
    chunks.push(currentChunk.trim());
  }
  return chunks;
}
function generateChunkId() {
  return `chunk_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}
function convertAlignmentToWords(text, alignment) {
  if (alignment.words && alignment.words.length > 0) {
    return alignment.words.map((w) => ({
      word: w.word,
      startTime: w.start_time_ms / 1e3,
      endTime: w.end_time_ms / 1e3
    }));
  }
  const words = [];
  const chars = alignment.characters;
  if (!chars || chars.length === 0) {
    const wordList = text.split(/\s+/);
    const avgDuration = 0.3;
    let currentTime = 0;
    for (const word of wordList) {
      words.push({
        word,
        startTime: currentTime,
        endTime: currentTime + avgDuration
      });
      currentTime += avgDuration + 0.05;
    }
    return words;
  }
  let currentWord = "";
  let wordStartTime = 0;
  let wordEndTime = 0;
  for (let i = 0; i < chars.length; i++) {
    const char = chars[i];
    const isWhitespace = /\s/.test(char.character);
    if (isWhitespace) {
      if (currentWord) {
        words.push({
          word: currentWord,
          startTime: wordStartTime / 1e3,
          endTime: wordEndTime / 1e3
        });
        currentWord = "";
      }
    } else {
      if (!currentWord) {
        wordStartTime = char.start_time_ms;
      }
      currentWord += char.character;
      wordEndTime = char.end_time_ms;
    }
  }
  if (currentWord) {
    words.push({
      word: currentWord,
      startTime: wordStartTime / 1e3,
      endTime: wordEndTime / 1e3
    });
  }
  return words;
}
async function generateNarrationWithTimestamps(config, logger19) {
  const log = logger19 || console;
  log.info("[ai:generateNarrationWithTimestamps] Starting", {
    textLength: config.text.length,
    chunkBySentence: config.chunkBySentence
  });
  if (!config.text || config.text.trim().length === 0) {
    return { success: false, error: "Text is empty" };
  }
  const apiKey = process.env.ELEVENLABS_API_KEY;
  if (!apiKey) {
    log.warn("[ai:generateNarrationWithTimestamps] No ElevenLabs key, falling back to Google");
    const basicResult = await generateNarration({ ...config, provider: "google" }, log);
    return {
      ...basicResult,
      timedNarration: basicResult.success ? createBasicTimedNarration(config.text, config) : void 0
    };
  }
  const textChunks = config.chunkBySentence ? chunkTextBySentence(config.text, config.maxChunkChars || 500) : [config.text];
  const chunks = [];
  let totalDuration = 0;
  let totalCost = 0;
  const chunkResults = /* @__PURE__ */ new Map();
  for (let i = 0; i < textChunks.length; i++) {
    const chunkText = textChunks[i];
    const chunkId = generateChunkId();
    try {
      const result = await generateChunkWithTimestamps(
        chunkText,
        config,
        apiKey,
        log
      );
      if (!result.success) {
        log.error("[ai:generateNarrationWithTimestamps] Chunk failed", {
          chunkIndex: i,
          error: result.error
        });
        continue;
      }
      const words = result.alignment ? convertAlignmentToWords(chunkText, result.alignment) : [];
      const chunkDuration = result.durationSeconds || estimateAudioDuration(chunkText);
      chunks.push({
        id: chunkId,
        text: chunkText,
        words,
        startTime: totalDuration,
        endTime: totalDuration + chunkDuration,
        audioUrl: result.audioUrl,
        audioDuration: chunkDuration
      });
      chunkResults.set(chunkId, result);
      totalDuration += chunkDuration;
      totalCost += result.cost || 0;
    } catch (error) {
      log.error("[ai:generateNarrationWithTimestamps] Chunk generation error", {
        chunkIndex: i,
        error
      });
    }
  }
  if (chunks.length === 0) {
    return { success: false, error: "No chunks were generated successfully" };
  }
  const timedNarration = {
    id: `narration_${Date.now()}`,
    chunks,
    totalDuration,
    voice: config.voiceId || "default",
    language: config.languageCode || "es-ES",
    provider: "elevenlabs",
    createdAt: (/* @__PURE__ */ new Date()).toISOString()
  };
  log.info("[ai:generateNarrationWithTimestamps] Completed", {
    chunksGenerated: chunks.length,
    totalDuration,
    totalCost
  });
  return {
    success: true,
    timedNarration,
    chunkResults,
    durationSeconds: totalDuration,
    cost: totalCost,
    provider: "elevenlabs"
  };
}
async function generateChunkWithTimestamps(text, config, apiKey, log) {
  const voiceId = config.voiceId || "onwK4e9ZLuTAKqWW03F9";
  const processedText = config.pronunciationDictionary ? applyPronunciationDictionary(text, config.pronunciationDictionary) : text;
  const voiceSettings = {
    stability: config.elevenLabsSettings?.stability ?? 0.5,
    similarity_boost: config.elevenLabsSettings?.similarity_boost ?? 0.75,
    ...config.elevenLabsSettings?.style !== void 0 && { style: config.elevenLabsSettings.style },
    ...config.elevenLabsSettings?.use_speaker_boost !== void 0 && {
      use_speaker_boost: config.elevenLabsSettings.use_speaker_boost
    }
  };
  try {
    const response = await fetch(
      `https://api.elevenlabs.io/v1/text-to-speech/${voiceId}/with-timestamps`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "xi-api-key": apiKey
        },
        body: JSON.stringify({
          text: processedText,
          model_id: "eleven_multilingual_v2",
          voice_settings: voiceSettings
        })
      }
    );
    if (!response.ok) {
      const errorText = await response.text();
      return { success: false, error: `ElevenLabs API error: ${errorText}` };
    }
    const data = await response.json();
    const audioBase64 = data.audio_base64;
    const alignment = data.alignment;
    let durationSeconds = 0;
    if (alignment?.characters?.length > 0) {
      const lastChar = alignment.characters[alignment.characters.length - 1];
      durationSeconds = lastChar.end_time_ms / 1e3;
    } else {
      durationSeconds = estimateAudioDuration(text);
    }
    const cost = estimateTTSCost(text, "elevenlabs");
    return {
      success: true,
      audioBase64,
      durationSeconds,
      mimeType: "audio/mpeg",
      cost,
      provider: "elevenlabs",
      alignment
    };
  } catch (error) {
    log.error("[ai:generateChunkWithTimestamps] Error", { error: error.message });
    return { success: false, error: error.message };
  }
}
function createBasicTimedNarration(text, config) {
  const chunks = config.chunkBySentence ? chunkTextBySentence(text, config.maxChunkChars || 500) : [text];
  let currentTime = 0;
  const timedChunks = chunks.map((chunkText) => {
    const duration = estimateAudioDuration(chunkText);
    const wordList = chunkText.split(/\s+/);
    const wordDuration = duration / wordList.length;
    let wordTime = 0;
    const words = wordList.map((word) => {
      const tw = {
        word,
        startTime: currentTime + wordTime,
        endTime: currentTime + wordTime + wordDuration
      };
      wordTime += wordDuration;
      return tw;
    });
    const chunk = {
      id: generateChunkId(),
      text: chunkText,
      words,
      startTime: currentTime,
      endTime: currentTime + duration,
      audioDuration: duration
    };
    currentTime += duration;
    return chunk;
  });
  return {
    id: `narration_${Date.now()}`,
    chunks: timedChunks,
    totalDuration: currentTime,
    voice: config.voiceId || "default",
    language: config.languageCode || "es-ES",
    provider: "google",
    createdAt: (/* @__PURE__ */ new Date()).toISOString()
  };
}
async function applyNarrationEdits(narration, edits, config, inpaintingConfig, logger19) {
  const log = logger19 || console;
  const startTime = Date.now();
  log.info("[ai:applyNarrationEdits] Starting", {
    narrationId: narration.id,
    editCount: edits.length
  });
  const regeneratedChunks = [];
  const deletedChunks = [];
  let costIncurred = 0;
  let updatedChunks = [...narration.chunks];
  for (const edit of edits) {
    switch (edit.type) {
      case "replace": {
        const chunkIndex = updatedChunks.findIndex((c) => c.id === edit.chunkId);
        if (chunkIndex === -1) continue;
        const prefixChunk = chunkIndex > 0 ? updatedChunks[chunkIndex - 1] : void 0;
        const suffixChunk = chunkIndex < updatedChunks.length - 1 ? updatedChunks[chunkIndex + 1] : void 0;
        log.info("[ai:applyNarrationEdits] Regenerating chunk", {
          chunkId: edit.chunkId,
          newTextLength: edit.newText.length,
          hasPrefix: !!prefixChunk,
          hasSuffix: !!suffixChunk
        });
        const result = await generateNarrationWithTimestamps({
          ...config,
          text: edit.newText,
          chunkBySentence: false
          // Single chunk
        }, log);
        if (result.success && result.timedNarration?.chunks[0]) {
          const newChunk = result.timedNarration.chunks[0];
          newChunk.id = edit.chunkId;
          newChunk.isModified = true;
          updatedChunks[chunkIndex] = newChunk;
          regeneratedChunks.push(edit.chunkId);
          costIncurred += result.cost || 0;
        }
        break;
      }
      case "delete": {
        const deleteIndex = updatedChunks.findIndex((c) => c.id === edit.chunkId);
        if (deleteIndex !== -1) {
          deletedChunks.push(edit.chunkId);
          updatedChunks = updatedChunks.filter((c) => c.id !== edit.chunkId);
        }
        break;
      }
      case "insert": {
        const insertAfterIndex = edit.afterChunkId ? updatedChunks.findIndex((c) => c.id === edit.afterChunkId) : -1;
        const result = await generateNarrationWithTimestamps({
          ...config,
          text: edit.text,
          chunkBySentence: false
        }, log);
        if (result.success && result.timedNarration?.chunks[0]) {
          const newChunk = result.timedNarration.chunks[0];
          newChunk.isModified = true;
          if (insertAfterIndex === -1) {
            updatedChunks.unshift(newChunk);
          } else {
            updatedChunks.splice(insertAfterIndex + 1, 0, newChunk);
          }
          regeneratedChunks.push(newChunk.id);
          costIncurred += result.cost || 0;
        }
        break;
      }
      case "split": {
        const splitIndex = updatedChunks.findIndex((c) => c.id === edit.chunkId);
        if (splitIndex === -1) continue;
        const chunk = updatedChunks[splitIndex];
        const wordIndex = edit.splitAtWord;
        if (wordIndex >= 0 && wordIndex < chunk.words.length - 1) {
          const firstHalfWords = chunk.words.slice(0, wordIndex);
          const secondHalfWords = chunk.words.slice(wordIndex);
          const firstText = firstHalfWords.map((w) => w.word).join(" ");
          const secondText = secondHalfWords.map((w) => w.word).join(" ");
          const result1 = await generateNarrationWithTimestamps({
            ...config,
            text: firstText,
            chunkBySentence: false
          }, log);
          const result2 = await generateNarrationWithTimestamps({
            ...config,
            text: secondText,
            chunkBySentence: false
          }, log);
          if (result1.success && result2.success && result1.timedNarration?.chunks[0] && result2.timedNarration?.chunks[0]) {
            const chunk1 = result1.timedNarration.chunks[0];
            const chunk2 = result2.timedNarration.chunks[0];
            chunk1.id = edit.chunkId;
            chunk1.isModified = true;
            chunk2.isModified = true;
            updatedChunks.splice(splitIndex, 1, chunk1, chunk2);
            regeneratedChunks.push(chunk1.id, chunk2.id);
            costIncurred += (result1.cost || 0) + (result2.cost || 0);
          }
        }
        break;
      }
    }
  }
  let currentTime = 0;
  for (const chunk of updatedChunks) {
    const duration = chunk.audioDuration;
    chunk.startTime = currentTime;
    chunk.endTime = currentTime + duration;
    const offset = chunk.startTime - chunk.words[0]?.startTime || 0;
    if (offset !== 0) {
      for (const word of chunk.words) {
        word.startTime += offset;
        word.endTime += offset;
      }
    }
    currentTime += duration;
  }
  const updatedNarration = {
    ...narration,
    chunks: updatedChunks,
    totalDuration: currentTime,
    updatedAt: (/* @__PURE__ */ new Date()).toISOString()
  };
  log.info("[ai:applyNarrationEdits] Completed", {
    regeneratedCount: regeneratedChunks.length,
    deletedCount: deletedChunks.length,
    newDuration: currentTime,
    costIncurred,
    processingTimeMs: Date.now() - startTime
  });
  return {
    narration: updatedNarration,
    regeneratedChunks,
    deletedChunks,
    newTotalDuration: currentTime,
    costIncurred,
    processingTimeMs: Date.now() - startTime
  };
}

// src/server/composition.ts
var DEFAULT_TRANSITION_DURATION = 500;
var DEFAULT_FPS = 30;
async function composeVideo(params, logger19) {
  const log = logger19 || console;
  const jobId = `comp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  log.info("[ai:composeVideo] Starting composition", {
    jobId,
    projectId: params.projectId,
    scenesCount: params.scenes.length,
    audioTracksCount: params.audioTracks.length
  });
  try {
    const validation = validateCompositionParams(params);
    if (!validation.valid) {
      return { success: false, error: validation.error, jobId };
    }
    const totalDuration = calculateTotalDuration(params);
    const ffmpegConfig = generateFFmpegConfig(params);
    log.info("[ai:composeVideo] FFmpeg config generated", {
      jobId,
      totalDuration,
      commandLength: ffmpegConfig.command.length
    });
    const result = await executeComposition(ffmpegConfig, params, log);
    if (result.success) {
      log.info("[ai:composeVideo] Composition completed", {
        jobId,
        videoUrl: result.videoUrl,
        durationSeconds: result.durationSeconds
      });
    }
    return { ...result, jobId };
  } catch (error) {
    log.error("[ai:composeVideo] Error", { jobId, error: error.message });
    return { success: false, error: error.message, jobId };
  }
}
async function generateSlideshowVideo(params, logger19) {
  const log = logger19 || console;
  const hasPerSceneAudio = params.images.some((img) => img.audioUrl);
  const hasSingleNarration = !!params.narrationUrl;
  log.info("[ai:generateSlideshowVideo] Starting", {
    imagesCount: params.images.length,
    hasMusic: !!params.musicUrl,
    audioMode: hasPerSceneAudio ? "per-scene" : hasSingleNarration ? "single-narration" : "no-audio",
    scenesWithAudio: params.images.filter((i) => i.audioUrl).length
  });
  const scenes = params.images.map((img, idx) => ({
    id: `slide_${idx}`,
    index: idx,
    type: "image",
    imageUrl: img.url,
    duration: img.duration,
    transition: "dissolve",
    transitionDuration: DEFAULT_TRANSITION_DURATION,
    imageAnimation: img.animation || "ken_burns",
    // Include per-scene audio URL if available
    audioUrl: img.audioUrl
  }));
  const audioTracks = [];
  if (hasPerSceneAudio) {
    params.images.forEach((img, idx) => {
      if (img.audioUrl) {
        audioTracks.push({
          type: "narration",
          url: img.audioUrl,
          volume: 100,
          // Timing info for the composer to sync correctly
          sceneIndex: idx,
          startTime: params.images.slice(0, idx).reduce((sum, i) => sum + i.duration, 0),
          duration: img.duration
        });
      }
    });
  } else if (hasSingleNarration) {
    audioTracks.push({ type: "narration", url: params.narrationUrl, volume: 100 });
  }
  if (params.musicUrl) {
    audioTracks.push({
      type: "music",
      url: params.musicUrl,
      volume: params.musicVolume || 20,
      fadeOut: true
    });
  }
  return composeVideo({
    projectId: params.projectId,
    scenes,
    audioTracks,
    branding: params.branding,
    export: params.export
  }, log);
}
function estimateCompositionCost(params) {
  const pricing = {
    base: 0.02,
    perMinute: 0.01,
    resolution: { "720p": 1, "1080p": 1.5, "4k": 3 }
  };
  const minutes = params.durationSeconds / 60;
  const resMultiplier = pricing.resolution[params.resolution];
  return pricing.base + minutes * pricing.perMinute * resMultiplier;
}
function validateCompositionParams(params) {
  if (!params.scenes || params.scenes.length === 0) {
    return { valid: false, error: "No scenes provided" };
  }
  for (const scene of params.scenes) {
    if (scene.type === "video" && !scene.videoUrl) {
      return { valid: false, error: `Scene ${scene.id}: video URL required` };
    }
    if (scene.type === "image" && !scene.imageUrl) {
      return { valid: false, error: `Scene ${scene.id}: image URL required` };
    }
    if (scene.duration <= 0) {
      return { valid: false, error: `Scene ${scene.id}: invalid duration` };
    }
  }
  return { valid: true };
}
function calculateTotalDuration(params) {
  let duration = 0;
  if (params.intro) {
    duration += params.intro.duration;
  }
  for (const scene of params.scenes) {
    duration += scene.duration;
  }
  if (params.outro) {
    duration += params.outro.duration;
  }
  return duration;
}
function generateFFmpegConfig(params) {
  const inputs = [];
  const filters = [];
  const fps = params.export.fps || DEFAULT_FPS;
  const resolutions = {
    "720p": "1280:720",
    "1080p": "1920:1080",
    "4k": "3840:2160"
  };
  const scale = resolutions[params.export.resolution];
  params.scenes.forEach((scene, idx) => {
    if (scene.type === "video" && scene.videoUrl) {
      inputs.push(`-i "${scene.videoUrl}"`);
    } else if (scene.imageUrl) {
      inputs.push(`-loop 1 -t ${scene.duration} -i "${scene.imageUrl}"`);
    }
    filters.push(`[${idx}:v]scale=${scale}:force_original_aspect_ratio=decrease,pad=${scale}:(ow-iw)/2:(oh-ih)/2,setsar=1[v${idx}]`);
  });
  params.audioTracks.forEach((track, idx) => {
    inputs.push(`-i "${track.url}"`);
  });
  const videoConcat = params.scenes.map((_, idx) => `[v${idx}]`).join("");
  filters.push(`${videoConcat}concat=n=${params.scenes.length}:v=1:a=0[outv]`);
  const outputPath = `/tmp/${params.projectId}_output.${params.export.format}`;
  const command = [
    "ffmpeg -y",
    ...inputs,
    `-filter_complex "${filters.join(";")}"`,
    '-map "[outv]"',
    `-r ${fps}`,
    `-c:v libx264 -preset fast`,
    outputPath
  ].join(" ");
  return { command, inputs, filters, outputPath };
}
var VIDEO_COMPOSER_URL = process.env.VIDEO_COMPOSER_URL || "https://video-composer-477867629958.europe-west1.run.app";
async function executeComposition(ffmpegConfig, params, log) {
  const totalDuration = calculateTotalDuration(params);
  if (VIDEO_COMPOSER_URL.includes("XXXXX")) {
    log.warn("[ai:executeComposition] VIDEO_COMPOSER_URL not configured, using placeholder");
    return createPlaceholderResult(params, totalDuration);
  }
  log.info("[ai:executeComposition] Calling Cloud Run service", {
    url: VIDEO_COMPOSER_URL,
    projectId: params.projectId,
    scenesCount: params.scenes.length
  });
  try {
    const response = await fetch(`${VIDEO_COMPOSER_URL}/compose`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        projectId: params.projectId,
        scenes: params.scenes.map((scene) => ({
          id: scene.id,
          index: scene.index,
          type: scene.type,
          imageUrl: scene.imageUrl,
          videoUrl: scene.videoUrl,
          duration: scene.duration,
          transition: scene.transition || "dissolve",
          transitionDuration: scene.transitionDuration || 500
        })),
        audioTracks: params.audioTracks.map((track) => ({
          type: track.type,
          url: track.url,
          volume: track.volume,
          fadeIn: track.fadeIn,
          fadeOut: track.fadeOut
        })),
        export: params.export,
        outputBucket: params.outputBucket || "eva-platform-prod.firebasestorage.app"
      })
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Cloud Run error: ${response.status} - ${errorText}`);
    }
    const result = await response.json();
    if (!result.success) {
      throw new Error(result.error || "Unknown composition error");
    }
    const cost = estimateCompositionCost({
      durationSeconds: result.durationSeconds || totalDuration,
      resolution: params.export.resolution
    });
    log.info("[ai:executeComposition] Composition complete", {
      videoUrl: result.videoUrl,
      durationSeconds: result.durationSeconds
    });
    return {
      success: true,
      videoUrl: result.videoUrl,
      thumbnailUrl: result.thumbnailUrl,
      durationSeconds: result.durationSeconds || totalDuration,
      resolution: params.export.resolution,
      cost
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : "Unknown error";
    log.error("[ai:executeComposition] Error calling Cloud Run", { error: errorMessage });
    return {
      success: false,
      error: `Composition failed: ${errorMessage}`
    };
  }
}
function createPlaceholderResult(params, totalDuration) {
  const cost = estimateCompositionCost({
    durationSeconds: totalDuration,
    resolution: params.export.resolution
  });
  return {
    success: true,
    videoUrl: `gs://${params.outputBucket || "boosting-videos"}/${params.projectId}/final.${params.export.format}`,
    thumbnailUrl: `gs://${params.outputBucket || "boosting-videos"}/${params.projectId}/thumbnail.jpg`,
    durationSeconds: totalDuration,
    resolution: params.export.resolution,
    cost
  };
}

// src/server/video-config.ts
var DEFAULT_ELEVENLABS_VOICES = [
  {
    id: "onwK4e9ZLuTAKqWW03F9",
    name: "Daniel - Steady Broadcaster",
    description: "Voz profesional masculina, calmada y clara. Ideal para presentaciones t\xE9cnicas B2B.",
    language: "multilingual",
    accent: "neutral",
    gender: "male",
    useCase: ["presentations", "training", "corporate"]
  },
  {
    id: "pFZP5JQG7iQjIQuC4Bku",
    name: "Lily - Corporate Professional",
    description: "Voz femenina profesional y clara para comunicados corporativos.",
    language: "multilingual",
    accent: "neutral",
    gender: "female",
    useCase: ["corporate", "announcements", "reports"]
  },
  {
    id: "IKne3meq5aSn9XLyUdCD",
    name: "Charlie - Casual Host",
    description: "Voz masculina conversacional y amigable.",
    language: "multilingual",
    accent: "neutral",
    gender: "male",
    useCase: ["casual", "interviews", "podcasts"]
  },
  {
    id: "nPczCjzI2devNBz1zQrb",
    name: "Brian - Deep Narrator",
    description: "Voz masculina profunda y autorativa. Perfecta para contenido t\xE9cnico e industrial.",
    language: "multilingual",
    accent: "american",
    gender: "male",
    useCase: ["technical", "industrial", "b2b", "narration"]
  },
  {
    id: "pFZP5JQG7iQjIQuC4Bku",
    name: "Lily - Corporate Professional",
    description: "Voz femenina profesional y clara para comunicados corporativos.",
    language: "multilingual",
    accent: "neutral",
    gender: "female",
    useCase: ["corporate", "announcements", "reports"]
  }
];
var DEFAULT_GOOGLE_VOICES = [
  { id: "es-ES-Chirp3-HD-Charon", name: "Charon (ES)", language: "es-ES", gender: "male", tier: "premium" },
  { id: "es-ES-Chirp3-HD-Kore", name: "Kore (ES)", language: "es-ES", gender: "female", tier: "premium" },
  { id: "en-US-Chirp3-HD-Charon", name: "Charon (EN)", language: "en-US", gender: "male", tier: "premium" },
  { id: "en-US-Chirp3-HD-Kore", name: "Kore (EN)", language: "en-US", gender: "female", tier: "premium" }
];
var DEFAULT_ELEVENLABS_SETTINGS = {
  stability: 0.5,
  similarity_boost: 0.75,
  style: 0,
  use_speaker_boost: true
};
var DEFAULT_VOICE_PRESETS = {
  professional: {
    name: "Profesional B2B",
    description: "Voz clara, estable, ideal para presentaciones t\xE9cnicas",
    voiceId: "onwK4e9ZLuTAKqWW03F9",
    settings: { stability: 0.7, similarity_boost: 0.8, style: 0, use_speaker_boost: true }
  },
  conversational: {
    name: "Conversacional",
    description: "Tono m\xE1s casual y cercano",
    voiceId: "IKne3meq5aSn9XLyUdCD",
    settings: { stability: 0.4, similarity_boost: 0.7, style: 0.3, use_speaker_boost: true }
  },
  training: {
    name: "Training/Tutorial",
    description: "Claro y pedag\xF3gico, buen ritmo de aprendizaje",
    voiceId: "pFZP5JQG7iQjIQuC4Bku",
    settings: { stability: 0.6, similarity_boost: 0.75, style: 0.1, use_speaker_boost: true }
  },
  technical: {
    name: "Experto T\xE9cnico",
    description: "Voz autorativa para contenido industrial/t\xE9cnico",
    voiceId: "nPczCjzI2devNBz1zQrb",
    settings: { stability: 0.65, similarity_boost: 0.8, style: 0, use_speaker_boost: true }
  }
};
var DEFAULT_MUSIC_MOODS = {
  corporate: { name: "Corporativo", description: "Profesional y motivador", tempo: "medium" },
  inspiring: { name: "Inspirador", description: "\xC9pico y motivacional", tempo: "medium-fast" },
  calm: { name: "Calmado", description: "Relajante y suave", tempo: "slow" },
  tech: { name: "Tecnol\xF3gico", description: "Moderno y electr\xF3nico", tempo: "medium" },
  upbeat: { name: "Animado", description: "Energ\xE9tico y positivo", tempo: "fast" },
  cinematic: { name: "Cinem\xE1tico", description: "Dram\xE1tico y \xE9pico", tempo: "variable" }
};
var DEFAULT_PRONUNCIATION_DICTIONARY = {
  "PoE": "P.o.E.",
  "PoE+": "P.o.E. plus",
  "Gbps": "gigabits per second",
  "Mbps": "megabits per second",
  "SFP": "S.F.P.",
  "SFP+": "S.F.P. plus",
  "VLAN": "V.LAN",
  "QoS": "Q.o.S.",
  "IoT": "I.o.T.",
  "IP": "I.P.",
  "CPU": "C.P.U.",
  "GPU": "G.P.U.",
  "API": "A.P.I.",
  "SaaS": "sas",
  "B2B": "B. to B.",
  "AI": "A.I.",
  "ML": "M.L.",
  "NAS": "nas",
  "RAID": "raid",
  "USB": "U.S.B.",
  "HDMI": "H.D.M.I.",
  "LED": "led",
  "LCD": "L.C.D.",
  "UPS": "U.P.S.",
  "VPN": "V.P.N.",
  "PLC": "P.L.C.",
  "SCADA": "SCADA",
  "HMI": "H.M.I.",
  "DIN": "D.I.N.",
  "RS485": "R.S. four eighty five",
  "RS232": "R.S. two thirty two",
  "Modbus": "Modbus",
  "Profinet": "Profinet",
  "EtherCAT": "EtherCAT",
  "CAN": "CAN bus",
  "MQTT": "M.Q.T.T.",
  "OPC-UA": "O.P.C. U.A."
};
var DEFAULT_VIDEO_SETTINGS = {
  defaultAspectRatio: "16:9",
  defaultTransition: "crossfade",
  transitionDuration: 0.5,
  kenBurnsEnabled: true,
  kenBurnsIntensity: 0.05
};
var DEFAULT_VIDEO_GENERATION_CONFIG = {
  tts: {
    defaultProvider: "elevenlabs",
    elevenlabs: {
      defaultVoiceId: "onwK4e9ZLuTAKqWW03F9",
      defaultModel: "eleven_multilingual_v2",
      availableVoices: DEFAULT_ELEVENLABS_VOICES,
      voiceSettings: DEFAULT_ELEVENLABS_SETTINGS,
      presets: DEFAULT_VOICE_PRESETS
    },
    google: {
      defaultVoiceId: "es-ES-Chirp3-HD-Charon",
      availableVoices: DEFAULT_GOOGLE_VOICES
    }
  },
  music: {
    enabled: true,
    defaultMood: "corporate",
    defaultVolume: 0.15,
    fadeInDuration: 2,
    fadeOutDuration: 3,
    moods: DEFAULT_MUSIC_MOODS
  },
  pronunciationDictionary: DEFAULT_PRONUNCIATION_DICTIONARY,
  video: DEFAULT_VIDEO_SETTINGS
};
async function loadVideoGenerationConfig(agentsService, logger19) {
  const log = logger19 || console;
  try {
    const agent = await agentsService.getAgent("video_generation_config");
    if (!agent?.systemPrompt) {
      log.info("[ai:loadVideoGenerationConfig] Agent not found, using defaults");
      return DEFAULT_VIDEO_GENERATION_CONFIG;
    }
    try {
      const config = JSON.parse(agent.systemPrompt);
      log.info("[ai:loadVideoGenerationConfig] Loaded from AI Agent", {
        hasElevenLabsVoices: config.tts?.elevenlabs?.availableVoices?.length || 0,
        hasPronunciationTerms: Object.keys(config.pronunciationDictionary || {}).length
      });
      return mergeWithDefaults(config);
    } catch (parseErr) {
      log.warn("[ai:loadVideoGenerationConfig] Failed to parse agent config", { parseErr });
      return DEFAULT_VIDEO_GENERATION_CONFIG;
    }
  } catch (err) {
    log.warn("[ai:loadVideoGenerationConfig] Error loading config", { err });
    return DEFAULT_VIDEO_GENERATION_CONFIG;
  }
}
function mergeWithDefaults(config) {
  return {
    tts: {
      defaultProvider: config.tts?.defaultProvider || DEFAULT_VIDEO_GENERATION_CONFIG.tts.defaultProvider,
      elevenlabs: {
        defaultVoiceId: config.tts?.elevenlabs?.defaultVoiceId || DEFAULT_VIDEO_GENERATION_CONFIG.tts.elevenlabs.defaultVoiceId,
        defaultModel: config.tts?.elevenlabs?.defaultModel || DEFAULT_VIDEO_GENERATION_CONFIG.tts.elevenlabs.defaultModel,
        availableVoices: config.tts?.elevenlabs?.availableVoices || DEFAULT_VIDEO_GENERATION_CONFIG.tts.elevenlabs.availableVoices,
        voiceSettings: { ...DEFAULT_VIDEO_GENERATION_CONFIG.tts.elevenlabs.voiceSettings, ...config.tts?.elevenlabs?.voiceSettings },
        presets: { ...DEFAULT_VIDEO_GENERATION_CONFIG.tts.elevenlabs.presets, ...config.tts?.elevenlabs?.presets }
      },
      google: {
        defaultVoiceId: config.tts?.google?.defaultVoiceId || DEFAULT_VIDEO_GENERATION_CONFIG.tts.google.defaultVoiceId,
        availableVoices: config.tts?.google?.availableVoices || DEFAULT_VIDEO_GENERATION_CONFIG.tts.google.availableVoices
      }
    },
    music: {
      enabled: config.music?.enabled ?? DEFAULT_VIDEO_GENERATION_CONFIG.music.enabled,
      defaultMood: config.music?.defaultMood || DEFAULT_VIDEO_GENERATION_CONFIG.music.defaultMood,
      defaultVolume: config.music?.defaultVolume ?? DEFAULT_VIDEO_GENERATION_CONFIG.music.defaultVolume,
      fadeInDuration: config.music?.fadeInDuration ?? DEFAULT_VIDEO_GENERATION_CONFIG.music.fadeInDuration,
      fadeOutDuration: config.music?.fadeOutDuration ?? DEFAULT_VIDEO_GENERATION_CONFIG.music.fadeOutDuration,
      moods: { ...DEFAULT_VIDEO_GENERATION_CONFIG.music.moods, ...config.music?.moods }
    },
    pronunciationDictionary: {
      ...DEFAULT_VIDEO_GENERATION_CONFIG.pronunciationDictionary,
      ...config.pronunciationDictionary
    },
    video: {
      ...DEFAULT_VIDEO_GENERATION_CONFIG.video,
      ...config.video
    }
  };
}
function getVoiceSettingsForStyle(config, style) {
  const styleToPreset = {
    professional: "professional",
    cinematic: "training",
    dynamic: "conversational",
    minimal: "technical"
  };
  const presetKey = styleToPreset[style] || "professional";
  const preset = config.tts.elevenlabs.presets[presetKey];
  if (preset) {
    return {
      voiceId: preset.voiceId,
      settings: preset.settings
    };
  }
  return {
    voiceId: config.tts.elevenlabs.defaultVoiceId,
    settings: config.tts.elevenlabs.voiceSettings
  };
}
function getPronunciationDictionary(config, customEntries) {
  return {
    ...config.pronunciationDictionary,
    ...customEntries
  };
}

// src/enrichment/enrichment.registry.ts
var PropertyTypeRegistry = class {
  types = /* @__PURE__ */ new Map();
  /**
   * Registra un nuevo tipo de propiedad.
   */
  register(definition) {
    this.types.set(definition.type, definition);
  }
  /**
   * Obtiene la definición de un tipo.
   */
  get(type) {
    return this.types.get(type);
  }
  /**
   * Verifica si un tipo está registrado.
   */
  has(type) {
    return this.types.has(type);
  }
  /**
   * Obtiene todos los tipos registrados.
   */
  getAll() {
    return Array.from(this.types.values());
  }
  /**
   * Valida un valor según el tipo.
   */
  validate(value, property) {
    const definition = this.types.get(property.type);
    if (!definition?.validator) return true;
    return definition.validator(value, property);
  }
  /**
   * Convierte a JSON Schema.
   */
  toJsonSchema(property) {
    const definition = this.types.get(property.type);
    if (definition?.toJsonSchema) {
      return definition.toJsonSchema(property);
    }
    return this.defaultToJsonSchema(property);
  }
  /**
   * Formatea para lenguaje natural.
   */
  toNaturalLanguage(value, property) {
    const definition = this.types.get(property.type);
    if (definition?.toNaturalLanguage) {
      return definition.toNaturalLanguage(value, property);
    }
    return `${property.label}: ${String(value)}`;
  }
  defaultToJsonSchema(property) {
    const base = {
      description: property.description
    };
    switch (property.type) {
      case "string":
      case "text":
        return { ...base, type: "string" };
      case "number":
        return { ...base, type: "number" };
      case "boolean":
        return { ...base, type: "boolean" };
      case "date":
        return { ...base, type: "string", format: "date-time" };
      case "enum":
        return { ...base, type: "string", enum: property.enumValues };
      case "array":
        if (property.arrayItemType === "object" && property.itemSchema) {
          const itemProperties = {};
          for (const [key, def] of Object.entries(property.itemSchema)) {
            itemProperties[key] = {
              type: def.type === "text" ? "string" : def.type,
              description: def.description,
              ...def.enumValues ? { enum: def.enumValues } : {}
            };
          }
          return {
            ...base,
            type: "array",
            items: {
              type: "object",
              properties: itemProperties,
              required: Object.keys(property.itemSchema)
            }
          };
        }
        return {
          ...base,
          type: "array",
          items: { type: property.arrayItemType || "string" }
        };
      case "object":
        if (property.nestedProperties) {
          const nestedProps = {};
          for (const nested of property.nestedProperties) {
            nestedProps[nested.name] = this.toJsonSchema(nested);
          }
          return { ...base, type: "object", properties: nestedProps };
        }
        return { ...base, type: "object" };
      default:
        return { ...base, type: "string" };
    }
  }
};
var FormatterRegistry = class {
  formatters = /* @__PURE__ */ new Map();
  /**
   * Registra un nuevo formateador.
   */
  register(definition) {
    this.formatters.set(definition.name, definition);
  }
  /**
   * Obtiene un formateador.
   */
  get(name) {
    return this.formatters.get(name);
  }
  /**
   * Formatea un valor.
   */
  format(value, property, formatName, locale) {
    const format = formatName || property.display?.format || "default";
    const formatter = this.formatters.get(format);
    if (formatter && formatter.compatibleTypes.includes(property.type)) {
      return formatter.format(value, property, locale);
    }
    return this.defaultFormat(value, property, locale);
  }
  defaultFormat(value, property, locale = "es-ES") {
    if (value === null || value === void 0) {
      return "";
    }
    const { prefix = "", suffix = "" } = property.display || {};
    switch (property.type) {
      case "boolean":
        return value ? "S\xED" : "No";
      case "number":
        const num = value;
        const formatted = new Intl.NumberFormat(locale).format(num);
        return `${prefix}${formatted}${suffix}`;
      case "date":
        const date = new Date(value);
        return new Intl.DateTimeFormat(locale).format(date);
      case "array":
        const arr = value;
        const maxItems = property.display?.maxItems || 5;
        const visible = arr.slice(0, maxItems);
        const remaining = arr.length - maxItems;
        const joined = visible.join(", ");
        return remaining > 0 ? `${joined} +${remaining} m\xE1s` : joined;
      case "enum":
        return String(value);
      case "string":
      case "text":
      default:
        const str = String(value);
        const maxLen = property.display?.maxLength;
        if (maxLen && str.length > maxLen) {
          return str.substring(0, maxLen) + "...";
        }
        return str;
    }
  }
  /**
   * Obtiene todos los formateadores registrados.
   */
  getAll() {
    return Array.from(this.formatters.values());
  }
};
var TransformRegistry = class {
  transforms = /* @__PURE__ */ new Map();
  /**
   * Registra una función de transformación.
   */
  register(name, transform) {
    this.transforms.set(name, transform);
  }
  /**
   * Obtiene una función de transformación.
   */
  get(name) {
    return this.transforms.get(name);
  }
  /**
   * Ejecuta una transformación.
   */
  transform(name, value, context) {
    const fn = this.transforms.get(name);
    if (!fn) return value;
    return fn(value, context);
  }
};
var propertyTypeRegistry = new PropertyTypeRegistry();
var formatterRegistry = new FormatterRegistry();
var transformRegistry = new TransformRegistry();
propertyTypeRegistry.register({
  type: "string",
  description: "Texto corto (max 500 caracteres)",
  jsonSchemaType: "string",
  supportedFormats: ["default"],
  toNaturalLanguage: (value, prop) => `${prop.label}: ${value}`
});
propertyTypeRegistry.register({
  type: "text",
  description: "Texto largo (sin l\xEDmite)",
  jsonSchemaType: "string",
  supportedFormats: ["default", "markdown"],
  toNaturalLanguage: (value, prop) => `${prop.label}: ${String(value).substring(0, 200)}...`
});
propertyTypeRegistry.register({
  type: "number",
  description: "Valor num\xE9rico",
  jsonSchemaType: "number",
  supportedFormats: ["default", "currency", "percent", "compact", "progress", "rating"],
  toNaturalLanguage: (value, prop) => {
    const suffix = prop.display?.suffix || "";
    return `${prop.label}: ${value}${suffix}`;
  }
});
propertyTypeRegistry.register({
  type: "boolean",
  description: "Valor verdadero/falso",
  jsonSchemaType: "boolean",
  supportedFormats: ["default"],
  toNaturalLanguage: (value, prop) => {
    return value ? prop.label : `no ${prop.label.toLowerCase()}`;
  }
});
propertyTypeRegistry.register({
  type: "date",
  description: "Fecha y hora",
  jsonSchemaType: "string",
  supportedFormats: ["default", "relative", "absolute"],
  toJsonSchema: (prop) => ({
    type: "string",
    format: "date-time",
    description: prop.description
  }),
  toNaturalLanguage: (value, prop) => {
    const date = new Date(value);
    return `${prop.label}: ${date.toLocaleDateString("es-ES")}`;
  }
});
propertyTypeRegistry.register({
  type: "enum",
  description: "Valor de lista predefinida",
  jsonSchemaType: "string",
  supportedFormats: ["default"],
  toJsonSchema: (prop) => ({
    type: "string",
    enum: prop.enumValues || [],
    description: prop.description
  }),
  toNaturalLanguage: (value, prop) => `${prop.label}: ${value}`
});
propertyTypeRegistry.register({
  type: "array",
  description: "Lista de valores",
  jsonSchemaType: "array",
  supportedFormats: ["default", "chips", "list"],
  toJsonSchema: (prop) => {
    if (prop.arrayItemType === "object" && prop.itemSchema) {
      const itemProperties = {};
      for (const [key, def] of Object.entries(prop.itemSchema)) {
        itemProperties[key] = {
          type: def.type === "text" ? "string" : def.type,
          description: def.description,
          ...def.enumValues ? { enum: def.enumValues } : {}
        };
      }
      return {
        type: "array",
        items: {
          type: "object",
          properties: itemProperties,
          required: Object.keys(prop.itemSchema)
        },
        description: prop.description
      };
    }
    return {
      type: "array",
      items: { type: prop.arrayItemType || "string" },
      description: prop.description
    };
  },
  toNaturalLanguage: (value, prop) => {
    const arr = value;
    if (prop.arrayItemType === "object" && Array.isArray(arr)) {
      return `${prop.label}: ${arr.length} items`;
    }
    return `${prop.label}: ${arr.join(", ")}`;
  }
});
propertyTypeRegistry.register({
  type: "object",
  description: "Objeto estructurado",
  jsonSchemaType: "object",
  supportedFormats: ["default"],
  toNaturalLanguage: (value, prop) => `${prop.label}: [objeto]`
});
formatterRegistry.register({
  name: "currency",
  compatibleTypes: ["number"],
  format: (value, prop, locale = "es-ES") => {
    const prefix = prop.display?.prefix || "\u20AC";
    const num = value;
    return `${prefix}${new Intl.NumberFormat(locale).format(num)}`;
  }
});
formatterRegistry.register({
  name: "percent",
  compatibleTypes: ["number"],
  format: (value, prop, locale = "es-ES") => {
    const num = value;
    const normalized = num > 1 ? num / 100 : num;
    return new Intl.NumberFormat(locale, { style: "percent" }).format(normalized);
  }
});
formatterRegistry.register({
  name: "compact",
  compatibleTypes: ["number"],
  format: (value, prop, locale = "es-ES") => {
    const num = value;
    return new Intl.NumberFormat(locale, { notation: "compact" }).format(num);
  }
});
formatterRegistry.register({
  name: "relative",
  compatibleTypes: ["date"],
  format: (value) => {
    const date = new Date(value);
    const now = /* @__PURE__ */ new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1e3 * 60 * 60 * 24));
    if (diffDays === 0) return "hoy";
    if (diffDays === 1) return "ayer";
    if (diffDays < 7) return `hace ${diffDays} d\xEDas`;
    if (diffDays < 30) return `hace ${Math.floor(diffDays / 7)} semanas`;
    if (diffDays < 365) return `hace ${Math.floor(diffDays / 30)} meses`;
    return `hace ${Math.floor(diffDays / 365)} a\xF1os`;
  }
});
formatterRegistry.register({
  name: "chips",
  compatibleTypes: ["array"],
  format: (value, prop) => {
    const arr = value;
    const maxItems = prop.display?.maxItems || 3;
    const visible = arr.slice(0, maxItems);
    const remaining = arr.length - maxItems;
    return remaining > 0 ? `${visible.join(" | ")} +${remaining}` : visible.join(" | ");
  }
});
transformRegistry.register("hubspot_boolean", (value) => {
  return value ? "true" : "false";
});
transformRegistry.register("hubspot_array", (value) => {
  if (Array.isArray(value)) {
    return value.join(";");
  }
  return String(value);
});
transformRegistry.register("hubspot_number", (value) => {
  return String(value);
});
transformRegistry.register("hubspot_date", (value) => {
  const date = new Date(value);
  return date.getTime();
});
function registerPropertyType(definition) {
  propertyTypeRegistry.register(definition);
}
function registerFormatter(definition) {
  formatterRegistry.register(definition);
}
function registerTransform(name, fn) {
  transformRegistry.register(name, fn);
}

// src/enrichment/schema-generator.ts
var getSystemPrompt = (language = "es") => `Eres un experto en dise\xF1o de esquemas de datos estructurados para sistemas de enriquecimiento con IA.

Tu tarea es convertir descripciones en lenguaje natural en un esquema estructurado de propiedades.

TIPOS DE PROPIEDADES DISPONIBLES:
${propertyTypeRegistry.getAll().map((t) => `- ${t.type}: ${t.description}`).join("\n")}

REGLAS IMPORTANTES:

1. **Nombres y etiquetas**: name en snake_case, label legible para humanos
2. **Descriptions**: Explica C\xD3MO encontrar/extraer la informaci\xF3n, no QU\xC9 es
3. **Para enums**: Incluye valores posibles realistas
4. **Propiedades de confianza**: A\xF1ade cuando corresponda (0-100)

REGLAS PARA CONSULTAS COMPLEJAS:

5. **Detecci\xF3n de m\xFAltiples items**: Si la consulta habla de "los productos", "las empresas", "listar todos", etc:
   - Crea una \xDANICA propiedad type: "array" que contenga TODOS los an\xE1lisis
   - El arrayItemType debe ser "object" con sub-propiedades dentro de itemSchema
   - NO crees propiedades separadas para cada aspecto
   
6. **Ejemplo de estructura para an\xE1lisis de m\xFAltiples items**:
   Si la consulta es "analiza todos los productos mencionados con su obsolescencia":
   \`\`\`json
   {
     "name": "productos_analizados",
     "label": "Productos Analizados",
     "type": "array",
     "arrayItemType": "object",
     "description": "Lista detallada de cada producto mencionado con su an\xE1lisis",
     "itemSchema": {
       "nombre": { "type": "string", "description": "Nombre del producto" },
       "fecha_lanzamiento": { "type": "date" },
       "obsoleto": { "type": "boolean" },
       "justificacion": { "type": "text" },
       "producto_reemplazo": { "type": "string" }
     }
   }
   \`\`\`

7. **B\xFAsqueda en internet**: Si la consulta menciona "contrastar en internet", "buscar online", "verificar fuentes":
   - A\xF1ade una propiedad "fuentes_verificacion" type: "array" con URLs
   - Esto indica que el enriquecimiento requerir\xE1 grounding

8. **Prioriza estructuras anidadas** sobre m\xFAltiples propiedades planas cuando el an\xE1lisis sea por item

9. Responde en ${language === "es" ? "espa\xF1ol" : language}

RESPONDE SOLO CON JSON V\xC1LIDO siguiendo el esquema proporcionado.`;
var getGenerationPrompt = (request) => {
  let prompt = `Genera un esquema de propiedades para: "${request.description}"`;
  if (request.domain) {
    prompt += `

DOMINIO: ${request.domain}`;
  }
  if (request.existingProperties?.length) {
    prompt += `

PROPIEDADES YA EXISTENTES (evitar duplicados): ${request.existingProperties.join(", ")}`;
  }
  if (request.examples?.length) {
    prompt += "\n\nEJEMPLOS DE OUTPUT ESPERADO:";
    for (const example of request.examples) {
      prompt += `
Input: "${example.input}" \u2192 Output: ${JSON.stringify(example.output)}`;
    }
  }
  return prompt;
};
var PROPERTY_RESPONSE_SCHEMA = {
  type: "object",
  properties: {
    properties: {
      type: "array",
      items: {
        type: "object",
        properties: {
          name: {
            type: "string",
            description: "Identificador \xFAnico en snake_case"
          },
          label: {
            type: "string",
            description: "Nombre legible para mostrar"
          },
          type: {
            type: "string",
            enum: ["string", "text", "number", "boolean", "date", "enum", "array", "object"],
            description: "Tipo de dato"
          },
          description: {
            type: "string",
            description: "Instrucciones para la IA sobre c\xF3mo extraer este dato"
          },
          enumValues: {
            type: "array",
            items: { type: "string" },
            description: "Valores posibles (solo para type: enum)"
          },
          arrayItemType: {
            type: "string",
            enum: ["string", "number", "boolean", "object"],
            description: 'Tipo de items (solo para type: array). Usa "object" para estructuras complejas'
          },
          itemSchema: {
            type: "object",
            description: 'Esquema de cada item cuando arrayItemType es "object". Define las sub-propiedades de cada elemento del array',
            additionalProperties: {
              type: "object",
              properties: {
                type: { type: "string", enum: ["string", "text", "number", "boolean", "date", "enum", "array"] },
                description: { type: "string" },
                enumValues: { type: "array", items: { type: "string" } }
              }
            }
          },
          requiresGrounding: {
            type: "boolean",
            description: "Si esta propiedad requiere b\xFAsqueda en internet para verificar la informaci\xF3n"
          },
          display: {
            type: "object",
            properties: {
              primary: {
                type: "boolean",
                description: "Si debe mostrarse en vistas compactas"
              },
              priority: {
                type: "number",
                description: "Prioridad de visualizaci\xF3n (1-10)"
              },
              format: {
                type: "string",
                description: "Formato de visualizaci\xF3n"
              },
              suffix: {
                type: "string",
                description: "Sufijo a mostrar"
              }
            }
          }
        },
        required: ["name", "label", "type", "description"]
      }
    }
  },
  required: ["properties"]
};
async function generateSchemaFromDescription(geminiClient, request, config = {}) {
  const modelConfig = getModelConfig("generateEnrichmentSchema");
  const {
    temperature = modelConfig.temperature,
    language = request.language || "es",
    maxProperties = 10
  } = config;
  const models = [modelConfig.primary, modelConfig.fallback].filter(Boolean);
  let lastError = null;
  for (const model of models) {
    try {
      const result = await geminiClient.generate({
        model,
        prompt: getGenerationPrompt(request),
        systemInstruction: getSystemPrompt(language),
        temperature,
        responseFormat: "json",
        responseSchema: PROPERTY_RESPONSE_SCHEMA,
        maxOutputTokens: modelConfig.maxOutputTokens || 4096
      });
      let parsed;
      try {
        parsed = JSON.parse(result.text);
      } catch (parseError) {
        const jsonMatch = result.text.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          try {
            parsed = JSON.parse(jsonMatch[0]);
          } catch {
            throw new Error(`Invalid JSON from AI (${model}): ${parseError.message}`);
          }
        } else {
          throw new Error(`Could not parse AI response (${model}): ${parseError.message}`);
        }
      }
      const rawProperties = parsed.properties || (Array.isArray(parsed) ? parsed : []);
      const properties = [];
      for (const raw of rawProperties.slice(0, maxProperties)) {
        const property = normalizeProperty(raw);
        if (validateProperty(property)) {
          properties.push(property);
        }
      }
      if (properties.length === 0) {
        throw new Error(`No valid properties generated from ${model}`);
      }
      return properties;
    } catch (error) {
      lastError = error;
    }
  }
  throw lastError || new Error("Failed to generate schema with all models");
}
function normalizeProperty(raw) {
  const name = String(raw.name || "").toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");
  return {
    name,
    label: String(raw.label || name),
    type: raw.type || "string",
    description: String(raw.description || ""),
    enumValues: Array.isArray(raw.enumValues) ? raw.enumValues : void 0,
    arrayItemType: raw.arrayItemType,
    validation: raw.required ? { required: true } : void 0,
    display: raw.display
  };
}
function validateProperty(property) {
  if (!property.name || property.name.length < 2) {
    return false;
  }
  if (!propertyTypeRegistry.has(property.type)) {
    return false;
  }
  if (property.type === "enum" && (!property.enumValues || property.enumValues.length === 0)) {
    return false;
  }
  return true;
}
function suggestDomainProperties(domain) {
  const suggestions = {
    prospection: [
      {
        name: "confidence",
        label: "Nivel de Confianza",
        type: "number",
        description: "Confianza en la informaci\xF3n extra\xEDda (0-100)",
        display: { format: "percent", hideIfEmpty: true }
      },
      {
        name: "data_sources",
        label: "Fuentes de Datos",
        type: "array",
        arrayItemType: "string",
        description: "URLs o fuentes de donde se extrajo la informaci\xF3n",
        display: { hideIfEmpty: true }
      }
    ],
    content: [
      {
        name: "relevance_score",
        label: "Puntuaci\xF3n de Relevancia",
        type: "number",
        description: "Qu\xE9 tan relevante es este contenido (0-100)",
        display: { format: "percent", primary: true }
      },
      {
        name: "key_topics",
        label: "Temas Clave",
        type: "array",
        arrayItemType: "string",
        description: "Principales temas cubiertos",
        display: { format: "chips", maxItems: 5 }
      }
    ]
  };
  return suggestions[domain] || [];
}

// src/enrichment/structured-extractor.ts
import { Logger as Logger23 } from "@boosting/core";
var buildSystemPrompt = (schema) => {
  const basePrompt = `Eres un agente de enriquecimiento de datos especializado.
Tu tarea es extraer informaci\xF3n estructurada seg\xFAn el esquema proporcionado.

${schema.systemContext ? `CONTEXTO DEL USUARIO:
${schema.systemContext}
` : ""}

REGLAS CR\xCDTICAS DE EXTRACCI\xD3N:
1. RESPETA ESTRICTAMENTE el esquema JSON proporcionado
2. PROHIBIDO INVENTAR DATOS: Si la informaci\xF3n no aparece expl\xEDcitamente en el CONTEXTO o la INVESTIGACI\xD3N, devuelve \`null\`.
3. PROHIBIDO INVENTAR URLs: NUNCA deduzcas, generes o adivines URLs (como perfiles de LinkedIn, Twitter, etc.). Si la URL exacta no aparece copiada en el texto de investigaci\xF3n, el valor DEBE ser \`null\`.
4. Para campos num\xE9ricos, extrae valores num\xE9ricos exactos.
5. Para enums, usa SOLO los valores permitidos del esquema.

PROPIEDADES A EXTRAER:`;
  const propertyDescriptions = schema.properties.map((prop) => {
    let desc = `- ${prop.name} (${prop.type}): ${prop.description}`;
    if (prop.type === "enum" && prop.enumValues) {
      desc += ` [Valores: ${prop.enumValues.join(", ")}]`;
    }
    if (prop.type === "array" && prop.arrayItemType === "object" && prop.itemSchema) {
      desc += `
  ESTRUCTURA DE CADA ELEMENTO DEL ARRAY:`;
      for (const [key, def] of Object.entries(prop.itemSchema)) {
        desc += `
    \u2022 ${key} (${def.type}): ${def.description || ""}`;
        if (def.enumValues) {
          desc += ` [Valores: ${def.enumValues.join(", ")}]`;
        }
      }
    }
    return desc;
  }).join("\n");
  return `${basePrompt}
${propertyDescriptions}`;
};
var buildExtractionPrompt = (request) => {
  let prompt = "DATOS DE ENTRADA:\n";
  for (const [key, value] of Object.entries(request.inputData)) {
    if (value !== null && value !== void 0) {
      prompt += `${key}: ${typeof value === "object" ? JSON.stringify(value) : value}
`;
    }
  }
  if (request.inputContext) {
    prompt += `
CONTEXTO ADICIONAL:
${request.inputContext}`;
  }
  prompt += "\n\nExtrae la informaci\xF3n seg\xFAn el esquema definido.";
  return prompt;
};
function schemaToJsonSchema(schema) {
  const properties = {};
  const required = [];
  for (const prop of schema.properties) {
    properties[prop.name] = propertyTypeRegistry.toJsonSchema(prop);
    if (prop.validation?.required) {
      required.push(prop.name);
    }
  }
  properties["_extraction_notes"] = {
    type: "string",
    description: "Notas sobre la extracci\xF3n (opcional)"
  };
  return {
    type: "object",
    properties,
    required
  };
}
async function executeEnrichment(geminiClient, request, config = {}) {
  const startTime = Date.now();
  const {
    temperature = request.schema.temperature ?? 0.2,
    maxRetries = 2
  } = config;
  const jsonSchema = schemaToJsonSchema(request.schema);
  const systemPrompt = buildSystemPrompt(request.schema);
  const extractionPrompt = buildExtractionPrompt(request);
  const model = config.model || request.schema.model || "gemini-3-flash-preview";
  let lastError = null;
  let researchContext = "";
  let groundingSources = [];
  let webSearchQueries = [];
  if (request.schema.groundingEnabled) {
    try {
      const researchPrompt = `${extractionPrompt}

TAREA: Usa la b\xFAsqueda web para encontrar informaci\xF3n real y actualizada sobre los datos anteriores y completar estas propiedades:
${request.schema.properties.map((p) => `- ${p.name}: ${p.description}`).join("\n")}

INSTRUCCIONES CR\xCDTICAS PARA LA B\xDASQUEDA:
1. Extrae datos ESPEC\xCDFICOS para cada propiedad solicitada.
2. URLs ESTRICTAS: Si se pide un enlace o perfil (LinkedIn, web, etc), copia y pega la URL EXACTA que devuelve Google Search. NO adivines URLs. NO omitas las URLs si la b\xFAsqueda las proporciona. NUNCA deduzcas una ruta como \`/in/nombre\`.
3. Si la b\xFAsqueda de Google no encuentra el dato de forma clara, di "Dato no encontrado en Google" y NUNCA lo inventes.
4. Incluye las fuentes y fechas relevantes.

RESPONDE EN FORMATO LIBRE CON TODA LA INFORMACI\xD3N LITERAL ENCONTRADA POR GOOGLE.`;
      const researchResult = await geminiClient.generate({
        model,
        prompt: researchPrompt,
        systemInstruction: `Eres un investigador experto. Busca informaci\xF3n actualizada en internet para responder a la consulta.
${request.schema.systemContext || ""}`,
        temperature: 0.3,
        tools: [{ googleSearch: {} }]
      });
      researchContext = researchResult.text;
      groundingSources = extractGroundingSources(researchResult);
      webSearchQueries = extractWebSearchQueries(researchResult);
      Logger23.getLogger("StructuredExtractor").info("Research step completed", {
        contextLength: researchContext.length,
        sourcesCount: groundingSources.length
      });
    } catch (error) {
      Logger23.getLogger("StructuredExtractor").error("Research step failed", { error });
    }
  }
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      let fullExtractionPrompt = extractionPrompt;
      if (researchContext) {
        fullExtractionPrompt += `

INFORMACI\xD3N DE INVESTIGACI\xD3N (obtenida de b\xFAsqueda web):
${researchContext}

INSTRUCCIONES: Usa la informaci\xF3n de investigaci\xF3n anterior para completar TODOS los campos del esquema.
Para cada elemento del array, aseg\xFArate de llenar TODAS las propiedades internas.`;
      }
      const result = await geminiClient.generate({
        model,
        prompt: fullExtractionPrompt,
        systemInstruction: systemPrompt,
        temperature,
        responseFormat: "json",
        responseSchema: jsonSchema
        // NO tools here - structured output only
      });
      const data = parseResponse(result.text, request.schema);
      const confidence = calculateConfidence(data, request.schema, groundingSources);
      return {
        data,
        sources: groundingSources,
        confidence: confidence.overall,
        propertyConfidence: confidence.perProperty,
        researchContext: researchContext || void 0,
        webSearchQueries: webSearchQueries.length > 0 ? webSearchQueries : void 0,
        tokensUsed: 0,
        model: result.model,
        executionTimeMs: Date.now() - startTime,
        executedAt: /* @__PURE__ */ new Date()
      };
    } catch (error) {
      lastError = error;
      if (!(error instanceof SyntaxError) && attempt < maxRetries) {
        continue;
      }
    }
  }
  return {
    data: {},
    sources: groundingSources,
    confidence: 0,
    tokensUsed: 0,
    model: "unknown",
    executionTimeMs: Date.now() - startTime,
    executedAt: /* @__PURE__ */ new Date(),
    errors: [{
      property: "_global",
      error: lastError?.message || "Unknown error"
    }]
  };
}
function parseResponse(text, schema) {
  const parsed = JSON.parse(text);
  const data = {};
  for (const prop of schema.properties) {
    const value = parsed[prop.name];
    data[prop.name] = normalizeValue(value, prop);
  }
  return data;
}
function normalizeValue(value, property) {
  if (value === null || value === void 0) {
    return property.defaultValue ?? null;
  }
  switch (property.type) {
    case "boolean":
      if (typeof value === "boolean") return value;
      if (typeof value === "string") {
        return value.toLowerCase() === "true" || value === "1" || value === "s\xED";
      }
      return Boolean(value);
    case "number":
      if (typeof value === "number") return value;
      const num = parseFloat(String(value).replace(/[^\d.-]/g, ""));
      return isNaN(num) ? null : num;
    case "enum":
      const strValue = String(value);
      if (property.enumValues?.includes(strValue)) {
        return strValue;
      }
      const match = property.enumValues?.find(
        (v) => v.toLowerCase() === strValue.toLowerCase()
      );
      return match || null;
    case "array":
      if (Array.isArray(value)) return value;
      if (typeof value === "string") {
        return value.split(/[,;]/).map((s) => s.trim()).filter(Boolean);
      }
      return [value];
    case "date":
      if (value instanceof Date) return value.toISOString();
      const date = new Date(value);
      return isNaN(date.getTime()) ? null : date.toISOString();
    default:
      return value;
  }
}
function extractGroundingSources(result) {
  const sources = [];
  const metadata = result.groundingMetadata;
  if (!metadata) return sources;
  if (metadata.groundingChunks) {
    for (const chunk of metadata.groundingChunks) {
      if (chunk.web?.uri) {
        sources.push({
          url: chunk.web.uri,
          title: chunk.web.title || extractDomain(chunk.web.uri),
          domain: extractDomain(chunk.web.uri),
          snippet: chunk.web.snippet
        });
      }
    }
  }
  const uniqueSources = /* @__PURE__ */ new Map();
  for (const source of sources) {
    if (!uniqueSources.has(source.url)) {
      uniqueSources.set(source.url, source);
    }
  }
  return Array.from(uniqueSources.values());
}
function extractWebSearchQueries(result) {
  const metadata = result.groundingMetadata;
  if (!metadata?.webSearchQueries) return [];
  return Array.isArray(metadata.webSearchQueries) ? metadata.webSearchQueries : [];
}
function extractDomain(url) {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}
function calculateConfidence(data, schema, sources) {
  const perProperty = {};
  let totalConfidence = 0;
  let countedFields = 0;
  for (const prop of schema.properties) {
    const value = data[prop.name];
    let confidence = 0;
    if (value !== null && value !== void 0) {
      confidence = 0.5;
      if (sources.length > 0 && schema.groundingEnabled) {
        confidence += 0.3;
      }
      if (isWellFormatted(value, prop)) {
        confidence += 0.2;
      }
    }
    perProperty[prop.name] = confidence;
    totalConfidence += confidence;
    countedFields++;
  }
  return {
    overall: countedFields > 0 ? totalConfidence / countedFields : 0,
    perProperty
  };
}
function isWellFormatted(value, property) {
  switch (property.type) {
    case "enum":
      return property.enumValues?.includes(String(value)) ?? false;
    case "number":
      return typeof value === "number" && !isNaN(value);
    case "boolean":
      return typeof value === "boolean";
    case "array":
      return Array.isArray(value) && value.length > 0;
    case "date":
      return !isNaN(new Date(value).getTime());
    default:
      return typeof value === "string" && value.length > 0;
  }
}
async function executeEnrichmentBatch(geminiClient, schema, items, userId, tenantId, options = {}) {
  const {
    concurrency = 3,
    onProgress,
    continueOnError = true
  } = options;
  const results = /* @__PURE__ */ new Map();
  let completed = 0;
  for (let i = 0; i < items.length; i += concurrency) {
    const chunk = items.slice(i, i + concurrency);
    const chunkPromises = chunk.map(async (item) => {
      try {
        const result = await executeEnrichment(geminiClient, {
          schema,
          inputData: item.data,
          inputContext: item.context,
          userId,
          tenantId
        }, options);
        results.set(item.id, result);
      } catch (error) {
        if (!continueOnError) throw error;
        results.set(item.id, {
          data: {},
          sources: [],
          confidence: 0,
          tokensUsed: 0,
          model: "unknown",
          executionTimeMs: 0,
          executedAt: /* @__PURE__ */ new Date(),
          errors: [{
            property: "_global",
            error: error.message
          }]
        });
      }
      completed++;
      onProgress?.(completed, items.length);
    });
    await Promise.all(chunkPromises);
  }
  return results;
}

// src/enrichment/context-builder.ts
function buildEnrichmentContext(enrichments, schemas, options = {}) {
  const {
    language = "es",
    minConfidence = 0,
    primaryOnly = false,
    maxSources = 10,
    summaryFormat = "prose"
  } = options;
  const summaryParts = [];
  const structured = {};
  const allSources = [];
  const confidenceScores = [];
  const lowConfidenceFields = [];
  for (const [schemaId, result] of Object.entries(enrichments)) {
    const schema = schemas[schemaId];
    if (!schema) continue;
    if (result.confidence < minConfidence) continue;
    structured[schemaId] = result.data;
    const schemaSummary = buildSchemaSummary(
      result,
      schema,
      { language, primaryOnly, minConfidence }
    );
    if (schemaSummary) {
      summaryParts.push(schemaSummary);
    }
    allSources.push(...result.sources);
    confidenceScores.push(result.confidence);
    if (result.propertyConfidence) {
      for (const [propName, conf] of Object.entries(result.propertyConfidence)) {
        if (conf < 0.5) {
          lowConfidenceFields.push(`${schema.name}.${propName}`);
        }
      }
    }
  }
  const uniqueSources = deduplicateSources(allSources).slice(0, maxSources);
  const avgConfidence = confidenceScores.length > 0 ? confidenceScores.reduce((a, b) => a + b, 0) / confidenceScores.length : 0;
  const summary = formatSummary(summaryParts, summaryFormat);
  return {
    summary,
    structured,
    verifiedSources: uniqueSources,
    reliability: {
      averageConfidence: avgConfidence,
      lowestConfidenceFields: lowConfidenceFields.slice(0, 5),
      totalSources: uniqueSources.length
    }
  };
}
function buildSchemaSummary(result, schema, options) {
  const parts = [];
  const { primaryOnly, minConfidence } = options;
  for (const prop of schema.properties) {
    if (primaryOnly && !prop.display?.primary) continue;
    const value = result.data[prop.name];
    if (value === null || value === void 0) continue;
    const propConfidence = result.propertyConfidence?.[prop.name] ?? result.confidence;
    if (propConfidence < minConfidence) continue;
    const naturalText = propertyTypeRegistry.toNaturalLanguage(value, prop);
    parts.push(naturalText);
  }
  if (parts.length === 0) return null;
  return parts.join(", ");
}
function formatSummary(parts, format) {
  if (parts.length === 0) return "";
  switch (format) {
    case "bullets":
      return parts.map((p) => `\u2022 ${p}`).join("\n");
    case "keyvalue":
      return parts.join("\n");
    case "prose":
    default:
      return parts.join(". ") + ".";
  }
}
function deduplicateSources(sources) {
  const unique = /* @__PURE__ */ new Map();
  for (const source of sources) {
    const key = source.url.replace(/\/$/, "").toLowerCase();
    if (!unique.has(key)) {
      unique.set(key, source);
    }
  }
  return Array.from(unique.values());
}
function groupSourcesByDomain(sources) {
  const grouped = /* @__PURE__ */ new Map();
  for (const source of sources) {
    const domain = source.domain || extractDomain2(source.url);
    const existing = grouped.get(domain) || [];
    existing.push(source);
    grouped.set(domain, existing);
  }
  return grouped;
}
function extractDomain2(url) {
  try {
    return new URL(url).hostname;
  } catch {
    return "unknown";
  }
}
function formatContextForPrompt(context, options = {}) {
  const {
    includeSources = true,
    includeReliability = true,
    maxLength
  } = options;
  const sections = [];
  if (context.summary) {
    sections.push(`INFORMACI\xD3N ENRIQUECIDA:
${context.summary}`);
  }
  if (includeReliability) {
    const reliability = context.reliability;
    const confidencePercent = Math.round(reliability.averageConfidence * 100);
    sections.push(`CONFIANZA: ${confidencePercent}% (${reliability.totalSources} fuentes verificadas)`);
    if (reliability.lowestConfidenceFields.length > 0) {
      sections.push(`NOTA: Baja confianza en: ${reliability.lowestConfidenceFields.join(", ")}`);
    }
  }
  if (includeSources && context.verifiedSources.length > 0) {
    const sourcesText = context.verifiedSources.slice(0, 5).map((s) => `- ${s.title || s.domain}: ${s.url}`).join("\n");
    sections.push(`FUENTES VERIFICADAS:
${sourcesText}`);
  }
  let result = sections.join("\n\n");
  if (maxLength && result.length > maxLength) {
    result = result.substring(0, maxLength - 3) + "...";
  }
  return result;
}
function extractFromContext(context, schemaId, propertyName) {
  const schemaData = context.structured[schemaId];
  if (!schemaData) return null;
  return schemaData[propertyName] ?? null;
}
function hasEnrichmentData(context, schemaId) {
  const schemaData = context.structured[schemaId];
  if (!schemaData) return false;
  return Object.values(schemaData).some((v) => v !== null && v !== void 0);
}
function compareEnrichments(oldResult, newResult, schema) {
  const changedProperties = [];
  const addedProperties = [];
  const removedProperties = [];
  for (const prop of schema.properties) {
    const oldValue = oldResult?.data[prop.name];
    const newValue = newResult.data[prop.name];
    const oldExists = oldValue !== null && oldValue !== void 0;
    const newExists = newValue !== null && newValue !== void 0;
    if (!oldExists && newExists) {
      addedProperties.push(prop.name);
    } else if (oldExists && !newExists) {
      removedProperties.push(prop.name);
    } else if (oldExists && newExists && !isEqual(oldValue, newValue)) {
      changedProperties.push(prop.name);
    }
  }
  return {
    changed: changedProperties.length > 0 || addedProperties.length > 0 || removedProperties.length > 0,
    changedProperties,
    addedProperties,
    removedProperties
  };
}
function isEqual(a, b) {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((v, i) => isEqual(v, b[i]));
  }
  if (typeof a === "object" && a !== null && b !== null) {
    return JSON.stringify(a) === JSON.stringify(b);
  }
  return false;
}

// src/enrichment/index.ts
function createEmptySchema(name = "Nuevo Esquema", domain) {
  return {
    id: `schema_${Date.now()}`,
    name,
    description: "",
    version: 1,
    properties: [],
    agentType: "grounding",
    groundingEnabled: true,
    domain,
    isActive: true,
    createdAt: /* @__PURE__ */ new Date(),
    updatedAt: /* @__PURE__ */ new Date()
  };
}
function createEmptyProperty(type = "string") {
  return {
    name: `prop_${Date.now()}`,
    label: "",
    type,
    description: "",
    display: {
      primary: false
    }
  };
}
function validateSchema(schema) {
  const errors = [];
  if (!schema.id) errors.push("El esquema debe tener un ID");
  if (!schema.name) errors.push("El esquema debe tener un nombre");
  if (schema.properties.length === 0) errors.push("El esquema debe tener al menos una propiedad");
  const names = /* @__PURE__ */ new Set();
  for (const prop of schema.properties) {
    if (!prop.name) {
      errors.push("Todas las propiedades deben tener un nombre");
    } else if (names.has(prop.name)) {
      errors.push(`Nombre de propiedad duplicado: ${prop.name}`);
    }
    names.add(prop.name);
    if (prop.type === "enum" && (!prop.enumValues || prop.enumValues.length === 0)) {
      errors.push(`La propiedad enum "${prop.name}" debe tener valores definidos`);
    }
  }
  return {
    valid: errors.length === 0,
    errors
  };
}
function cloneSchema(schema, newName) {
  return {
    ...schema,
    id: `schema_${Date.now()}`,
    name: newName || `${schema.name} (copia)`,
    version: 1,
    createdAt: /* @__PURE__ */ new Date(),
    updatedAt: /* @__PURE__ */ new Date()
  };
}

// src/enrichment/waterfall/waterfall.types.ts
var PROVIDER_LABELS = {
  apollo: "Apollo.io",
  pdl: "People Data Labs",
  grounding: "Google Search"
};
var PROVIDER_ICONS = {
  apollo: "\u26A1",
  pdl: "\u{1F4CA}",
  grounding: "\u{1F50D}"
};

// src/enrichment/waterfall/providers/apollo.provider.ts
var APOLLO_SUPPORTED_FIELDS = [
  "linkedin_url",
  "job_title",
  "email",
  "phone",
  "seniority",
  "department",
  "location",
  "company_name",
  "company_domain",
  "company_industry",
  "company_size",
  "company_linkedin",
  "headline"
];
var ApolloProvider = class {
  constructor(apolloService, tenantId) {
    this.apolloService = apolloService;
    this.tenantId = tenantId;
  }
  name = "apollo";
  supportedFields = APOLLO_SUPPORTED_FIELDS;
  async enrich(input) {
    const results = {};
    const parts = this.parseName(input);
    const response = await this.apolloService.enrichPerson({
      email: input.email,
      linkedin_url: input.linkedinUrl,
      first_name: parts.firstName,
      last_name: parts.lastName,
      organization_name: input.company,
      organization_domain: input.companyDomain
    });
    const person = response.person;
    if (!person) return results;
    if (person.linkedin_url) {
      results.linkedin_url = {
        value: person.linkedin_url,
        confidence: 0.95,
        source: "apollo",
        verified: true
      };
    }
    if (person.title) {
      results.job_title = {
        value: person.title,
        confidence: 0.95,
        source: "apollo",
        verified: true
      };
    }
    if (person.email && person.email_status !== "bounced") {
      results.email = {
        value: person.email,
        confidence: person.email_status === "verified" ? 0.99 : 0.7,
        source: "apollo",
        verified: person.email_status === "verified"
      };
    }
    if (person.phone_numbers?.length) {
      const primary = person.phone_numbers[0];
      results.phone = {
        value: primary.raw_number,
        confidence: 0.9,
        source: "apollo",
        verified: true
      };
    }
    if (person.seniority) {
      results.seniority = {
        value: person.seniority,
        confidence: 0.9,
        source: "apollo",
        verified: true
      };
    }
    if (person.departments?.length) {
      results.department = {
        value: person.departments[0],
        confidence: 0.9,
        source: "apollo",
        verified: true
      };
    }
    if (person.headline) {
      results.headline = {
        value: person.headline,
        confidence: 0.95,
        source: "apollo",
        verified: true
      };
    }
    const locationParts = [person.city, person.state, person.country].filter(Boolean);
    if (locationParts.length > 0) {
      results.location = {
        value: locationParts.join(", "),
        confidence: 0.9,
        source: "apollo",
        verified: true
      };
    }
    const org = person.organization;
    if (org) {
      if (org.name) {
        results.company_name = { value: org.name, confidence: 0.95, source: "apollo", verified: true };
      }
      if (org.primary_domain) {
        results.company_domain = { value: org.primary_domain, confidence: 0.95, source: "apollo", verified: true };
      }
      if (org.industry) {
        results.company_industry = { value: org.industry, confidence: 0.9, source: "apollo", verified: true };
      }
      if (org.estimated_num_employees) {
        results.company_size = { value: org.estimated_num_employees, confidence: 0.85, source: "apollo", verified: true };
      }
      if (org.linkedin_url) {
        results.company_linkedin = { value: org.linkedin_url, confidence: 0.95, source: "apollo", verified: true };
      }
    }
    return results;
  }
  async isAvailable(_tenantId) {
    return !!this.apolloService;
  }
  estimateCost() {
    return 0.01;
  }
  parseName(input) {
    if (input.firstName && input.lastName) {
      return { firstName: input.firstName, lastName: input.lastName };
    }
    if (input.fullName) {
      const parts = input.fullName.trim().split(/\s+/);
      return {
        firstName: parts[0],
        lastName: parts.slice(1).join(" ") || void 0
      };
    }
    return {};
  }
};

// src/enrichment/waterfall/providers/pdl.provider.ts
var PDL_SUPPORTED_FIELDS = [
  "linkedin_url",
  "job_title",
  "email",
  "phone",
  "location",
  "company_name",
  "company_domain",
  "company_industry",
  "company_size",
  "company_linkedin",
  "headline",
  "skills",
  "salary_range",
  "years_experience",
  "summary"
];
var PDLProvider = class {
  constructor(pdlService, tenantId) {
    this.pdlService = pdlService;
    this.tenantId = tenantId;
  }
  name = "pdl";
  supportedFields = PDL_SUPPORTED_FIELDS;
  async enrich(input) {
    const results = {};
    const fullName = input.fullName || [input.firstName, input.lastName].filter(Boolean).join(" ") || void 0;
    const person = await this.pdlService.enrichPerson({
      email: input.email,
      linkedinUrl: input.linkedinUrl,
      name: fullName,
      company: input.company
    });
    if (!person) return results;
    if (person.linkedin_url) {
      results.linkedin_url = {
        value: person.linkedin_url,
        confidence: 0.95,
        source: "pdl",
        verified: true
      };
    }
    if (person.job_title) {
      results.job_title = {
        value: person.job_title,
        confidence: 0.9,
        source: "pdl",
        verified: true
      };
    }
    if (person.work_email) {
      results.email = {
        value: person.work_email,
        confidence: 0.85,
        source: "pdl",
        verified: true
      };
    } else if (person.personal_emails?.length) {
      results.email = {
        value: person.personal_emails[0],
        confidence: 0.7,
        source: "pdl",
        verified: false
      };
    }
    if (person.mobile_phone) {
      results.phone = {
        value: person.mobile_phone,
        confidence: 0.85,
        source: "pdl",
        verified: true
      };
    } else if (person.phone_numbers?.length) {
      results.phone = {
        value: person.phone_numbers[0],
        confidence: 0.7,
        source: "pdl",
        verified: false
      };
    }
    const locationParts = [person.location_locality, person.location_region, person.location_country].filter(Boolean);
    if (locationParts.length > 0) {
      results.location = {
        value: locationParts.join(", "),
        confidence: 0.85,
        source: "pdl",
        verified: true
      };
    }
    if (person.headline) {
      results.headline = {
        value: person.headline,
        confidence: 0.9,
        source: "pdl",
        verified: true
      };
    }
    if (person.summary) {
      results.summary = {
        value: person.summary,
        confidence: 0.9,
        source: "pdl",
        verified: true
      };
    }
    if (person.skills?.length) {
      results.skills = {
        value: person.skills,
        confidence: 0.85,
        source: "pdl",
        verified: true
      };
    }
    if (person.job_company_name) {
      results.company_name = { value: person.job_company_name, confidence: 0.9, source: "pdl", verified: true };
    }
    if (person.job_company_website) {
      results.company_domain = { value: person.job_company_website, confidence: 0.9, source: "pdl", verified: true };
    }
    if (person.job_company_industry) {
      results.company_industry = { value: person.job_company_industry, confidence: 0.85, source: "pdl", verified: true };
    }
    if (person.job_company_size) {
      results.company_size = { value: person.job_company_size, confidence: 0.8, source: "pdl", verified: true };
    }
    if (person.job_company_linkedin_url) {
      results.company_linkedin = { value: person.job_company_linkedin_url, confidence: 0.95, source: "pdl", verified: true };
    }
    if (person.inferred_salary) {
      results.salary_range = { value: person.inferred_salary, confidence: 0.6, source: "pdl", verified: false };
    }
    if (person.inferred_years_experience) {
      results.years_experience = { value: person.inferred_years_experience, confidence: 0.7, source: "pdl", verified: false };
    }
    return results;
  }
  async isAvailable(_tenantId) {
    return !!this.pdlService;
  }
  estimateCost() {
    return 0.03;
  }
};

// src/enrichment/waterfall/providers/grounding.provider.ts
var GROUNDING_SUPPORTED_FIELDS = [
  "job_title",
  "company_name",
  "company_industry",
  "company_size",
  "company_description",
  "headline",
  "summary",
  "location",
  // linkedin_url deliberadamente EXCLUIDO como campo soportado principal
  // Solo se usa como último recurso si está en el routing
  "linkedin_url"
];
var GroundingProvider = class {
  constructor(geminiClient, model = "gemini-3-flash-preview") {
    this.geminiClient = geminiClient;
    this.model = model;
  }
  name = "grounding";
  supportedFields = GROUNDING_SUPPORTED_FIELDS;
  async enrich(input) {
    const results = {};
    const fullName = input.fullName || [input.firstName, input.lastName].filter(Boolean).join(" ") || "desconocido";
    const company = input.company || input.companyDomain || "desconocida";
    const researchPrompt = `Busca informaci\xF3n profesional sobre ${fullName} que trabaja en ${company}.

DATOS DISPONIBLES:
${input.email ? `- Email: ${input.email}` : ""}
${input.linkedinUrl ? `- LinkedIn: ${input.linkedinUrl}` : ""}
${input.companyDomain ? `- Web empresa: ${input.companyDomain}` : ""}

INFORMACI\xD3N A BUSCAR:
- Puesto de trabajo actual (job title)
- Descripci\xF3n de la empresa
- Industria de la empresa
- Ubicaci\xF3n del profesional
- Tama\xF1o de la empresa (n\xFAmero de empleados)

REGLAS ESTRICTAS:
1. Solo incluye datos que aparezcan EXPL\xCDCITAMENTE en los resultados de Google.
2. Si Google Search devuelve una URL de perfil de LinkedIn, copia y pega la URL EXACTA. NUNCA deduzcas ni construyas URLs.
3. Si no encuentras un dato, di "NO ENCONTRADO" \u2014 NUNCA inventes.

Responde en formato libre con toda la informaci\xF3n literal encontrada.`;
    try {
      const researchResult = await this.geminiClient.generate({
        model: this.model,
        prompt: researchPrompt,
        systemInstruction: "Eres un investigador que busca datos profesionales en internet. Solo reporta datos expl\xEDcitos de los resultados de b\xFAsqueda. NUNCA inventes URLs ni datos.",
        temperature: 0.1,
        tools: [{ googleSearch: {} }]
      });
      const researchText = researchResult.text;
      const groundingMetadata = researchResult.groundingMetadata;
      const extractionPrompt = `Bas\xE1ndote EXCLUSIVAMENTE en la siguiente investigaci\xF3n, extrae los datos en formato JSON.

INVESTIGACI\xD3N:
${researchText}

REGLAS:
1. Solo usa datos que aparezcan literalmente en la investigaci\xF3n anterior.
2. Si un dato no aparece claramente, pon null.
3. Para linkedin_url: SOLO incluye si aparece una URL completa de linkedin.com en la investigaci\xF3n. Si no hay URL exacta, pon null. NUNCA deduzcas URLs.

Responde con un JSON con estos campos:
{
  "job_title": "string o null",
  "company_name": "string o null",
  "company_industry": "string o null",
  "company_size": "string o null",
  "company_description": "string o null",
  "location": "string o null",
  "headline": "string o null",
  "linkedin_url": "string o null (SOLO si aparece URL exacta)"
}`;
      const extractionResult = await this.geminiClient.generate({
        model: this.model,
        prompt: extractionPrompt,
        systemInstruction: "Extrae datos estructurados. NUNCA inventes datos ni URLs. Usa null si no hay informaci\xF3n.",
        temperature: 0,
        responseFormat: "json"
      });
      const parsed = JSON.parse(extractionResult.text);
      for (const [field, value] of Object.entries(parsed)) {
        if (value !== null && value !== void 0 && value !== "" && value !== "null") {
          if (field === "linkedin_url") {
            const url = String(value);
            if (!url.includes("linkedin.com/in/") && !url.includes("linkedin.com/company/")) {
              continue;
            }
            const isFromGrounding = this.urlExistsInGrounding(url, groundingMetadata);
            results[field] = {
              value: url,
              confidence: isFromGrounding ? 0.7 : 0.3,
              source: "grounding",
              verified: isFromGrounding
            };
          } else {
            results[field] = {
              value,
              confidence: 0.6,
              source: "grounding",
              verified: false
            };
          }
        }
      }
    } catch (error) {
    }
    return results;
  }
  async isAvailable(_tenantId) {
    return !!this.geminiClient;
  }
  estimateCost() {
    return 2e-3;
  }
  /**
   * Verifica si una URL aparece realmente en los resultados de grounding.
   * Esto evita que Gemini invente URLs que no vienen de Google.
   */
  urlExistsInGrounding(url, groundingMetadata) {
    if (!groundingMetadata) return false;
    const metadata = groundingMetadata;
    const chunks = metadata?.groundingChunks || [];
    const urlDomain = this.extractDomain(url);
    for (const chunk of chunks) {
      const chunkUrl = chunk?.web?.uri || "";
      if (chunkUrl.includes("linkedin.com") && chunkUrl.includes(urlDomain)) {
        return true;
      }
    }
    return false;
  }
  extractDomain(url) {
    try {
      return new URL(url).pathname;
    } catch {
      return url;
    }
  }
};

// src/enrichment/waterfall/field-routing.ts
var apolloConfig = (priority) => ({
  provider: "apollo",
  enabled: true,
  priority,
  costPerCall: 0.01,
  maxRetries: 1,
  timeoutMs: 1e4
});
var pdlConfig = (priority) => ({
  provider: "pdl",
  enabled: true,
  priority,
  costPerCall: 0.03,
  maxRetries: 1,
  timeoutMs: 1e4
});
var groundingConfig = (priority) => ({
  provider: "grounding",
  enabled: true,
  priority,
  costPerCall: 2e-3,
  maxRetries: 2,
  timeoutMs: 3e4
});
var DEFAULT_FIELD_ROUTING = {
  fieldRoutes: {
    // ── CONTACT DATA (APIs verificadas primero) ──────────────
    /** LinkedIn URL: Apollo > PDL > Grounding (último recurso) */
    linkedin_url: [
      apolloConfig(1),
      pdlConfig(2),
      groundingConfig(3)
    ],
    /** Job Title: Apollo > PDL > Grounding */
    job_title: [
      apolloConfig(1),
      pdlConfig(2),
      groundingConfig(3)
    ],
    /** Email: Apollo (verified emails) > PDL */
    email: [
      apolloConfig(1),
      pdlConfig(2)
      // NO grounding — Gemini no puede verificar emails
    ],
    /** Phone: Apollo > PDL */
    phone: [
      apolloConfig(1),
      pdlConfig(2)
    ],
    /** Seniority: Apollo > PDL */
    seniority: [
      apolloConfig(1),
      pdlConfig(2)
    ],
    /** Department: Apollo > PDL */
    department: [
      apolloConfig(1),
      pdlConfig(2)
    ],
    /** Headline: Apollo > PDL > Grounding */
    headline: [
      apolloConfig(1),
      pdlConfig(2),
      groundingConfig(3)
    ],
    /** Location: Apollo > PDL > Grounding */
    location: [
      apolloConfig(1),
      pdlConfig(2),
      groundingConfig(3)
    ],
    // ── COMPANY DATA (Grounding puede ser bueno aquí) ─────────
    /** Company name: Apollo > PDL > Grounding */
    company_name: [
      apolloConfig(1),
      pdlConfig(2),
      groundingConfig(3)
    ],
    /** Company domain: Apollo > PDL */
    company_domain: [
      apolloConfig(1),
      pdlConfig(2)
    ],
    /** Company industry: Apollo > PDL > Grounding */
    company_industry: [
      apolloConfig(1),
      pdlConfig(2),
      groundingConfig(3)
    ],
    /** Company size: Apollo > PDL > Grounding */
    company_size: [
      apolloConfig(1),
      pdlConfig(2),
      groundingConfig(3)
    ],
    /** Company LinkedIn: Apollo > PDL */
    company_linkedin: [
      apolloConfig(1),
      pdlConfig(2)
    ],
    /** Company description: Grounding es lo mejor */
    company_description: [
      groundingConfig(1)
    ],
    // ── PDL-EXCLUSIVE DATA ──────────────────────────────────
    /** Skills: Solo PDL tiene skills */
    skills: [
      pdlConfig(1)
    ],
    /** Salary range: Solo PDL (inferred) */
    salary_range: [
      pdlConfig(1)
    ],
    /** Years experience: Solo PDL */
    years_experience: [
      pdlConfig(1)
    ],
    /** Summary/Bio: PDL > Grounding */
    summary: [
      pdlConfig(1),
      groundingConfig(2)
    ]
  },
  /** Fallback para campos no configurados */
  defaultRoute: [
    groundingConfig(1)
  ]
};
function getProvidersForField(fieldName, routing = DEFAULT_FIELD_ROUTING) {
  const normalized = normalizeFieldName(fieldName);
  const route = routing.fieldRoutes[normalized];
  if (route) {
    return route.filter((p) => p.enabled).sort((a, b) => a.priority - b.priority);
  }
  return routing.defaultRoute.filter((p) => p.enabled).sort((a, b) => a.priority - b.priority);
}
function normalizeFieldName(name) {
  const lower = name.toLowerCase().replace(/\s+/g, "_");
  const aliases = {
    "url_del_perfil_de_linkedin": "linkedin_url",
    "linkedin_profile_url": "linkedin_url",
    "linkedin_profile": "linkedin_url",
    "perfil_linkedin": "linkedin_url",
    "titulo_del_puesto_de_trabajo": "job_title",
    "titulo_del_puesto": "job_title",
    "puesto_de_trabajo": "job_title",
    "titulo_puesto": "job_title",
    "cargo": "job_title",
    "title": "job_title",
    "correo_electronico": "email",
    "correo": "email",
    "telefono": "phone",
    "telephone": "phone",
    "ubicacion": "location",
    "ciudad": "location",
    "empresa": "company_name",
    "nombre_empresa": "company_name",
    "industria": "company_industry",
    "sector": "company_industry",
    "tamano_empresa": "company_size",
    "num_empleados": "company_size",
    "employees": "company_size",
    "descripcion_empresa": "company_description",
    "habilidades": "skills",
    "experiencia": "years_experience",
    "salario": "salary_range",
    "resumen": "summary",
    "bio": "summary"
  };
  return aliases[lower] || lower;
}

// src/enrichment/waterfall/waterfall.engine.ts
import { Logger as Logger24 } from "@boosting/core";
var logger18 = Logger24.getLogger("WaterfallEngine");
var WaterfallEngine = class {
  providers;
  options;
  constructor(providers, options) {
    this.providers = /* @__PURE__ */ new Map();
    for (const p of providers) {
      this.providers.set(p.name, p);
    }
    this.options = {
      routing: options?.routing || DEFAULT_FIELD_ROUTING,
      logger: options?.logger || {
        info: (...args) => logger18.info(String(args[0]), args[1]),
        error: (...args) => logger18.error(String(args[0]), args[1]),
        warn: (...args) => logger18.warn?.(String(args[0]), args[1])
      },
      debug: options?.debug ?? false,
      batchProviderCalls: options?.batchProviderCalls ?? true
    };
  }
  /**
   * Enriquecer un contacto usando el patrón waterfall.
   *
   * @param input - Datos del contacto
   * @param requestedFields - Campos a enriquecer (ej: ['linkedin_url', 'job_title'])
   * @param tenantId - ID del tenant (para verificar disponibilidad de proveedores)
   */
  async enrichContact(input, requestedFields, tenantId) {
    const startTime = Date.now();
    const resolvedFields = {};
    const waterfall = {};
    const providersUsed = /* @__PURE__ */ new Set();
    for (const field of requestedFields) {
      waterfall[field] = { tried: [], resolvedBy: null, errors: {} };
    }
    if (this.options.batchProviderCalls) {
      await this.enrichBatched(input, requestedFields, tenantId, resolvedFields, waterfall, providersUsed);
    } else {
      await this.enrichPerField(input, requestedFields, tenantId, resolvedFields, waterfall, providersUsed);
    }
    const executionTimeMs = Date.now() - startTime;
    const totalCost = this.calculateCost(providersUsed);
    this.options.logger?.info?.("Waterfall enrichment completed", {
      fields: requestedFields.length,
      resolved: Object.keys(resolvedFields).filter((f) => resolvedFields[f]?.value !== null).length,
      providers: [...providersUsed],
      cost: totalCost,
      timeMs: executionTimeMs
    });
    return {
      fields: resolvedFields,
      providersUsed: [...providersUsed],
      totalCost,
      executionTimeMs,
      waterfall
    };
  }
  // ============================================
  // BATCHED APPROACH (RECOMMENDED)
  // ============================================
  /**
   * Optimized: Groups fields by provider priority order,
   * calls each provider ONCE, and fills in resolved fields.
   */
  async enrichBatched(input, requestedFields, tenantId, resolvedFields, waterfall, providersUsed) {
    const providerOrder = this.buildProviderOrder(requestedFields);
    const providerCache = /* @__PURE__ */ new Map();
    for (const providerName of providerOrder) {
      const unresolvedFields = requestedFields.filter((field) => {
        if (resolvedFields[field]?.value !== null && resolvedFields[field]?.value !== void 0) return false;
        const providers = getProvidersForField(field, this.options.routing);
        return providers.some((p) => p.provider === providerName);
      });
      if (unresolvedFields.length === 0) continue;
      const provider = this.providers.get(providerName);
      if (!provider) continue;
      try {
        const available = await provider.isAvailable(tenantId);
        if (!available) {
          if (this.options.debug) {
            this.options.logger?.info?.(`Provider ${providerName} not available, skipping`);
          }
          continue;
        }
      } catch {
        continue;
      }
      let providerResults;
      if (providerCache.has(providerName)) {
        providerResults = providerCache.get(providerName);
      } else {
        try {
          providersUsed.add(providerName);
          providerResults = await provider.enrich(input);
          providerCache.set(providerName, providerResults);
          if (this.options.debug) {
            this.options.logger?.info?.(`Provider ${providerName} returned`, {
              fields: Object.keys(providerResults)
            });
          }
        } catch (error) {
          const errMsg = error instanceof Error ? error.message : String(error);
          this.options.logger?.error?.(`Provider ${providerName} failed`, { error: errMsg });
          for (const field of unresolvedFields) {
            waterfall[field].tried.push(providerName);
            if (!waterfall[field].errors) waterfall[field].errors = {};
            waterfall[field].errors[providerName] = errMsg;
          }
          continue;
        }
      }
      for (const field of unresolvedFields) {
        waterfall[field].tried.push(providerName);
        const result = providerResults[field];
        if (result && result.value !== null && result.value !== void 0) {
          resolvedFields[field] = result;
          waterfall[field].resolvedBy = providerName;
        }
      }
    }
    for (const field of requestedFields) {
      if (!resolvedFields[field]) {
        resolvedFields[field] = {
          value: null,
          confidence: 0,
          source: "grounding",
          // default source label
          verified: false
        };
      }
    }
  }
  // ============================================
  // PER-FIELD APPROACH (SIMPLER, SLOWER)
  // ============================================
  /**
   * Simple: For each field, try providers in order until resolved.
   */
  async enrichPerField(input, requestedFields, tenantId, resolvedFields, waterfall, providersUsed) {
    const providerCache = /* @__PURE__ */ new Map();
    for (const field of requestedFields) {
      const providerConfigs = getProvidersForField(field, this.options.routing);
      for (const config of providerConfigs) {
        const provider = this.providers.get(config.provider);
        if (!provider) continue;
        try {
          const available = await provider.isAvailable(tenantId);
          if (!available) continue;
        } catch {
          continue;
        }
        waterfall[field].tried.push(config.provider);
        providersUsed.add(config.provider);
        try {
          let results;
          if (providerCache.has(config.provider)) {
            results = providerCache.get(config.provider);
          } else {
            results = await provider.enrich(input);
            providerCache.set(config.provider, results);
          }
          const result = results[field];
          if (result && result.value !== null && result.value !== void 0) {
            resolvedFields[field] = result;
            waterfall[field].resolvedBy = config.provider;
            break;
          }
        } catch (error) {
          const errMsg = error instanceof Error ? error.message : String(error);
          if (!waterfall[field].errors) waterfall[field].errors = {};
          waterfall[field].errors[config.provider] = errMsg;
        }
      }
      if (!resolvedFields[field]) {
        resolvedFields[field] = {
          value: null,
          confidence: 0,
          source: "grounding",
          verified: false
        };
      }
    }
  }
  // ============================================
  // HELPERS
  // ============================================
  /**
   * Build unique ordered list of providers across all requested fields.
   * Priority: providers that appear first (priority=1) in most fields come first.
   */
  buildProviderOrder(requestedFields) {
    const providerPriorities = /* @__PURE__ */ new Map();
    for (const field of requestedFields) {
      const providers = getProvidersForField(field, this.options.routing);
      for (const config of providers) {
        const current = providerPriorities.get(config.provider) ?? Infinity;
        providerPriorities.set(config.provider, Math.min(current, config.priority));
      }
    }
    return [...providerPriorities.entries()].sort((a, b) => a[1] - b[1]).map(([name]) => name);
  }
  /**
   * Calculate total cost based on providers used.
   */
  calculateCost(providersUsed) {
    let total = 0;
    for (const name of providersUsed) {
      const provider = this.providers.get(name);
      if (provider) {
        total += provider.estimateCost();
      }
    }
    return total;
  }
};
export {
  AVAILABLE_MODELS,
  AgentsConfigService,
  ApolloProvider,
  BatchClassificationResultSchema,
  CENTRAL_AGENT_TYPES,
  CHANNEL_LIMITS,
  CONTENT_FORMAT_LABELS,
  CONTENT_HUB_AGENTS_WITH_VARIANTS,
  CONTENT_HUB_AGENT_TYPES,
  ChatEngine,
  ClassifierConfigSchema,
  ClassifyProcurementRequestSchema,
  ClassifyProcurementResponseSchema,
  ContentAgent,
  CostTracker,
  CrisisDetector,
  DEFAULT_AGENTS_CONFIG,
  DEFAULT_AI_AGENTS,
  DEFAULT_ARTICLE_IMAGE_AGENT,
  DEFAULT_CRISIS_RESOURCES,
  DEFAULT_CRITICAL_KEYWORDS,
  DEFAULT_DERIVATION_TEMPLATES,
  DEFAULT_DOCUMENT_PROCESSING_CONFIG,
  DEFAULT_ELEVENLABS_SETTINGS,
  DEFAULT_ELEVENLABS_VOICES,
  DEFAULT_ENRICHMENT_AGENT,
  DEFAULT_FIELD_ROUTING,
  DEFAULT_GEMINI_MODEL,
  DEFAULT_GOOGLE_VOICES,
  DEFAULT_IMAGE_GEN_CONFIG,
  DEFAULT_INFOGRAPHIC_AGENT,
  DEFAULT_LOGO_CONFIG,
  DEFAULT_MASTER_TEMPLATES,
  DEFAULT_MEDIA_CLASSIFICATION_PROMPTS,
  DEFAULT_MODEL_CONFIGS,
  DEFAULT_MUSIC_MOODS,
  DEFAULT_OCR_CONFIG,
  DEFAULT_PLACEMENT_AGENT,
  DEFAULT_PRONUNCIATION_DICTIONARY,
  DEFAULT_RESEARCH_FOCUS_AREAS,
  DEFAULT_SITUATION_KEYWORDS,
  DEFAULT_STORYBOARD_AGENT,
  DEFAULT_STYLE_AGENTS,
  DEFAULT_TABLE_REFINEMENT_CONFIG,
  DEFAULT_VIDEO_GENERATION_CONFIG,
  DEFAULT_VIDEO_NARRATION_AGENT,
  DEFAULT_VIDEO_SETTINGS,
  DEFAULT_VOICE_PRESETS,
  DeepResearchClient,
  DeepResearchService,
  DocumentProcessingService,
  ELEVENLABS_PRICING,
  EMBEDDING_DIMENSIONS,
  EMBEDDING_MODELS,
  EMBEDDING_PRICING,
  EmbeddingsService,
  EscalationHandler,
  GEMINI3_MODELS,
  GEMINI_ERROR_CODES,
  GEMINI_PRICING,
  GeminiClient,
  GeminiService,
  GroundingProvider,
  IMAGEN_MODELS,
  IMAGE_GENERATION_CONFIG,
  IMAGE_MODEL_CONFIG,
  ImageGeneratorService,
  KLING_MODELS,
  KLING_PRICING,
  KlingClient,
  LEGACY_MODELS,
  MAX_BATCH_SIZE,
  MAX_EMBEDDING_CHARS,
  MODEL_LABELS,
  OCRService,
  OCR_ERROR_MESSAGES,
  PDLProvider,
  PLACEMENT_CONFIG,
  PROCESSING_PRESETS,
  PROVIDER_ICONS,
  PROVIDER_LABELS,
  ProcurementClassificationSchema,
  PromptVariableSchema,
  RASK_PRICING,
  RUNWAY_MODELS,
  RUNWAY_PRICING,
  ResearchAgent,
  RunwayClient,
  SEEDANCE_MODELS,
  SEEDANCE_PRICING,
  SEOOrchestratorAgent,
  SITUATION_LABELS,
  SORA_MODELS,
  SORA_PRICING,
  STYLE_PROMPTS,
  SoraClient,
  StoredPromptSchema,
  StrategyAgent,
  TASK_CATEGORIES,
  TTS_PRICING,
  TableRefinementService,
  UnifiedTemplateService,
  VARIANT_LABELS,
  VEO_3_FAST_PRICE_PER_SECOND,
  VEO_3_PRICE_PER_SECOND,
  VEO_EXTERNAL_PRICING,
  VEO_MODELS,
  VEO_PRICING,
  VIDEO_GENERATION_LOCATION,
  VEO_MODELS3 as VIDEO_MODELS,
  VIDEO_MODEL_PRICING,
  VIDEO_PROVIDERS,
  VISION_API_PRICING,
  VISION_PRICING,
  VideoGeneratorService,
  WHISPER_PRICING,
  WaterfallEngine,
  YOUTUBE_API_PRICING,
  applyMediaPromptTemplate,
  applyNarrationEdits,
  applyPronunciationDictionary,
  buildEnrichmentContext,
  calculateExternalAPICost,
  callImagenAPI,
  chunkTextBySentence,
  cloneSchema,
  compareEnrichments,
  composeVideo,
  cosineSimilarity,
  createAgentsConfigService,
  createChatEngine,
  createCostTracker,
  createCrisisDetector,
  createDeepResearchClient,
  createDeepResearchService,
  createDocumentProcessingService,
  createEmbeddingsService,
  createEmptyProperty,
  createEmptySchema,
  createEscalationHandler,
  createGeminiClient,
  createGeminiService,
  createImageGenerator,
  createKlingClient,
  createOCRService,
  createRunwayClient,
  createSoraClient,
  createTableRefinementService,
  createUnifiedTemplateService,
  createVideoGenerator,
  editImage,
  editImageWithCustomPrompt,
  editImageWithGemini,
  enhancePromptWithGemini,
  estimateAudioDuration,
  estimateCompositionCost,
  estimateProjectVideoCost,
  estimateSceneCost,
  estimateTTSCost,
  estimateVideoCostByModel,
  estimateVideoGenerationCost,
  executeEnrichment,
  executeEnrichmentBatch,
  extractAgentsConfig,
  extractFromContext,
  extractLogoConfig,
  fetchReferenceImages,
  formatContextForPrompt,
  formatterRegistry,
  generateImage,
  generateImageQuick,
  generateNarration,
  generateNarrationBatch,
  generateNarrationWithTimestamps,
  generateSceneVideo,
  generateSchemaFromDescription,
  generateSlideshowVideo,
  generateVideoFromImage,
  generateVideoFromText,
  generateVideoQuick,
  getAllDefaultDerivationTemplates,
  getAllDefaultMasterTemplates,
  getAllModelConfigs,
  getAspectRatioForPlacement,
  getAvailableTaskTypes,
  getAvailableVideoProviders,
  getChannelDerivationTemplates,
  getChannelLimits,
  getClassificationJsonSchema,
  getDefaultMasterTemplate,
  getDefaultMediaClassificationPrompts,
  getDerivationTemplate,
  getDynamicModelConfig,
  getDynamicModelForTask,
  getFallbackModel,
  getFunnelFocusText,
  getMediaClassificationPromptsPath,
  getModelConfig,
  getModelForTask,
  getPlacementForIndex,
  getPronunciationDictionary,
  getProvidersForField,
  getVideoProviderForUseCase,
  getVideoProvidersByTier,
  getVoiceSettingsForStyle,
  groupSourcesByDomain,
  handleGeminiError,
  hasEnrichmentData,
  initializeModelConfigSystem,
  invalidateModelConfigCache,
  isGemini3Model,
  isModernGeminiModel,
  loadVideoGenerationConfig,
  logAIUsage,
  logEmbeddingUsage,
  mergeWithDefaultPrompts,
  normalizeModelName,
  propertyTypeRegistry,
  registerFormatter,
  registerPropertyType,
  registerTransform,
  safelyParseJsonResponse,
  schemaToJsonSchema,
  suggestDomainProperties,
  taskRequiresGrounding,
  trackAIUsage,
  trackExternalAPIUsage,
  transformRegistry,
  validateSchema
};
